/**
 * FractionWidgets.js — deterministic fraction visuals + interactive widgets
 * for the /learn "stage that breathes" lesson player (Task #251).
 *
 * Two exports:
 *
 *   fractionSVG(spec) -> string
 *     A crisp, static SVG for a TEACHING board (board.kind === "visual").
 *     spec = { type:"pie"|"bar"|"numberline", den, num, label?, color? }
 *
 *   renderFractionWidget(mountEl, spec, { onInteract, onComplete }) -> widget
 *     An INTERACTIVE practice widget. Returns the SAME shape the templated
 *     problem widgets do — { check(): {correct}, reveal(), isComplete() } — so
 *     it drops straight into TutorStageController._renderProblemCard's wiring
 *     (Check / Skip / Why / Here's-the-answer / assess) with zero special-casing.
 *     spec.type = "shade" | "build" | "numberline" | "compare".
 *
 * Everything is graded CLIENT-SIDE and DETERMINISTICALLY (no network, no AI) —
 * fractions are exact arithmetic, so this is safe and instant. This module owns
 * NO 3D; it only builds DOM and plays lightweight lesson UI tap cues via
 * LessonSound. The sacred /lab audio/A2F (TTS) path is untouched.
 */

import lessonSound from "./LessonSound.js";
// THE grader table (Task #485) — new widgets' practice check() delegates to
// the same grading brain the server executes. graders.mjs never imports this
// file back (hard cycle ban), so the dependency is strictly one-way.
import { gradeWidget } from "./graders.mjs";
import {
  gradeTiles, normCell, reduceFrac,
  compareWinner, isRectangleSelection, _compareOptions as compareOptions,
} from "./FractionGrading.js";

/* ============================ math helpers ============================ */

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { [a, b] = [b, a % b]; }
  return a || 1;
}

// Parse "3/4", "3 / 4", or {num,den} -> {num, den}. Whole numbers -> /1.
function parseFraction(v) {
  if (v && typeof v === "object" && v.den != null) {
    return { num: Number(v.num) || 0, den: Number(v.den) || 1 };
  }
  const s = String(v == null ? "" : v).trim();
  const m = s.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (m) return { num: parseInt(m[1], 10), den: parseInt(m[2], 10) || 1 };
  const n = parseInt(s, 10);
  return { num: isNaN(n) ? 0 : n, den: 1 };
}

function valueEqual(a, b) {
  return a.num * b.den === b.num * a.den;
}

const SVGNS = "http://www.w3.org/2000/svg";
function svgEl(name, attrs) {
  const el = document.createElementNS(SVGNS, name);
  if (attrs) for (const k in attrs) el.setAttribute(k, attrs[k]);
  return el;
}

function polar(cx, cy, r, deg) {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

// Pie sector path from startDeg to endDeg (0deg = top, clockwise).
function sectorPath(cx, cy, r, startDeg, endDeg) {
  const [x0, y0] = polar(cx, cy, r, startDeg);
  const [x1, y1] = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M${cx.toFixed(2)},${cy.toFixed(2)} L${x0.toFixed(2)},${y0.toFixed(2)} ` +
    `A${r},${r} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)} Z`;
}

/* ============================ static visuals ============================ */

// Build the segment <path>/<rect> nodes for a pie or bar with `den` parts;
// `filled` is a Set or predicate(i)->bool deciding which are shaded.
function pieSegments(den, filledFn) {
  const cx = 60, cy = 60, r = 54;
  const step = 360 / den;
  const out = [];
  for (let i = 0; i < den; i++) {
    const p = svgEl("path", {
      d: sectorPath(cx, cy, r, i * step, (i + 1) * step),
      class: "ts-seg" + (filledFn(i) ? " on" : ""),
      "data-idx": String(i),
    });
    out.push(p);
  }
  return out;
}

function barSegments(den, filledFn) {
  const W = 240, H = 64, gap = 3;
  const segW = (W - gap * (den - 1)) / den;
  const out = [];
  for (let i = 0; i < den; i++) {
    const rect = svgEl("rect", {
      x: (i * (segW + gap)).toFixed(2),
      y: "0",
      width: segW.toFixed(2),
      height: String(H),
      rx: "6",
      class: "ts-seg" + (filledFn(i) ? " on" : ""),
      "data-idx": String(i),
    });
    out.push(rect);
  }
  return out;
}

function buildShapeSVG(shape, den, filledFn) {
  if (shape === "bar") {
    const svg = svgEl("svg", { viewBox: "0 0 240 64", class: "ts-frac-svg ts-frac-bar" });
    barSegments(den, filledFn).forEach((s) => svg.appendChild(s));
    return svg;
  }
  const svg = svgEl("svg", { viewBox: "0 0 120 120", class: "ts-frac-svg ts-frac-pie" });
  pieSegments(den, filledFn).forEach((s) => svg.appendChild(s));
  return svg;
}

function numberLineSVG(den, num, opts = {}) {
  const W = 280, H = 70, padX = 22, y = 38;
  const x0 = padX, x1 = W - padX, span = x1 - x0;
  const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, class: "ts-frac-svg ts-frac-nl" });
  svg.appendChild(svgEl("line", { x1: x0, y1: y, x2: x1, y2: y, class: "ts-nl-axis" }));
  for (let i = 0; i <= den; i++) {
    const x = x0 + (span * i) / den;
    const major = i === 0 || i === den;
    svg.appendChild(svgEl("line", {
      x1: x, y1: y - (major ? 11 : 7), x2: x, y2: y + (major ? 11 : 7),
      class: "ts-nl-tick" + (major ? " major" : ""),
    }));
  }
  // end labels 0 and 1
  const l0 = svgEl("text", { x: x0, y: y + 26, class: "ts-nl-label" }); l0.textContent = "0";
  const l1 = svgEl("text", { x: x1, y: y + 26, class: "ts-nl-label" }); l1.textContent = "1";
  svg.appendChild(l0); svg.appendChild(l1);
  if (num != null && opts.marker !== false) {
    const x = x0 + (span * num) / den;
    svg.appendChild(svgEl("line", { x1: x, y1: y - 16, x2: x, y2: y + 16, class: "ts-nl-mark" }));
    svg.appendChild(svgEl("circle", { cx: x, cy: y, r: 6, class: "ts-nl-dot" }));
  }
  return svg;
}

/* ---------- area-based region layouts (Brilliant tap-to-shade) ----------
   Each layout tiles a unit shape into regions; `points` are in a 0..100
   viewBox and `area` is the region's fraction of the whole (areas sum to 1).
   The learner shades any set of regions whose AREA sums to the target. */
const REGION_LAYOUTS = {
  "square-halves": [
    { points: "0,0 100,0 100,50 0,50", area: 1 / 2 },
    { points: "0,50 100,50 100,100 0,100", area: 1 / 2 },
  ],
  "square-half-quarters": [
    { points: "0,0 100,0 100,50 0,50", area: 1 / 2 },
    { points: "0,50 50,50 50,100 0,100", area: 1 / 4 },
    { points: "50,50 100,50 100,100 50,100", area: 1 / 4 },
  ],
  "square-3band": [
    { points: "0,0 100,0 100,33.34 0,33.34", area: 1 / 3 },
    { points: "0,33.34 50,33.34 50,66.67 0,66.67", area: 1 / 6 },
    { points: "50,33.34 100,33.34 100,66.67 50,66.67", area: 1 / 6 },
    { points: "0,66.67 100,66.67 100,100 0,100", area: 1 / 3 },
  ],
  "square-cols-6": [
    { points: "0,0 25,0 25,100 0,100", area: 1 / 4 },
    { points: "25,0 50,0 50,50 25,50", area: 1 / 8 },
    { points: "25,50 50,50 50,100 25,100", area: 1 / 8 },
    { points: "50,0 75,0 75,50 50,50", area: 1 / 8 },
    { points: "50,50 75,50 75,100 50,100", area: 1 / 8 },
    { points: "75,0 100,0 100,100 75,100", area: 1 / 4 },
  ],
  "square-rows-5": [
    { points: "0,0 100,0 100,20 0,20", area: 1 / 5 },
    { points: "0,20 50,20 50,40 0,40", area: 1 / 10 },
    { points: "50,20 100,20 100,40 50,40", area: 1 / 10 },
    { points: "0,40 100,40 100,60 0,60", area: 1 / 5 },
    { points: "0,60 50,60 50,80 0,80", area: 1 / 10 },
    { points: "50,60 100,60 100,80 50,80", area: 1 / 10 },
    { points: "0,80 100,80 100,100 0,100", area: 1 / 5 },
  ],
  "triangle-4": [
    { points: "50,6 29,50 71,50", area: 1 / 4 },
    { points: "8,94 29,50 50,94", area: 1 / 4 },
    { points: "29,50 71,50 50,94", area: 1 / 4 },
    { points: "71,50 92,94 50,94", area: 1 / 4 },
  ],
};

/* ---------- parametric region generators (Task #268) -------------------
   Authoring a tap-to-shade shape by hand-listing polygon points is tedious and
   error-prone, so common families are generated on demand from a short layout
   string. Every generator tiles the 0..100 viewBox into pieces whose `area`
   fields sum to exactly 1 (equal-area unless the family is explicitly unequal),
   so grading-by-area stays exact. Named REGION_LAYOUTS above still win for
   bespoke shapes; one-off UNEQUAL shapes are authored as explicit `regions`. */

const pt = (x, y) => `${(+x).toFixed(2)},${(+y).toFixed(2)}`;
const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
const polyPts = (verts) => verts.map((v) => pt(v[0], v[1])).join(" ");

// R rows × C cols of equal rectangles.
function genGrid(rows, cols) {
  const out = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = (c * 100) / cols, x1 = ((c + 1) * 100) / cols;
      const y0 = (r * 100) / rows, y1 = ((r + 1) * 100) / rows;
      out.push({ points: polyPts([[x0, y0], [x1, y0], [x1, y1], [x0, y1]]), area: 1 / (rows * cols) });
    }
  }
  return out;
}

// R×C grid, but every cell in the LAST column is split into top/bottom halves —
// an intentionally UNEQUAL partition (most cells 1/(R·C), the split ones each
// 1/(2·R·C)) whose areas still sum to 1. For teaching "you can only name a
// fraction when the pieces are EQUAL".
function genGridSplit(rows, cols) {
  const out = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = (c * 100) / cols, x1 = ((c + 1) * 100) / cols;
      const y0 = (r * 100) / rows, y1 = ((r + 1) * 100) / rows;
      if (c === cols - 1) {
        const ym = (y0 + y1) / 2, area = 1 / (2 * rows * cols);
        out.push({ points: polyPts([[x0, y0], [x1, y0], [x1, ym], [x0, ym]]), area });
        out.push({ points: polyPts([[x0, ym], [x1, ym], [x1, y1], [x0, y1]]), area });
      } else {
        out.push({ points: polyPts([[x0, y0], [x1, y0], [x1, y1], [x0, y1]]), area: 1 / (rows * cols) });
      }
    }
  }
  return out;
}

// R×C grid where every cell is cut by a diagonal into two triangles. `alt`
// flips the diagonal on a checkerboard for a pinwheel look.
function genGridDiag(rows, cols, alt) {
  const out = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = (c * 100) / cols, x1 = ((c + 1) * 100) / cols;
      const y0 = (r * 100) / rows, y1 = ((r + 1) * 100) / rows;
      const A = [x0, y0], B = [x1, y0], C = [x1, y1], D = [x0, y1];
      const area = 1 / (2 * rows * cols);
      if (alt && (r + c) % 2) {
        out.push({ points: polyPts([A, B, D]), area });
        out.push({ points: polyPts([B, C, D]), area });
      } else {
        out.push({ points: polyPts([A, B, C]), area });
        out.push({ points: polyPts([A, C, D]), area });
      }
    }
  }
  return out;
}

const TRI = [[50, 6], [8, 94], [92, 94]]; // shared outer triangle

// Recursive medial subdivision: depth 1 → 4 triangles, depth 2 → 16, ...
function medialTris(A, B, C, depth) {
  if (depth <= 0) return [[A, B, C]];
  const Mab = mid(A, B), Mbc = mid(B, C), Mca = mid(C, A);
  return [].concat(
    medialTris(A, Mab, Mca, depth - 1),
    medialTris(Mab, B, Mbc, depth - 1),
    medialTris(Mca, Mbc, C, depth - 1),
    medialTris(Mab, Mbc, Mca, depth - 1),
  );
}
function genTriangleMedial(depth) {
  const tris = medialTris(TRI[0], TRI[1], TRI[2], depth);
  const area = 1 / Math.pow(4, depth);
  return tris.map((t) => ({ points: polyPts(t), area }));
}

// The three medians split a triangle into 6 equal-area triangles.
function genTriangleMedians() {
  const [A, B, C] = TRI;
  const G = [(A[0] + B[0] + C[0]) / 3, (A[1] + B[1] + C[1]) / 3];
  const Mab = mid(A, B), Mbc = mid(B, C), Mca = mid(C, A);
  const tris = [
    [A, Mab, G], [Mab, B, G], [B, Mbc, G],
    [Mbc, C, G], [C, Mca, G], [Mca, A, G],
  ];
  return tris.map((t) => ({ points: polyPts(t), area: 1 / 6 }));
}

// Sierpinski-style UNEQUAL triangle: the top medial sub-triangle is split into
// 4, the other three are left whole → 3×(1/4) + 4×(1/16) pieces (sums to 1).
function genTriangleSierpinski() {
  const [A, B, C] = TRI;
  const Mab = mid(A, B), Mbc = mid(B, C), Mca = mid(C, A);
  const out = [
    { points: polyPts([Mab, B, Mbc]), area: 1 / 4 },   // bottom-left whole
    { points: polyPts([Mca, Mbc, C]), area: 1 / 4 },   // bottom-right whole
    { points: polyPts([Mab, Mbc, Mca]), area: 1 / 4 }, // center whole
  ];
  medialTris(A, Mab, Mca, 1).forEach((t) => out.push({ points: polyPts(t), area: 1 / 16 }));
  return out;
}

function hexVerts() {
  const cx = 50, cy = 50, R = 46;
  const v = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90);
    v.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
  }
  return v;
}
// Regular hexagon split into n equal-area triangles from the center.
// n ∈ {6,12,18}: each of the 6 sextants is divided into n/6 equal slivers by
// splitting its outer edge into equal segments (equal base ⇒ equal area).
function genHex(n) {
  const C = [50, 50], V = hexVerts(), per = n / 6, area = 1 / n, out = [];
  for (let i = 0; i < 6; i++) {
    const a = V[i], b = V[(i + 1) % 6];
    for (let k = 0; k < per; k++) {
      const p = lerp(a, b, k / per), q = lerp(a, b, (k + 1) / per);
      out.push({ points: polyPts([C, p, q]), area });
    }
  }
  return out;
}

// Square cut into n equal triangles around the center (n even). Each side is
// split into n/4 equal segments; triangles run center→segment.
function genPinwheel(n) {
  const C = [50, 50], area = 1 / n, out = [];
  const corners = [[0, 0], [100, 0], [100, 100], [0, 100]];
  const per = n / 4;
  for (let s = 0; s < 4; s++) {
    const a = corners[s], b = corners[(s + 1) % 4];
    for (let k = 0; k < per; k++) {
      const p = lerp(a, b, k / per), q = lerp(a, b, (k + 1) / per);
      out.push({ points: polyPts([C, p, q]), area });
    }
  }
  return out;
}

const _paramCache = {};
// Parse a layout string like "grid-3x4", "triangle-16", "hex-12", "pinwheel-8".
function parametricRegions(name) {
  if (typeof name !== "string") return null;
  if (_paramCache[name]) return _paramCache[name];
  let out = null;
  let m;
  if ((m = name.match(/^grid-(\d+)x(\d+)$/))) {
    out = genGrid(+m[1], +m[2]);
  } else if ((m = name.match(/^grid-split-(\d+)x(\d+)$/))) {
    out = genGridSplit(+m[1], +m[2]);
  } else if ((m = name.match(/^grid-?diag-(\d+)x(\d+)(-alt)?$/))) {
    out = genGridDiag(+m[1], +m[2], !!m[3]);
  } else if ((m = name.match(/^square-diagonals-(\d+)x(\d+)(-alt)?$/))) {
    out = genGridDiag(+m[1], +m[2], !!m[3]);
  } else if (name === "triangle-medians" || name === "triangle-6") {
    out = genTriangleMedians();
  } else if (name === "triangle-sierpinski" || name === "triangle-sierpinski-6") {
    out = genTriangleSierpinski();
  } else if ((m = name.match(/^triangle-(\d+)$/))) {
    const n = +m[1];
    if (n === 4) out = genTriangleMedial(1);
    else if (n === 16) out = genTriangleMedial(2);
    else if (n === 64) out = genTriangleMedial(3);
  } else if ((m = name.match(/^hex(?:agon)?-(\d+)$/))) {
    const n = +m[1];
    if (n === 6 || n === 12 || n === 18) out = genHex(n);
  } else if ((m = name.match(/^pinwheel-(\d+)$/))) {
    const n = +m[1];
    if (n >= 4 && n % 4 === 0) out = genPinwheel(n);
  }
  if (out) _paramCache[name] = out;
  return out;
}

// Show a value 0..1 as a small exact fraction when possible, else a percent.
// Cap covers every denominator the course counter needs (grids up to ~10×12,
// products like 1/50, 3/77) so the live readout stays a clean fraction.
function friendlyFraction(v) {
  const eps = 1e-6;
  for (let den = 1; den <= 120; den++) {
    const num = Math.round(v * den);
    if (Math.abs(num / den - v) < eps) {
      if (den === 1) return String(num);
      const r = reduceFrac(num, den);
      return r.num + "/" + r.den;
    }
  }
  return Math.round(v * 100) + "%";
}

// ── practice verdicts come from the ONE grader table ────────────────────────
// Task #485: every gradable builder DELEGATES its practice check() verdict to
// graders.mjs (the same module the server executes for skill-checks), so the
// browser's instant feedback and the server's authoritative verdict can never
// drift. The renderer owns UX and interaction constraints (completeness gates,
// rectangle rules); it never computes the math verdict itself. A grader
// exception means a malformed trusted spec or broken wiring — fail LOUD and
// refuse credit rather than silently minting a second-brain verdict.
function practiceVerdict(gradeSpecArg, value) {
  try {
    const v = gradeWidget(gradeSpecArg, value);
    if (!v) {
      console.error("[FractionWidgets] no grader entry for practice check:", gradeSpecArg && gradeSpecArg.type);
      return false;
    }
    return !!v.correct;
  } catch (err) {
    console.error("[FractionWidgets] practice grading failed:", gradeSpecArg && gradeSpecArg.type, err);
    return false;
  }
}

// The grader grades the GEOMETRY THE LEARNER SEES. A practice area spec may
// name only a `layout` (presentation) without baking `regions`/`den` (grading
// source); resolving presentation → areas is the renderer's job, so the
// delegated spec carries the resolved region areas. Authored keys always win —
// only gaps are filled. Exported for the parity harness
// (tests/learn/practice_grader_parity.mjs), which must grade the exact spec
// view the builders grade.
export function areaGradeSpec(spec, regions) {
  const out = Object.assign({}, spec);
  if (regions && regions.length) {
    if (!(Array.isArray(out.regions) && out.regions.length)) {
      out.regions = regions.map((r) => ({ area: r.area }));
    }
    if (out.target == null) out.target = { num: spec.num, den: spec.den };
  } else if (spec.type === "shade") {
    // uniform figure: practice always graded the shaded COUNT against the
    // clamped numerator — mirror that exactly when no target is authored.
    const den = Math.max(1, parseInt(spec.den, 10) || 1);
    if (out.target == null) {
      out.target = { num: Math.max(0, Math.min(den, parseInt(spec.num, 10) || 0)), den };
    }
  } else if (out.target == null) {
    out.target = { num: spec.num, den: spec.den };
  }
  return out;
}

// Resolve a region list from an explicit `regions` array, a named `layout`, or
// a parametric layout string (e.g. "grid-3x4", "hex-12"). Exported so the
// headless content validator (scripts/validate_learn_course.mjs) can check a
// shade target is actually reachable from the SAME region areas the widget uses.
export function regionsFor(spec) {
  if (spec && Array.isArray(spec.regions) && spec.regions.length) return spec.regions;
  if (spec && spec.layout) {
    if (REGION_LAYOUTS[spec.layout]) return REGION_LAYOUTS[spec.layout];
    const gen = parametricRegions(spec.layout);
    if (gen) return gen;
  }
  return null;
}

function regionShapeSVG(regions, filledFn) {
  const svg = svgEl("svg", { viewBox: "0 0 100 100", class: "ts-frac-svg ts-frac-region" });
  regions.forEach((r, i) => {
    svg.appendChild(svgEl("polygon", {
      points: r.points,
      class: "ts-region" + (filledFn(i) ? " on" : ""),
      "data-idx": String(i),
    }));
  });
  return svg;
}

// Smallest subset of region areas summing (within eps) to a target value 0..1.
// Used by reveal() — area shading has many right answers, so we show one clean
// minimal set. Region counts are small (<=8), so the brute scan is trivial.
function subsetForTarget(regions, target) {
  const n = regions.length, eps = 1e-6;
  let best = null;
  for (let mask = 1; mask < (1 << n); mask++) {
    let sum = 0, bits = 0;
    for (let i = 0; i < n; i++) if (mask & (1 << i)) { sum += regions[i].area; bits++; }
    if (Math.abs(sum - target) < eps && (!best || bits < best.bits)) best = { mask, bits };
  }
  return best ? best.mask : 0;
}

function targetValue(spec) {
  const t = parseFraction(spec.target != null ? spec.target : { num: spec.num, den: spec.den });
  return t.den ? t.num / t.den : 0;
}

// Operator glyph between two tiles terms. "eq" is the default so existing
// equivalent-equation widgets keep rendering "=".
function opSign(op) {
  return op === "add" ? "+" : op === "mul" ? "×" : "=";
}

// Turn a server-supplied reveal answer ("5/8", 0.625, {num,den}) into a 0..1
// value. Area skill-checks strip the target from the shipped beat, so on a miss
// the server returns the authored target for reveal() to reconstruct a subset.
function forcedValue(forced, fallback) {
  if (forced == null) return fallback;
  if (typeof forced === "number") return Number.isFinite(forced) ? forced : fallback;
  const f = parseFraction(forced);
  return f.den ? f.num / f.den : fallback;
}

/**
 * fractionSVG(spec) -> HTML string for a teaching board.
 */
export function fractionSVG(spec) {
  const s = spec || {};
  const regions = regionsFor(s);
  if (regions) {
    const shaded = new Set((s.shaded || []).map(Number));
    const wrap = document.createElement("div");
    wrap.className = "ts-visual";
    wrap.appendChild(regionShapeSVG(regions, (i) => shaded.has(i)));
    if (s.label) {
      const cap = document.createElement("div");
      cap.className = "ts-visual-cap";
      cap.textContent = s.label;
      wrap.appendChild(cap);
    }
    return wrap.outerHTML;
  }
  const den = Math.max(1, parseInt(s.den, 10) || 1);
  const num = Math.max(0, Math.min(den, parseInt(s.num, 10) || 0));
  const type = s.type || "pie";
  let node;
  if (type === "numberline") {
    node = numberLineSVG(den, num);
  } else {
    node = buildShapeSVG(type === "bar" ? "bar" : "pie", den, (i) => i < num);
  }
  const wrap = document.createElement("div");
  wrap.className = "ts-visual";
  wrap.appendChild(node);
  if (s.label) {
    const cap = document.createElement("div");
    cap.className = "ts-visual-cap";
    cap.textContent = s.label;
    wrap.appendChild(cap);
  }
  return wrap.outerHTML;
}

/* ============================ interactive widgets ============================ */

function makeFeedbackNode() {
  const fb = document.createElement("div");
  fb.className = "ts-fw-msg";
  return fb;
}

// Area-based tap-to-shade (Brilliant): shade any set of regions summing to the
// target AREA. Clean black-outlined regions fill blue on tap.
function buildShadeRegions(mountEl, spec, regions, cbs) {
  const tv = targetValue(spec);
  const eps = 1e-6;
  const state = new Set();

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-shade";
  const svg = regionShapeSVG(regions, () => false);
  stage.appendChild(svg);
  const shadedArea = () => {
    let s = 0; state.forEach((i) => { s += regions[i].area; }); return s;
  };
  // Live shaded-fraction readout (Brilliant "½ shaded" feedback as you tap). It
  // shows the learner's OWN running total — never a correctness verdict — so it
  // can't leak a skill-check answer.
  const count = document.createElement("div");
  count.className = "ts-fw-count";
  const renderCount = () => {
    count.textContent = state.size
      ? friendlyFraction(shadedArea()) + " shaded"
      : "tap to shade";
  };
  renderCount();
  stage.appendChild(count);
  mountEl.appendChild(stage);

  let locked = false;
  svg.querySelectorAll(".ts-region").forEach((poly, i) => {
    poly.classList.add("ts-tappable");
    poly.addEventListener("click", () => {
      if (locked) return;
      if (state.has(i)) { state.delete(i); poly.classList.remove("on"); lessonSound.play("tapOff"); }
      else { state.add(i); poly.classList.add("on"); lessonSound.play("tapOn"); }
      renderCount();
      if (cbs && cbs.onInteract) cbs.onInteract();
    });
  });

  return {
    isComplete: () => state.size > 0,
    // `value` = the chosen region indices, posted verbatim for server grading of
    // area skill-checks (the authored target is stripped from the shipped beat).
    // Practice verdict = the one grader table, fed the resolved region areas.
    check: () => ({
      correct: practiceVerdict(areaGradeSpec(spec, regions), Array.from(state).map(Number)),
      value: Array.from(state).map(Number),
    }),
    getValue: () => Array.from(state).map(Number),
    // Compact, human-readable summary so the tutor chat can see the learner's
    // actual work (value + target are 0..1 fractions of the whole).
    getState: () => {
      const v = shadedArea();
      return {
        value: Math.round(v * 1000) / 1000,
        target: Math.round(tv * 1000) / 1000,
        label: state.size
          ? "shaded " + state.size + " region" + (state.size === 1 ? "" : "s") +
            " (~" + Math.round(v * 100) + "% of the whole)"
          : "nothing shaded yet",
      };
    },
    reset: () => {
      if (locked) return;
      state.clear();
      svg.querySelectorAll(".ts-region").forEach((p) => p.classList.remove("on"));
      renderCount();
      if (cbs && cbs.onInteract) cbs.onInteract();
    },
    reveal: (forced) => {
      locked = true;
      state.clear();
      const mask = subsetForTarget(regions, forcedValue(forced, tv));
      svg.querySelectorAll(".ts-region").forEach((p, i) => {
        if (mask & (1 << i)) { p.classList.add("on"); state.add(i); }
        else p.classList.remove("on");
      });
      renderCount();
    },
  };
}

// "Tap the parts to shade <target>." Learner toggles segments on/off.
function buildShade(mountEl, spec, cbs) {
  const _regions = regionsFor(spec);
  if (_regions) return buildShadeRegions(mountEl, spec, _regions, cbs);
  const den = Math.max(1, parseInt(spec.den, 10) || 1);
  const target = Math.max(0, Math.min(den, parseInt(spec.num, 10) || 0));
  const shape = spec.shape === "bar" ? "bar" : "pie";
  const state = new Set();

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage";
  const svg = buildShapeSVG(shape, den, () => false);
  stage.appendChild(svg);
  const count = document.createElement("div");
  count.className = "ts-fw-count";
  const renderCount = () => { count.textContent = state.size + " / " + den + " shaded"; };
  renderCount();
  stage.appendChild(count);
  mountEl.appendChild(stage);

  let locked = false;
  svg.querySelectorAll(".ts-seg").forEach((seg) => {
    seg.classList.add("ts-tappable");
    seg.addEventListener("click", () => {
      if (locked) return;
      const i = seg.getAttribute("data-idx");
      if (state.has(i)) { state.delete(i); seg.classList.remove("on"); lessonSound.play("tapOff"); }
      else { state.add(i); seg.classList.add("on"); lessonSound.play("tapOn"); }
      renderCount();
      if (cbs && cbs.onInteract) cbs.onInteract();
    });
  });
  if (cbs && cbs.onComplete) cbs.onComplete();

  return {
    isComplete: () => true,
    // `value` = shaded segment indices (numbers), posted for server grading of
    // uniform-figure skill-checks; the practice verdict is the grader's.
    check: () => ({
      correct: practiceVerdict(areaGradeSpec(spec, null), Array.from(state).map(Number)),
      value: Array.from(state).map(Number),
    }),
    getValue: () => Array.from(state).map(Number),
    getState: () => ({
      value: Math.round((state.size / den) * 1000) / 1000,
      target: Math.round((target / den) * 1000) / 1000,
      label: state.size + "/" + den + " shaded",
    }),
    reveal: (forced) => {
      locked = true;
      state.clear();
      const cnt = forced != null
        ? Math.round(forcedValue(forced, target / den) * den)
        : target;
      svg.querySelectorAll(".ts-seg").forEach((seg, i) => {
        if (i < cnt) { seg.classList.add("on"); state.add(seg.getAttribute("data-idx")); }
        else seg.classList.remove("on");
      });
      renderCount();
    },
  };
}

// Two steppers to BUILD a fraction. Grades exact or by value (default value).
function buildBuild(mountEl, spec, cbs) {
  const target = parseFraction(spec.target != null ? spec.target : spec.answer);
  const grade = spec.grade === "exact" ? "exact" : "value";
  const maxDen = Math.max(2, parseInt(spec.maxDen, 10) || 12);
  let num = spec.startNum != null ? parseInt(spec.startNum, 10) : 1;
  let den = spec.startDen != null ? parseInt(spec.startDen, 10) : 2;

  // Optional reference shape to read from (e.g. a pre-shaded pie).
  if (spec.reference) {
    const ref = document.createElement("div");
    ref.className = "ts-fw-ref";
    ref.innerHTML = fractionSVG(spec.reference);
    mountEl.appendChild(ref);
  }

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-build";
  const preview = document.createElement("div");
  preview.className = "ts-fw-preview";

  const fracBox = document.createElement("div");
  fracBox.className = "ts-fw-frac";
  const numStep = stepper("numerator");
  const bar = document.createElement("div"); bar.className = "ts-fw-fracbar";
  const denStep = stepper("denominator");
  fracBox.appendChild(numStep.el);
  fracBox.appendChild(bar);
  fracBox.appendChild(denStep.el);

  stage.appendChild(preview);
  stage.appendChild(fracBox);
  mountEl.appendChild(stage);

  let locked = false;
  function stepper(label) {
    const el = document.createElement("div");
    el.className = "ts-fw-stepper";
    const minus = document.createElement("button");
    minus.type = "button"; minus.className = "ts-fw-pm"; minus.textContent = "–";
    minus.setAttribute("aria-label", "decrease " + label);
    const val = document.createElement("span"); val.className = "ts-fw-val";
    const plus = document.createElement("button");
    plus.type = "button"; plus.className = "ts-fw-pm"; plus.textContent = "+";
    plus.setAttribute("aria-label", "increase " + label);
    el.appendChild(plus); el.appendChild(val); el.appendChild(minus);
    return { el, val, minus, plus };
  }

  function clamp() {
    den = Math.max(1, Math.min(maxDen, den));
    num = Math.max(0, Math.min(den, num));
  }
  function render() {
    clamp();
    numStep.val.textContent = String(num);
    denStep.val.textContent = String(den);
    preview.innerHTML = "";
    preview.appendChild(buildShapeSVG("bar", den, (i) => i < num));
  }
  function bump(which, d) {
    if (locked) return;
    if (which === "num") num += d; else den += d;
    if (which === "den" && num > den) num = den;
    render();
    lessonSound.play("tapOn");
    if (cbs && cbs.onInteract) cbs.onInteract();
  }
  numStep.plus.addEventListener("click", () => bump("num", 1));
  numStep.minus.addEventListener("click", () => bump("num", -1));
  denStep.plus.addEventListener("click", () => bump("den", 1));
  denStep.minus.addEventListener("click", () => bump("den", -1));
  render();
  if (cbs && cbs.onComplete) cbs.onComplete();

  return {
    isComplete: () => true,
    check: () => {
      const cur = { num, den };
      const correct = grade === "exact"
        ? (cur.num === target.num && cur.den === target.den)
        : valueEqual(cur, target);
      return { correct };
    },
    getState: () => ({
      value: den ? Math.round((num / den) * 1000) / 1000 : 0,
      target: target.den ? Math.round((target.num / target.den) * 1000) / 1000 : 0,
      label: "built " + num + "/" + den,
    }),
    reveal: () => {
      locked = true;
      num = target.num; den = target.den;
      render();
    },
  };
}

// Drag a dot to the target tick on a 0..1 number line. Snaps to ticks.
function buildNumberLine(mountEl, spec, cbs) {
  const den = Math.max(1, parseInt(spec.den, 10) || 1);
  const target = Math.max(0, Math.min(den, parseInt(spec.num, 10) || 0));
  let pos = spec.start != null ? Math.max(0, Math.min(den, parseInt(spec.start, 10))) : 0;

  const W = 280, padX = 22, y = 38;
  const x0 = padX, x1 = W - padX, span = x1 - x0;

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-nl";
  const svg = numberLineSVG(den, null, { marker: false });
  const mark = svgEl("line", { class: "ts-nl-mark", x1: 0, y1: y - 16, x2: 0, y2: y + 16 });
  const dot = svgEl("circle", { class: "ts-nl-dot ts-nl-handle", cx: 0, cy: y, r: 9 });
  const lbl = svgEl("text", { class: "ts-nl-handle-label", x: 0, y: y - 22 });
  svg.appendChild(mark); svg.appendChild(dot); svg.appendChild(lbl);
  stage.appendChild(svg);
  mountEl.appendChild(stage);

  let locked = false;
  function place(p) {
    pos = Math.max(0, Math.min(den, p));
    const x = x0 + (span * pos) / den;
    mark.setAttribute("x1", x); mark.setAttribute("x2", x);
    dot.setAttribute("cx", x);
    lbl.setAttribute("x", x);
    lbl.textContent = pos + "/" + den;
  }
  function clientToTick(clientX) {
    const rect = svg.getBoundingClientRect();
    const rel = (clientX - rect.left) / rect.width; // 0..1 in viewBox space
    const vx = rel * W;
    const frac = (vx - x0) / span;
    return Math.max(0, Math.min(den, Math.round(frac * den)));
  }
  function onMove(e) {
    if (locked) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    place(clientToTick(cx));
    if (cbs && cbs.onInteract) cbs.onInteract();
  }
  function onUp() {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  }
  svg.addEventListener("pointerdown", (e) => {
    if (locked) return;
    place(clientToTick(e.clientX));
    if (cbs && cbs.onInteract) cbs.onInteract();
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  });
  place(pos);
  if (cbs && cbs.onComplete) cbs.onComplete();

  return {
    isComplete: () => true,
    check: () => ({ correct: pos === target }),
    getState: () => ({
      value: den ? Math.round((pos / den) * 1000) / 1000 : 0,
      target: den ? Math.round((target / den) * 1000) / 1000 : 0,
      label: "dot at " + pos + "/" + den,
    }),
    reveal: () => { locked = true; place(target); },
  };
}

// Compare N fractions and tap the bigger (or smaller) one. Each option may be a
// FIGURE (bar/pie/region via fractionSVG) or an ABSTRACT num/den card. Renders
// 2-up or, for 3–4 options, a 2×2 grid. The winning index is COMPUTED from the
// values (never authored) and mirrored byte-for-byte on the server, so the XP
// verdict is authoritative. Legacy { left, right } specs still work.
function buildCompare(mountEl, spec, cbs) {
  const options = compareOptions(spec);
  const winner = compareWinner(spec); // computed index, or -1
  let choice = null, locked = false;

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-compare" +
    (options.length > 2 ? " ts-fw-compare-multi" : "");

  function abstractFracEl(num, den) {
    const box = document.createElement("span");
    box.className = "ts-cmp-frac";
    box.innerHTML =
      '<span class="ts-cmp-n">' + num + "</span>" +
      '<span class="ts-cmp-bar"></span>' +
      '<span class="ts-cmp-d">' + den + "</span>";
    return box;
  }
  function optFrac(o) {
    return (o && o.num != null && o.den != null)
      ? { num: Number(o.num), den: Number(o.den) } : parseFraction(o);
  }
  function optionVisual(opt) {
    const o = opt || {};
    const hasFigure = Array.isArray(o.regions) || o.layout ||
      (!o.abstract && (o.type === "bar" || o.type === "pie" || o.type === "numberline"));
    if (hasFigure) {
      const vis = document.createElement("div");
      vis.className = "ts-fw-opt-vis";
      vis.innerHTML = fractionSVG(o);
      return vis;
    }
    const f = optFrac(o);
    return abstractFracEl(f.num, f.den);
  }
  function optionLabel(opt) {
    const o = opt || {};
    if (o.label) return o.label;
    const f = optFrac(o);
    return f.num + "/" + f.den;
  }

  options.forEach((opt, i) => {
    const btn = document.createElement("button");
    btn.type = "button"; btn.className = "ts-fw-opt"; btn.dataset.idx = String(i);
    btn.appendChild(optionVisual(opt));
    const cap = document.createElement("div");
    cap.className = "ts-fw-opt-label"; cap.textContent = optionLabel(opt);
    btn.appendChild(cap);
    btn.addEventListener("click", () => {
      if (locked) return;
      choice = i;
      lessonSound.play("choice");
      stage.querySelectorAll(".ts-fw-opt").forEach((b) => b.classList.toggle("chosen", b === btn));
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (cbs && cbs.onComplete) cbs.onComplete();
    });
    stage.appendChild(btn);
  });
  mountEl.appendChild(stage);

  return {
    isComplete: () => choice != null,
    // `value` = chosen index, posted for authoritative server grading; the
    // one grader table also computes the instant practice verdict.
    check: () => ({ correct: choice != null && practiceVerdict(spec, choice), value: choice }),
    getValue: () => choice,
    getState: () => ({
      value: choice,
      target: winner,
      label: choice != null ? ("picked " + optionLabel(options[choice])) : "no choice yet",
    }),
    reveal: (forced) => {
      locked = true;
      const w = forced != null ? Number(forced) : winner;
      stage.querySelectorAll(".ts-fw-opt").forEach((b, i) => {
        b.classList.toggle("is-answer", i === w);
        b.classList.remove("chosen");
      });
    },
  };
}

// Equivalent-fraction equation row with tappable number tiles (Task #251).
// Renders  LEFT = (blank)/den  and a tray of number tiles; tapping a tile fills
// the blank. The CORRECT answer is NEVER required on the client: for skill-check
// beats the server strips `spec.answer` and grades the chosen value, so this
// widget exposes `value` on check() (and getValue()) for the controller to post.
// When `spec.answer` IS present (authored practice) check() also grades locally.
function buildNumberTile(mountEl, spec, cbs) {
  const left = parseFraction(spec.left || "1/2");
  const rm = String(spec.right || "?/2").split("/");
  const rightDen = parseInt(rm[1], 10) || 2;
  const tiles = Array.isArray(spec.tiles) && spec.tiles.length
    ? spec.tiles.slice() : [1, 2, 3, 4];
  const answer = spec.answer != null ? Number(spec.answer) : null;

  let chosen = null;
  let locked = false;

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-tile";

  const eq = document.createElement("div");
  eq.className = "ts-tile-eq";
  eq.innerHTML =
    '<span class="ts-tile-frac">' +
      '<span class="ts-tile-n">' + left.num + "</span>" +
      '<span class="ts-tile-bar"></span>' +
      '<span class="ts-tile-d">' + left.den + "</span>" +
    "</span>" +
    '<span class="ts-tile-eq-sign">=</span>' +
    '<span class="ts-tile-frac">' +
      '<span class="ts-tile-n ts-tile-blank">?</span>' +
      '<span class="ts-tile-bar"></span>' +
      '<span class="ts-tile-d">' + rightDen + "</span>" +
    "</span>";
  stage.appendChild(eq);
  const blank = eq.querySelector(".ts-tile-blank");

  const tray = document.createElement("div");
  tray.className = "ts-tile-tray";
  tiles.forEach((t) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "ts-tile";
    b.textContent = String(t);
    b.addEventListener("click", () => {
      if (locked) return;
      chosen = Number(t);
      lessonSound.play("choice");
      blank.textContent = String(t);
      blank.classList.add("filled");
      tray.querySelectorAll(".ts-tile").forEach((x) => x.classList.toggle("chosen", x === b));
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (cbs && cbs.onComplete) cbs.onComplete();
    });
    tray.appendChild(b);
  });
  stage.appendChild(tray);
  mountEl.appendChild(stage);

  return {
    isComplete: () => chosen != null,
    check: () => ({ correct: chosen != null && practiceVerdict(spec, chosen), value: chosen }),
    getValue: () => chosen,
    getState: () => ({
      value: chosen,
      target: answer,
      label: chosen != null
        ? ("chose " + chosen + " → " + left.num + "/" + left.den + " = " + chosen + "/" + rightDen)
        : "no tile chosen yet",
    }),
    // `forced` lets the controller reveal the server-known answer when the
    // client copy was stripped (skill-check). Falls back to the authored answer.
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      if (a != null && !Number.isNaN(a)) {
        blank.textContent = String(a);
        blank.classList.add("filled", "is-answer");
        tray.querySelectorAll(".ts-tile").forEach((x) =>
          x.classList.toggle("is-answer", Number(x.textContent) === a));
      }
    },
  };
}

// Digit-tile fraction-builder (Task #268). A structured fraction EQUATION whose
// cells are printed constants, blanks, or "base ×/÷ blank"; the learner taps
// number tiles to fill the blanks left→right, numerator before denominator.
// Grading (slots / equivalent) is shared with the server via FractionGrading.js,
// so for skill-check beats the answer is stripped and the server is authoritative
// — check() always exposes `value` (the filled blanks) for the controller to post.
function buildTiles(mountEl, spec, cbs) {
  const fractions = Array.isArray(spec.fractions) && spec.fractions.length
    ? spec.fractions : [{ num: { slot: true }, den: { slot: true } }];
  // ops[i] is the operator BETWEEN term i and i+1: "eq" (default) | "add" | "mul".
  const ops = Array.isArray(spec.ops) ? spec.ops : [];
  const tileDefs = Array.isArray(spec.tiles) && spec.tiles.length
    ? spec.tiles.slice() : [1, 2, 3, 4];
  const reusable = !!spec.reusable;

  // Optional reference shape to read the fraction from (e.g. a shaded pie).
  if (spec.reference) {
    const ref = document.createElement("div");
    ref.className = "ts-fw-ref";
    ref.innerHTML = fractionSVG(spec.reference);
    mountEl.appendChild(ref);
  }

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-tile ts-fw-tiles";

  const slotEls = [];   // blank DOM nodes, in canonical fill order
  const slots = [];     // filled values (null | int), same order
  const slotTile = [];  // tile-instance index occupying each slot (or null)
  let locked = false;

  function makeBlank() {
    const b = document.createElement("span");
    b.className = "ts-tile-blank ts-tile-slot";
    b.textContent = "?";
    const idx = slotEls.length;
    b.dataset.slot = String(idx);
    slotEls.push(b); slots.push(null); slotTile.push(null);
    return b;
  }
  function cellNode(cell) {
    const nc = normCell(cell);
    if (nc.kind === "fixed") {
      const s = document.createElement("span");
      s.className = "ts-tile-n"; s.textContent = String(nc.value);
      return s;
    }
    if (nc.kind === "slot") return makeBlank();
    // op: "base × □" / "base ÷ □"
    const wrap = document.createElement("span");
    wrap.className = "ts-tile-op";
    const base = document.createElement("span");
    base.className = "ts-tile-opbase"; base.textContent = String(nc.base);
    const sign = document.createElement("span");
    sign.className = "ts-tile-opsign"; sign.textContent = nc.op === "div" ? "÷" : "×";
    wrap.appendChild(base); wrap.appendChild(sign); wrap.appendChild(makeBlank());
    return wrap;
  }
  function fracNode(f) {
    const col = document.createElement("span");
    col.className = "ts-tile-frac";
    const top = document.createElement("span");
    top.className = "ts-tile-row"; top.appendChild(cellNode(f.num));
    const bar = document.createElement("span"); bar.className = "ts-tile-bar";
    const bot = document.createElement("span");
    bot.className = "ts-tile-row"; bot.appendChild(cellNode(f.den));
    col.appendChild(top); col.appendChild(bar); col.appendChild(bot);
    return col;
  }
  // A term is a fraction ({num,den}) OR a whole number ({whole: CELL}) rendered
  // as a single big cell that lines up with the fraction bars beside it.
  function termNode(f) {
    if (f && typeof f === "object" && f.whole !== undefined) {
      const col = document.createElement("span");
      col.className = "ts-tile-whole";
      col.appendChild(cellNode(f.whole));
      return col;
    }
    return fracNode(f || {});
  }

  const eq = document.createElement("div");
  eq.className = "ts-tile-eq";
  fractions.forEach((f, i) => {
    if (i > 0) {
      const sgn = document.createElement("span");
      sgn.className = "ts-tile-eq-sign"; sgn.textContent = opSign(ops[i - 1]);
      eq.appendChild(sgn);
    }
    eq.appendChild(termNode(f || {}));
  });
  stage.appendChild(eq);

  const tray = document.createElement("div");
  tray.className = "ts-tile-tray";
  const tileBtns = [];
  const tileUsed = [];
  function renderSlots() {
    slotEls.forEach((el, i) => {
      if (slots[i] == null) { el.textContent = "?"; el.classList.remove("filled"); }
      else { el.textContent = String(slots[i]); el.classList.add("filled"); }
    });
  }
  function renderTiles() {
    tileBtns.forEach((b, ti) => {
      const used = !reusable && tileUsed[ti];
      b.classList.toggle("used", used);
      b.disabled = used;
    });
  }
  tileDefs.forEach((t, ti) => {
    const b = document.createElement("button");
    b.type = "button"; b.className = "ts-tile";
    b.textContent = String(t); b.dataset.tile = String(ti);
    tileBtns.push(b); tileUsed.push(false);
    b.addEventListener("click", () => {
      if (locked) return;
      if (!reusable && tileUsed[ti]) return;
      const slot = slots.indexOf(null);
      if (slot < 0) return; // all blanks already filled
      slots[slot] = Number(t);
      slotTile[slot] = reusable ? null : ti;
      if (!reusable) tileUsed[ti] = true;
      lessonSound.play("choice");
      renderSlots(); renderTiles();
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (slots.every((v) => v != null) && cbs && cbs.onComplete) cbs.onComplete();
    });
    tray.appendChild(b);
  });
  stage.appendChild(tray);
  mountEl.appendChild(stage);

  // Tap a filled blank to clear it (and free its tile when non-reusable).
  slotEls.forEach((el, i) => {
    el.classList.add("ts-tappable");
    el.addEventListener("click", () => {
      if (locked || slots[i] == null) return;
      const ti = slotTile[i];
      slots[i] = null; slotTile[i] = null;
      if (ti != null) tileUsed[ti] = false;
      lessonSound.play("tapOff");
      renderSlots(); renderTiles();
      if (cbs && cbs.onInteract) cbs.onInteract();
    });
  });
  renderSlots(); renderTiles();

  function eqString() {
    let si = 0;
    const cellStr = (cell) => {
      const nc = normCell(cell);
      if (nc.kind === "fixed") return String(nc.value);
      if (nc.kind === "slot") { const v = slots[si++]; return v == null ? "?" : String(v); }
      const v = slots[si++];
      return nc.base + (nc.op === "div" ? "÷" : "×") + (v == null ? "?" : String(v));
    };
    const termStr = (f) => (f && typeof f === "object" && f.whole !== undefined)
      ? cellStr(f.whole)
      : cellStr((f || {}).num) + "/" + cellStr((f || {}).den);
    let out = termStr(fractions[0] || {});
    for (let k = 1; k < fractions.length; k++) {
      out += " " + opSign(ops[k - 1]) + " " + termStr(fractions[k] || {});
    }
    return out;
  }

  return {
    isComplete: () => slots.length > 0 && slots.every((v) => v != null),
    check: () => ({ correct: practiceVerdict(spec, slots.slice()), value: slots.slice() }),
    getValue: () => slots.slice(),
    getState: () => ({
      value: eqString(),
      target: spec.target != null ? String(spec.target)
        : (Array.isArray(spec.answer) ? "[" + spec.answer.join(",") + "]" : null),
      label: slots.some((v) => v != null) ? ("building: " + eqString()) : "nothing placed yet",
    }),
    reset: () => {
      if (locked) return;
      for (let i = 0; i < slots.length; i++) { slots[i] = null; slotTile[i] = null; }
      tileUsed.fill(false);
      renderSlots(); renderTiles();
      if (cbs && cbs.onInteract) cbs.onInteract();
    },
    reveal: (forced) => {
      locked = true;
      const ans = Array.isArray(forced) ? forced
        : (Array.isArray(spec.answer) ? spec.answer : null);
      if (ans) {
        for (let i = 0; i < slots.length && i < ans.length; i++) slots[i] = Number(ans[i]);
        renderSlots();
        slotEls.forEach((el) => el.classList.add("is-answer"));
      }
    },
  };
}

// Multiple-choice with SHAPE-DIAGRAM options (Task #268). Each option is a
// fractionSVG spec (region/layout + shaded, or pie/bar). Single-select; grading
// is by chosen index. For skill-check beats the server strips `answer` and
// grades, so check() exposes `value` (the index). On reveal each option shows
// its own value, computed client-side from the shaded area — no answer shipped.
function buildPick(mountEl, spec, cbs) {
  const options = Array.isArray(spec.options) ? spec.options : [];
  const answer = spec.answer != null ? Number(spec.answer) : null;
  let choice = null, locked = false;
  if (spec.prompt) {
    const p = document.createElement("div");
    p.className = "ts-fw-pick-prompt";
    p.textContent = spec.prompt;
    mountEl.appendChild(p);
  }
  if (typeof spec.svg === "string" && spec.svg.trim()) {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig ts-board-svg";
    fig.innerHTML = spec.svg;
    mountEl.appendChild(fig);
  }
  if (typeof spec.code === "string" && spec.code.trim()) {
    const pre = document.createElement("pre");
    pre.className = "ts-code";
    pre.textContent = spec.code;
    mountEl.appendChild(pre);
  }
  if (spec.image && spec.image.src) {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig ts-board-img";
    fig.innerHTML = '<img src="' + esc(spec.image.src) + '" alt="' + esc(spec.image.alt || "") + '" loading="eager" />';
    mountEl.appendChild(fig);
  }
  if (spec.graph) {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig";
    const gfig = lineGraphSVG(spec.graph);
    if (spec.scene && typeof spec.scene === "object") {
      try {
        sceneOverlay(gfig, spec.scene);
      } catch (_) {
      }
    }
    fig.appendChild(gfig.el);
    mountEl.appendChild(fig);
  }
  if (spec.diagram) {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig";
    fig.innerHTML = areaDiagramSVG(spec.diagram);
    mountEl.appendChild(fig);
  }
  if (spec.scene3d) {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig";
    fig.innerHTML = iso3dSVG(spec.scene3d);
    mountEl.appendChild(fig);
  }
  if (spec.figure === "gearChain" || spec.figure === "gearSingle") {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig";
    fig.innerHTML = gearChainSVG(spec.figure === "gearSingle" ? "single" : "triple");
    mountEl.appendChild(fig);
  }
  if (Array.isArray(spec.graphs) || spec.toggle) {
    // stacked panels (+ optional f′/f″/f⁽³⁾ toggle) above the options,
    // mirroring Brilliant's dual-panel problem figures
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig ts-board-fig-stack";
    (spec.graphs || []).forEach((s) => {
      try {
        fig.appendChild(lineGraphSVG(s).el);
      } catch (_) {
      }
    });
    const togHTML = figToggleHTML(spec.toggle);
    if (togHTML) {
      const tmp = document.createElement("div");
      tmp.innerHTML = togHTML;
      while (tmp.firstChild) fig.appendChild(tmp.firstChild);
    }
    mountEl.appendChild(fig);
  }
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-pick";
  // Brilliant lays 4 short options in a 2x2 grid (not a full-width stack).
  // Auto-match for 4 short text/number options; `cols` can force it.
  const allShort = options.length >= 3 && options.every((o) => (typeof o === "string" || typeof o === "number") && String(o).length <= 6);
  if (Number(spec.cols) === 3) stage.classList.add("ts-fw-pick-3col");
  else if (Number(spec.cols) === 2 || (spec.cols == null && options.length === 4 && allShort)) stage.classList.add("ts-fw-pick-2col");
  function optValue(opt) {
    const regions = regionsFor(opt);
    if (regions) {
      const shaded = new Set((opt.shaded || []).map(Number));
      let s = 0;
      regions.forEach((r, i) => {
        if (shaded.has(i)) s += r.area;
      });
      return s;
    }
    const den = Math.max(1, parseInt(opt.den, 10) || 1);
    const num = Math.max(0, parseInt(opt.num, 10) || 0);
    return den ? num / den : 0;
  }
  options.forEach((opt, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ts-fw-pickopt";
    btn.dataset.idx = String(i);
    if (opt == null || typeof opt === "string" || typeof opt === "number") {
      // FIX: plain text options (integers etc.) used to fall through to the
      // fraction SVG and render as blank cards. Render them as text.
      const txt = document.createElement("div");
      txt.className = "ts-fw-pick-text";
      txt.textContent = String(opt == null ? "" : opt).replace(/-(\d)/g, "\u2212$1");
      btn.appendChild(txt);
    } else if (opt.graph) {
      // graph-thumbnail option cards (Brilliant's "which secant is best" style)
      const vis = document.createElement("div");
      vis.className = "ts-fw-pickvis ts-fw-pickthumb";
      try {
        vis.appendChild(lineGraphSVG(opt.graph).el);
      } catch (_) {
      }
      btn.appendChild(vis);
    } else {
    const vis = document.createElement("div");
    vis.className = "ts-fw-pickvis";
    vis.innerHTML = fractionSVG(opt || {});
    btn.appendChild(vis);
    }
    if (opt && opt.label) {
      const cap = document.createElement("div");
      cap.className = "ts-fw-pick-cap";
      cap.textContent = opt.label;
      btn.appendChild(cap);
    }
    const val = document.createElement("div");
    val.className = "ts-fw-pick-val";
    btn.appendChild(val);
    btn.addEventListener("click", () => {
      if (locked) return;
      choice = i;
      lessonSound.play("choice");
      stage.querySelectorAll(".ts-fw-pickopt").forEach((b) => b.classList.toggle("chosen", b === btn));
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (cbs && cbs.onComplete) cbs.onComplete();
    });
    stage.appendChild(btn);
  });
  mountEl.appendChild(stage);
  function selectIdx(i) {
    choice = (i != null && i >= 0 && i < options.length) ? i : null;
    stage.querySelectorAll(".ts-fw-pickopt").forEach((b, j) => b.classList.toggle("chosen", choice != null && j === choice));
  }
  return {
    isComplete: () => choice != null,
    // buildPick also backs graphTrace/curveTrace/secantSlider via reduced
    // specs without a `type`; those grade exactly like a pick.
    check: () => ({ correct: choice != null && practiceVerdict(spec.type ? spec : Object.assign({ type: "pick" }, spec), choice), value: choice }),
    getValue: () => choice,
    setValue: (v) => { if (locked) return; selectIdx(Array.isArray(v) ? Number(v[0]) : Number(v)); },
    reset: () => { if (locked) return; selectIdx(null); },
    getState: () => ({
      value: choice,
      target: answer,
      label: choice != null ? "picked option " + (choice + 1) : "no choice yet"
    }),
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      stage.querySelectorAll(".ts-fw-pickopt").forEach((b, i) => {
        b.classList.toggle("is-answer", a != null && i === a);
        b.classList.remove("chosen");
        b.classList.add("show-val");
        const valEl = b.querySelector(".ts-fw-pick-val");
        if (valEl) valEl.textContent = friendlyFraction(optValue(options[i] || {}));
      });
    }
  };
}

// Build the cell indices of an a×b solid rectangle anchored top-left, sized to
// `need` cells. Prefers the authored factor guides (fa×fb) when they fit, else
// picks any factor pair that fits the grid.
function rectangleForCount(need, rows, cols, fa, fb) {
  const fits = (w, h) => w > 0 && h > 0 && w <= cols && h <= rows && w * h === need;
  let w = 0, h = 0;
  if (fits(fa, fb)) { w = fa; h = fb; }
  else if (fits(fb, fa)) { w = fb; h = fa; }
  else {
    for (let hh = 1; hh <= rows && !w; hh++) {
      if (need % hh === 0 && need / hh <= cols) { h = hh; w = need / hh; }
    }
  }
  const out = [];
  if (!w) { for (let i = 0; i < need && i < rows * cols; i++) out.push(i); return out; }
  for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) out.push(r * cols + c);
  return out;
}

// Overlapping area-model grid for fraction ADDITION and MULTIPLICATION (Task
// #269, the Brilliant "area model"). An rows×cols grid; the learner taps cells
// to build a total, each cell = 1/(rows·cols).
//   mode "add":      shade any cells whose areas sum to the target.
//   mode "multiply": shade the solid a×c block that IS the product (requireRect).
// Optional factorA (columns) / factorB (rows) paint faint tint guides so the
// product rectangle is discoverable. Graded by AREA on the server; `value` is
// the chosen cell indices, so the authored target is never shipped.
function buildOverlapGrid(mountEl, spec, cbs) {
  const rows = Math.max(1, parseInt((spec.grid && spec.grid.rows) || spec.rows, 10) || 1);
  const cols = Math.max(1, parseInt((spec.grid && spec.grid.cols) || spec.cols, 10) || 1);
  const total = rows * cols;
  const cellArea = 1 / total;
  const mode = (spec.mode === "multiply" || spec.mode === "mul") ? "multiply" : "add";
  const requireRect = mode === "multiply" || !!spec.requireRect;
  const tv = targetValue(spec);
  const eps = 1e-6;
  const state = new Set();
  const fa = parseInt(spec.factorA, 10); // columns to tint-guide
  const fb = parseInt(spec.factorB, 10); // rows to tint-guide

  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-overlap";
  const svg = svgEl("svg", { viewBox: "0 0 100 100", class: "ts-frac-svg ts-frac-grid" });
  if (fa > 0) svg.appendChild(svgEl("rect", {
    x: "0", y: "0", width: ((100 * fa) / cols).toFixed(2), height: "100",
    class: "ts-grid-guide guide-a",
  }));
  if (fb > 0) svg.appendChild(svgEl("rect", {
    x: "0", y: "0", width: "100", height: ((100 * fb) / rows).toFixed(2),
    class: "ts-grid-guide guide-b",
  }));
  const cellEls = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      const rect = svgEl("rect", {
        x: ((100 * c) / cols).toFixed(2), y: ((100 * r) / rows).toFixed(2),
        width: (100 / cols).toFixed(2), height: (100 / rows).toFixed(2),
        class: "ts-gridcell ts-tappable", "data-idx": String(idx),
      });
      cellEls.push(rect);
      svg.appendChild(rect);
    }
  }
  stage.appendChild(svg);

  const count = document.createElement("div");
  count.className = "ts-fw-count";
  const shadedArea = () => state.size * cellArea;
  const renderCount = () => {
    count.textContent = state.size ? friendlyFraction(shadedArea()) + " shaded" : "tap to shade";
  };
  renderCount();
  stage.appendChild(count);
  mountEl.appendChild(stage);

  let locked = false;
  cellEls.forEach((rect, idx) => {
    rect.addEventListener("click", () => {
      if (locked) return;
      if (state.has(idx)) { state.delete(idx); rect.classList.remove("on"); lessonSound.play("tapOff"); }
      else { state.add(idx); rect.classList.add("on"); lessonSound.play("tapOn"); }
      renderCount();
      if (cbs && cbs.onInteract) cbs.onInteract();
    });
  });

  const rectOK = () => !requireRect || isRectangleSelection(Array.from(state), rows, cols);
  function shadeIndices(indices) {
    state.clear();
    const want = new Set(indices.map(Number));
    cellEls.forEach((rect, i) => {
      if (want.has(i)) { rect.classList.add("on"); state.add(i); }
      else rect.classList.remove("on");
    });
    renderCount();
  }

  return {
    isComplete: () => state.size > 0,
    // rectOK() is a renderer-only interaction constraint (multiply mode wants
    // a clean rectangle); the AREA verdict itself is the grader's.
    check: () => ({
      correct: practiceVerdict(areaGradeSpec(spec, null), Array.from(state).map(Number)) && rectOK(),
      value: Array.from(state).map(Number),
    }),
    getValue: () => Array.from(state).map(Number),
    getState: () => {
      const v = shadedArea();
      return {
        value: Math.round(v * 1000) / 1000,
        target: Math.round(tv * 1000) / 1000,
        label: state.size
          ? "shaded " + state.size + "/" + total + " cells (" + friendlyFraction(v) + ")"
          : "nothing shaded yet",
      };
    },
    reset: () => {
      if (locked) return;
      shadeIndices([]);
      if (cbs && cbs.onInteract) cbs.onInteract();
    },
    reveal: (forced) => {
      locked = true;
      const need = Math.round(forcedValue(forced, tv) * total);
      const indices = requireRect
        ? rectangleForCount(need, rows, cols, fa, fb)
        : Array.from({ length: Math.min(need, total) }, (_, i) => i);
      shadeIndices(indices);
    },
  };
}

// ---------------------------------------------------------------------------
// NEW WIDGET: signedTiles — zero-pair tile columns (Negative Numbers).
// Fixed columns show a value as a stack of +1 / −1 tiles; slot columns are
// built by the student via a drag handle (drag away from the row to add, toward
// it to remove; click handle adds one; click a tile removes it). Live net
// readout above each column; mixed-sign stacks dim their zero pairs.
// Submission value = array of slot nets, in slot order.
function buildSignedTiles(mountEl, spec, cbs) {
  const eq = Array.isArray(spec.equation) ? spec.equation : [];
  const signs = spec.signs === "both" ? "both" : spec.signs === "plus" ? "plus" : "minus";
  const gradeMode = spec.grade === "sum" ? "sum" : "slots";
  const answer = Array.isArray(spec.answer) ? spec.answer.map(Number) : spec.answer != null ? [Number(spec.answer)] : null;
  const answerSum = spec.answerSum != null ? Number(spec.answerSum) : null;
  let locked = false;
  const slots = [];
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-st";
  const row = document.createElement("div");
  row.className = "ts-st-row";
  stage.appendChild(row);
  function tileEl(sign) {
    const t = document.createElement("button");
    t.type = "button";
    t.className = "ts-st-tile " + (sign > 0 ? "is-plus" : "is-minus");
    t.textContent = sign > 0 ? "+" : "−";
    return t;
  }
  function fmt(n) { return n < 0 ? "−" + Math.abs(n) : String(n); }
  function renderCol(col) {
    const st = col.state;
    col.valEl.textContent = st.plus + st.minus === 0 ? (col.fixed ? "0" : "?") : fmt(st.plus - st.minus);
    col.plusWrap.innerHTML = "";
    col.minusWrap.innerHTML = "";
    const paired = Math.min(st.plus, st.minus);
    for (let i = 0; i < st.plus; i++) {
      const t = tileEl(1);
      if (i < paired) t.classList.add("is-paired");
      if (!col.fixed) t.addEventListener("click", () => { if (locked) return; st.plus--; renderCol(col); ping(); });
      col.plusWrap.appendChild(t);
    }
    for (let i = 0; i < st.minus; i++) {
      const t = tileEl(-1);
      if (i < paired) t.classList.add("is-paired");
      if (!col.fixed) t.addEventListener("click", () => { if (locked) return; st.minus--; renderCol(col); ping(); });
      col.minusWrap.appendChild(t);
    }
  }
  function ping() {
    lessonSound.play("tapOn");
    if (cbs && cbs.onInteract) cbs.onInteract();
    if (isComplete() && cbs && cbs.onComplete) cbs.onComplete();
  }
  function makeHandle(col, sign) {
    const h = document.createElement("button");
    h.type = "button";
    h.className = "ts-st-handle " + (sign > 0 ? "is-plus" : "is-minus");
    h.setAttribute("aria-label", sign > 0 ? "Add a +1 tile" : "Add a −1 tile");
    h.textContent = sign > 0 ? "+" : "−";
    const max = col.max;
    const count = () => sign > 0 ? col.state.plus : col.state.minus;
    const bump = (d) => {
      if (locked) return;
      if (d > 0 && count() >= max) return;
      if (d < 0 && count() <= 0) return;
      if (sign > 0) col.state.plus += d; else col.state.minus += d;
      renderCol(col); ping();
    };
    h.addEventListener("click", () => { if (!h.__dragged) bump(1); h.__dragged = false; });
    h.addEventListener("pointerdown", (e) => {
      if (locked) return;
      h.setPointerCapture(e.pointerId);
      const y0 = e.clientY, n0 = count();
      const STEP = 26;
      const move = (ev) => {
        const dy = sign > 0 ? y0 - ev.clientY : ev.clientY - y0;
        const want = Math.max(0, Math.min(max, n0 + Math.round(dy / STEP)));
        if (want !== count()) {
          h.__dragged = true;
          if (sign > 0) col.state.plus = want; else col.state.minus = want;
          renderCol(col); ping();
        }
      };
      const up = () => { h.removeEventListener("pointermove", move); h.removeEventListener("pointerup", up); };
      h.addEventListener("pointermove", move);
      h.addEventListener("pointerup", up);
    });
    return h;
  }
  eq.forEach((part) => {
    if (part && part.op) {
      const op = document.createElement("div");
      op.className = "ts-st-op";
      op.textContent = part.op;
      row.appendChild(op);
      return;
    }
    const col = { fixed: !(part && part.slot), max: Math.max(1, Number(part && part.max) || 9), state: { plus: 0, minus: 0 } };
    const wrap = document.createElement("div");
    wrap.className = "ts-st-col" + (col.fixed ? "" : " is-slot");
    col.colEl = wrap;
    col.valEl = document.createElement("div");
    col.valEl.className = "ts-st-val";
    wrap.appendChild(col.valEl);
    const box = document.createElement("div");
    box.className = "ts-st-box";
    col.plusWrap = document.createElement("div");
    col.plusWrap.className = "ts-st-stack is-plus";
    col.minusWrap = document.createElement("div");
    col.minusWrap.className = "ts-st-stack is-minus";
    if (!col.fixed && (signs === "plus" || signs === "both")) box.appendChild(makeHandle(col, 1));
    box.appendChild(col.plusWrap);
    box.appendChild(col.minusWrap);
    if (!col.fixed && (signs === "minus" || signs === "both")) box.appendChild(makeHandle(col, -1));
    wrap.appendChild(box);
    if (col.fixed) {
      const v = Number(part.tiles) || 0;
      if (v >= 0) col.state.plus = v; else col.state.minus = -v;
    } else {
      slots.push(col);
    }
    renderCol(col);
    row.appendChild(wrap);
  });
  mountEl.appendChild(stage);
  const slotNets = () => slots.map((c) => c.state.plus - c.state.minus);
  const slotFilled = () => slots.every((c) => c.state.plus + c.state.minus > 0);
  function isComplete() { return slots.length === 0 ? true : slotFilled(); }
  function isCorrect() {
    // Sum mode keeps the renderer-only completeness gate (an untouched slot
    // must not earn credit for a net of 0); the VERDICT itself is the grader's.
    if (gradeMode === "sum" && !slotFilled()) return false;
    return practiceVerdict(spec, slotNets());
  }
  function setSlot(col, v) {
    col.state.plus = v > 0 ? v : 0;
    col.state.minus = v < 0 ? -v : 0;
    renderCol(col);
  }
  return {
    isComplete,
    check: () => ({ correct: isCorrect(), value: slotNets() }),
    getValue: () => slotNets(),
    getState: () => ({ value: slotNets(), label: "slot values: " + slotNets().map(fmt).join(", ") }),
    reset: () => { slots.forEach((c) => { c.state.plus = 0; c.state.minus = 0; renderCol(c); }); },
    reveal: (forced) => {
      locked = true;
      let a = forced != null ? forced : answer != null ? answer : answerSum != null ? [answerSum] : null;
      if (a == null) return;
      if (!Array.isArray(a)) a = [a];
      slots.forEach((c, i) => { if (a[i] != null) setSlot(c, Number(a[i])); c.colEl && c.colEl.classList.add("is-answer"); });
    }
  };
}

// ---------------------------------------------------------------------------
// NEW WIDGET: intTiles — an integer equation with 1..N blanks and a tile bank.
// Tap a bank tile -> fills the leftmost empty blank. Tap a filled blank to clear.
// Submission value = array of blank values in blank order ("exact" grades as an
// order-insensitive multiset, since these equations are commutative sums).
function buildIntTiles(mountEl, spec, cbs) {
  const template = String(spec.template || "?");
  const bank = Array.isArray(spec.tiles) ? spec.tiles.slice() : [];
  const gradeMode = spec.grade === "sum" ? "sum" : "exact";
  const answer = Array.isArray(spec.answer) ? spec.answer.map(Number) : spec.answer != null ? [Number(spec.answer)] : null;
  const answerSum = spec.answerSum != null ? Number(spec.answerSum) : null;
  let locked = false;
  const fmt = (n) => n < 0 ? "−" + Math.abs(n) : String(n);
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-it";
  if (spec.graph) {
    const fig = document.createElement("div");
    fig.className = "ts-gr-fig";
    fig.appendChild(lineGraphSVG(spec.graph).el);
    stage.appendChild(fig);
  }
  const eqEl = document.createElement("div");
  eqEl.className = "ts-it-eq";
  const blanks = [];
  template.split(/(\?)/).forEach((piece) => {
    if (piece === "?") {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ts-it-blank";
      b.textContent = "";
      const entry = { el: b, value: null, tileBtn: null };
      b.addEventListener("click", () => {
        if (locked || entry.value == null) return;
        entry.value = null;
        b.textContent = "";
        b.classList.remove("filled");
        if (entry.tileBtn) entry.tileBtn.classList.remove("used");
        entry.tileBtn = null;
        lessonSound.play("tapOff");
        if (cbs && cbs.onInteract) cbs.onInteract();
      });
      blanks.push(entry);
      eqEl.appendChild(b);
    } else if (piece) {
      const s = document.createElement("span");
      s.className = "ts-it-text";
      s.textContent = piece.replace(/-(\d)/g, "−$1");
      eqEl.appendChild(s);
    }
  });
  stage.appendChild(eqEl);
  const tray = document.createElement("div");
  // Bug fix (v2 handoff #3): big tile banks (4+) wrap into a tidy 2-col grid
  tray.className = "ts-it-tray" + (bank.length >= 4 ? " is-grid" : "");
  bank.forEach((t) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "ts-it-tile";
    b.textContent = fmt(Number(t));
    b.addEventListener("click", () => {
      if (locked || b.classList.contains("used")) return;
      const empty = blanks.find((x) => x.value == null);
      if (!empty) return;
      empty.value = Number(t);
      empty.el.textContent = fmt(Number(t));
      empty.el.classList.add("filled");
      empty.tileBtn = b;
      b.classList.add("used");
      lessonSound.play("choice");
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (blanks.every((x) => x.value != null) && cbs && cbs.onComplete) cbs.onComplete();
    });
    tray.appendChild(b);
  });
  stage.appendChild(tray);
  mountEl.appendChild(stage);
  const values = () => blanks.map((b) => b.value);
  const filled = () => blanks.every((b) => b.value != null);
  function isCorrect() {
    if (!filled()) return false; // renderer completeness gate (UI state)
    return practiceVerdict(spec, values());
  }
  return {
    isComplete: filled,
    check: () => ({ correct: isCorrect(), value: values() }),
    getValue: values,
    getState: () => ({ value: values(), label: "blanks: " + values().map((v) => v == null ? "_" : fmt(v)).join(", ") }),
    reset: () => {
      blanks.forEach((b) => {
        b.value = null; b.el.textContent = ""; b.el.classList.remove("filled");
        if (b.tileBtn) b.tileBtn.classList.remove("used");
        b.tileBtn = null;
      });
    },
    reveal: (forced) => {
      locked = true;
      let a = forced != null ? forced : answer;
      if (a == null) return;
      if (!Array.isArray(a)) a = [a];
      blanks.forEach((b, i) => {
        if (a[i] == null) return;
        b.value = Number(a[i]);
        b.el.textContent = fmt(Number(a[i]));
        b.el.classList.add("filled", "is-answer");
      });
    }
  };
}

// --- line-graph figure + calculus widgets (graphTrace, slopeFit, curveTrace,
//     tangentSeek, regionPick) ---
function lineGraphSVG(g) {
  const W = g.w || 380, H = g.h || 290;
  const L = 48, R = 20, T = 18, B = 42;
  const xmax = g.xmax != null ? Number(g.xmax) : 10;
  const ymax = g.ymax != null ? Number(g.ymax) : 10;
  const xmin = g.xmin != null ? Number(g.xmin) : 0;
  const ymin = g.ymin != null ? Number(g.ymin) : 0;
  const X = (x) => L + (W - L - R) * ((x - xmin) / (xmax - xmin));
  const Y = (y) => H - B - (H - T - B) * ((y - ymin) / (ymax - ymin));
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 " + W + " " + H);
  svg.setAttribute("class", "ts-gr-svg");
  const mk = (tag, attrs, parent) => {
    const el = document.createElementNS(NS, tag);
    for (const k in attrs) el.setAttribute(k, attrs[k]);
    (parent || svg).appendChild(el);
    return el;
  };
  const axis = "#20242a", ink = "#565c66", blue = "#4f6ef7", orange = "#f5a04a";
  // axes sit at x=0 / y=0 when inside the window, else hug the near edge
  const ax0 = X(Math.min(Math.max(0, xmin), xmax));
  const ay0 = Y(Math.min(Math.max(0, ymin), ymax));
  // ticks accept a plain number OR { v, label } for a custom label (e.g.
  // { v: Math.PI, label: "π" }, { v: 0.785, label: "π/4" }).
  const tPos = (t) => (t && typeof t === "object") ? Number(t.v != null ? t.v : (t.x != null ? t.x : t.y)) : Number(t);
  const tLab = (t) => (t && typeof t === "object" && t.label != null) ? String(t.label) : String(t).replace(/-/, "−");
  if (g.gridlines) {
    (g.xticks || []).forEach((t) => mk("line", { x1: X(tPos(t)), y1: Y(ymax), x2: X(tPos(t)), y2: Y(ymin), stroke: "#e4e7ec", "stroke-width": 1 }));
    (g.yticks || []).forEach((t) => mk("line", { x1: X(xmin), y1: Y(tPos(t)), x2: X(xmax), y2: Y(tPos(t)), stroke: "#e4e7ec", "stroke-width": 1 }));
  }
  // full unit grid (Brilliant's coordinate-plane look): g.grid = { step: 1 }
  // — light lines at every step across the WHOLE window, all four quadrants
  if (g.grid) {
    const st = Number(g.grid.step) || 1;
    for (let t = Math.ceil(xmin / st) * st; t <= xmax + 1e-9; t += st)
      mk("line", { x1: X(t), y1: Y(ymax), x2: X(t), y2: Y(ymin), stroke: "#e9ecf1", "stroke-width": 1 });
    for (let t = Math.ceil(ymin / st) * st; t <= ymax + 1e-9; t += st)
      mk("line", { x1: X(xmin), y1: Y(t), x2: X(xmax), y2: Y(t), stroke: "#e9ecf1", "stroke-width": 1 });
  }
  mk("line", { x1: xmin < 0 ? 8 : ax0, y1: ay0, x2: W - 8, y2: ay0, stroke: axis, "stroke-width": 1.6 });
  mk("line", { x1: ax0, y1: ay0, x2: ax0, y2: 8, stroke: axis, "stroke-width": 1.6 });
  mk("path", { d: "M " + (W - 8) + " " + ay0 + " m 0 -4.5 l 8 4.5 l -8 4.5 z", fill: axis });
  if (xmin < 0) mk("path", { d: "M 8 " + ay0 + " m 0 -4.5 l -8 4.5 l 8 4.5 z", fill: axis });
  mk("path", { d: "M " + ax0 + " 8 m -4.5 0 l 4.5 -8 l 4.5 8 z", fill: axis });
  (g.xticks || []).forEach((t) => {
    const p = tPos(t);
    if (p !== 0) mk("line", { x1: X(p), y1: ay0 - 4, x2: X(p), y2: ay0 + 4, stroke: axis, "stroke-width": 1.3 });
    const tx = mk("text", { x: X(p), y: ay0 + 20, "text-anchor": "middle", "font-size": 11.5, fill: ink });
    tx.textContent = tLab(t);
  });
  (g.yticks || []).forEach((t) => {
    const p = tPos(t);
    if (p !== 0) mk("line", { x1: ax0 - 4, y1: Y(p), x2: ax0 + 4, y2: Y(p), stroke: axis, "stroke-width": 1.3 });
    const tx = mk("text", { x: ax0 - 9, y: Y(p) + 4, "text-anchor": "end", "font-size": 11.5, fill: ink });
    tx.textContent = tLab(t);
  });
  if (g.xlabel) {
    const tx = mk("text", { x: W - 4, y: ay0 - 8, "text-anchor": "end", "font-size": 12, fill: ink, "font-style": "italic" });
    tx.textContent = g.xlabel;
  }
  if (g.ylabel) {
    const tx = mk("text", { x: ax0 + 8, y: 14, "text-anchor": "start", "font-size": 12, fill: ink, "font-style": "italic" });
    tx.textContent = g.ylabel;
  }
  function lineEnds(m, b) {
    const yAt = (x) => m * x + b;
    const cands = [xmin, xmax];
    if (m !== 0) { cands.push((ymin - b) / m, (ymax - b) / m); }
    const xs = cands.filter((x) => x >= xmin - 1e-9 && x <= xmax + 1e-9 && yAt(x) >= ymin - 1e-9 && yAt(x) <= ymax + 1e-9).sort((a, b2) => a - b2);
    if (xs.length < 2) return null;
    const x1 = xs[0], x2 = xs[xs.length - 1];
    return { x1: X(x1), y1: Y(yAt(x1)), x2: X(x2), y2: Y(yAt(x2)) };
  }
  function setLine(el, m, b) {
    const e = lineEnds(m, b);
    if (!e) { el.setAttribute("visibility", "hidden"); return; }
    el.removeAttribute("visibility");
    el.setAttribute("x1", e.x1); el.setAttribute("y1", e.y1);
    el.setAttribute("x2", e.x2); el.setAttribute("y2", e.y2);
  }
  if (g.line) {
    const el = mk("line", { stroke: blue, "stroke-width": 2.5, "stroke-linecap": "round" });
    setLine(el, Number(g.line.m) || 0, Number(g.line.b) || 0);
  }
  // sampled curve: g.curve = { samples: [[x,y],...] } — smooth polyline; the
  // shared mechanism for every non-linear figure (parabolas, waves, ...)
  let samples = null;
  if (g.curve && Array.isArray(g.curve.samples) && g.curve.samples.length > 1) {
    samples = g.curve.samples.map((p) => [Number(p[0]), Number(p[1])]).sort((a, b2) => a[0] - b2[0]);
    // area fills under the curve (Brilliant's area-problem shading):
    // g.areas = [{from, to, color: "blue"|"grey"}] — drawn beneath the curve line
    (g.areas || []).forEach((ar) => {
      const seg = samples.filter((p) => p[0] >= ar.from - 1e-9 && p[0] <= ar.to + 1e-9);
      if (seg.length < 2) return;
      let d2 = "M " + X(seg[0][0]).toFixed(2) + " " + Y(0).toFixed(2);
      seg.forEach((p) => { d2 += " L " + X(p[0]).toFixed(2) + " " + Y(p[1]).toFixed(2); });
      d2 += " L " + X(seg[seg.length - 1][0]).toFixed(2) + " " + Y(0).toFixed(2) + " Z";
      mk("path", { d: d2, fill: ar.color === "grey" ? "rgba(160,165,175,0.35)" : ar.color === "orange" ? "rgba(245,176,66,0.85)" : "rgba(123,142,248,0.65)", stroke: "none" });
    });
    // Riemann rectangles: g.bars = {from, to, n, highlight, rule}
    // — n equal-width bars whose heights sample the curve at each midpoint
    // (default) or at the left/right edge when rule is "left"/"right";
    // highlight (optional index) draws that one bar darker
    if (g.bars && Number(g.bars.n) > 0) {
      const bFrom = Number(g.bars.from), bTo = Number(g.bars.to), bN = Math.round(Number(g.bars.n));
      const hAt = (cx0) => { const cx = Math.max(samples[0][0], Math.min(samples[samples.length - 1][0], cx0)); for (let i = 1; i < samples.length; i++) { if (cx <= samples[i][0]) { const [x0, y0] = samples[i - 1], [x1, y1] = samples[i]; const t = x1 === x0 ? 0 : (cx - x0) / (x1 - x0); return y0 + t * (y1 - y0); } } return samples[samples.length - 1][1]; };
      const bw = (bTo - bFrom) / bN;
      for (let i = 0; i < bN; i++) {
        const bx0 = bFrom + i * bw;
        const bh = hAt(g.bars.rule === "left" ? bx0 : g.bars.rule === "right" ? bx0 + bw : bx0 + bw / 2);
        if (bh <= 0) continue;
        mk("rect", { x: X(bx0).toFixed(2), y: Y(bh).toFixed(2), width: (X(bx0 + bw) - X(bx0)).toFixed(2), height: (Y(0) - Y(bh)).toFixed(2), fill: g.bars.highlight === i ? "rgba(72,101,244,0.92)" : "rgba(123,142,248,0.6)", stroke: "rgba(255,255,255,0.9)", "stroke-width": 1 });
      }
    }
    // dashed bound markers: g.marks = [{x, label}]
    (g.marks || []).forEach((m) => {
      const yTop = (() => { const cx = Math.max(samples[0][0], Math.min(samples[samples.length - 1][0], m.x)); for (let i = 1; i < samples.length; i++) { if (cx <= samples[i][0]) { const [x0, y0] = samples[i - 1], [x1, y1] = samples[i]; const t = x1 === x0 ? 0 : (cx - x0) / (x1 - x0); return y0 + t * (y1 - y0); } } return samples[samples.length - 1][1]; })();
      mk("line", { x1: X(m.x), y1: Y(0), x2: X(m.x), y2: Y(yTop), stroke: "#20242a", "stroke-width": 1.6, "stroke-dasharray": "5 4" });
      if (m.label) { const tl = mk("text", { x: X(m.x), y: Y(0) + 16, fill: "#20242a", "font-size": 12, "font-weight": 600, "text-anchor": "middle" }); tl.textContent = m.label; }
    });
    const d = samples.map((p, i) => (i ? "L" : "M") + X(p[0]).toFixed(2) + " " + Y(p[1]).toFixed(2)).join(" ");
    mk("path", { d, fill: "none", stroke: g.curveColor === "dark" ? "#20242a" : blue, "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  }
  // additional overlaid polylines (Taylor-vs-true, two functions on one axis):
  // g.curves = [{ samples:[[x,y],...], color:"blue"|"orange"|"dark" }] — each is
  // clipped to the window and broken where it exits (so blow-ups don't streak).
  (g.curves || []).forEach((cv) => {
    const cs = (cv.samples || []).map((p) => [Number(p[0]), Number(p[1])]);
    const col = cv.color === "orange" ? orange : cv.color === "dark" ? "#20242a" : blue;
    let d3 = "", pen = false;
    cs.forEach((p) => {
      if (p[1] < ymin || p[1] > ymax) { pen = false; return; }
      d3 += (pen ? "L" : "M") + X(p[0]).toFixed(1) + " " + Y(p[1]).toFixed(1) + " ";
      pen = true;
    });
    mk("path", { d: d3, fill: "none", stroke: col, "stroke-width": 2.4, "stroke-linecap": "round", "stroke-linejoin": "round" });
  });
  function curveAt(x) {
    if (!samples) return null;
    const cx = Math.max(samples[0][0], Math.min(samples[samples.length - 1][0], x));
    for (let i = 1; i < samples.length; i++) {
      if (cx <= samples[i][0]) {
        const [x0, y0] = samples[i - 1], [x1, y1] = samples[i];
        const t = x1 === x0 ? 0 : (cx - x0) / (x1 - x0);
        return y0 + t * (y1 - y0);
      }
    }
    return samples[samples.length - 1][1];
  }
  function curveSlope(x) {
    if (!samples) return null;
    const h = (samples[samples.length - 1][0] - samples[0][0]) / 200;
    return (curveAt(x + h) - curveAt(x - h)) / (2 * h);
  }
  (g.segments || []).forEach((s) => {
    mk("line", { x1: X(s.x1), y1: Y(s.y1), x2: X(s.x2), y2: Y(s.y2), stroke: s.color === "blue" ? blue : orange, "stroke-width": 2.2, "stroke-linecap": "round" });
  });
  // discrete sequence plots: g.dots = [[x, y], ...] or [{x, y, color: "orange"}, ...]
  // {open: true} draws a HOLLOW circle (an open point = a hole/limit endpoint on
  // a discontinuous graph); otherwise a filled dot (a solid point = the value).
  (g.dots || []).forEach((d) => {
    const px = Array.isArray(d) ? d[0] : d.x, py = Array.isArray(d) ? d[1] : d.y;
    const col = (!Array.isArray(d) && d.color === "orange") ? orange : blue;
    if (!Array.isArray(d) && d.open) {
      mk("circle", { cx: X(px), cy: Y(py), r: 4.5, fill: "#fff", stroke: col, "stroke-width": 2 });
    } else {
      mk("circle", { cx: X(px), cy: Y(py), r: 4.5, fill: col });
    }
  });
  // free-floating labels, e.g. curve names ({x, y, text, color: "blue"|"orange"|"dark"})
  (g.texts || []).forEach((t) => {
    const el2 = mk("text", { x: X(t.x), y: Y(t.y), fill: t.color === "orange" ? orange : t.color === "dark" ? "#20242a" : blue, "font-size": 13, "font-weight": 600, "text-anchor": "middle" });
    el2.textContent = t.text;
  });
  const overlay = mk("g", {});
  return { el: svg, X, Y, W, H, L, R, xmin, xmax, ymin, ymax, ax0, ay0, setLine, lineEnds, curveAt, curveSlope, blue, orange, mkIn: (tag, attrs) => mk(tag, attrs, overlay) };
}

// shared horizontal-drag plumbing for graph handles
function attachDrag(g, handles, setX, getX, cbs) {
  let dragging = false;
  function evtX(ev) {
    const r = g.el.getBoundingClientRect();
    const sx = r.width / g.W;
    const px = (ev.clientX - r.left) / sx;
    return g.xmin + (px - g.X(g.xmin)) / (g.X(g.xmax) - g.X(g.xmin)) * (g.xmax - g.xmin);
  }
  handles.forEach((h) => {
    h.addEventListener("pointerdown", (ev) => {
      dragging = true;
      h.setPointerCapture && h.setPointerCapture(ev.pointerId);
      ev.preventDefault();
      if (cbs && cbs.onInteract) cbs.onInteract();
    });
    h.addEventListener("pointermove", (ev) => {
      if (!dragging) return;
      setX(evtX(ev));
    });
    h.addEventListener("pointerup", () => { dragging = false; });
    h.addEventListener("pointercancel", () => { dragging = false; });
  });
}

// Draggable trace point along a line + multiple-choice grading (Brilliant's
// "explore the graph, then answer" scaffold). Grading is identical to pick.
function buildGraphTrace(mountEl, spec, cbs) {
  const gspec = spec.graph || {};
  const m = gspec.line ? Number(gspec.line.m) || 0 : 0;
  const b = gspec.line ? Number(gspec.line.b) || 0 : 0;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(gspec);
  stage.appendChild(g.el);
  const dashV = g.mkIn("line", { stroke: "#b9bec7", "stroke-width": 1.5, "stroke-dasharray": "4 4" });
  const dashH = g.mkIn("line", { stroke: "#b9bec7", "stroke-width": 1.5, "stroke-dasharray": "4 4" });
  const dot = g.mkIn("circle", { r: 5, fill: "#4f6ef7" });
  const handleY = g.ay0 + 32;
  const ring = g.mkIn("circle", { r: 13, cy: handleY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, cy: handleY, fill: "#4f6ef7", class: "ts-gt-handle" });
  let hx = g.xmin;
  function setX(x) {
    hx = Math.max(g.xmin, Math.min(g.xmax, x));
    const px = g.X(hx), py = g.Y(m * hx + b);
    ring.setAttribute("cx", px);
    knob.setAttribute("cx", px);
    dot.setAttribute("cx", px); dot.setAttribute("cy", py);
    dashV.setAttribute("x1", px); dashV.setAttribute("y1", g.ay0);
    dashV.setAttribute("x2", px); dashV.setAttribute("y2", py);
    dashH.setAttribute("x1", g.ax0); dashH.setAttribute("y1", py);
    dashH.setAttribute("x2", px); dashH.setAttribute("y2", py);
    const show = hx > g.xmin + 1e-9 ? "visible" : "hidden";
    dashV.setAttribute("visibility", show);
    dashH.setAttribute("visibility", show);
  }
  setX(g.xmin);
  attachDrag(g, [ring, knob], setX, () => hx, cbs);
  mountEl.appendChild(stage);
  return buildPick(mountEl, { options: spec.options, answer: spec.answer }, cbs);
}

// Secant explorer on a sampled curve: a fixed point + a draggable point, the
// secant through both, and a live slope readout. Without `fixed`, it becomes a
// TANGENT explorer: the orange line is the tangent at the draggable point.
// Graded like pick.
function buildCurveTrace(mountEl, spec, cbs) {
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  const hasFixed = Array.isArray(spec.fixed);
  const ax = hasFixed ? Number(spec.fixed[0]) : null;
  const label = spec.readout || (hasFixed ? "Slope" : "f′(a)");
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  stage.appendChild(g.el);
  const secant = g.mkIn("line", { stroke: g.orange, "stroke-width": 2.2, "stroke-linecap": "round" });
  if (hasFixed) g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: g.X(ax), cy: g.Y(g.curveAt(ax)) });
  const dashV = hasFixed ? null : g.mkIn("line", { stroke: "#b9bec7", "stroke-width": 1.5, "stroke-dasharray": "4 4" });
  const dot = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7" });
  const handleY = g.ay0 + 32;
  const ring = g.mkIn("circle", { r: 13, cy: handleY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, cy: handleY, fill: "#4f6ef7", class: "ts-gt-handle" });
  const fmt1 = (n) => (Math.round(n * 10) / 10).toFixed(1).replace(/-/, "−");
  let hx = spec.start != null ? Number(spec.start) : g.xmin;
  function setX(x) {
    hx = Math.max(g.xmin + 0.03, Math.min(g.xmax - 0.03, x));
    const py = g.Y(g.curveAt(hx));
    ring.setAttribute("cx", g.X(hx));
    knob.setAttribute("cx", g.X(hx));
    dot.setAttribute("cx", g.X(hx)); dot.setAttribute("cy", py);
    let s;
    if (hasFixed) {
      s = Math.abs(hx - ax) < 0.02 ? g.curveSlope(ax) : (g.curveAt(hx) - g.curveAt(ax)) / (hx - ax);
      const e = g.lineEnds(s, g.curveAt(ax) - s * ax);
      if (e) {
        secant.removeAttribute("visibility");
        secant.setAttribute("x1", e.x1); secant.setAttribute("y1", e.y1);
        secant.setAttribute("x2", e.x2); secant.setAttribute("y2", e.y2);
      } else secant.setAttribute("visibility", "hidden");
    } else {
      s = g.curveSlope(hx);
      const halfW = (g.xmax - g.xmin) * 0.11;
      secant.setAttribute("x1", g.X(hx - halfW)); secant.setAttribute("y1", g.Y(g.curveAt(hx) + s * (-halfW)));
      secant.setAttribute("x2", g.X(hx + halfW)); secant.setAttribute("y2", g.Y(g.curveAt(hx) + s * halfW));
      dashV.setAttribute("x1", g.X(hx)); dashV.setAttribute("y1", g.ay0);
      dashV.setAttribute("x2", g.X(hx)); dashV.setAttribute("y2", py);
    }
    readout.textContent = label + " = " + fmt1(s);
  }
  setX(hx);
  attachDrag(g, [ring, knob], setX, () => hx, cbs);
  mountEl.appendChild(stage);
  return buildPick(mountEl, { options: spec.options, answer: spec.answer }, cbs);
}

// Clickable region overlays on a graph ("select the region where ...").
// Single-select; graded like pick (answer = region index).
function buildRegionPick(mountEl, spec, cbs) {
  const answer = spec.answer != null ? Number(spec.answer) : null;
  let choice = null, locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  const yTop = g.Y(g.ymax) - 4, yBot = g.Y(g.ymin) + 4;
  const rects = (spec.regions || []).map((r, i) => {
    const el = g.mkIn("rect", {
      x: g.X(r.x1), y: yTop,
      width: Math.max(4, g.X(r.x2) - g.X(r.x1)), height: Math.max(4, yBot - yTop),
      rx: 10, fill: "#eef0f3", "fill-opacity": 0.45,
      stroke: "#d9dde3", "stroke-width": 1.5, class: "ts-rp-region"
    });
    el.addEventListener("click", () => {
      if (locked) return;
      choice = i;
      lessonSound.play("choice");
      rects.forEach((q, j) => {
        q.setAttribute("fill", j === i ? "#e9f7ee" : "#eef0f3");
        q.setAttribute("stroke", j === i ? "#2f9e63" : "#d9dde3");
        q.setAttribute("stroke-width", j === i ? "2" : "1.5");
      });
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (cbs && cbs.onComplete) cbs.onComplete();
    });
    return el;
  });
  mountEl.appendChild(stage);
  return {
    isComplete: () => choice != null,
    check: () => ({ correct: choice != null && practiceVerdict(spec, choice), value: choice }),
    getValue: () => choice,
    getState: () => ({ value: choice, target: answer, label: choice != null ? "picked region " + (choice + 1) : "no region yet" }),
    reset: () => {
      choice = null;
      rects.forEach((q) => { q.setAttribute("fill", "#eef0f3"); q.setAttribute("stroke", "#d9dde3"); q.setAttribute("stroke-width", "1.5"); });
    },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      rects.forEach((q, j) => {
        q.setAttribute("fill", j === a ? "#e9f7ee" : "#eef0f3");
        q.setAttribute("stroke", j === a ? "#2f9e63" : "#d9dde3");
      });
    }
  };
}

// Drag-to-target: move the point until the tangent's slope hits the target.
// The HANDLE POSITION is the graded submission (the rounded readout must match).
function buildTangentSeek(mountEl, spec, cbs) {
  const target = Number(spec.target);
  const tol = spec.tolerance != null ? Number(spec.tolerance) : 0.05;
  let moved = false, locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  stage.appendChild(g.el);
  const tangent = g.mkIn("line", { stroke: g.orange, "stroke-width": 2.2, "stroke-linecap": "round" });
  const dot = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7" });
  const dashV = g.mkIn("line", { stroke: "#b9bec7", "stroke-width": 1.5, "stroke-dasharray": "4 4" });
  const handleY = g.ay0 + 32;
  const ring = g.mkIn("circle", { r: 13, cy: handleY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, cy: handleY, fill: "#4f6ef7", class: "ts-gt-handle" });
  const fmt1 = (n) => (Math.round(n * 10) / 10).toFixed(1).replace(/-/, "−");
  let hx = spec.start != null ? Number(spec.start) : (g.xmin + g.xmax) / 2;
  function slopeAt(x) { return g.curveSlope(x); }
  function setX(x) {
    hx = Math.max(g.xmin + 0.05, Math.min(g.xmax - 0.05, x));
    const py = g.Y(g.curveAt(hx));
    ring.setAttribute("cx", g.X(hx));
    knob.setAttribute("cx", g.X(hx));
    dot.setAttribute("cx", g.X(hx)); dot.setAttribute("cy", py);
    dashV.setAttribute("x1", g.X(hx)); dashV.setAttribute("y1", g.ay0);
    dashV.setAttribute("x2", g.X(hx)); dashV.setAttribute("y2", py);
    const s = slopeAt(hx);
    const halfW = (g.xmax - g.xmin) * 0.11;
    const x1 = hx - halfW, x2 = hx + halfW;
    tangent.setAttribute("x1", g.X(x1)); tangent.setAttribute("y1", g.Y(g.curveAt(hx) + s * (x1 - hx)));
    tangent.setAttribute("x2", g.X(x2)); tangent.setAttribute("y2", g.Y(g.curveAt(hx) + s * (x2 - hx)));
    readout.textContent = "Slope = " + fmt1(s);
  }
  setX(hx);
  attachDrag(g, [ring, knob], (x) => { if (!locked) { moved = true; setX(x); } }, () => hx, {
    onInteract: () => {
      moved = true;
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (cbs && cbs.onComplete) cbs.onComplete();
    }
  });
  mountEl.appendChild(stage);
  return {
    isComplete: () => moved,
    check: () => ({ correct: practiceVerdict(spec, hx), value: hx }),
    getValue: () => hx,
    setValue: (v) => { if (!locked) { moved = true; setX(Number(v)); } },
    getState: () => ({ value: hx, target, label: "point at x = " + hx.toFixed(2) + ", slope " + fmt1(slopeAt(hx)) }),
    reset: () => { moved = false; setX(spec.start != null ? Number(spec.start) : (g.xmin + g.xmax) / 2); },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : (spec.answerX != null ? Number(spec.answerX) : hx);
      setX(a);
    }
  };
}

// Typed coefficient + live preview line through a scatter (Brilliant's "enter
// the rate of change" fit widget). Grades the entered number.
function buildSlopeFit(mountEl, spec, cbs) {
  const gspec = spec.graph || {};
  const intercept = spec.intercept != null ? Number(spec.intercept) : 0;
  const answer = Number(spec.answer);
  let locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-sf";
  const g = lineGraphSVG(gspec);
  stage.appendChild(g.el);
  const preview = g.mkIn("line", { stroke: "#4f6ef7", "stroke-width": 2.5, "stroke-linecap": "round", visibility: "hidden" });
  const eq = document.createElement("div");
  eq.className = "ts-sf-eq";
  let input = null;
  String(spec.template || "?").split(/(\?)/).forEach((piece) => {
    if (piece === "?") {
      input = document.createElement("input");
      input.type = "text";
      input.inputMode = "decimal";
      input.className = "ts-sf-input";
      input.autocomplete = "off";
      eq.appendChild(input);
    } else if (piece) {
      const s = document.createElement("span");
      s.className = "ts-sf-text";
      s.textContent = piece.replace(/-(\d)/g, "−$1");
      eq.appendChild(s);
    }
  });
  stage.appendChild(eq);
  mountEl.appendChild(stage);
  function parseVal() {
    if (!input) return null;
    let t = input.value.trim().replace(/−/g, "-");
    if (!t) return null;
    const frac = t.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
    if (frac) {
      const d = Number(frac[2]);
      return d === 0 ? null : Number(frac[1]) / d;
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  function redraw() {
    if (spec.preview === false) return;
    const v = parseVal();
    if (v == null) preview.setAttribute("visibility", "hidden");
    else g.setLine(preview, v, intercept);
  }
  if (input) input.addEventListener("input", () => {
    if (locked) return;
    redraw();
    if (cbs && cbs.onInteract) cbs.onInteract();
    if (parseVal() != null && cbs && cbs.onComplete) cbs.onComplete();
  });
  return {
    isComplete: () => parseVal() != null,
    check: () => {
      const v = parseVal();
      return { correct: v != null && practiceVerdict(spec, v), value: v };
    },
    getValue: parseVal,
    getState: () => ({ value: parseVal(), target: answer, label: input && input.value ? "entered " + input.value : "empty" }),
    reset: () => {
      if (input) input.value = "";
      redraw();
    },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      if (input) {
        input.value = String(a).replace(/-(\d)/g, "−$1");
        input.classList.add("is-answer");
        input.readOnly = true;
      }
      g.setLine(preview, a, intercept);
    }
  };
}

/* ===================================================================== *
 *  Calculus v3 handoff widgets (ported verbatim from the Lesson Studio
 *  player.js bundle). Depend only on lineGraphSVG()/attachDrag()/
 *  lessonSound already defined above. Existing widgets are unchanged.
 * ===================================================================== */

// Interactive SECANT-slider (Brilliant "slide the secant's second point toward
// the tangent point and watch the slope close in" explorer) answered by MCQ.
// A fixed tangent point sits on the curve; dragging the handle slides the
// OTHER secant endpoint along the curve while the live "Slope =" readout shows
// the secant slope converging toward the tangent's. The learner then picks the
// tangent slope. Distinct from tangentSeek (which grades a dragged x-position)
// and from a static pick — the drag exploration IS the interaction here.
// spec: { type:"secantSlider", fixed:[x,y], start:<x>, readout:"Slope",
//         options:[...], answer:<idx>, cols, graph:{...curve:{samples}} }
function buildSecantSlider(mountEl, spec, cbs) {
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  const ax = Number((spec.fixed || [0, 0])[0]);
  const label = spec.readout || "Slope";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  stage.appendChild(g.el);
  const secant = g.mkIn("line", { stroke: g.orange, "stroke-width": 2.2, "stroke-linecap": "round" });
  // fixed tangent point (stays put on the curve)
  g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: g.X(ax), cy: g.Y(g.curveAt(ax)) });
  // moving secant endpoint
  const dot = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7" });
  const handleY = g.ay0 + 32;
  const ring = g.mkIn("circle", { r: 13, cy: handleY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, cy: handleY, fill: "#4f6ef7", class: "ts-gt-handle" });
  const fmt1 = (n) => (Math.round(n * 10) / 10).toFixed(1).replace(/-/, "−");
  let hx = spec.start != null ? Number(spec.start) : g.xmin + (g.xmax - g.xmin) * 0.08;
  function setX(x) {
    hx = Math.max(g.xmin + 0.03, Math.min(g.xmax - 0.03, x));
    // never let the moving point sit exactly on the fixed one (0/0 slope)
    if (Math.abs(hx - ax) < 0.06) hx = ax + (hx >= ax ? 0.06 : -0.06);
    const py = g.Y(g.curveAt(hx));
    ring.setAttribute("cx", g.X(hx));
    knob.setAttribute("cx", g.X(hx));
    dot.setAttribute("cx", g.X(hx)); dot.setAttribute("cy", py);
    const s = (g.curveAt(hx) - g.curveAt(ax)) / (hx - ax);
    const e = g.lineEnds(s, g.curveAt(ax) - s * ax);
    if (e) {
      secant.removeAttribute("visibility");
      secant.setAttribute("x1", e.x1); secant.setAttribute("y1", e.y1);
      secant.setAttribute("x2", e.x2); secant.setAttribute("y2", e.y2);
    } else secant.setAttribute("visibility", "hidden");
    readout.textContent = label + " = " + fmt1(s);
  }
  setX(hx);
  attachDrag(g, [ring, knob], setX, () => hx, cbs);
  mountEl.appendChild(stage);
  return buildPick(mountEl, { options: spec.options, answer: spec.answer, cols: spec.cols }, cbs);
}

// Clickable POINTS on a curve ("which points have positive f'' ?").
// Drag a TANGENT along a curve and judge the SIGN of the derivative at a
// marked feature (Brilliant's "what is the derivative at the minimum?" — a
// draggable tangent, dashed guide at the feature, NO numeric readout, answered
// by a categorical Negative/Zero/Positive pick). Distinct from curveTrace
// (numeric slope readout) and secantSlider (two-point secant + numeric MCQ).
// spec: { type:"tangentSign", start:<x>, options:[...], answer:<idx>, cols,
//         graph:{...curve:{samples}, marks:[{x,label}]} }.
function buildTangentSign(mountEl, spec, cbs) {
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  const tangent = g.mkIn("line", { stroke: g.orange, "stroke-width": 2.2, "stroke-linecap": "round" });
  const dashV = g.mkIn("line", { stroke: "#b9bec7", "stroke-width": 1.5, "stroke-dasharray": "4 4" });
  const dot = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7" });
  const handleY = g.ay0 + 32;
  const ring = g.mkIn("circle", { r: 13, cy: handleY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, cy: handleY, fill: "#4f6ef7", class: "ts-gt-handle" });
  let hx = spec.start != null ? Number(spec.start) : (g.xmin + g.xmax) / 2;
  function setX(x) {
    hx = Math.max(g.xmin + 0.05, Math.min(g.xmax - 0.05, x));
    const py = g.Y(g.curveAt(hx));
    ring.setAttribute("cx", g.X(hx)); knob.setAttribute("cx", g.X(hx));
    dot.setAttribute("cx", g.X(hx)); dot.setAttribute("cy", py);
    dashV.setAttribute("x1", g.X(hx)); dashV.setAttribute("y1", g.ay0);
    dashV.setAttribute("x2", g.X(hx)); dashV.setAttribute("y2", py);
    const s = g.curveSlope(hx);
    const halfW = (g.xmax - g.xmin) * 0.12;
    tangent.setAttribute("x1", g.X(hx - halfW)); tangent.setAttribute("y1", g.Y(g.curveAt(hx) + s * (-halfW)));
    tangent.setAttribute("x2", g.X(hx + halfW)); tangent.setAttribute("y2", g.Y(g.curveAt(hx) + s * halfW));
  }
  setX(hx);
  attachDrag(g, [ring, knob], setX, () => hx, cbs);
  mountEl.appendChild(stage);
  return buildPick(mountEl, { options: spec.options, answer: spec.answer, cols: spec.cols }, cbs);
}

// Drag the upper limit b to SWEEP the shaded area under a curve; a live
// A(0, b) readout numerically integrates the curve from 0 to b. The sweep is
// EXPLORATION (Brilliant "The Area Problem" — accumulated area = the integral).
// The GRADED answer is either an options grid (spec.options + spec.answer) or a
// typed number (spec.answerNum + spec.template "A′(0, 4) = ?"). check()
// delegates to whichever answer block is present. spec.graph must carry
// curve.samples (and typically curveColor:"dark" so the fill reads under it).
function buildAreaSweep(mountEl, spec, cbs) {
  const gspec = spec.graph || {};
  const g = lineGraphSVG(gspec);
  const a0 = spec.areaFrom != null ? Number(spec.areaFrom) : 0;
  // fixed mode: no drag handle / no live readout — a STATIC shaded integral
  // from a0 to `start`, paired with a typed answer (Brilliant's "∫₀⁸ v dt = __").
  const fixed = !!spec.fixed;
  const samples = ((gspec.curve && gspec.curve.samples) || []).map((p) => [Number(p[0]), Number(p[1])]);
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  stage.appendChild(g.el);
  // overlay draw order: area fill (bottom) -> curve redraw -> bound dash -> dot -> handle
  const area = g.mkIn("path", { fill: "rgba(123,142,248,0.6)", stroke: "none" });
  const curveTop = g.mkIn("path", { fill: "none", stroke: "#20242a", "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  curveTop.setAttribute("d", samples.map((p, i) => (i ? "L" : "M") + g.X(p[0]).toFixed(2) + " " + g.Y(p[1]).toFixed(2)).join(" "));
  const dashV = g.mkIn("line", { stroke: "#20242a", "stroke-width": 1.6, "stroke-dasharray": "5 4" });
  const dot = g.mkIn("circle", { r: 5, fill: "#20242a" });
  const handleY = g.ay0 + 32;
  const ring = g.mkIn("circle", { r: 13, cy: handleY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, cy: handleY, fill: "#20242a", class: "ts-gt-handle" });
  const lbl = spec.areaLabel || "A";
  function integ(b) {
    const lo = Math.min(a0, b), hi = Math.max(a0, b), N = 240, h = (hi - lo) / N;
    let s = 0;
    for (let i = 0; i <= N; i++) { const x = lo + i * h; s += (i === 0 || i === N ? 0.5 : 1) * g.curveAt(x); }
    return s * h * (b < a0 ? -1 : 1);
  }
  let hx = spec.start != null ? Number(spec.start) : (a0 + g.xmax) / 2;
  function setX(x) {
    hx = Math.max(g.xmin + 0.02, Math.min(g.xmax - 0.02, x));
    const lo = Math.min(a0, hx), hi = Math.max(a0, hx), N = 64;
    let d = "M " + g.X(lo).toFixed(2) + " " + g.Y(0).toFixed(2);
    for (let i = 0; i <= N; i++) { const xx = lo + (hi - lo) * i / N; d += " L " + g.X(xx).toFixed(2) + " " + g.Y(Math.max(0, g.curveAt(xx))).toFixed(2); }
    d += " L " + g.X(hi).toFixed(2) + " " + g.Y(0).toFixed(2) + " Z";
    area.setAttribute("d", d);
    const py = g.Y(g.curveAt(hx));
    ring.setAttribute("cx", g.X(hx)); knob.setAttribute("cx", g.X(hx));
    dot.setAttribute("cx", g.X(hx)); dot.setAttribute("cy", py);
    dashV.setAttribute("x1", g.X(hx)); dashV.setAttribute("y1", g.ay0);
    dashV.setAttribute("x2", g.X(hx)); dashV.setAttribute("y2", py);
    if (!fixed) readout.textContent = lbl + "(" + a0 + ", b) = " + integ(hx).toFixed(2);
  }
  setX(fixed ? (spec.start != null ? Number(spec.start) : g.xmax) : hx);
  if (fixed) {
    // static integral: hide the live readout + the draggable handle/marker
    readout.style.display = "none";
    [dashV, dot, ring, knob].forEach((el) => el.setAttribute("visibility", "hidden"));
  } else {
    attachDrag(g, [ring, knob], setX, () => hx, {
      onInteract: () => { if (cbs && cbs.onInteract) cbs.onInteract(); }
    });
  }
  mountEl.appendChild(stage);
  // ---- graded answer block ----
  if (spec.options) {
    return buildPick(mountEl, { options: spec.options, answer: spec.answer, cols: spec.cols }, cbs);
  }
  // typed numeric answer (Brilliant's "A′(0, 4) = ___")
  const answer = Number(spec.answerNum);
  const tol = spec.tolerance != null ? Number(spec.tolerance) : 1e-9;
  let locked = false, input = null;
  const eq = document.createElement("div");
  eq.className = "ts-sf-eq";
  String(spec.template || "?").split(/(\?)/).forEach((piece) => {
    if (piece === "?") {
      input = document.createElement("input");
      input.type = "text"; input.inputMode = "decimal";
      input.className = "ts-sf-input"; input.autocomplete = "off";
      eq.appendChild(input);
    } else if (piece) {
      const sp = document.createElement("span");
      sp.className = "ts-sf-text";
      sp.textContent = piece.replace(/-(\d)/g, "−$1");
      eq.appendChild(sp);
    }
  });
  mountEl.appendChild(eq);
  function parseVal() {
    if (!input) return null;
    const t = input.value.trim().replace(/−/g, "-");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  if (input) input.addEventListener("input", () => {
    if (locked) return;
    if (cbs && cbs.onInteract) cbs.onInteract();
    if (parseVal() != null && cbs && cbs.onComplete) cbs.onComplete();
  });
  return {
    isComplete: () => parseVal() != null,
    check: () => { const v = parseVal(); return { correct: v != null && Math.abs(v - answer) <= tol, value: v }; },
    getValue: parseVal,
    getState: () => ({ value: parseVal(), target: answer, label: input && input.value ? "entered " + input.value : "empty" }),
    reset: () => { if (input) input.value = ""; },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      if (input) { input.value = String(a).replace(/-(\d)/g, "−$1"); input.classList.add("is-answer"); input.readOnly = true; }
    }
  };
}

// Drag a slider to increase the number of Riemann rectangles n; the shaded
// rectangles refine under the curve and a live "A(π) ≈ …" readout shows the
// Riemann-sum estimate converging to the true integral (Brilliant "The
// Integral" — estimate the area by refining the partition). n=1 midpoint over
// [0,π] of sin gives π (the bounding box), converging to 2. The graded answer
// delegates to a pick (spec.options + spec.answer). spec: { graph:{curve.samples},
// from, to, nMin(1), nMax(20), rule("mid"|"left"|"right"), areaLabel("A"),
// argLabel(upper-limit text e.g. "π"), options, answer, cols }.
function buildRiemannSlider(mountEl, spec, cbs) {
  const gspec = spec.graph || {};
  const g = lineGraphSVG(gspec);
  const from = Number(spec.from), to = Number(spec.to);
  const nMin = spec.nMin != null ? Math.round(Number(spec.nMin)) : 1;
  const nMax = spec.nMax != null ? Math.round(Number(spec.nMax)) : 20;
  const rule = spec.rule || "mid";
  const samples = ((gspec.curve && gspec.curve.samples) || []).map((p) => [Number(p[0]), Number(p[1])]);
  const lbl = spec.areaLabel || "A";
  const arg = spec.argLabel != null ? spec.argLabel : String(to);
  const NS = "http://www.w3.org/2000/svg";
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  stage.appendChild(g.el);
  const barsG = g.mkIn("g", {});
  const curveTop = g.mkIn("path", { fill: "none", stroke: "#20242a", "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  curveTop.setAttribute("d", samples.map((p, i) => (i ? "L" : "M") + g.X(p[0]).toFixed(2) + " " + g.Y(p[1]).toFixed(2)).join(" "));
  function midOf(i, bw) { const x0 = from + i * bw; return rule === "left" ? x0 : rule === "right" ? x0 + bw : x0 + bw / 2; }
  function riemann(n) { const bw = (to - from) / n; let s = 0; for (let i = 0; i < n; i++) s += g.curveAt(midOf(i, bw)) * bw; return s; }
  function drawBars(n) {
    while (barsG.firstChild) barsG.removeChild(barsG.firstChild);
    const bw = (to - from) / n;
    for (let i = 0; i < n; i++) {
      const x0 = from + i * bw, h = g.curveAt(midOf(i, bw));
      if (h <= 0) continue;
      const r = document.createElementNS(NS, "rect");
      r.setAttribute("x", g.X(x0).toFixed(2)); r.setAttribute("y", g.Y(h).toFixed(2));
      r.setAttribute("width", (g.X(x0 + bw) - g.X(x0)).toFixed(2)); r.setAttribute("height", (g.Y(0) - g.Y(h)).toFixed(2));
      r.setAttribute("fill", "rgba(123,142,248,0.55)"); r.setAttribute("stroke", "rgba(255,255,255,0.9)"); r.setAttribute("stroke-width", "1");
      barsG.appendChild(r);
    }
  }
  const slider = document.createElement("input");
  slider.type = "range"; slider.min = String(nMin); slider.max = String(nMax); slider.step = "1"; slider.value = String(nMin);
  slider.className = "ts-rs-slider";
  function update() { const n = Math.round(Number(slider.value)); drawBars(n); readout.textContent = lbl + "(" + arg + ") ≈ " + riemann(n).toFixed(4); }
  update();
  slider.addEventListener("input", () => { update(); if (cbs && cbs.onInteract) cbs.onInteract(); });
  stage.appendChild(slider);
  mountEl.appendChild(stage);
  return buildPick(mountEl, { options: spec.options, answer: spec.answer, cols: spec.cols }, cbs);
}

// Sequence-limit explorer: drag the integer index n and watch a term aₙ
// converge, with a live "aₙ formula = substituted = value" readout, then pick
// the limit (Brilliant "Sequences and Limits"). spec: { display:"3 − 1/n"
// (symbolic, uses the letter n), expr:"3 - 1/n" (JS-evaluable in n),
// nMin(1), nMax(1000), decimals(3), options, answer, cols }. The drag is
// EXPLORATION; grading delegates to the pick.
function buildSeqSlider(mountEl, spec, cbs) {
  const nMin = spec.nMin != null ? Math.round(Number(spec.nMin)) : 1;
  const nMax = spec.nMax != null ? Math.round(Number(spec.nMax)) : 1000;
  const decimals = spec.decimals != null ? Number(spec.decimals) : 3;
  const display = String(spec.display || "aₙ");
  let aOfN;
  try { aOfN = new Function("n", "return (" + (spec.expr || "0") + ");"); }
  catch (e) { aOfN = () => NaN; }
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  const slider = document.createElement("input");
  slider.type = "range"; slider.min = String(nMin); slider.max = String(nMax); slider.step = "1"; slider.value = String(nMin);
  slider.className = "ts-rs-slider";
  slider.style.width = "300px"; slider.style.margin = "0";
  const row = document.createElement("div");
  row.style.cssText = "display:flex;align-items:center;justify-content:center;gap:12px;margin:14px 0 4px;";
  const lbl = document.createElement("span");
  lbl.textContent = "n"; lbl.style.cssText = "font-style:italic;color:#565c66;font-size:15px;";
  row.appendChild(lbl); row.appendChild(slider);
  function update() {
    const n = Math.round(Number(slider.value));
    const sub = display.replace(/\bn\b/g, String(n));
    const v = aOfN(n);
    readout.textContent = display + " = " + sub + " = " + (Number.isFinite(v) ? v.toFixed(decimals) : "?");
  }
  update();
  slider.addEventListener("input", () => { update(); if (cbs && cbs.onInteract) cbs.onInteract(); });
  stage.appendChild(row);
  mountEl.appendChild(stage);
  return buildPick(mountEl, { options: spec.options, answer: spec.answer, cols: spec.cols }, cbs);
}

// Type a starting value and the whole sequence plots live as dots (Brilliant's
// "Apply: Carbon Dating" — type c0 and watch cn = c0/2^n appear at k=0..N).
// Graded on the typed number (answerNum). The live dot-plot is the delight;
// the graded mechanic is a typed-numeric answer.
function buildSeqFill(mountEl, spec, cbs) {
  const answer = Number(spec.answerNum);
  const tol = spec.tolerance != null ? Number(spec.tolerance) : 1e-9;
  const from = spec.plotFrom != null ? Math.round(Number(spec.plotFrom)) : 0;
  const to = spec.plotTo != null ? Math.round(Number(spec.plotTo)) : 3;
  let plotFn;
  try { plotFn = new Function("V", "k", "return (" + (spec.plotExpr || "0") + ");"); }
  catch (e) { plotFn = () => NaN; }
  let locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-sf";
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  const dotsG = g.mkIn("g", {});
  function drawDots(V) {
    while (dotsG.firstChild) dotsG.removeChild(dotsG.firstChild);
    if (!Number.isFinite(V)) return;
    for (let k = from; k <= to; k++) {
      const y = plotFn(V, k);
      if (!Number.isFinite(y)) continue;
      const c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      c.setAttribute("cx", g.X(k)); c.setAttribute("cy", g.Y(y));
      c.setAttribute("r", 5); c.setAttribute("fill", "#20242a");
      dotsG.appendChild(c);
    }
  }
  const eq = document.createElement("div");
  eq.className = "ts-sf-eq";
  let input = null;
  String(spec.template || (spec.label || "a") + "₀ = ?").split(/(\?)/).forEach((piece) => {
    if (piece === "?") {
      input = document.createElement("input");
      input.type = "text";
      input.inputMode = "decimal";
      input.className = "ts-sf-input";
      input.autocomplete = "off";
      eq.appendChild(input);
    } else if (piece) {
      const s = document.createElement("span");
      s.className = "ts-sf-text";
      s.textContent = piece.replace(/-(\d)/g, "−$1");
      eq.appendChild(s);
    }
  });
  stage.appendChild(eq);
  mountEl.appendChild(stage);
  function parseVal() {
    if (!input) return null;
    const t = input.value.trim().replace(/−/g, "-");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  if (input) input.addEventListener("input", () => {
    if (locked) return;
    drawDots(parseVal());
    if (cbs && cbs.onInteract) cbs.onInteract();
    if (parseVal() != null && cbs && cbs.onComplete) cbs.onComplete();
  });
  return {
    isComplete: () => parseVal() != null,
    check: () => {
      const v = parseVal();
      return { correct: v != null && practiceVerdict(spec, v), value: v };
    },
    getValue: parseVal,
    getState: () => ({ value: parseVal(), target: answer, label: input && input.value ? "entered " + input.value : "empty" }),
    reset: () => { if (input) input.value = ""; drawDots(null); },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      if (input) { input.value = String(a); input.classList.add("is-answer"); input.readOnly = true; }
      drawDots(a);
    }
  };
}

// Bare typed-number answer with an optional centered formula line (Brilliant's
// "What is S₄?" — a series/expression shown above a single typed box, NO chart).
// Graded on the typed number. Use when Brilliant shows a formula + number box
// and no graph, so we don't force a spurious empty axes onto the problem.
function buildNumFill(mountEl, spec, cbs) {
  const answer = Number(spec.answerNum != null ? spec.answerNum : spec.answer);
  const tol = spec.tolerance != null ? Number(spec.tolerance) : 1e-9;
  let locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-sf";
  if (spec.formula) {
    const f = document.createElement("div");
    f.className = "ts-nf-formula";
    f.textContent = String(spec.formula);
    stage.appendChild(f);
  }
  const eq = document.createElement("div");
  eq.className = "ts-sf-eq";
  let input = null;
  String(spec.template || "?").split(/(\?)/).forEach((piece) => {
    if (piece === "?") {
      input = document.createElement("input");
      input.type = "text"; input.inputMode = "decimal";
      input.className = "ts-sf-input"; input.autocomplete = "off";
      eq.appendChild(input);
    } else if (piece) {
      const s = document.createElement("span");
      s.className = "ts-sf-text";
      s.textContent = piece.replace(/-(\d)/g, "−$1");
      eq.appendChild(s);
    }
  });
  stage.appendChild(eq);
  mountEl.appendChild(stage);
  function parseVal() {
    if (!input) return null;
    let t = input.value.trim().replace(/−/g, "-");
    if (!t) return null;
    const frac = t.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
    if (frac) { const d = Number(frac[2]); return d === 0 ? null : Number(frac[1]) / d; }
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  if (input) input.addEventListener("input", () => {
    if (locked) return;
    if (cbs && cbs.onInteract) cbs.onInteract();
    if (parseVal() != null && cbs && cbs.onComplete) cbs.onComplete();
  });
  return {
    isComplete: () => parseVal() != null,
    check: () => { const v = parseVal(); return { correct: v != null && practiceVerdict(spec, v), value: v }; },
    getValue: parseVal,
    getState: () => ({ value: parseVal(), target: answer, label: input && input.value ? "entered " + input.value : "empty" }),
    reset: () => { if (input) input.value = ""; },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? Number(forced) : answer;
      if (input) { input.value = String(a); input.classList.add("is-answer"); input.readOnly = true; }
    }
  };
}

// Drag a handle to set δ = half-width of the input region [c−δ, c+δ] around a
// center c; the ε-band [L−ε, L+ε] is fixed. The overlap rectangle shades live
// and a "δ = X.XX" readout tracks the drag. Graded: the largest δ that keeps
// the line f(x)=L+m(x−c) inside the ε-band, i.e. δ = ε/|m| (Brilliant "Function
// Limits" ε-δ drag). A genuinely-new graded SPATIAL interaction — not a typed
// proxy. spec: {center, limit, slope, epsilon, tolerance?, start?, graph}.
function buildDeltaSeek(mountEl, spec, cbs) {
  const c = spec.center != null ? Number(spec.center) : 0;
  const L = spec.limit != null ? Number(spec.limit) : 0;
  const eps = spec.epsilon != null ? Number(spec.epsilon) : 1;
  const m = spec.slope != null ? Number(spec.slope) : 1;
  const target = eps / Math.abs(m);
  const tol = spec.tolerance != null ? Number(spec.tolerance) : Math.max(0.08, target * 0.12);
  let moved = false, locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  stage.appendChild(g.el);
  const rect = g.mkIn("rect", { fill: "rgba(245,160,74,0.20)", stroke: "none" });
  const mkDash = (a) => g.mkIn("line", Object.assign({ stroke: "#b9bec7", "stroke-width": 1.4, "stroke-dasharray": "5 4" }, a));
  mkDash({ x1: g.X(g.xmin), x2: g.X(g.xmax), y1: g.Y(L + eps), y2: g.Y(L + eps) });
  mkDash({ x1: g.X(g.xmin), x2: g.X(g.xmax), y1: g.Y(L - eps), y2: g.Y(L - eps) });
  const vL = mkDash({});
  const vR = mkDash({});
  const line = g.mkIn("line", { stroke: "#4f6ef7", "stroke-width": 2.5, "stroke-linecap": "round" });
  g.setLine(line, m, L - m * c);
  const handleY = g.Y(L);
  const ring = g.mkIn("circle", { r: 13, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle", cy: handleY });
  const knob = g.mkIn("circle", { r: 8.5, fill: "#4f6ef7", class: "ts-gt-handle", cy: handleY });
  const fmt2 = (n) => (Math.round(n * 100) / 100).toFixed(2);
  const dmax = Math.min(c - g.xmin, g.xmax - c) - 0.05;
  function setDelta(d) {
    const delta = Math.max(0, Math.min(dmax, d));
    buildDeltaSeek._d = delta;
    const xl = c - delta, xr = c + delta;
    rect.setAttribute("x", g.X(xl)); rect.setAttribute("width", g.X(xr) - g.X(xl));
    rect.setAttribute("y", g.Y(L + eps)); rect.setAttribute("height", g.Y(L - eps) - g.Y(L + eps));
    vL.setAttribute("x1", g.X(xl)); vL.setAttribute("x2", g.X(xl)); vL.setAttribute("y1", g.ay0); vL.setAttribute("y2", g.Y(g.ymax));
    vR.setAttribute("x1", g.X(xr)); vR.setAttribute("x2", g.X(xr)); vR.setAttribute("y1", g.ay0); vR.setAttribute("y2", g.Y(g.ymax));
    ring.setAttribute("cx", g.X(xr)); knob.setAttribute("cx", g.X(xr));
    readout.textContent = "δ = " + fmt2(delta);
    return delta;
  }
  let delta = setDelta(spec.start != null ? Number(spec.start) : Math.min(target * 0.45, dmax));
  attachDrag(g, [ring, knob], (x) => { if (!locked) { moved = true; delta = setDelta(x - c); } }, () => c + delta, {
    onInteract: () => { moved = true; if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); }
  });
  mountEl.appendChild(stage);
  return {
    isComplete: () => moved,
    check: () => ({ correct: practiceVerdict(spec, delta), value: delta }),
    getValue: () => delta,
    setValue: (d) => { moved = true; delta = setDelta(Number(d)); },
    getState: () => ({ value: delta, target: target, label: "δ = " + fmt2(delta) }),
    reset: () => { moved = false; delta = setDelta(spec.start != null ? Number(spec.start) : Math.min(target * 0.45, dmax)); },
    reveal: (forced) => { locked = true; delta = setDelta(forced != null ? Number(forced) : target); }
  };
}

// Drag the endpoint of a curve VERTICALLY to set f(endX); the curve is a
// parabola with a fixed vertex (vx, vy) reshaped to pass through the moving
// endpoint (Brilliant IVT "set f(4) so f(x)=3 has a solution on (1,4)"). A
// dashed horizontal target line marks y = target; graded correct once the
// endpoint clears the target so IVT forces the crossing inside the interval.
// A genuinely-new graded SPATIAL (vertical-drag) interaction. spec:
// {vertex:[vx,vy], endX, target, startY?, minY?, maxY?, margin?, graph}.
function buildEndpointSeek(mountEl, spec, cbs) {
  const vx = spec.vertex ? Number(spec.vertex[0]) : 1;
  const vy = spec.vertex ? Number(spec.vertex[1]) : 1;
  const endX = spec.endX != null ? Number(spec.endX) : 4;
  const target = spec.target != null ? Number(spec.target) : 3;
  const margin = spec.margin != null ? Number(spec.margin) : 0.05;
  let moved = false, locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  g.mkIn("line", { x1: g.X(g.xmin), x2: g.X(g.xmax), y1: g.Y(target), y2: g.Y(target), stroke: "#b9bec7", "stroke-width": 1.4, "stroke-dasharray": "5 4" });
  const path = g.mkIn("path", { fill: "none", stroke: "#4f6ef7", "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: g.X(vx), cy: g.Y(vy) });
  const ring = g.mkIn("circle", { r: 13, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 8.5, fill: "#4f6ef7", class: "ts-gt-handle" });
  const dmaxY = spec.maxY != null ? Number(spec.maxY) : g.ymax - 0.2;
  const dminY = spec.minY != null ? Number(spec.minY) : g.ymin + 0.2;
  let yEnd = spec.startY != null ? Number(spec.startY) : (vy - 0.5);
  function redraw() {
    const a = (yEnd - vy) / Math.pow(endX - vx, 2);
    let d = ""; const x0 = g.xmin + 0.1, x1 = endX + 0.3;
    for (let k = 0; k <= 40; k++) { const x = x0 + (x1 - x0) * k / 40; const y = vy + a * Math.pow(x - vx, 2); d += (k ? "L" : "M") + g.X(x).toFixed(1) + " " + g.Y(y).toFixed(1) + " "; }
    path.setAttribute("d", d);
    ring.setAttribute("cx", g.X(endX)); ring.setAttribute("cy", g.Y(yEnd));
    knob.setAttribute("cx", g.X(endX)); knob.setAttribute("cy", g.Y(yEnd));
    readout.textContent = "f(" + endX + ") = " + (Math.round(yEnd * 10) / 10).toFixed(1);
  }
  redraw();
  let dragging = false;
  function evtY(ev) {
    const r = g.el.getBoundingClientRect();
    const sy = r.height / g.H;
    const py = (ev.clientY - r.top) / sy;
    return g.ymin + (g.Y(g.ymin) - py) / (g.Y(g.ymin) - g.Y(g.ymax)) * (g.ymax - g.ymin);
  }
  [ring, knob].forEach((h) => {
    h.addEventListener("pointerdown", (ev) => { if (locked) return; dragging = true; h.setPointerCapture && h.setPointerCapture(ev.pointerId); ev.preventDefault(); moved = true; if (cbs && cbs.onInteract) cbs.onInteract(); });
    h.addEventListener("pointermove", (ev) => { if (!dragging || locked) return; yEnd = Math.max(dminY, Math.min(dmaxY, evtY(ev))); redraw(); });
    h.addEventListener("pointerup", () => { dragging = false; if (cbs && cbs.onComplete) cbs.onComplete(); });
    h.addEventListener("pointercancel", () => { dragging = false; });
  });
  mountEl.appendChild(stage);
  return {
    isComplete: () => moved,
    check: () => ({ correct: yEnd > target + margin, value: yEnd }),
    getValue: () => yEnd,
    setValue: (v) => { moved = true; yEnd = Math.max(dminY, Math.min(dmaxY, Number(v))); redraw(); },
    getState: () => ({ value: yEnd, target: target, label: "f(" + endX + ") = " + yEnd.toFixed(1) }),
    reset: () => { moved = false; yEnd = spec.startY != null ? Number(spec.startY) : (vy - 0.5); redraw(); },
    reveal: (forced) => { locked = true; yEnd = forced != null ? Number(forced) : (target + 1); redraw(); }
  };
}

// Drag a SLIDER to place the vertical asymptote (pole) x = b of a rational
// function f(x) = Π(x−r)^p / (x−b), then read off whether f attains an
// absolute maximum on a closed interval (Brilliant EVT "find b so that f has
// an absolute maximum on [0,5]"). f is continuous on the interval ⟺ the pole
// sits OUTSIDE it, so the graded win-condition is a THRESHOLD, b > passMin
// (push the pole past the right endpoint) — not a single target value. The
// curve and dashed asymptote redraw live as b changes; a genuinely-new graded
// SLIDER interaction. spec: { factors:[[root,power],...], bMin,bMax,bStart,
// bStep, passMin, margin?, interval:[a,b], graph:{xmin,xmax,ymin,ymax,...} }.
function buildPoleSlider(mountEl, spec, cbs) {
  const factors = spec.factors || [[1, 2], [5, 3]];
  const bMin = spec.bMin != null ? Number(spec.bMin) : 2;
  const bMax = spec.bMax != null ? Number(spec.bMax) : 6;
  const bStep = spec.bStep != null ? Number(spec.bStep) : 0.2;
  const passMin = spec.passMin != null ? Number(spec.passMin) : 5;
  const margin = spec.margin != null ? Number(spec.margin) : 0.1;
  const iv = spec.interval || [0, 5];
  let moved = false, locked = false;
  const NS = "http://www.w3.org/2000/svg";
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  // right-endpoint marker of the closed interval + moving pole + endpoint dots
  g.mkIn("line", { x1: g.X(iv[1]), x2: g.X(iv[1]), y1: g.Y(g.ymin), y2: g.Y(g.ymax), stroke: "#e4e7ec", "stroke-width": 1.4, "stroke-dasharray": "3 4" });
  const asym = g.mkIn("line", { stroke: "#f5a04a", "stroke-width": 1.8, "stroke-dasharray": "5 4" });
  const curvePath = g.mkIn("path", { fill: "none", stroke: "#4f6ef7", "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  const dot0 = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7" });
  g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: g.X(iv[1]), cy: g.Y(0) });
  function fval(x, b) { let n = 1; for (const fr of factors) n *= Math.pow(x - fr[0], fr[1]); return n / (x - b); }
  let bVal = spec.bStart != null ? Number(spec.bStart) : 4;
  function redraw() {
    const N = 280; let d = "", pen = false;
    for (let k = 0; k <= N; k++) {
      const x = g.xmin + (g.xmax - g.xmin) * k / N;
      if (Math.abs(x - bVal) < 0.05) { pen = false; continue; }
      const y = fval(x, bVal);
      if (y < g.ymin || y > g.ymax) { pen = false; continue; }
      d += (pen ? "L" : "M") + g.X(x).toFixed(1) + " " + g.Y(y).toFixed(1) + " ";
      pen = true;
    }
    curvePath.setAttribute("d", d);
    asym.setAttribute("x1", g.X(bVal)); asym.setAttribute("x2", g.X(bVal));
    asym.setAttribute("y1", g.Y(g.ymin)); asym.setAttribute("y2", g.Y(g.ymax));
    const f0 = fval(iv[0], bVal);
    if (f0 >= g.ymin && f0 <= g.ymax) { dot0.removeAttribute("visibility"); dot0.setAttribute("cx", g.X(iv[0])); dot0.setAttribute("cy", g.Y(f0)); }
    else dot0.setAttribute("visibility", "hidden");
    readout.textContent = "b = " + bVal.toFixed(1);
  }
  redraw();
  // slider track below the graph (its own SVG so clientX maps cleanly to b)
  const track = document.createElementNS(NS, "svg");
  track.setAttribute("viewBox", "0 0 " + g.W + " 46");
  track.setAttribute("class", "ts-gr-svg");
  track.style.marginTop = "2px";
  const tmk = (tag, a) => { const e = document.createElementNS(NS, tag); for (const k in a) e.setAttribute(k, a[k]); track.appendChild(e); return e; };
  const tx0 = 52, tx1 = g.W - 24, trackY = 28;
  const bToX = (b) => tx0 + (tx1 - tx0) * (b - bMin) / (bMax - bMin);
  tmk("line", { x1: tx0, y1: trackY, x2: tx1, y2: trackY, stroke: "#d3d8e0", "stroke-width": 4, "stroke-linecap": "round" });
  for (let b = bMin; b <= bMax + 1e-9; b += 0.5) tmk("line", { x1: bToX(b), y1: trackY - 4, x2: bToX(b), y2: trackY + 4, stroke: "#c2c8d2", "stroke-width": 1.4 });
  const lblMin = tmk("text", { x: tx0, y: 14, "text-anchor": "middle", "font-size": 11.5, fill: "#565c66" }); lblMin.textContent = bMin.toFixed(1);
  const lblMax = tmk("text", { x: tx1, y: 14, "text-anchor": "middle", "font-size": 11.5, fill: "#565c66" }); lblMax.textContent = bMax.toFixed(1);
  const tRing = tmk("circle", { r: 12, cy: trackY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const tKnob = tmk("circle", { r: 8, cy: trackY, fill: "#4f6ef7", class: "ts-gt-handle" });
  function placeKnob() { tRing.setAttribute("cx", bToX(bVal)); tKnob.setAttribute("cx", bToX(bVal)); }
  placeKnob();
  function setB(b) {
    let nb = Math.max(bMin, Math.min(bMax, b));
    nb = Math.round(nb / bStep) * bStep;
    bVal = Math.round(nb * 100) / 100;
    redraw(); placeKnob();
  }
  let dragging = false;
  function evtB(ev) {
    const r = track.getBoundingClientRect();
    const sx = r.width / g.W;
    const px = (ev.clientX - r.left) / sx;
    return bMin + (px - tx0) / (tx1 - tx0) * (bMax - bMin);
  }
  [tRing, tKnob].forEach((h) => {
    h.addEventListener("pointerdown", (ev) => { if (locked) return; dragging = true; h.setPointerCapture && h.setPointerCapture(ev.pointerId); ev.preventDefault(); moved = true; if (cbs && cbs.onInteract) cbs.onInteract(); });
    h.addEventListener("pointermove", (ev) => { if (!dragging || locked) return; setB(evtB(ev)); });
    h.addEventListener("pointerup", () => { dragging = false; if (cbs && cbs.onComplete) cbs.onComplete(); });
    h.addEventListener("pointercancel", () => { dragging = false; });
  });
  stage.appendChild(track);
  mountEl.appendChild(stage);
  return {
    isComplete: () => moved,
    check: () => ({ correct: bVal > passMin + margin, value: bVal }),
    getValue: () => bVal,
    setValue: (b) => { moved = true; setB(Number(b)); },
    getState: () => ({ value: bVal, label: "b = " + bVal.toFixed(1) }),
    reset: () => { moved = false; setB(spec.bStart != null ? Number(spec.bStart) : 4); },
    reveal: (forced) => { locked = true; setB(forced != null ? Number(forced) : (passMin + 0.6)); }
  };
}

// Drag a DEGREE slider to grow a Taylor polynomial and watch it hug the true
// function (Brilliant "Which Taylor approximation is best? / Choose the lowest
// degree to approximate f(a)"). The true fn (orange) is static; the Taylor
// partial sum (blue) redraws live as the degree N changes, clipped & broken
// where it blows up outside the window. A genuinely-new graded SLIDER
// interaction — graded degree === target (the best/lowest degree the problem
// asks for), not a value on the curve. spec: { func:"sin"|"cos"|"exp",
// degMin, degMax, degStart, degStep, target, graph:{xmin,xmax,ymin,ymax,...} }.
function buildTaylorSlider(mountEl, spec, cbs) {
  const func = spec.func || "sin";
  const degMin = spec.degMin != null ? Number(spec.degMin) : 1;
  const degMax = spec.degMax != null ? Number(spec.degMax) : 9;
  const degStep = spec.degStep != null ? Number(spec.degStep) : 2;
  const target = spec.target != null ? Number(spec.target) : degMax;
  let moved = false, locked = false;
  const NS = "http://www.w3.org/2000/svg";
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  function fact(n) { let f = 1; for (let i = 2; i <= n; i++) f *= i; return f; }
  function trueF(x) { return func === "sin" ? Math.sin(x) : func === "cos" ? Math.cos(x) : Math.exp(x); }
  function taylorF(x, d) {
    let s = 0;
    if (func === "exp") { let term = 1; s = 1; for (let n = 1; n <= d; n++) { term *= x / n; s += term; } }
    else if (func === "sin") { for (let k = 0; (2 * k + 1) <= d; k++) s += Math.pow(-1, k) / fact(2 * k + 1) * Math.pow(x, 2 * k + 1); }
    else { for (let k = 0; (2 * k) <= d; k++) s += Math.pow(-1, k) / fact(2 * k) * Math.pow(x, 2 * k); }
    return s;
  }
  const N = 170;
  const clipPath = (fn) => {
    let d2 = "", pen = false;
    for (let k = 0; k <= N; k++) {
      const x = g.xmin + (g.xmax - g.xmin) * k / N, y = fn(x);
      if (y < g.ymin || y > g.ymax) { pen = false; continue; }
      d2 += (pen ? "L" : "M") + g.X(x).toFixed(1) + " " + g.Y(y).toFixed(1) + " ";
      pen = true;
    }
    return d2;
  };
  // orange true curve (static)
  g.mkIn("path", { d: clipPath(trueF), fill: "none", stroke: "#f5a04a", "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  const bluePath = g.mkIn("path", { fill: "none", stroke: "#4f6ef7", "stroke-width": 2.5, "stroke-linecap": "round", "stroke-linejoin": "round" });
  let deg = spec.degStart != null ? Number(spec.degStart) : degMin;
  function redraw() {
    bluePath.setAttribute("d", clipPath((x) => taylorF(x, deg)));
    readout.textContent = "degree = " + deg;
  }
  redraw();
  // degree slider track below the graph
  const track = document.createElementNS(NS, "svg");
  track.setAttribute("viewBox", "0 0 " + g.W + " 46");
  track.setAttribute("class", "ts-gr-svg");
  track.style.marginTop = "2px";
  const tmk = (tag, a) => { const e = document.createElementNS(NS, tag); for (const k in a) e.setAttribute(k, a[k]); track.appendChild(e); return e; };
  const tx0 = 52, tx1 = g.W - 24, trackY = 28;
  const dToX = (dv) => tx0 + (tx1 - tx0) * (dv - degMin) / (degMax - degMin);
  tmk("line", { x1: tx0, y1: trackY, x2: tx1, y2: trackY, stroke: "#d3d8e0", "stroke-width": 4, "stroke-linecap": "round" });
  for (let dv = degMin; dv <= degMax + 1e-9; dv += degStep) tmk("line", { x1: dToX(dv), y1: trackY - 4, x2: dToX(dv), y2: trackY + 4, stroke: "#c2c8d2", "stroke-width": 1.4 });
  const lblMin = tmk("text", { x: tx0, y: 14, "text-anchor": "middle", "font-size": 11.5, fill: "#565c66" }); lblMin.textContent = String(degMin);
  const lblMax = tmk("text", { x: tx1, y: 14, "text-anchor": "middle", "font-size": 11.5, fill: "#565c66" }); lblMax.textContent = String(degMax);
  const label = tmk("text", { y: 42, "text-anchor": "middle", "font-size": 11.5, fill: "#565c66" }); label.setAttribute("x", g.W / 2); label.textContent = "degree";
  const tRing = tmk("circle", { r: 12, cy: trackY, fill: "#fff", stroke: "#dfe3e9", "stroke-width": 1.5, class: "ts-gt-handle" });
  const tKnob = tmk("circle", { r: 8, cy: trackY, fill: "#4f6ef7", class: "ts-gt-handle" });
  function placeKnob() { tRing.setAttribute("cx", dToX(deg)); tKnob.setAttribute("cx", dToX(deg)); }
  placeKnob();
  function setDeg(dv) {
    let n = Math.max(degMin, Math.min(degMax, dv));
    n = Math.round((n - degMin) / degStep) * degStep + degMin;
    deg = n; redraw(); placeKnob();
  }
  let dragging = false;
  function evtDeg(ev) {
    const r = track.getBoundingClientRect();
    const sx = r.width / g.W;
    const px = (ev.clientX - r.left) / sx;
    return degMin + (px - tx0) / (tx1 - tx0) * (degMax - degMin);
  }
  [tRing, tKnob].forEach((h) => {
    h.addEventListener("pointerdown", (ev) => { if (locked) return; dragging = true; h.setPointerCapture && h.setPointerCapture(ev.pointerId); ev.preventDefault(); moved = true; if (cbs && cbs.onInteract) cbs.onInteract(); });
    h.addEventListener("pointermove", (ev) => { if (!dragging || locked) return; setDeg(evtDeg(ev)); });
    h.addEventListener("pointerup", () => { dragging = false; if (cbs && cbs.onComplete) cbs.onComplete(); });
    h.addEventListener("pointercancel", () => { dragging = false; });
  });
  stage.appendChild(track);
  mountEl.appendChild(stage);
  return {
    isComplete: () => moved,
    check: () => ({ correct: deg === target, value: deg }),
    getValue: () => deg,
    setValue: (dv) => { moved = true; setDeg(Number(dv)); },
    getState: () => ({ value: deg, target: target, label: "degree = " + deg }),
    reset: () => { moved = false; setDeg(spec.degStart != null ? Number(spec.degStart) : degMin); },
    reveal: (forced) => { locked = true; setDeg(forced != null ? Number(forced) : target); }
  };
}

// orderTiles — tap the tiles in the order they belong (Task #485's headline
// widget: renderer + grader authored together, landed as ONE JS change).
// spec: { items:[ "5/8" | 0.5 | {label,value} ...], order:"asc"|"desc",
//         prompt?, answer? }   Submission = full permutation of item indices.
// Practice check() delegates to THE grader (graders.mjs) — one grading brain;
// reveal() arranges to the grader's canonical answer, which is computable
// from the public items even when the authored `answer` was stripped.
function buildOrderTiles(mountEl, spec, cbs) {
  const items = Array.isArray(spec.items) ? spec.items : [];
  let seq = [];      // placement order: seq[k] = item index placed at slot k
  let locked = false;
  if (spec.prompt) {
    const p = document.createElement("div");
    p.className = "ts-fw-pick-prompt";
    p.textContent = spec.prompt;
    mountEl.appendChild(p);
  }
  const hint = document.createElement("div");
  hint.className = "ts-fw-pick-prompt ts-fw-order-hint";
  hint.textContent = "Tap the tiles from " +
    (String(spec.order || "asc").toLowerCase() === "desc"
      ? "largest to smallest" : "smallest to largest") + ".";
  mountEl.appendChild(hint);
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-fw-order";
  const btns = [];
  function labelFor(it) {
    if (it && typeof it === "object") return String(it.label != null ? it.label : it.value);
    return String(it);
  }
  function paint() {
    btns.forEach((b, i) => {
      const at = seq.indexOf(i);
      b.classList.toggle("chosen", at !== -1);
      const badge = b.querySelector(".ts-fw-order-n");
      if (badge) badge.textContent = at === -1 ? "" : String(at + 1);
    });
  }
  items.forEach((it, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ts-fw-pickopt ts-fw-ordertile";
    btn.dataset.idx = String(i);
    const txt = document.createElement("div");
    txt.className = "ts-fw-pick-text";
    txt.textContent = labelFor(it).replace(/-(\d)/g, "\u2212$1");
    btn.appendChild(txt);
    const badge = document.createElement("div");
    badge.className = "ts-fw-order-n";
    badge.style.cssText = "font-weight:700;opacity:.65;font-size:12px;margin-top:2px;min-height:14px;";
    btn.appendChild(badge);
    btn.addEventListener("click", () => {
      if (locked) return;
      const at = seq.indexOf(i);
      if (at === -1) seq.push(i);
      else seq.splice(at, 1);      // tap a placed tile to take it back
      lessonSound.play("choice");
      paint();
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (seq.length === items.length && cbs && cbs.onComplete) cbs.onComplete();
    });
    stage.appendChild(btn);
    btns.push(btn);
  });
  mountEl.appendChild(stage);
  function arrangeTo(perm) {
    seq = Array.isArray(perm)
      ? perm.map(Number).filter((x) => Number.isInteger(x) && x >= 0 && x < items.length)
      : [];
    paint();
  }
  return {
    isComplete: () => seq.length === items.length,
    check: () => {
      const v = gradeWidget(spec, seq);
      return { correct: !!(v && v.correct), value: seq.slice() };
    },
    getValue: () => seq.slice(),
    setValue: (v) => { if (!locked) arrangeTo(v); },
    reset: () => { if (!locked) { seq = []; paint(); } },
    getState: () => ({
      value: seq.slice(),
      label: seq.length
        ? "arranged: " + seq.map((i) => labelFor(items[i])).join(", ") +
          (seq.length < items.length ? " (" + seq.length + " of " + items.length + " placed)" : "")
        : "nothing placed yet",
    }),
    reveal: (forced) => {
      locked = true;
      let a = forced;
      if (a == null) {
        const v = gradeWidget(spec, null);
        a = v ? v.answer : null;
      }
      if (Array.isArray(a)) arrangeTo(a);
      btns.forEach((b) => b.classList.add("is-answer", "show-val"));
    },
  };
}

/**
 * BUILDERS — the renderer registry: widget type → builder(mountEl, spec, cbs).
 * The open-ended counterpart of the grader table in graders.mjs. The shared
 * validator derives its widget-type knowledge from knownWidgetTypes(), so a
 * new widget lands by adding its builder here (plus a graders.mjs entry when
 * it is gradable) — no closed enumeration anywhere else.
 */
const BUILDERS = {
  shade: buildShade,
  build: buildBuild,
  numberline: buildNumberLine,
  compare: buildCompare,
  numberTile: buildNumberTile,
  tiles: buildTiles,
  pick: buildPick,
  signedTiles: buildSignedTiles,
  intTiles: buildIntTiles,
  graphTrace: buildGraphTrace,
  slopeFit: buildSlopeFit,
  curveTrace: buildCurveTrace,
  tangentSeek: buildTangentSeek,
  regionPick: buildRegionPick,
  pointSelect: buildPointSelect,
  overlapGrid: buildOverlapGrid,
  secantSlider: buildSecantSlider,
  tangentSign: buildTangentSign,
  areaSweep: buildAreaSweep,
  riemannSlider: buildRiemannSlider,
  seqSlider: buildSeqSlider,
  seqFill: buildSeqFill,
  numFill: buildNumFill,
  deltaSeek: buildDeltaSeek,
  endpointSeek: buildEndpointSeek,
  poleSlider: buildPoleSlider,
  taylorSlider: buildTaylorSlider,
  vecFill: buildVecFill,
  vecTiles: buildVecTiles,
  vecDraw: buildVecDraw,
  scalarSolve: buildScalarSolve,
  lincombFill: buildLincombFill,
  magDir: buildMagDir,
  polarComp: buildPolarComp,
  velBearing: buildVelBearing,
  vecScale: buildVecScale,
  scaleEq: buildScaleEq,
  unitCompare: buildUnitCompare,
  cosSelect: buildCosSelect,
  dotProduct: buildDotProduct,
  dotSolve: buildDotSolve,
  dotScale: buildDotScale,
  dotMag: buildDotMag,
  angleDot: buildAngleDot,
  selfDot: buildSelfDot,
  orthoDot: buildOrthoDot,
  combFill: buildCombFill,
  scalarChain: buildScalarChain,
  combResult: buildCombResult,
  symFill: buildSymFill,
  vecTransform: buildVecTransform,
  basisMap: buildBasisMap,
  linMap: buildLinMap,
  combMap: buildCombMap,
  matrixGrid: buildMatrixGrid,
  matVec: buildMatVec,
  matCols: buildMatCols,
  matOp: buildMatOp,
  matMul: buildMatMul,
  matSym: buildMatSym,
  matTile: buildMatTile,
  matSize: buildMatSize,
  detArea: buildDetArea,
  detComplete: buildDetComplete,
  detSym: buildDetSym,
  detFill: buildDetFill,
  invFill: buildInvFill,
  invFormula: buildInvFormula,
  symDeriv: buildSymDeriv,
  matPick: buildMatPick,
  sysLines: buildSysLines,
  sysSolve: buildSysSolve,
  sysMatrix: buildSysMatrix,
  gearSystem: buildGearSystem,
  orderTiles: buildOrderTiles,
};

/** Every widget type this renderer can build (derived, never hand-listed). */
export function knownWidgetTypes() {
  return Object.keys(BUILDERS);
}

/* ---------------------------------------------------------------------------
 * Task #524 — ONE shared fallback serializer: widget state → words.
 *
 * Every builder's getState() feeds the tutor-chat context bridge (the legend
 * "seeing" the student's work mid-activity). Builders that ship a human
 * `label` win; for the rest this compact stringifier guarantees NO widget
 * type is ever silent, whatever shape its value takes (numbers, strings,
 * arrays of inputs, small objects). The regression gate
 * (scripts/check_tutor_context.mjs) renders every type and fails loudly if a
 * state ever describes to "".
 * ------------------------------------------------------------------------- */
export function compactStateValue(v, depth = 0) {
  if (v == null) return "";
  if (typeof v === "number") {
    return Number.isFinite(v) ? String(Math.round(v * 1000) / 1000) : "";
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "string") {
    const s = v.trim();
    return s ? s.slice(0, 60) : "_"; // "_" = a blank box the student left empty
  }
  if (Array.isArray(v)) {
    if (depth >= 2) return "[\u2026]";
    const items = v.slice(0, 12).map((x) => compactStateValue(x, depth + 1) || "_");
    return "[" + items.join(", ") + (v.length > 12 ? ", \u2026" : "") + "]";
  }
  if (typeof v === "object") {
    if (depth >= 2) return "{\u2026}";
    let keys = [];
    try { keys = Object.keys(v); } catch (_) { return ""; }
    const items = keys.slice(0, 8).map(
      (k) => String(k).slice(0, 24) + ": " + (compactStateValue(v[k], depth + 1) || "_"));
    return "{" + items.join(", ") + (keys.length > 8 ? ", \u2026" : "") + "}";
  }
  return "";
}

/** Human-readable description of a widget getState() snapshot. Never throws,
 * never returns silence for a state that carries a label or any value. */
export function describeWidgetState(st) {
  try {
    if (!st || typeof st !== "object") return "";
    if (typeof st.label === "string" && st.label.trim()) {
      return st.label.trim().slice(0, 200);
    }
    const raw = st.value;
    const empty = raw == null ||
      (Array.isArray(raw) && raw.length === 0) ||
      (typeof raw === "string" && !raw.trim());
    if (empty) return "nothing entered yet";
    const v = compactStateValue(raw);
    return v ? ("current entry: " + v).slice(0, 200) : "nothing entered yet";
  } catch (_) {
    return "";
  }
}

/**
 * renderFractionWidget — entry point. Returns { check, reveal, isComplete }.
 */
export function renderFractionWidget(mountEl, spec, cbs = {}) {
  const s = spec || {};
  mountEl.classList.add("ts-fw");
  const build = BUILDERS[s.type];
  if (build) return build(mountEl, s, cbs);
  // Unknown widget — render nothing interactive but stay gradeable as a
  // no-op so the lesson never hard-crashes on a bad authored spec.
  const note = makeFeedbackNode();
  note.textContent = "This activity couldn't load.";
  mountEl.appendChild(note);
  return { isComplete: () => true, check: () => ({ correct: true }), reveal: () => {} };
}

/* ---------------------------------------------------------------- *
 *  v2 (Calculus full-course) additions — ported from the reference *
 *  player: pointSelect widget + teach-board figure helpers.        *
 * ---------------------------------------------------------------- */

// Local HTML-escaper for the string-built SVG/figure helpers below.
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// Clickable POINTS on a curve ("which points have positive f'' ?").
// Single- or multi-select; graded on the exact set of selected indices.
function buildPointSelect(mountEl, spec, cbs) {
  const answer = (Array.isArray(spec.answer) ? spec.answer : [spec.answer]).map(Number).sort();
  const multi = spec.multi !== false && answer.length > 1 || spec.multi === true;
  let locked = false;
  const chosen = new Set();
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const g = lineGraphSVG(spec.graph || {});
  stage.appendChild(g.el);
  const rings = (spec.points || []).map((p, i) => {
    const halo = g.mkIn("circle", { cx: g.X(p[0]), cy: g.Y(p[1]), r: 13, fill: "transparent", stroke: "#d3d7de", "stroke-width": 1.5, class: "ts-ps-point" });
    g.mkIn("circle", { cx: g.X(p[0]), cy: g.Y(p[1]), r: 3.5, fill: "#4f6ef7", "pointer-events": "none" });
    halo.addEventListener("click", () => {
      if (locked) return;
      if (chosen.has(i)) chosen.delete(i);
      else {
        if (!multi) chosen.clear();
        chosen.add(i);
      }
      rings.forEach((r, j) => {
        r.setAttribute("stroke", chosen.has(j) ? "#2f9e63" : "#d3d7de");
        r.setAttribute("stroke-width", chosen.has(j) ? "2.5" : "1.5");
        r.setAttribute("fill", chosen.has(j) ? "rgba(47,158,99,0.08)" : "transparent");
      });
      lessonSound.play("choice");
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (chosen.size && cbs && cbs.onComplete) cbs.onComplete();
    });
    return halo;
  });
  mountEl.appendChild(stage);
  const value = () => [...chosen].sort();
  return {
    isComplete: () => chosen.size > 0,
    check: () => {
      const v = value();
      return { correct: practiceVerdict(spec, v), value: v };
    },
    getValue: value,
    getState: () => ({ value: value(), target: answer, label: chosen.size ? "picked point(s) " + value().join(",") : "none picked" }),
    reset: () => {
      chosen.clear();
      rings.forEach((r) => { r.setAttribute("stroke", "#d3d7de"); r.setAttribute("stroke-width", "1.5"); r.setAttribute("fill", "transparent"); });
    },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? (Array.isArray(forced) ? forced : [forced]).map(Number) : answer;
      rings.forEach((r, j) => {
        const on = a.includes(j);
        r.setAttribute("stroke", on ? "#2f9e63" : "#d3d7de");
        r.setAttribute("stroke-width", on ? "2.5" : "1.5");
        r.setAttribute("fill", on ? "rgba(47,158,99,0.12)" : "transparent");
      });
    },
  };
}

// Animated bottle-fill illustration (SMIL, survives innerHTML serialization).
// Pairs with a board graph for "rate of change" intro scenes.
function bottleFillSVG() {
  return '<svg class="ts-bottle" viewBox="0 0 100 210" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="30" y="4" width="40" height="10" rx="3" fill="#c7cbd4"/>' +
    '<rect x="36" y="14" width="28" height="12" rx="3" fill="#dfe3e9"/>' +
    '<defs><clipPath id="ts-bottle-clip"><rect x="22" y="30" width="56" height="170" rx="12"/></clipPath></defs>' +
    '<rect x="22" y="30" width="56" height="170" rx="12" fill="#eef0f5"/>' +
    '<g clip-path="url(#ts-bottle-clip)"><rect x="22" y="200" width="56" height="0" fill="#7b8ef8" opacity="0.9">' +
    '<animate attributeName="y" from="200" to="55" dur="2.6s" fill="freeze"/>' +
    '<animate attributeName="height" from="0" to="145" dur="2.6s" fill="freeze"/></rect></g>' +
    '<rect x="22" y="30" width="56" height="170" rx="12" fill="none" stroke="#b9bec7" stroke-width="2.5"/>' +
    ["55", "90", "125", "160"].map((y) => '<line x1="26" y1="' + y + '" x2="36" y2="' + y + '" stroke="#c2c7d0" stroke-width="2"/>').join("") +
    "</svg>";
}
// Two bottles filling at different rates (Brilliant's constant-multiple intro):
// the wide one holds 2x the narrow one at every moment.
function bottlePairSVG() {
  return '<svg class="ts-bottle ts-bottle-pair" viewBox="0 0 190 210" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="30" y="4" width="40" height="10" rx="3" fill="#c7cbd4"/>' +
    '<rect x="36" y="14" width="28" height="12" rx="3" fill="#dfe3e9"/>' +
    '<defs><clipPath id="ts-bp-clip1"><rect x="22" y="30" width="56" height="170" rx="12"/></clipPath>' +
    '<clipPath id="ts-bp-clip2"><rect x="128" y="30" width="24" height="170" rx="9"/></clipPath></defs>' +
    '<rect x="22" y="30" width="56" height="170" rx="12" fill="#eef0f5"/>' +
    '<g clip-path="url(#ts-bp-clip1)"><rect x="22" y="200" width="56" height="0" fill="#aeb9fa" opacity="0.9">' +
    '<animate attributeName="y" from="200" to="128" dur="2.6s" fill="freeze"/>' +
    '<animate attributeName="height" from="0" to="72" dur="2.6s" fill="freeze"/></rect></g>' +
    '<rect x="22" y="30" width="56" height="170" rx="12" fill="none" stroke="#b9bec7" stroke-width="2.5"/>' +
    ["55", "90", "125", "160"].map((y) => '<line x1="26" y1="' + y + '" x2="36" y2="' + y + '" stroke="#c2c7d0" stroke-width="2"/>').join("") +
    '<rect x="128" y="30" width="24" height="170" rx="9" fill="#eef0f5"/>' +
    '<g clip-path="url(#ts-bp-clip2)"><rect x="128" y="200" width="24" height="0" fill="#7b8ef8" opacity="0.95">' +
    '<animate attributeName="y" from="200" to="56" dur="2.6s" fill="freeze"/>' +
    '<animate attributeName="height" from="0" to="144" dur="2.6s" fill="freeze"/></rect></g>' +
    '<rect x="128" y="30" width="24" height="170" rx="9" fill="none" stroke="#b9bec7" stroke-width="2.5"/>' +
    "</svg>";
}
// Gear illustrations — Brilliant's chain-rule intro. "triple" = three linked
// gears labeled x/u/y; "single" = one gear with rim point p, arc s, angle x, radius r.
function gearChainSVG(mode) {
  const tooth = (cx, cy, r, fill) => {
    let t = "";
    for (let i = 0; i < 16; i++) {
      const a = i * Math.PI / 8;
      t += '<circle cx="' + (cx + (r + 4) * Math.cos(a)).toFixed(1) + '" cy="' + (cy + (r + 4) * Math.sin(a)).toFixed(1) + '" r="6" fill="' + fill + '"/>';
    }
    return t + '<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + fill + '"/>' +
      '<circle cx="' + cx + '" cy="' + cy + '" r="13" fill="rgba(0,0,0,0.18)" stroke="#555" stroke-width="1.5"/>';
  };
  const lab = (x, y, t) => '<text x="' + x + '" y="' + y + '" font-size="14" font-weight="600" fill="#555" text-anchor="middle">' + t + "</text>";
  if (mode === "single") {
    const cx = 120, cy = 115, r = 82;
    const px = cx + r * Math.cos(-Math.PI / 3), py = cy + r * Math.sin(-Math.PI / 3);
    return '<svg class="ts-gear-fig" viewBox="0 0 290 235" xmlns="http://www.w3.org/2000/svg" style="max-width:300px">' +
      tooth(cx, cy, r, "#aeb9fa") +
      '<line x1="' + cx + '" y1="' + cy + '" x2="' + (cx + r) + '" y2="' + cy + '" stroke="#20242a" stroke-width="1.8"/>' +
      '<line x1="' + cx + '" y1="' + cy + '" x2="' + px.toFixed(1) + '" y2="' + py.toFixed(1) + '" stroke="#20242a" stroke-width="1.8"/>' +
      '<path d="M ' + (cx + 26) + " " + cy + " A 26 26 0 0 0 " + (cx + 26 * Math.cos(-Math.PI / 3)).toFixed(1) + " " + (cy + 26 * Math.sin(-Math.PI / 3)).toFixed(1) + '" fill="none" stroke="#4f6ef7" stroke-width="3"/>' +
      '<path d="M ' + (cx + r + 16) + " " + cy + " A " + (r + 16) + " " + (r + 16) + ' 0 0 0 ' + (cx + (r + 16) * Math.cos(-Math.PI / 3)).toFixed(1) + " " + (cy + (r + 16) * Math.sin(-Math.PI / 3)).toFixed(1) + '" fill="none" stroke="#20242a" stroke-width="1.6"/>' +
      '<circle cx="' + px.toFixed(1) + '" cy="' + py.toFixed(1) + '" r="5" fill="#4f6ef7"/>' +
      lab(cx + 40, cy - 14, "x") + lab(cx + r / 2 + 10, cy + 18, "r") + lab(px + 12, py - 10, "p") + lab(cx + (r + 34) * Math.cos(-Math.PI / 5.5), cy + (r + 34) * Math.sin(-Math.PI / 5.5), "s") +
      "</svg>";
  }
  return '<svg class="ts-gear-fig" viewBox="0 0 420 200" xmlns="http://www.w3.org/2000/svg" style="max-width:430px">' +
    tooth(100, 100, 72, "#8b9bf9") + tooth(213, 100, 26, "#f2d377") + tooth(295, 100, 44, "#f0a0a0") +
    '<line x1="100" y1="100" x2="168" y2="100" stroke="#555" stroke-width="1.6"/>' +
    '<line x1="213" y1="100" x2="236" y2="100" stroke="#555" stroke-width="1.6"/>' +
    '<line x1="295" y1="100" x2="336" y2="100" stroke="#555" stroke-width="1.6"/>' +
    lab(140, 92, "x") + lab(227, 92, "u") + lab(318, 92, "y") +
    "</svg>";
}
// Area diagram — Brilliant's product/power-rule square: a base rectangle with
// optional Δ-strips ("dashed" outline or "orange" filled) and side labels.
// spec: {w, h, top, left, dtop, dleft, center, strips: "dashed"|"orange"|null}
function areaDiagramSVG(o) {
  const W = o.w || 170, H = o.h || 170, D = 30, x0 = 46, y0 = 34;
  const hasStrips = o.strips === "dashed" || o.strips === "orange";
  const tw = x0 + W + (hasStrips ? D + 18 : 18), th = y0 + H + (hasStrips ? D + 16 : 16);
  let s = '<svg class="ts-area-diagram" viewBox="0 0 ' + tw + " " + th + '" xmlns="http://www.w3.org/2000/svg" style="max-width:' + Math.round(tw * 1.35) + 'px">';
  const label = (x, y, t, anchor) => '<text x="' + x + '" y="' + y + '" font-size="14" font-weight="600" fill="#20242a" text-anchor="' + (anchor || "middle") + '">' + esc(t) + "</text>";
  if (o.strips === "orange") {
    s += '<rect x="' + (x0 + W) + '" y="' + y0 + '" width="' + D + '" height="' + H + '" fill="#e8912d"/>';
    s += '<rect x="' + x0 + '" y="' + (y0 + H) + '" width="' + W + '" height="' + D + '" fill="#e8912d"/>';
    s += '<rect x="' + (x0 + W) + '" y="' + (y0 + H) + '" width="' + D + '" height="' + D + '" fill="none" stroke="#c9ccd4" stroke-width="1.6" stroke-dasharray="4 3"/>';
  }
  if (hasStrips) {
    s += '<rect x="' + x0 + '" y="' + y0 + '" width="' + (W + D) + '" height="' + (H + D) + '" fill="none" stroke="#20242a" stroke-width="2" stroke-dasharray="5 4"/>';
  }
  s += '<rect x="' + x0 + '" y="' + y0 + '" width="' + W + '" height="' + H + '" fill="#f2f3f6" stroke="#20242a" stroke-width="2.2"/>';
  if (o.top) s += label(x0 + W / 2, y0 - 12, o.top);
  if (o.left) s += label(x0 - 12, y0 + H / 2 + 5, o.left, "end");
  if (hasStrips && o.dtop) s += label(x0 + W + D / 2, y0 - 12, o.dtop);
  if (hasStrips && o.dleft) s += label(x0 - 12, y0 + H + D / 2 + 5, o.dleft, "end");
  if (o.center) s += label(x0 + W / 2, y0 + H / 2 + 6, o.center);
  return s + "</svg>";
}
// SMIL helper — animations survive innerHTML serialization and start on insert.
function smil(el, attr, opts) {
  const a = document.createElementNS("http://www.w3.org/2000/svg", "animate");
  a.setAttribute("attributeName", attr);
  if (opts.values) a.setAttribute("values", opts.values);
  else {
    a.setAttribute("from", opts.from);
    a.setAttribute("to", opts.to);
  }
  a.setAttribute("dur", opts.dur || "2.6s");
  a.setAttribute("fill", "freeze");
  if (opts.begin) a.setAttribute("begin", opts.begin);
  el.appendChild(a);
}
// Animated / illustrated teach-card scenes drawn over a board graph.
function sceneOverlay(g, scene) {
  const k = scene.kind;
  if (k === "secantConverge") {
    // a secant through (dotFrom → fixed) slides into the tangent at `fixed`
    const line = g.mkIn("line", {
      stroke: g.orange, "stroke-width": 2.2, "stroke-linecap": "round",
      x1: g.X(scene.from.x1), y1: g.Y(scene.from.y1), x2: g.X(scene.from.x2), y2: g.Y(scene.from.y2),
    });
    smil(line, "x1", { from: g.X(scene.from.x1), to: g.X(scene.to.x1) });
    smil(line, "y1", { from: g.Y(scene.from.y1), to: g.Y(scene.to.y1) });
    smil(line, "x2", { from: g.X(scene.from.x2), to: g.X(scene.to.x2) });
    smil(line, "y2", { from: g.Y(scene.from.y2), to: g.Y(scene.to.y2) });
    g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: g.X(scene.fixed[0]), cy: g.Y(scene.fixed[1]) });
    const dot = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: g.X(scene.dotFrom[0]), cy: g.Y(scene.dotFrom[1]) });
    smil(dot, "cx", { from: g.X(scene.dotFrom[0]), to: g.X(scene.fixed[0]) });
    smil(dot, "cy", { from: g.Y(scene.dotFrom[1]), to: g.Y(scene.fixed[1]) });
  } else if (k === "zoomLens") {
    // magnifier over a curve point: up close, the curve is its tangent
    const px = scene.point[0], py = scene.point[1];
    const cx = g.X(px), cy = g.Y(py);
    g.mkIn("circle", { cx, cy, r: 10, fill: "none", stroke: "#20242a", "stroke-width": 1.5 });
    g.mkIn("circle", { cx, cy, r: 3.5, fill: "#4f6ef7" });
    const lx = cx + (scene.lensDx != null ? scene.lensDx : 70), ly = cy - (scene.lensDy != null ? scene.lensDy : 85);
    const R = 46;
    g.mkIn("line", { x1: cx + 7, y1: cy - 7, x2: lx - R * 0.6, y2: ly + R * 0.75, stroke: "#20242a", "stroke-width": 1.2 });
    g.mkIn("circle", { cx: lx, cy: ly, r: R, fill: "#fcfcfd", stroke: "#20242a", "stroke-width": 1.5 });
    const s = scene.slope != null ? scene.slope : g.curveSlope(px);
    const sxu = g.X(px + 1) - g.X(px), syu = g.Y(py + 1) - g.Y(py);
    const ang = Math.atan2(s * syu, sxu);
    const dx = Math.cos(ang) * R * 0.72, dy = Math.sin(ang) * R * 0.72;
    g.mkIn("line", { x1: lx - dx, y1: ly - dy, x2: lx + dx, y2: ly + dy, stroke: g.blue, "stroke-width": 3, "stroke-linecap": "round" });
    g.mkIn("circle", { cx: lx, cy: ly, r: 4, fill: "#4f6ef7" });
  } else if (k === "tiltLine") {
    // a line through the origin sweeping between slopes (f(x) = mx intro)
    const ms = scene.slopes || [-0.8, 0.9, 0.6];
    const ends = (m) => {
      const e = g.lineEnds(m, 0);
      return e || { x1: g.X(g.xmin), y1: g.Y(0), x2: g.X(g.xmax), y2: g.Y(0) };
    };
    const first = ends(ms[0]);
    const line = g.mkIn("line", { stroke: g.blue, "stroke-width": 2.5, "stroke-linecap": "round", x1: first.x1, y1: first.y1, x2: first.x2, y2: first.y2 });
    ["x1", "y1", "x2", "y2"].forEach((attr) => {
      smil(line, attr, { values: ms.map((m) => ends(m)[attr]).join(";"), dur: "3s" });
    });
  } else if (k === "tangentSweep") {
    // a tangent segment rides the curve from x0 to x1 (Brilliant's Play sweep)
    const x0 = scene.x0 != null ? scene.x0 : g.xmin + 0.1;
    const x1 = scene.x1 != null ? scene.x1 : g.xmax - 0.1;
    const n = 24, half = (g.xmax - g.xmin) * 0.1;
    const dotsX = [], dotsY = [], e1x = [], e1y = [], e2x = [], e2y = [];
    for (let i = 0; i <= n; i++) {
      const x = x0 + (x1 - x0) * (i / n);
      const y = g.curveAt(x), s = g.curveSlope(x);
      dotsX.push(g.X(x).toFixed(1)); dotsY.push(g.Y(y).toFixed(1));
      e1x.push(g.X(x - half).toFixed(1)); e1y.push(g.Y(y - s * half).toFixed(1));
      e2x.push(g.X(x + half).toFixed(1)); e2y.push(g.Y(y + s * half).toFixed(1));
    }
    const seg = g.mkIn("line", { stroke: g.orange, "stroke-width": 2.2, "stroke-linecap": "round", x1: e1x[0], y1: e1y[0], x2: e2x[0], y2: e2y[0] });
    smil(seg, "x1", { values: e1x.join(";"), dur: "3s" });
    smil(seg, "y1", { values: e1y.join(";"), dur: "3s" });
    smil(seg, "x2", { values: e2x.join(";"), dur: "3s" });
    smil(seg, "y2", { values: e2y.join(";"), dur: "3s" });
    const dot = g.mkIn("circle", { r: 4.5, fill: "#4f6ef7", cx: dotsX[0], cy: dotsY[0] });
    smil(dot, "cx", { values: dotsX.join(";"), dur: "3s" });
    smil(dot, "cy", { values: dotsY.join(";"), dur: "3s" });
  } else if (k === "dropTrace") {
    // a marker rides the height curve while a "phone" falls beside the axis
    const pts = [];
    const n = 18;
    for (let i = 0; i <= n; i++) {
      const x = g.xmin + (g.xmax - g.xmin) * (i / n);
      pts.push([g.X(x), g.Y(g.curveAt(x))]);
    }
    const dot = g.mkIn("circle", { r: 5, fill: "#4f6ef7", cx: pts[0][0], cy: pts[0][1] });
    smil(dot, "cx", { values: pts.map((p) => p[0].toFixed(1)).join(";"), dur: "2.2s" });
    smil(dot, "cy", { values: pts.map((p) => p[1].toFixed(1)).join(";"), dur: "2.2s" });
    const phone = g.mkIn("rect", { x: g.X(g.xmin) - 34, y: pts[0][1] - 18, width: 18, height: 30, rx: 4, fill: "#20242a" });
    smil(phone, "y", { values: pts.map((p) => (p[1] - 18).toFixed(1)).join(";"), dur: "2.2s" });
  }
}
// A `toggle` spec ({labels, panels:[graphSpec,...], active}) renders one
// switchable panel under the fixed one — Brilliant's f′/f″/f⁽³⁾ pill switcher.
function figToggleHTML(tog) {
  if (!tog || !Array.isArray(tog.panels) || !tog.panels.length) return "";
  const act = Math.min(Math.max(0, tog.active | 0), tog.panels.length - 1);
  let panels = "", tabs = "";
  tog.panels.forEach((spec, i) => {
    let svg = "";
    try {
      svg = lineGraphSVG(spec).el.outerHTML;
    } catch (_) {}
    panels += '<div class="ts-fig-panel"' + (i === act ? "" : " hidden") + ' data-i="' + i + '">' + svg + "</div>";
    const lab = tog.labels && tog.labels[i] != null ? tog.labels[i] : "f" + "\u2032".repeat(i + 1);
    tabs += '<button type="button" class="ts-fig-tab' + (i === act ? " active" : "") + '" data-i="' + i + '">' + esc(String(lab)) + "</button>";
  });
  return '<div class="ts-fig-toggle">' + panels + '<div class="ts-fig-tabs">' + tabs + "</div></div>";
}
// One document-level delegate drives every fig-toggle (boards are re-rendered
// via innerHTML, so per-node listeners would be lost). Guarded so the module
// stays importable in Node (scripts/validate_learn_course.mjs).
if (typeof document !== "undefined") document.addEventListener("click", (e) => {
  const tab = e.target && e.target.closest ? e.target.closest(".ts-fig-tab") : null;
  if (!tab) return;
  const wrap = tab.closest(".ts-fig-toggle");
  if (!wrap) return;
  const i = tab.dataset.i;
  wrap.querySelectorAll(".ts-fig-tab").forEach((b) => b.classList.toggle("active", b === tab));
  wrap.querySelectorAll(".ts-fig-panel").forEach((p) => {
    p.hidden = p.dataset.i !== i;
  });
});
// Isometric 3D scenes for surfaces of revolution (Calculus Level 5).
// spec = { type: "revolveRegion" | "sphere" | "rects" | "cylinders" | "cone" |
//          "polys" | "polyCircle" | "horn" | "sphereShell" | "cylinderDx", n }
// Static SVG projections of Brilliant's live Angle/Bars-slider figures.
function iso3dSVG(spec) {
  const W = 460, H = 340, cx = 230, cy = 180, k = 92;
  const P = (x, y, z) => [cx + (x - z) * 0.866 * k, cy - y * k + (x + z) * 0.5 * k];
  const f = (x) => Math.sqrt(Math.max(0, 1 - x * x));
  let out = "";
  const line = (a, b, attrs) => { const p1 = P(...a), p2 = P(...b); out += '<line x1="' + p1[0].toFixed(1) + '" y1="' + p1[1].toFixed(1) + '" x2="' + p2[0].toFixed(1) + '" y2="' + p2[1].toFixed(1) + '" ' + (attrs || 'stroke="#9aa1ab" stroke-width="1.3"') + "/>"; };
  const path = (pts, attrs, close) => { let d = pts.map((p, i) => (i ? "L" : "M") + P(...p).map((v) => v.toFixed(1)).join(" ")).join(" "); if (close) d += " Z"; out += '<path d="' + d + '" ' + attrs + "/>"; };
  const label = (p3, text) => { const p = P(...p3); out += '<text x="' + p[0].toFixed(1) + '" y="' + p[1].toFixed(1) + '" font-size="13" fill="#5b6470" font-style="italic">' + text + "</text>"; };
  const circleYZ = (x0, R, attrs) => { const pts = []; for (let i = 0; i <= 28; i++) { const th = i / 28 * 2 * Math.PI; pts.push([x0, R * Math.cos(th), R * Math.sin(th)]); } path(pts, attrs, true); };
  const semiPts = []; for (let i = 0; i <= 40; i++) { const x = -1 + 2 * i / 40; semiPts.push([x, f(x), 0]); }
  const axes = () => { line([-1.55, 0, 0], [1.85, 0, 0]); line([0, -1.35, 0], [0, 1.7, 0]); line([0, 0, -1.1], [0, 0, 1.75]); label([1.95, 0.02, 0], "x"); label([0.04, 1.82, 0], "y"); label([0, 0.02, 1.95], "z"); };
  const t = spec.type;
  const blue = "rgba(123,142,248,0.75)", edge = 'stroke="#20242a" stroke-width="1.4"';
  if (t === "revolveRegion") {
    axes();
    path(semiPts.concat([[1, 0, 0], [-1, 0, 0]]), 'fill="' + blue + '" ' + edge, true);
  } else if (t === "sphere") {
    axes();
    out += '<circle cx="' + cx + '" cy="' + cy + '" r="' + k + '" fill="rgba(123,142,248,0.55)" stroke="#20242a" stroke-width="1.4"/>';
    circleYZ(0, 1, 'fill="none" stroke="#20242a" stroke-width="1.1" stroke-dasharray="5 4"');
  } else if (t === "rects") {
    const n = spec.n || 3, w = 2 / n;
    axes();
    path(semiPts, 'fill="none" ' + edge);
    for (let i = 0; i < n; i++) { const x0 = -1 + i * w, x1 = x0 + w, h = f(x0 + w / 2); path([[x0, 0, 0], [x1, 0, 0], [x1, h, 0], [x0, h, 0]], 'fill="rgba(123,142,248,0.8)" ' + edge, true); }
  } else if (t === "cylinders") {
    const n = spec.n || 8, w = 2 / n;
    axes();
    for (let i = 0; i < n; i++) { const x0 = -1 + i * w, x1 = x0 + w, R = f(x0 + w / 2);
      circleYZ(x0, R, 'fill="rgba(123,142,248,0.45)" stroke="#20242a" stroke-width="1"');
      const top0 = P(x0, R, 0), top1 = P(x1, R, 0), bot0 = P(x0, -R, 0), bot1 = P(x1, -R, 0);
      out += '<line x1="' + top0[0].toFixed(1) + '" y1="' + top0[1].toFixed(1) + '" x2="' + top1[0].toFixed(1) + '" y2="' + top1[1].toFixed(1) + '" stroke="#20242a" stroke-width="1"/>';
      out += '<line x1="' + bot0[0].toFixed(1) + '" y1="' + bot0[1].toFixed(1) + '" x2="' + bot1[0].toFixed(1) + '" y2="' + bot1[1].toFixed(1) + '" stroke="#20242a" stroke-width="1"/>';
      circleYZ(x1, R, 'fill="rgba(93,113,226,0.85)" stroke="#20242a" stroke-width="1"');
    }
  } else if (t === "cone") {
    // f(x) = (r/h)x revolved about the x-axis: apex at the origin, base at x = 1.45
    axes();
    const bx = 1.45, R = 0.6;
    path([[0, 0, 0], [bx, R, 0], [bx, -R, 0]], 'fill="rgba(123,142,248,0.5)" stroke="#20242a" stroke-width="1.3"', true);
    circleYZ(bx, R, 'fill="rgba(72,101,244,0.85)" stroke="#20242a" stroke-width="1.3"');
    line([0, 0, 0], [bx, 0, 0], 'stroke="#20242a" stroke-width="1.1" stroke-dasharray="5 4"');
    line([bx, 0, 0], [bx, R, 0], 'stroke="#20242a" stroke-width="1.1" stroke-dasharray="5 4"');
    label([0.65, -0.22, 0], "h"); label([bx + 0.08, R * 0.45, 0], "r");
  } else if (t === "polys") {
    // generic flat polygon scene in unit-square coordinates (y up), rotated slightly
    const S = 252, rot = -0.32, cs = Math.cos(rot), sn = Math.sin(rot);
    const T = (p) => { const ux = p[0] - 0.5, uy = p[1] - 0.5; const rx = ux * cs - uy * sn, ry = ux * sn + uy * cs; return [(cx + rx * S).toFixed(1), (cy - ry * S).toFixed(1)]; };
    (spec.polys || []).forEach((pg) => {
      out += '<path d="' + pg.pts.map((p, i) => (i ? "L " : "M ") + T(p).join(" ")).join(" ") + ' Z" fill="' + (pg.fill || "none") + '" stroke="' + (pg.stroke || "#20242a") + '" stroke-width="1.4"/>';
    });
  } else if (t === "polyCircle") {
    // regular n-gon inscribed in a circle (flat 2D scene, no axes)
    const n = Math.max(3, spec.n || 6), R = 118;
    out += '<circle cx="' + cx + '" cy="' + cy + '" r="' + R + '" fill="none" stroke="#20242a" stroke-width="1.6"/>';
    let d = "";
    for (let i = 0; i < n; i++) { const th = -Math.PI / 2 + i * 2 * Math.PI / n; const px = cx + R * Math.cos(th), py = cy + R * Math.sin(th); d += (i ? " L " : "M ") + px.toFixed(1) + " " + py.toFixed(1); out += '<circle cx="' + px.toFixed(1) + '" cy="' + py.toFixed(1) + '" r="3.4" fill="#20242a"/>'; }
    out = '<path d="' + d + ' Z" fill="rgba(123,142,248,0.75)" stroke="#20242a" stroke-width="1.5"/>' + out;
  } else if (t === "horn") {
    // Gabriel's Horn: f(x) = 1/x (x ≥ 1) revolved about the x-axis
    axes();
    const fh = (x) => 0.32 / x, hx0 = 0.3, hx1 = 2.05;
    const top = [], bot = [];
    for (let i = 0; i <= 30; i++) { const x = hx0 + (hx1 - hx0) * i / 30; top.push([x, fh(x), 0]); bot.push([hx1 + hx0 - x, -fh(hx1 + hx0 - x), 0]); }
    path(top.concat(bot), 'fill="rgba(123,142,248,0.6)" stroke="#20242a" stroke-width="1.3"', true);
    circleYZ(hx0, fh(hx0), 'fill="rgba(160,175,252,0.7)" stroke="#20242a" stroke-width="1.2"');
    circleYZ(hx1, fh(hx1), 'fill="rgba(72,101,244,0.9)" stroke="#20242a" stroke-width="1"');
  } else if (t === "sphereShell") {
    // sphere of radius r plus a shell of thickness Δr (cutaway suggestion)
    out += '<circle cx="' + cx + '" cy="' + cy + '" r="' + (k * 1.22) + '" fill="rgba(93,113,226,0.5)" stroke="#20242a" stroke-width="1.4"/>';
    out += '<circle cx="' + cx + '" cy="' + cy + '" r="' + k + '" fill="rgba(160,175,252,0.85)" stroke="#20242a" stroke-width="1.2"/>';
    out += '<line x1="' + cx + '" y1="' + cy + '" x2="' + (cx - k) + '" y2="' + cy + '" stroke="#20242a" stroke-width="1.6"/>';
    out += '<line x1="' + (cx - k) + '" y1="' + cy + '" x2="' + (cx - k * 1.22) + '" y2="' + cy + '" stroke="#20242a" stroke-width="1.6"/>';
    out += '<text x="' + (cx - k * 0.5) + '" y="' + (cy - 8) + '" font-size="13" fill="#20242a" font-style="italic">r</text>';
    out += '<text x="' + (cx - k * 1.34) + '" y="' + (cy - 8) + '" font-size="13" fill="#20242a">Δr</text>';
    circleYZ(0, 1, 'fill="none" stroke="#20242a" stroke-width="1" stroke-dasharray="5 4"');
  } else if (t === "cylinderDx") {
    const R = 0.95, x0 = -0.55, x1 = 0.55;
    circleYZ(x0, R, 'fill="rgba(123,142,248,0.4)" stroke="#20242a" stroke-width="1.4"');
    const top0 = P(x0, R, 0), top1 = P(x1, R, 0), bot0 = P(x0, -R, 0), bot1 = P(x1, -R, 0);
    out += '<line x1="' + top0[0].toFixed(1) + '" y1="' + top0[1].toFixed(1) + '" x2="' + top1[0].toFixed(1) + '" y2="' + top1[1].toFixed(1) + '" stroke="#20242a" stroke-width="1.4"/>';
    out += '<line x1="' + bot0[0].toFixed(1) + '" y1="' + bot0[1].toFixed(1) + '" x2="' + bot1[0].toFixed(1) + '" y2="' + bot1[1].toFixed(1) + '" stroke="#20242a" stroke-width="1.4"/>';
    circleYZ(x1, R, 'fill="rgba(72,101,244,0.9)" stroke="#20242a" stroke-width="1.4"');
    const lp = P(x0 - 0.05, -R - 0.35, 0); out += '<text x="' + lp[0].toFixed(1) + '" y="' + lp[1].toFixed(1) + '" font-size="14" fill="#20242a" font-style="italic">dx</text>';
    const rp = P(x1 + 0.25, -R - 0.25, 0); out += '<text x="' + rp[0].toFixed(1) + '" y="' + rp[1].toFixed(1) + '" font-size="14" fill="#20242a">πr²</text>';
  }
  return '<svg viewBox="0 0 ' + W + " " + H + '" xmlns="http://www.w3.org/2000/svg" style="max-width:420px;width:100%;display:block;margin:0 auto">' + out + "</svg>";
}
// Teach-board figures — boards may carry a `graph` (lineGraphSVG spec),
// `graphs` (stacked panels), a `toggle` (switchable panel), `scene`,
// `diagram`, or `scene3d`; the figure renders ON the card like Brilliant's
// teach pages. Returns an HTML string for TutorStageController._boardHTML.
export function boardFigureHTML(board) {
  if (board.scene3d) {
    return '<div class="ts-board-fig">' + iso3dSVG(board.scene3d) + "</div>";
  }
  if (board.diagram) {
    return '<div class="ts-board-fig">' + areaDiagramSVG(board.diagram) + "</div>";
  }
  const togHTML = figToggleHTML(board.toggle);
  if (Array.isArray(board.graphs)) {
    let out = "";
    board.graphs.forEach((spec) => {
      try {
        out += lineGraphSVG(spec).el.outerHTML;
      } catch (_) {}
    });
    out += togHTML;
    return out ? '<div class="ts-board-fig ts-board-fig-stack">' + out + "</div>" : "";
  }
  let g = null;
  if (board.graph) {
    try {
      g = lineGraphSVG(board.graph);
    } catch (_) {
      g = null;
    }
  }
  if (board.scene === "bottleFill") {
    return '<div class="ts-scene-fill">' + (g ? g.el.outerHTML : "") + bottleFillSVG() + "</div>";
  }
  if (board.scene === "bottlePair") {
    return '<div class="ts-scene-fill">' + (g ? g.el.outerHTML : "") + bottlePairSVG() + "</div>";
  }
  if (board.scene === "gearChain" || board.scene === "gearSingle") {
    return '<div class="ts-board-fig">' + gearChainSVG(board.scene === "gearSingle" ? "single" : "triple") + "</div>";
  }
  if (g && board.scene && typeof board.scene === "object") {
    try {
      sceneOverlay(g, board.scene);
    } catch (_) {}
  }
  if (togHTML) return '<div class="ts-board-fig ts-board-fig-stack">' + (g ? g.el.outerHTML : "") + togHTML + "</div>";
  return g ? '<div class="ts-board-fig">' + g.el.outerHTML + "</div>" : "";
}

/* ---------------------------------------------------------------- *
 *  v3 (Vectors & Matrices full-course) additions — ported verbatim *
 *  from the audited handoff player (LGA-1 GO, 921/921 grading      *
 *  sweep clean). 47 widget builders + helpers.                      *
 * ---------------------------------------------------------------- */

// Fraction-string parser used by the vector/matrix builders' scalar specs
// (accepts "a/b" strings, {num,den} objects, or plain integers).
function parseFrac(v) {
  if (v && typeof v === "object" && v.den != null) {
    return { num: Math.trunc(Number(v.num) || 0), den: Math.trunc(Number(v.den) || 1) || 1 };
  }
  const s = String(v == null ? "" : v).trim();
  const m = s.match(/^(-?\d+)\s*\/\s*(-?\d+)$/);
  if (m) return { num: parseInt(m[1], 10), den: parseInt(m[2], 10) || 1 };
  const n = parseInt(s, 10);
  return { num: isNaN(n) ? 0 : n, den: 1 };
}

// Vector component fill-in (Vectors & Matrices course). Points + hollow
// targets on a grid; the learner types the vector's components and a live
// arrow preview shows every point shifted by the entered vector.
// spec: { type:"vecFill", points:[[x,y],...], targets:[[x,y],...],
//         answer:[dx,dy], fixedX / fixedY (number: that component is given),
//         graph:{xmin,xmax,ymin,ymax,xticks,yticks} (optional — omit for the
//         pure typed-entry variant with shifts too big for a grid) }
// Render a scalar coefficient (Scalar Multiplication): a plain number inline,
// or a proper stacked fraction when given as an "a/b" string (e.g. "-1/3").
function scalarNode(n) {
  const s = document.createElement("span"); s.className = "ts-vt-scalar";
  const str = String(n).replace(/-/g, "−");
  const m = str.match(/^(−?)(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    if (m[1]) { const sg = document.createElement("span"); sg.textContent = "−"; sg.style.marginRight = "2px"; s.appendChild(sg); }
    const f = document.createElement("span"); f.className = "ts-vt-frac";
    const nu = document.createElement("span"); nu.className = "ts-vt-frac-num"; nu.textContent = m[2];
    const de = document.createElement("span"); de.className = "ts-vt-frac-den"; de.textContent = m[3];
    f.appendChild(nu); f.appendChild(de); s.appendChild(f);
  } else { s.textContent = str; }
  return s;
}
function buildVecFill(mountEl, spec, cbs) {
  const answer = (spec.answer || []).map(Number);
  const pts = (spec.points || []).map((p) => [Number(p[0]), Number(p[1])]);
  let locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-vf";
  let g = null;
  let arrows = [];
  if (spec.graph && pts.length) {
    const gspec = Object.assign({ grid: { step: 1 } }, spec.graph);
    gspec.dots = pts.map((p) => [p[0], p[1]]);
    // square units, Brilliant-style: derive the height from the ranges so one
    // grid step is the same length on both axes
    const W0 = gspec.w || 380;
    const plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    (spec.targets || []).forEach((t) => {
      g.mkIn("circle", { cx: g.X(Number(t[0])), cy: g.Y(Number(t[1])), r: 6.5, fill: "#fff", stroke: "#b9bec7", "stroke-width": 1.8 });
    });
    arrows = pts.map(() => {
      const line = g.mkIn("line", { stroke: "#4f6ef7", "stroke-width": 2.2, "stroke-linecap": "round", visibility: "hidden" });
      const head = g.mkIn("path", { fill: "#4f6ef7", visibility: "hidden" });
      return { line, head };
    });
    stage.appendChild(g.el);
  }
  const row = document.createElement("div");
  row.className = "ts-vf-row";
  // optional "[a/b] + [c/d] =" equation prefix (component-wise addition, typed),
  // or a MISSING-ADDEND equation "[a/b] + [slots] = [resultVec]" when resultVec
  // is set (Opposite Vectors: the typed answer is the addend that zeroes the sum)
  const vfFmt = (n) => String(n).replace(/-(\d)/g, "−$1");
  const vfStatic = (vec) => { const m = document.createElement("div"); m.className = "ts-vf-mat"; vec.forEach((n) => { const s = document.createElement("span"); s.className = "ts-vf-fixed"; s.textContent = vfFmt(Number(n)); m.appendChild(s); }); return m; };
  const vfOp = (t) => { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; };
  const vfHasResult = Array.isArray(spec.resultVec) && spec.resultVec.length === 2;
  // optional scalar-multiple prefix "c [v] =" (Scalar Multiplication typed)
  if (spec.scalar != null && Array.isArray(spec.scaledVec)) {
    row.appendChild(scalarNode(spec.scalar));
    row.appendChild(vfStatic(spec.scaledVec.map(Number))); row.appendChild(vfOp("="));
  }
  // scalar-prefixed addends "a[v] + b[w] =" (Linear Combinations, typed)
  if (Array.isArray(spec.scalarAddends) && spec.scalarAddends.length) {
    spec.scalarAddends.forEach((sa, k) => {
      row.appendChild(scalarNode(sa[0]));
      row.appendChild(vfStatic(sa[1].map(Number)));
      row.appendChild(vfOp(k < spec.scalarAddends.length - 1 ? (spec.op || "+") : "="));
    });
  }
  if (Array.isArray(spec.addends) && spec.addends.length) {
    spec.addends.forEach((ad, k) => {
      row.appendChild(vfStatic(ad));
      const last = k === spec.addends.length - 1;
      row.appendChild(vfOp(last && !vfHasResult ? "=" : (spec.op || "+")));
    });
  }
  const col = document.createElement("div");
  col.className = "ts-vf-mat";
  const inputs = [null, null];
  ["fixedX", "fixedY"].forEach((key, i) => {
    if (spec[key] != null) {
      const s = document.createElement("span");
      s.className = "ts-vf-fixed";
      s.textContent = String(spec[key]).replace(/-(\d)/g, "−$1");
      col.appendChild(s);
    } else {
      const inp = document.createElement("input");
      inp.type = "text";
      inp.inputMode = "numeric";
      inp.className = "ts-sf-input ts-vf-input";
      inp.autocomplete = "off";
      inputs[i] = inp;
      col.appendChild(inp);
    }
  });
  row.appendChild(col);
  if (vfHasResult) { row.appendChild(vfOp("=")); row.appendChild(vfStatic(spec.resultVec.map(Number))); }
  stage.appendChild(row);
  mountEl.appendChild(stage);
  function compVal(i) {
    if (i === 0 && spec.fixedX != null) return Number(spec.fixedX);
    if (i === 1 && spec.fixedY != null) return Number(spec.fixedY);
    const inp = inputs[i];
    if (!inp) return null;
    const t = inp.value.trim().replace(/−/g, "-");
    if (!t || t === "-") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  function vecVal() {
    const dx = compVal(0), dy = compVal(1);
    return dx == null || dy == null ? null : [dx, dy];
  }
  function redraw() {
    if (!g) return;
    const v = vecVal();
    arrows.forEach((a, i) => {
      if (!v || (v[0] === 0 && v[1] === 0)) {
        a.line.setAttribute("visibility", "hidden");
        a.head.setAttribute("visibility", "hidden");
        return;
      }
      const [px, py] = pts[i];
      const x1 = g.X(px), y1 = g.Y(py), x2 = g.X(px + v[0]), y2 = g.Y(py + v[1]);
      a.line.removeAttribute("visibility");
      a.line.setAttribute("x1", x1); a.line.setAttribute("y1", y1);
      a.line.setAttribute("x2", x2); a.line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1);
      const hl = 9, hw = 5.5;
      const bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      a.head.removeAttribute("visibility");
      a.head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) +
        " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) +
        " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    });
  }
  inputs.forEach((inp) => {
    if (!inp) return;
    inp.addEventListener("input", () => {
      if (locked) return;
      redraw();
      if (cbs && cbs.onInteract) cbs.onInteract();
      if (vecVal() != null && cbs && cbs.onComplete) cbs.onComplete();
    });
  });
  return {
    isComplete: () => vecVal() != null,
    check: () => {
      const v = vecVal();
      return { correct: v != null && Math.abs(v[0] - answer[0]) < 1e-9 && Math.abs(v[1] - answer[1]) < 1e-9, value: v };
    },
    getValue: vecVal,
    getState: () => ({ value: vecVal(), target: answer }),
    reset: () => {
      inputs.forEach((inp) => { if (inp) inp.value = ""; });
      redraw();
    },
    reveal: (forced) => {
      locked = true;
      const a = forced != null ? forced : answer;
      inputs.forEach((inp, i) => {
        if (!inp) return;
        inp.value = String(a[i]).replace(/-(\d)/g, "−$1");
        inp.classList.add("is-answer");
        inp.readOnly = true;
      });
      redraw();
    }
  };
}

// Faithful DRAG-TILES vector builder (Brilliant "shift these points to the
// targets / complete both components"): a grid shows blue points + hollow
// target circles; the learner DRAGS number tiles from a palette into the two
// slots of a [x / y] column vector (or clicks a tile → next empty slot), and
// live shift-arrows preview the translation. Graded when the built vector ==
// the answer shift. HONORS Brilliant's manipulative tile-drag — the faithful
// replacement for the flattened typed vecFill. spec: { points?, targets?,
// answer:[x,y], fixedX?, fixedY?, tiles?:[min,max]|[vals], graph? }.
function buildVecTiles(mountEl, spec, cbs) {
  const answer = (spec.answer || []).map(Number);
  const pts = (spec.points || []).map((p) => [Number(p[0]), Number(p[1])]);
  const hasFixedX = spec.fixedX != null, hasFixedY = spec.fixedY != null;
  let locked = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-vt";
  const slotVal = [hasFixedX ? Number(spec.fixedX) : null, hasFixedY ? Number(spec.fixedY) : null];
  // ---- grid: shift-arrows over points/targets (default) OR labeled tip-to-tail
  //      vectors with a live blue resultant (addition/subtraction mode) ----
  let g = null, arrows = [], resArrow = null;
  const vecMode = Array.isArray(spec.vectors) && spec.vectors.length;
  const COL = { orange: "#f5a04a", green: "#3aa76d", blue: "#4f6ef7", grey: "#b9bec7", dark: "#20242a", purple: "#8a5cf6" };
  function drawArrowEls(line, head, x1, y1, x2, y2) {
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
  }
  if (spec.graph && (pts.length || vecMode)) {
    const gspec = Object.assign({ grid: { step: 1 } }, spec.graph);
    if (pts.length) gspec.dots = pts.map((p) => [p[0], p[1]]);
    const W0 = gspec.w || 360, plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    if (vecMode) {
      spec.vectors.forEach((vc) => {
        const col = COL[vc.color] || COL.orange;
        const line = g.mkIn("line", { stroke: col, "stroke-width": 2.4, "stroke-linecap": "round" });
        const head = g.mkIn("path", { fill: col });
        const f = vc.from.map(Number), t = vc.to.map(Number);
        drawArrowEls(line, head, g.X(f[0]), g.Y(f[1]), g.X(t[0]), g.Y(t[1]));
        if (vc.label) { const pts = (g._labPts ??= []); let lx, ly; for (const c of [0.5, 0.74, 0.26]) { lx = g.X(f[0] + (t[0] - f[0]) * c) + 7; ly = g.Y(f[1] + (t[1] - f[1]) * c) - 4; if (!pts.some((p) => Math.hypot(p[0] - lx, p[1] - ly) < 16)) break; } pts.push([lx, ly]); const tl = g.mkIn("text", { x: lx, y: ly, fill: col, "font-size": 13, "font-weight": 700, "font-style": "italic" }); tl.textContent = vc.label; }
      });
      const rl = g.mkIn("line", { stroke: COL.blue, "stroke-width": 3.1, "stroke-linecap": "round", visibility: "hidden" });
      const rh = g.mkIn("path", { fill: COL.blue, visibility: "hidden" });
      resArrow = { line: rl, head: rh, from: (spec.resultantFrom || [0, 0]).map(Number), delta: !!spec.resultantDelta };
    } else {
      (spec.targets || []).forEach((t) => { g.mkIn("circle", { cx: g.X(Number(t[0])), cy: g.Y(Number(t[1])), r: 6.5, fill: "#fff", stroke: "#b9bec7", "stroke-width": 1.8 }); });
      arrows = pts.map(() => ({ line: g.mkIn("line", { stroke: "#4f6ef7", "stroke-width": 2.2, "stroke-linecap": "round", visibility: "hidden" }), head: g.mkIn("path", { fill: "#4f6ef7", visibility: "hidden" }) }));
    }
    stage.appendChild(g.el);
  }
  function redraw() {
    if (!g) return;
    const vx = slotVal[0], vy = slotVal[1], ok = vx != null && vy != null;
    if (resArrow) {
      if (!ok) { resArrow.line.setAttribute("visibility", "hidden"); resArrow.head.setAttribute("visibility", "hidden"); }
      else { resArrow.line.removeAttribute("visibility"); resArrow.head.removeAttribute("visibility"); const ex = resArrow.delta ? resArrow.from[0] + vx : vx, ey = resArrow.delta ? resArrow.from[1] + vy : vy; drawArrowEls(resArrow.line, resArrow.head, g.X(resArrow.from[0]), g.Y(resArrow.from[1]), g.X(ex), g.Y(ey)); }
      return;
    }
    arrows.forEach((a, i) => {
      if (!ok || (vx === 0 && vy === 0)) { a.line.setAttribute("visibility", "hidden"); a.head.setAttribute("visibility", "hidden"); return; }
      const [px, py] = pts[i];
      a.line.removeAttribute("visibility"); a.head.removeAttribute("visibility");
      drawArrowEls(a.line, a.head, g.X(px), g.Y(py), g.X(px + vx), g.Y(py + vy));
    });
  }
  // ---- equation: optional "[a/b] + [c/d] =" prefix, then the answer [x/y] bracket ----
  const fmt = (n) => String(n).replace(/-(\d)/g, "−$1");
  const brk = document.createElement("div"); brk.className = "ts-vt-brk";
  function staticBracket(vec) {
    const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk";
    const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l);
    const c = document.createElement("div"); c.className = "ts-vt-col";
    vec.forEach((n) => { const cell = document.createElement("div"); cell.className = "ts-vt-static"; cell.textContent = fmt(Number(n)); c.appendChild(cell); });
    wrap.appendChild(c);
    const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r);
    return wrap;
  }
  function opSpan(txt) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = txt; return s; }
  function scalarSpan(n) { return scalarNode(n); }
  // optional scalar-multiple prefix "c [v] =" (Scalar Multiplication: c·v = slots)
  if (spec.scalar != null && Array.isArray(spec.scaledVec)) {
    brk.appendChild(scalarSpan(spec.scalar));
    brk.appendChild(staticBracket(spec.scaledVec.map(Number)));
    brk.appendChild(opSpan("="));
  }
  // optional scalar-prefixed addends "a[v] + b[w] =" (Linear Combinations)
  if (Array.isArray(spec.scalarAddends) && spec.scalarAddends.length) {
    spec.scalarAddends.forEach((sa, k) => {
      brk.appendChild(scalarSpan(sa[0]));
      brk.appendChild(staticBracket(sa[1].map(Number)));
      brk.appendChild(opSpan(k < spec.scalarAddends.length - 1 ? (spec.op || "+") : "="));
    });
  }
  // "[a/b] + [slots] = [resultVec]" (missing-addend: Opposite Vectors) when
  // resultVec is set; otherwise the classic "[addends] = [slots]" equation.
  const vtHasResult = Array.isArray(spec.resultVec) && spec.resultVec.length === 2;
  if (Array.isArray(spec.addends) && spec.addends.length) {
    spec.addends.forEach((ad, k) => {
      brk.appendChild(staticBracket(ad.map(Number)));
      const last = k === spec.addends.length - 1;
      brk.appendChild(opSpan(last && !vtHasResult ? "=" : (spec.op || "+")));
    });
  }
  const lb = document.createElement("span"); lb.className = "ts-vt-bracket"; lb.textContent = "["; brk.appendChild(lb);
  const colEl = document.createElement("div"); colEl.className = "ts-vt-col"; brk.appendChild(colEl);
  const rb = document.createElement("span"); rb.className = "ts-vt-bracket"; rb.textContent = "]"; brk.appendChild(rb);
  if (vtHasResult) { brk.appendChild(opSpan("=")); brk.appendChild(staticBracket(spec.resultVec.map(Number))); }
  const slotEls = [];
  function slotFixed(i) { return (i === 0 && hasFixedX) || (i === 1 && hasFixedY); }
  [0, 1].forEach((i) => {
    const s = document.createElement("div"); s.className = "ts-vt-slot";
    if (slotFixed(i)) { s.classList.add("is-fixed"); s.textContent = fmt(slotVal[i]); }
    else { s.classList.add("is-empty"); s.addEventListener("click", () => { if (locked) return; if (slotVal[i] != null) { slotVal[i] = null; renderSlots(); redraw(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); }
    slotEls.push(s); colEl.appendChild(s);
  });
  function renderSlots() {
    slotEls.forEach((s, i) => {
      if (slotFixed(i)) return;
      if (slotVal[i] == null) { s.textContent = ""; s.classList.add("is-empty"); s.classList.remove("is-filled"); }
      else { s.textContent = fmt(slotVal[i]); s.classList.remove("is-empty"); s.classList.add("is-filled"); }
    });
  }
  stage.appendChild(brk);
  // ---- tile palette ----
  let tileVals;
  if (Array.isArray(spec.tiles) && spec.tiles.length && spec.tiles.length !== 2) tileVals = spec.tiles.map(Number);
  else { let lo = -5, hi = 5; if (Array.isArray(spec.tiles) && spec.tiles.length === 2) { lo = Number(spec.tiles[0]); hi = Number(spec.tiles[1]); } tileVals = []; for (let v = lo; v <= hi; v++) tileVals.push(v); }
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < 2; i++) { if (!slotFixed(i) && slotVal[i] == null) return i; } return -1; }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  tileVals.forEach((v) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(v);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slotVal[i] = v; renderSlots(); redraw(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false;
      const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 18) + "px"; };
      move(ev);
      const up = (e) => {
        document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove();
        if (!dragged) return;
        for (let i = 0; i < slotEls.length; i++) {
          if (slotFixed(i)) continue;
          const r = slotEls[i].getBoundingClientRect();
          if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slotVal[i] = v; renderSlots(); redraw(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; }
        }
      };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal);
  mountEl.appendChild(stage);
  renderSlots(); redraw();
  function vecVal() { return slotVal[0] != null && slotVal[1] != null ? [slotVal[0], slotVal[1]] : null; }
  return {
    isComplete: () => vecVal() != null,
    check: () => { const v = vecVal(); return { correct: v != null && v[0] === answer[0] && v[1] === answer[1], value: v }; },
    getValue: vecVal,
    setValue: (v) => { if (!Array.isArray(v)) return; if (!hasFixedX) slotVal[0] = Number(v[0]); if (!hasFixedY) slotVal[1] = Number(v[1]); renderSlots(); redraw(); },
    getState: () => ({ value: vecVal(), target: answer }),
    reset: () => { if (!hasFixedX) slotVal[0] = null; if (!hasFixedY) slotVal[1] = null; renderSlots(); redraw(); },
    reveal: (forced) => { locked = true; const a = forced || answer; slotVal[0] = a[0]; slotVal[1] = a[1]; renderSlots(); redraw(); slotEls.forEach((s) => s.classList.add("is-answer")); }
  };
}

// DRAG a vector ARROW in the plane (Brilliant "Draw the vector sum"): fixed
// context vectors (v⃗ orange, w⃗ green) are shown; the learner drags the tip of
// a blue arrow (from a fixed tail) to a grid point, snapping to integers, with
// a live "(x, y)" readout. Graded when the tip lands on the answer. A
// genuinely-new graded 2D SPATIAL drag (both clientX→x and clientY→y). spec:
// { vectors:[{from,to,label,color}], answer:[x,y], from?:[x,y], start?:[x,y],
//   tolerance?, graph }.
function buildVecDraw(mountEl, spec, cbs) {
  // optional SNAP-TO-TRANSFORMED-LATTICE mode (L10.3 rotate/stretch drags): the tip
  // snaps to integer combinations a·v + b·w of a transformed basis, so the learner
  // drags along the visible warped grid. answerLattice gives the target in lattice
  // coords → exact Cartesian image (handles surds like 3√2/2 with no float literals).
  let SNAP = null;
  if (spec.snap && Array.isArray(spec.snap.v) && Array.isArray(spec.snap.w)) {
    const sv = spec.snap.v.map(Number), sw = spec.snap.w.map(Number);
    SNAP = { sv, sw, det: sv[0] * sw[1] - sv[1] * sw[0] };
  }
  const answer = (SNAP && Array.isArray(spec.answerLattice))
    ? [SNAP.sv[0] * spec.answerLattice[0] + SNAP.sw[0] * spec.answerLattice[1],
       SNAP.sv[1] * spec.answerLattice[0] + SNAP.sw[1] * spec.answerLattice[1]]
    : (spec.answer || [0, 0]).map(Number);
  const tail = (spec.from || [0, 0]).map(Number);
  const tol = spec.tolerance != null ? Number(spec.tolerance) : 0.4;
  let locked = false, moved = false;
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-gt";
  const readout = document.createElement("div");
  readout.className = "ts-gt-readout";
  stage.appendChild(readout);
  const gspec = Object.assign({ grid: { step: 1 } }, spec.graph || {});
  const W0 = gspec.w || 360, plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
  if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
  const g = lineGraphSVG(gspec);
  const COL = { orange: "#f5a04a", green: "#3aa76d", blue: "#4f6ef7", grey: "#b9bec7", purple: "#8a5cf6", teal: "#279b8d" };
  function drawArrow(line, head, x1, y1, x2, y2) {
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
  }
  // optional SKEWED {v,w} LATTICE (Linear Combination Grids): faint lines through
  // every integer combination a·v + b·w, drawn behind the vectors as scaffolding.
  if (spec.lattice && Array.isArray(spec.lattice.v) && Array.isArray(spec.lattice.w)) {
    const lv = spec.lattice.v.map(Number), lw = spec.lattice.w.map(Number), R = spec.lattice.range || 4, latCol = "#ddd3c2";
    for (let k = -R; k <= R; k++) {
      const a = [k * lv[0] - R * lw[0], k * lv[1] - R * lw[1]], b = [k * lv[0] + R * lw[0], k * lv[1] + R * lw[1]];
      g.mkIn("line", { x1: g.X(a[0]), y1: g.Y(a[1]), x2: g.X(b[0]), y2: g.Y(b[1]), stroke: latCol, "stroke-width": 1 });
      const c = [k * lw[0] - R * lv[0], k * lw[1] - R * lv[1]], d = [k * lw[0] + R * lv[0], k * lw[1] + R * lv[1]];
      g.mkIn("line", { x1: g.X(c[0]), y1: g.Y(c[1]), x2: g.X(d[0]), y2: g.Y(d[1]), stroke: latCol, "stroke-width": 1 });
    }
  }
  // optional dashed SEGMENT between two points (Linear Interpolation: the line
  // through the tips of v⃗ and w⃗ that every (1−t)v⃗ + t w⃗ lies on).
  if (Array.isArray(spec.segment) && spec.segment.length === 2) {
    const a = spec.segment[0].map(Number), b = spec.segment[1].map(Number);
    g.mkIn("line", { x1: g.X(a[0]), y1: g.Y(a[1]), x2: g.X(b[0]), y2: g.Y(b[1]), stroke: "#b9bec7", "stroke-width": 1.6, "stroke-dasharray": "5 4" });
  }
  // fixed context vectors
  (spec.vectors || []).forEach((vc) => {
    const col = COL[vc.color] || COL.orange, f = vc.from.map(Number), t = vc.to.map(Number);
    const line = g.mkIn("line", { stroke: col, "stroke-width": 2.4, "stroke-linecap": "round" });
    const head = g.mkIn("path", { fill: col });
    drawArrow(line, head, g.X(f[0]), g.Y(f[1]), g.X(t[0]), g.Y(t[1]));
    if (vc.label) { const pts = (g._labPts ??= []); let lx, ly; for (const c of [0.5, 0.74, 0.26]) { lx = g.X(f[0] + (t[0] - f[0]) * c) + 7; ly = g.Y(f[1] + (t[1] - f[1]) * c) - 4; if (!pts.some((p) => Math.hypot(p[0] - lx, p[1] - ly) < 16)) break; } pts.push([lx, ly]); const tl = g.mkIn("text", { x: lx, y: ly, fill: col, "font-size": 13, "font-weight": 700, "font-style": "italic" }); tl.textContent = vc.label; }
  });
  // draggable blue arrow
  const dLine = g.mkIn("line", { stroke: COL.blue, "stroke-width": 2.8, "stroke-linecap": "round" });
  const dHead = g.mkIn("path", { fill: COL.blue });
  const ring = g.mkIn("circle", { r: 13, fill: "rgba(79,110,247,0.12)", stroke: COL.blue, "stroke-width": 1.5, class: "ts-gt-handle" });
  const knob = g.mkIn("circle", { r: 6, fill: COL.blue, class: "ts-gt-handle" });
  let tip = (spec.start || [1, 1]).map(Number);
  function redraw() {
    drawArrow(dLine, dHead, g.X(tail[0]), g.Y(tail[1]), g.X(tip[0]), g.Y(tip[1]));
    ring.setAttribute("cx", g.X(tip[0])); ring.setAttribute("cy", g.Y(tip[1]));
    knob.setAttribute("cx", g.X(tip[0])); knob.setAttribute("cy", g.Y(tip[1]));
    const rd = (n) => String(Math.round(n * 100) / 100).replace(/-/, "−");
    readout.textContent = "(" + rd(tip[0]) + ", " + rd(tip[1]) + ")";
  }
  redraw();
  stage.appendChild(g.el);
  function evtXY(ev) {
    const r = g.el.getBoundingClientRect(), sx = r.width / g.W, sy = r.height / g.H;
    const px = (ev.clientX - r.left) / sx, py = (ev.clientY - r.top) / sy;
    const gx = g.xmin + (px - g.X(g.xmin)) / (g.X(g.xmax) - g.X(g.xmin)) * (g.xmax - g.xmin);
    const gy = g.ymin + (g.Y(g.ymin) - py) / (g.Y(g.ymin) - g.Y(g.ymax)) * (g.ymax - g.ymin);
    if (SNAP) {
      const a = Math.round((SNAP.sw[1] * gx - SNAP.sw[0] * gy) / SNAP.det);
      const b = Math.round((-SNAP.sv[1] * gx + SNAP.sv[0] * gy) / SNAP.det);
      return [SNAP.sv[0] * a + SNAP.sw[0] * b, SNAP.sv[1] * a + SNAP.sw[1] * b];
    }
    return [Math.max(g.xmin, Math.min(g.xmax, Math.round(gx))), Math.max(g.ymin, Math.min(g.ymax, Math.round(gy)))];
  }
  let dragging = false;
  [ring, knob].forEach((h) => {
    h.addEventListener("pointerdown", (ev) => { if (locked) return; dragging = true; h.setPointerCapture && h.setPointerCapture(ev.pointerId); ev.preventDefault(); moved = true; if (cbs && cbs.onInteract) cbs.onInteract(); });
    h.addEventListener("pointermove", (ev) => { if (!dragging || locked) return; tip = evtXY(ev); redraw(); });
    h.addEventListener("pointerup", () => { dragging = false; if (cbs && cbs.onComplete) cbs.onComplete(); });
    h.addEventListener("pointercancel", () => { dragging = false; });
  });
  mountEl.appendChild(stage);
  return {
    isComplete: () => moved,
    check: () => ({ correct: Math.abs(tip[0] - answer[0]) <= tol && Math.abs(tip[1] - answer[1]) <= tol, value: tip.slice() }),
    getValue: () => tip.slice(),
    setValue: (v) => { moved = true; tip = [Number(v[0]), Number(v[1])]; redraw(); },
    getState: () => ({ value: tip.slice(), target: answer }),
    reset: () => { moved = false; tip = (spec.start || [1, 1]).map(Number); redraw(); },
    reveal: (forced) => { locked = true; tip = (forced || answer).slice(); redraw(); }
  };
}

// SOLVE FOR THE SCALAR (Brilliant "[?] v⃗ = w⃗"): a single typed coefficient box
// sits before a static vector, equal to a static result; graded when the typed
// scalar c satisfies c·v = result. A genuinely-new graded interaction (the
// answer is a scalar, not a vector). spec: { vec:[a,b], result:[x,y], answer:c }.
function buildScalarSolve(mountEl, spec, cbs) {
  const answer = Number(spec.answer);
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vf";
  const row = document.createElement("div"); row.className = "ts-vf-row";
  const fmt = (n) => String(n).replace(/-(\d)/g, "−$1");
  const vfStatic = (vec) => { const m = document.createElement("div"); m.className = "ts-vf-mat"; vec.forEach((n) => { const s = document.createElement("span"); s.className = "ts-vf-fixed"; s.textContent = fmt(Number(n)); m.appendChild(s); }); return m; };
  const vfOp = (t) => { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; };
  const inp = document.createElement("input"); inp.type = "text"; inp.inputMode = "numeric"; inp.className = "ts-sf-input ts-vf-input ts-vf-scalar"; inp.autocomplete = "off";
  if (spec.lhs != null) {
    // magnitude-coefficient mode: "|2v| = [box] |v|" — type the scalar |c| (faithful to Brilliant's 1-digit keypad, not a pick)
    const lhsEl = document.createElement("span"); lhsEl.className = "ts-vt-op"; lhsEl.style.fontWeight = "600"; lhsEl.innerHTML = spec.lhs;
    row.appendChild(lhsEl); row.appendChild(vfOp("=")); row.appendChild(inp);
    if (spec.suffix != null) { const sfx = document.createElement("span"); sfx.className = "ts-vt-op"; sfx.style.fontWeight = "600"; sfx.innerHTML = spec.suffix; row.appendChild(sfx); }
  } else {
    row.appendChild(inp);
    row.appendChild(vfStatic((spec.vec || []).map(Number)));
    row.appendChild(vfOp("="));
    row.appendChild(vfStatic((spec.result || []).map(Number)));
  }
  stage.appendChild(row); mountEl.appendChild(stage);
  function val() { const t = inp.value.trim().replace(/−/g, "-"); if (!t || t === "-") return null; const n = Number(t); return Number.isFinite(n) ? n : null; }
  inp.addEventListener("input", () => { if (locked) return; if (cbs && cbs.onInteract) cbs.onInteract(); if (val() != null && cbs && cbs.onComplete) cbs.onComplete(); });
  return {
    isComplete: () => val() != null,
    check: () => { const v = val(); return { correct: v != null && Math.abs(v - answer) < 1e-9, value: v }; },
    getValue: val,
    setValue: (v) => { const n = Array.isArray(v) ? v[0] : v; inp.value = String(n).replace(/-(\d)/g, "−$1"); },
    getState: () => ({ value: val(), target: answer }),
    reset: () => { inp.value = ""; },
    reveal: (forced) => { locked = true; inp.value = String(forced != null ? forced : answer).replace(/-(\d)/g, "−$1"); inp.classList.add("is-answer"); inp.readOnly = true; }
  };
}

// MAKE THE EQUATION TRUE (Brilliant "Solving Linear Combinations"): a
// scalar-prefixed combination a·[v] + b·[w] = [result] where ANY coefficient
// or vector component may be a fillable TILE slot (null in the spec). Tiles fill
// the empty slots left-to-right / top-to-bottom; graded when every slot matches.
// spec: { terms:[{scalar:num|null, vec:[num|null, num|null]}], result:[x,y],
//         answer:[...slot values in creation order], op?, tiles?:[min,max] }.
function buildLincombFill(mountEl, spec, cbs) {
  const terms = spec.terms || [], result = (spec.result || []).map(Number), answer = (spec.answer || []).map(Number);
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt";
  const brk = document.createElement("div"); brk.className = "ts-vt-brk";
  const fmt = (n) => String(n).replace(/-(\d)/g, "−$1");
  const slots = [];
  function makeSlot(extra) {
    const s = document.createElement("div"); s.className = "ts-vt-slot is-empty" + (extra ? " " + extra : "");
    const idx = slots.length;
    s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
    slots.push({ el: s, val: null }); return s;
  }
  function staticCell(n) { const c = document.createElement("div"); c.className = "ts-vt-static"; c.textContent = fmt(Number(n)); return c; }
  function bracketOf(vec) {
    const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk";
    const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l);
    const col = document.createElement("div"); col.className = "ts-vt-col";
    vec.forEach((n) => { col.appendChild(n == null ? makeSlot() : staticCell(n)); });
    wrap.appendChild(col);
    const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r);
    return wrap;
  }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  terms.forEach((t, k) => {
    if (t.scalar === false) { /* plain vector, no coefficient (relative-velocity equations) */ } else if (t.scalar == null) brk.appendChild(makeSlot("ts-vt-coeffslot")); else brk.appendChild(scalarNode(t.scalar));
    brk.appendChild(bracketOf(t.vec));
    brk.appendChild(opSpan(k < terms.length - 1 ? (spec.op || "+") : "="));
  });
  brk.appendChild(bracketOf(result));
  stage.appendChild(brk);
  let lo = -5, hi = 5; if (Array.isArray(spec.tiles) && spec.tiles.length === 2) { lo = +spec.tiles[0]; hi = +spec.tiles[1]; }
  const tileVals = []; for (let v = lo; v <= hi; v++) tileVals.push(v);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  tileVals.forEach((v) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(v);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = v; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false;
      const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 18) + "px"; }; move(ev);
      const up = (e) => {
        document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove();
        if (!dragged) return;
        for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = v; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } }
      };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => { const vs = allVals(); return { correct: vs.every((v, i) => v != null && v === answer[i]), value: vs }; },
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? Number(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answer }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || answer; slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// MAGNITUDE & DIRECTION (Brilliant "Find |v⃗| and tan θ"): a vector is drawn on a
// grid with its polar angle θ and right-triangle legs; the learner TYPES the
// magnitude |v⃗| = √(x²+y²) and tan θ = y/x. Answers are graded NUMERICALLY, so a
// typed "√13", "3/4", "-2/3", or a decimal are all accepted. spec:
// { vec:[x,y], answers:{mag:"5"|"√13", tan:"3/4"}, ask?:["mag","tan"], graph }.
function buildMagDir(mountEl, spec, cbs) {
  const vec = (spec.vec || [0, 0]).map(Number);
  const trueMag = Math.hypot(vec[0], vec[1]);
  const trueTan = vec[0] !== 0 ? vec[1] / vec[0] : null;
  const ask = spec.ask || ["mag", "tan"];
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-md";
  const OR = "#f5a04a";
  if (spec.graph && !spec.noGraph) {
    const gspec = Object.assign({ grid: { step: 1 } }, spec.graph);
    const W0 = gspec.w || 340, plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    // right-triangle legs (faint dashed): horizontal x-leg + vertical y-leg
    g.mkIn("line", { x1: g.X(0), y1: g.Y(0), x2: g.X(vec[0]), y2: g.Y(0), stroke: "#c9cfd8", "stroke-width": 1.4, "stroke-dasharray": "4 3" });
    g.mkIn("line", { x1: g.X(vec[0]), y1: g.Y(0), x2: g.X(vec[0]), y2: g.Y(vec[1]), stroke: "#c9cfd8", "stroke-width": 1.4, "stroke-dasharray": "4 3" });
    // polar-angle arc from +x axis to v⃗
    const r = 22, a1 = Math.atan2(vec[1], vec[0]);
    const ax0 = g.X(0) + r, ay0 = g.Y(0), ax1 = g.X(0) + r * Math.cos(a1), ay1 = g.Y(0) - r * Math.sin(a1);
    const large = Math.abs(a1) > Math.PI ? 1 : 0, sweep = a1 > 0 ? 0 : 1;
    g.mkIn("path", { d: "M " + ax0.toFixed(1) + " " + ay0.toFixed(1) + " A " + r + " " + r + " 0 " + large + " " + sweep + " " + ax1.toFixed(1) + " " + ay1.toFixed(1), fill: "none", stroke: "#9aa0aa", "stroke-width": 1.5 });
    const lm = a1 / 2, tl = g.mkIn("text", { x: g.X(0) + (r + 11) * Math.cos(lm), y: g.Y(0) - (r + 11) * Math.sin(lm) + 4, fill: "#20242a", "font-size": 13, "font-style": "italic" }); tl.textContent = "θ";
    // the vector arrow
    const line = g.mkIn("line", { stroke: OR, "stroke-width": 2.6, "stroke-linecap": "round" });
    const head = g.mkIn("path", { fill: OR });
    const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(vec[0]), y2 = g.Y(vec[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    const vlab = g.mkIn("text", { x: g.X(vec[0] / 2) + 7, y: g.Y(vec[1] / 2) - 4, fill: OR, "font-size": 13, "font-weight": 700, "font-style": "italic" }); vlab.textContent = "v";
    stage.appendChild(g.el);
  } else if (spec.showVec) {
    // no grid — show the vector as a static column bracket (Computing from Components)
    const fmt = (n) => String(n).replace(/-(\d)/g, "−$1");
    const brk = document.createElement("div"); brk.className = "ts-md-brk";
    const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "[";
    const col = document.createElement("div"); col.className = "ts-vt-col";
    vec.forEach((n) => { const cell = document.createElement("div"); cell.className = "ts-vt-static"; cell.textContent = fmt(n); col.appendChild(cell); });
    const rgt = document.createElement("span"); rgt.className = "ts-vt-bracket"; rgt.textContent = "]";
    brk.appendChild(l); brk.appendChild(col); brk.appendChild(rgt); stage.appendChild(brk);
  }
  // typed-answer rows
  const rows = document.createElement("div"); rows.className = "ts-md-rows";
  const inputs = {};
  function mkRow(key, label) {
    const row = document.createElement("div"); row.className = "ts-md-row";
    const l = document.createElement("span"); l.className = "ts-md-label"; l.innerHTML = label;
    const inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-md-input"; inp.autocomplete = "off";
    row.appendChild(l); row.appendChild(inp); rows.appendChild(row); inputs[key] = inp;
  }
  if (spec.form === "atan") {
    // "θ = tan⁻¹( □ )" — the box holds the arctan argument y/x (Polar Angles)
    const row = document.createElement("div"); row.className = "ts-md-row";
    const pre = document.createElement("span"); pre.className = "ts-md-label"; pre.innerHTML = "θ = tan<sup>−1</sup>&#8201;(";
    const inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-md-input"; inp.autocomplete = "off";
    const post = document.createElement("span"); post.className = "ts-md-label"; post.textContent = ")";
    row.appendChild(pre); row.appendChild(inp); row.appendChild(post); rows.appendChild(row); inputs.atan = inp;
  } else if (spec.form === "angle") {
    // "θ = □" — the box holds the FULL polar angle as an expression, e.g.
    // "π + tan⁻¹(−5/3)" (quadrant-adjusted arctan). Graded modulo 2π.
    const row = document.createElement("div"); row.className = "ts-md-row";
    const pre = document.createElement("span"); pre.className = "ts-md-label"; pre.textContent = "θ =";
    const inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-md-input ts-md-wide"; inp.autocomplete = "off";
    row.appendChild(pre); row.appendChild(inp); rows.appendChild(row); inputs.angle = inp;
  } else {
    if (ask.indexOf("mag") >= 0) mkRow("mag", "|v| =");
    if (ask.indexOf("tan") >= 0) mkRow("tan", "tan θ =");
  }
  stage.appendChild(rows); mountEl.appendChild(stage);
  function parseVal(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, "");
    if (!t) return null;
    let m = t.match(/^(-?\d*(?:\.\d+)?)?(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?$/i);
    if (m) { const coef = (m[1] == null || m[1] === "") ? 1 : (m[1] === "-" ? -1 : Number(m[1])); return coef * Math.sqrt(Number(m[2])); }
    m = t.match(/^(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)$/);
    if (m) return Number(m[1]) / Number(m[2]);
    const n = Number(t); return Number.isFinite(n) ? n : null;
  }
  const near = (a, b) => a != null && b != null && Math.abs(a - b) < 1e-6;
  const trueAngle = Math.atan2(vec[1], vec[0]);
  function parseAngle(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/π/g, "PI").replace(/pi/gi, "PI").replace(/tan⁻¹/g, "ATAN").replace(/tan\^?-?1/gi, "ATAN").replace(/arctan/gi, "ATAN").replace(/atan/gi, "ATAN").replace(/√/g, "SQRT").replace(/sqrt/gi, "SQRT");
    // implicit multiplication: "5PI" → "5*PI", ")(" → ")*(" — so coefficient·π (5π/4, 3π/2, 2π−tan⁻¹…) evaluates instead of throwing a syntax error
    t = t.replace(/(\d)(PI|ATAN|SQRT|\()/g, "$1*$2").replace(/(\))(\d|PI|ATAN|SQRT|\()/g, "$1*$2");
    if (!/^[0-9.+\-*/()PIATANSQRT]*$/.test(t)) return null;
    try { const v = new Function("PI", "ATAN", "SQRT", "return (" + t + ");")(Math.PI, Math.atan, Math.sqrt); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const angleEq = (a, b) => { if (a == null || b == null) return false; let d = (a - b) % (2 * Math.PI); if (d > Math.PI) d -= 2 * Math.PI; if (d < -Math.PI) d += 2 * Math.PI; return Math.abs(d) < 0.02; };
  const valOf = (k) => k === "angle" ? parseAngle(inputs[k].value) : parseVal(inputs[k].value);
  function vals() { const o = {}; for (const k in inputs) o[k] = valOf(k); return o; }
  Object.values(inputs).forEach((inp) => inp.addEventListener("input", () => {
    if (locked) return; if (cbs && cbs.onInteract) cbs.onInteract();
    const v = vals(); if (Object.keys(inputs).every((k) => v[k] != null) && cbs && cbs.onComplete) cbs.onComplete();
  }));
  return {
    isComplete: () => { const v = vals(); return Object.keys(inputs).every((k) => v[k] != null); },
    check: () => {
      const v = vals();
      let ok = true;
      if (inputs.mag) ok = ok && near(v.mag, trueMag);
      if (inputs.tan) ok = ok && (trueTan == null ? v.tan == null : near(v.tan, trueTan));
      if (inputs.atan) ok = ok && (trueTan == null ? v.atan == null : near(v.atan, trueTan));
      if (inputs.angle) ok = ok && angleEq(v.angle, trueAngle);
      return { correct: ok, value: Object.keys(inputs).reduce((o, k) => (o[k] = inputs[k].value, o), {}) };
    },
    getValue: () => Object.keys(inputs).reduce((o, k) => (o[k] = inputs[k].value, o), {}),
    setValue: (arr) => { if (Array.isArray(arr)) { const keys = Object.keys(inputs); keys.forEach((k, i) => { inputs[k].value = arr[i] != null ? String(arr[i]) : ""; }); } else if (arr && typeof arr === "object") { for (const k in inputs) if (arr[k] != null) inputs[k].value = String(arr[k]); } },
    getState: () => ({
      // Task #524 — expose the student's typed entries (raw strings, in input
      // order) so the tutor-chat bridge can see their work, not just the goal.
      value: Object.keys(inputs).reduce((o, k) => (o[k] = inputs[k].value, o), {}),
      target: { mag: trueMag, tan: trueTan },
    }),
    reset: () => { for (const k in inputs) inputs[k].value = ""; },
    reveal: () => { locked = true; const a = spec.answers || {}; if (inputs.mag) inputs.mag.value = a.mag != null ? a.mag : (Number.isInteger(trueMag) ? String(trueMag) : "√" + (vec[0] * vec[0] + vec[1] * vec[1])); if (inputs.tan) inputs.tan.value = a.tan != null ? a.tan : (trueTan != null ? String(vec[1]) + "/" + String(vec[0]) : ""); if (inputs.atan) inputs.atan.value = a.atan != null ? a.atan : (trueTan != null ? String(vec[1]) + "/" + String(vec[0]) : ""); if (inputs.angle) inputs.angle.value = a.angle != null ? a.angle : String(trueAngle.toFixed(4)); for (const k in inputs) { inputs[k].readOnly = true; inputs[k].classList.add("is-answer"); } }
  };
}

// COMPONENTS FROM AN ANGLE (Brilliant "Vector Components" / components-from-angles):
// the REVERSE of Magnitude & Direction — given a vector's magnitude r and an angle θ
// read off the figure, fill the component formulas v_x = r·cos(θ°), v_y = r·sin(θ°).
// Three input modes mirror Brilliant's three graded interactions on this lesson:
//   mode:"tiles"  → drag/click number+angle TILES into the r and θ formula slots
//                   (answer = the 4 slot strings in order [r, "θ°", r, "θ°"]).
//   mode:"typed"  → r is pre-printed; TYPE the polar angle into each θ box. Graded
//                   NUMERICALLY (r·cos(θ°)≈vx and r·sin(θ°)≈vy) so any coterminal θ works.
//   mode:"column" → a [x / y] column bracket; each box takes a trig EXPRESSION
//                   (e.g. "5sin(40°)", "-5cos(40°)"), graded by numeric evaluation ≈ vec.
// spec: { mode, vec:[vx,vy], mag?, angleLabel?, arcFrom?:[dx,dy], answer?:[4 strings],
//         tiles?:[strings], answerAngle?, answerExpr?:[x,y], tol?, graph }.
function buildPolarComp(mountEl, spec, cbs) {
  const vec = (spec.vec || [0, 0]).map(Number);
  const mode = spec.mode || "typed";
  const tol = spec.tol != null ? Number(spec.tol) : 0.06;
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-pc";
  const OR = "#f5a04a";
  // ---- grid + vector(s) + angle arc + magnitude label ----
  // `spec.vectors` (array of {mag, deg, color, arcFrom, angleLabel, magLabel})
  // draws several vectors (Combining Vectors); otherwise the single spec.vec.
  if (spec.graph) {
    const gspec = Object.assign({ grid: { step: 1 } }, spec.graph);
    const W0 = gspec.w || 330, plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    const COL = { orange: "#f5a04a", teal: "#279b8d", blue: "#4f6ef7", green: "#3aa76d", purple: "#8a5cf6" };
    function drawOneVec(vx, vy, o) {
      o = o || {}; const col = COL[o.color] || COL.orange;
      const from = (o.arcFrom || [1, 0]).map(Number);
      const a0 = Math.atan2(from[1], from[0]), a1 = Math.atan2(vy, vx), r = 22;
      const ax0 = g.X(0) + r * Math.cos(a0), ay0 = g.Y(0) - r * Math.sin(a0);
      const ax1 = g.X(0) + r * Math.cos(a1), ay1 = g.Y(0) - r * Math.sin(a1);
      let d = a1 - a0; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
      const large = Math.abs(d) > Math.PI ? 1 : 0, sweep = d < 0 ? 1 : 0;
      g.mkIn("path", { d: "M " + ax0.toFixed(1) + " " + ay0.toFixed(1) + " A " + r + " " + r + " 0 " + large + " " + sweep + " " + ax1.toFixed(1) + " " + ay1.toFixed(1), fill: "none", stroke: "#9aa0aa", "stroke-width": 1.5 });
      // Angle label rides the arc bisector, centered, with extra radius on
      // narrow angles so it clears the vector (goldframe collision fix, #349).
      if (o.angleLabel) { const lm = a0 + d / 2, lr = r + (Math.abs(d) < 0.5 ? 24 : 14), tl = g.mkIn("text", { x: g.X(0) + lr * Math.cos(lm), y: g.Y(0) - lr * Math.sin(lm) + 4, fill: "#20242a", "font-size": 12.5, "text-anchor": "middle" }); tl.textContent = o.angleLabel; }
      const line = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
      const head = g.mkIn("path", { fill: col });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(vx), y2 = g.Y(vy); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
      // Magnitude label offsets PERPENDICULAR to the vector on the side away
      // from the angle arc, so it can never smudge into the angle label
      // (was a fixed +8,−5 offset — goldframe caught 78-92% overlaps, #349).
      if (o.magLabel != null) { const side = a1 + (d >= 0 ? 1 : -1) * Math.PI / 2, pl = g.mkIn("text", { x: g.X(vx / 2) + 13 * Math.cos(side), y: g.Y(vy / 2) - 13 * Math.sin(side) + 4, fill: "#20242a", "font-size": 12.5, "font-weight": 700, "text-anchor": "middle" }); pl.textContent = String(o.magLabel); }
    }
    if (Array.isArray(spec.vectors) && spec.vectors.length) {
      spec.vectors.forEach((vc) => { const m = Number(vc.mag), dg = Number(vc.deg) * Math.PI / 180; drawOneVec(m * Math.cos(dg), m * Math.sin(dg), { color: vc.color, arcFrom: vc.arcFrom, angleLabel: vc.angleLabel, magLabel: vc.magLabel != null ? vc.magLabel : vc.mag }); });
    } else {
      drawOneVec(vec[0], vec[1], { color: "orange", arcFrom: spec.arcFrom, angleLabel: spec.angleLabel, magLabel: spec.mag });
    }
    stage.appendChild(g.el);
  }
  const fmtN = (n) => String(n).replace(/-(\d)/g, "−$1");
  // degrees-aware trig-expression evaluator (sin/cos/tan take DEGREES).
  function parseTrig(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "").replace(/°/g, "");
    if (!t) return null;
    t = t.replace(/sin/gi, "SIN").replace(/cos/gi, "COS").replace(/tan/gi, "TAN");
    t = t.replace(/(\d)(SIN|COS|TAN|\()/g, "$1*$2").replace(/(\))(\d|SIN|COS|TAN|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SINCOSTAN]*$/.test(t)) return null;
    try { const v = new Function("SIN", "COS", "TAN", "return (" + t + ");")((d) => Math.sin(d * Math.PI / 180), (d) => Math.cos(d * Math.PI / 180), (d) => Math.tan(d * Math.PI / 180)); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const parseDeg = (s) => { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/°/g, "").replace(/\s/g, ""); if (!t || t === "-") return null; const n = Number(t); return Number.isFinite(n) ? n : null; };

  // ============ TILES MODE ============
  if (mode === "tiles") {
    const answer = (spec.answer || []).map(String);
    const slots = [];
    function makeSlot() {
      const s = document.createElement("span"); s.className = "ts-pc-slot is-empty"; const idx = slots.length;
      s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
      slots.push({ el: s, val: null }); return s;
    }
    const lbl = (h) => { const e = document.createElement("span"); e.className = "ts-pc-lab"; e.innerHTML = h; return e; };
    const rows = document.createElement("div"); rows.className = "ts-pc-rows";
    [["v", "x", "cos"], ["v", "y", "sin"]].forEach(([v, sub, fn]) => {
      const row = document.createElement("div"); row.className = "ts-pc-row";
      row.appendChild(lbl("v<sub>" + sub + "</sub> &nbsp;=&nbsp;"));
      row.appendChild(makeSlot());
      row.appendChild(lbl("&nbsp;" + fn + "&#8201;("));
      row.appendChild(makeSlot());
      row.appendChild(lbl(")"));
      rows.appendChild(row);
    });
    stage.appendChild(rows);
    const pal = document.createElement("div"); pal.className = "ts-pc-pal";
    function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
    function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmtN(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
    function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
    (spec.tiles || []).forEach((tv) => {
      const t = document.createElement("div"); t.className = "ts-pc-tile"; t.textContent = fmtN(tv);
      t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
      t.addEventListener("pointerdown", (ev) => {
        if (locked) return; ev.preventDefault();
        const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
        let dragged = false;
        const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 14) + "px"; }; move(ev);
        const up = (e) => {
          document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove();
          if (!dragged) return;
          for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } }
        };
        document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
      });
      pal.appendChild(t);
    });
    stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
    const allVals = () => slots.map((s) => s.val);
    return {
      isComplete: () => slots.every((s) => s.val != null),
      check: () => { const vs = allVals(); return { correct: vs.every((v, i) => v != null && String(v) === answer[i]), value: vs }; },
      getValue: allVals,
      setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
      getState: () => ({ value: allVals(), target: answer }),
      reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
      reveal: (forced) => { locked = true; const a = forced || answer; slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
    };
  }

  // ============ SUMTILES MODE (Combining Vectors: add two decompositions) ============
  // vₓ = m₁cos(□) + m₂cos(□),  v_y = m₁sin(□) + m₂sin(□); angle tiles fill the 4
  // slots. Graded NUMERICALLY: the two magnitudes (fixed) times the chosen angles
  // must sum to the resultant `vec` — so a swapped angle (wrong magnitude) fails.
  if (mode === "sumtiles") {
    const mags = (spec.mags || []).map(Number);
    const answer = (spec.answer || []).map(String);
    const slots = [];
    function makeSlot() {
      const s = document.createElement("span"); s.className = "ts-pc-slot is-empty"; const idx = slots.length;
      s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
      slots.push({ el: s, val: null }); return s;
    }
    const lbl = (h) => { const e = document.createElement("span"); e.className = "ts-pc-lab"; e.innerHTML = h; return e; };
    const rows = document.createElement("div"); rows.className = "ts-pc-rows";
    (spec.bearing ? [["E", "sin"], ["N", "cos"]] : [["x", "cos"], ["y", "sin"]]).forEach(([sub, fn]) => {
      const row = document.createElement("div"); row.className = "ts-pc-row";
      row.appendChild(lbl("v<sub>" + sub + "</sub> &nbsp;=&nbsp; " + fmtN(mags[0]) + "&#8201;" + fn + "("));
      row.appendChild(makeSlot());
      row.appendChild(lbl(") &nbsp;+&nbsp; " + fmtN(mags[1]) + "&#8201;" + fn + "("));
      row.appendChild(makeSlot());
      row.appendChild(lbl(")"));
      rows.appendChild(row);
    });
    stage.appendChild(rows);
    const pal = document.createElement("div"); pal.className = "ts-pc-pal";
    function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
    function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = s.val; s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
    function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
    (spec.tiles || []).forEach((tv) => {
      const t = document.createElement("div"); t.className = "ts-pc-tile"; t.textContent = String(tv);
      t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
      t.addEventListener("pointerdown", (ev) => {
        if (locked) return; ev.preventDefault();
        const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
        let dragged = false;
        const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 14) + "px"; }; move(ev);
        const up = (e) => {
          document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove();
          if (!dragged) return;
          for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } }
        };
        document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
      });
      pal.appendChild(t);
    });
    stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
    const allVals = () => slots.map((s) => s.val), DR = Math.PI / 180;
    function correct() {
      const v = allVals(); if (v.some((x) => x == null)) return false;
      const A = parseDeg(v[0]), B = parseDeg(v[1]), C = parseDeg(v[2]), Dd = parseDeg(v[3]);
      if ([A, B, C, Dd].some((x) => x == null)) return false;
      // per-slot angle match (mod 360): closes the even-cosine loophole where a wrong-quadrant
      // tile summed to the same North component and passed. Coterminal equivalents still pass.
      if (answer && answer.length === v.length) {
        const norm = (x) => ((x % 360) + 360) % 360;
        for (let i = 0; i < v.length; i++) { const want = parseDeg(answer[i]); if (want == null) continue; let d = Math.abs(norm(parseDeg(v[i])) - norm(want)); if (d > 180) d = 360 - d; if (d > 0.5) return false; }
      }
      const rx = spec.bearing ? mags[0] * Math.sin(A * DR) + mags[1] * Math.sin(B * DR) : mags[0] * Math.cos(A * DR) + mags[1] * Math.cos(B * DR);
      const ry = spec.bearing ? mags[0] * Math.cos(C * DR) + mags[1] * Math.cos(Dd * DR) : mags[0] * Math.sin(C * DR) + mags[1] * Math.sin(Dd * DR);
      const t = spec.tol != null ? spec.tol : 0.12;
      return Math.abs(rx - vec[0]) < t && Math.abs(ry - vec[1]) < t;
    }
    return {
      isComplete: () => slots.every((s) => s.val != null),
      check: () => ({ correct: correct(), value: allVals() }),
      getValue: allVals,
      setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
      getState: () => ({ value: allVals(), target: answer }),
      reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
      reveal: (forced) => { locked = true; const a = forced || answer; slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
    };
  }

  // ============ COLUMN MODE (trig expressions in a [x/y] bracket) ============
  if (mode === "column") {
    const brk = document.createElement("div"); brk.className = "ts-pc-colwrap";
    const lb = document.createElement("span"); lb.className = "ts-vt-bracket"; lb.textContent = "[";
    const col = document.createElement("div"); col.className = "ts-pc-col";
    const inpX = document.createElement("input"), inpY = document.createElement("input");
    [inpX, inpY].forEach((inp) => { inp.type = "text"; inp.className = "ts-sf-input ts-pc-cinput"; inp.autocomplete = "off"; inp.placeholder = "e.g. 5cos(30°)"; const cell = document.createElement("div"); cell.className = "ts-pc-ccell"; cell.appendChild(inp); col.appendChild(cell); });
    const rb = document.createElement("span"); rb.className = "ts-vt-bracket"; rb.textContent = "]";
    brk.appendChild(lb); brk.appendChild(col); brk.appendChild(rb); stage.appendChild(brk); mountEl.appendChild(stage);
    const vx = () => parseTrig(inpX.value), vy = () => parseTrig(inpY.value);
    [inpX, inpY].forEach((inp) => inp.addEventListener("input", () => { if (locked) return; if (cbs && cbs.onInteract) cbs.onInteract(); if (vx() != null && vy() != null && cbs && cbs.onComplete) cbs.onComplete(); }));
    return {
      isComplete: () => vx() != null && vy() != null,
      check: () => { const a = vx(), b = vy(); return { correct: a != null && b != null && Math.abs(a - vec[0]) < tol && Math.abs(b - vec[1]) < tol, value: [inpX.value, inpY.value] }; },
      getValue: () => [inpX.value, inpY.value],
      setValue: (arr) => { if (Array.isArray(arr)) { inpX.value = arr[0] != null ? String(arr[0]) : ""; inpY.value = arr[1] != null ? String(arr[1]) : ""; } },
      getState: () => ({ value: [inpX.value, inpY.value], target: vec }),
      reset: () => { inpX.value = ""; inpY.value = ""; },
      reveal: (forced) => { locked = true; const a = forced || spec.answerExpr || [String(vec[0]), String(vec[1])]; inpX.value = a[0]; inpY.value = a[1]; [inpX, inpY].forEach((inp) => { inp.readOnly = true; inp.classList.add("is-answer"); }); }
    };
  }

  // ============ TYPED MODE (r pre-printed, type the polar angle θ) ============
  const rows = document.createElement("div"); rows.className = "ts-pc-rows";
  const inpX = document.createElement("input"), inpY = document.createElement("input");
  // bearing:true → East = |v|·sin(β°), North = |v|·cos(β°) (nautical convention); else polar x=cos/y=sin.
  const bearing = !!spec.bearing;
  (bearing ? [["E", "sin", inpX], ["N", "cos", inpY]] : [["x", "cos", inpX], ["y", "sin", inpY]]).forEach(([sub, fn, inp]) => {
    const row = document.createElement("div"); row.className = "ts-pc-row";
    const l = document.createElement("span"); l.className = "ts-pc-lab"; l.innerHTML = "v<sub>" + sub + "</sub> &nbsp;=&nbsp; " + fmtN(spec.mag != null ? spec.mag : "r") + " &#8201;" + fn + "&#8201;(";
    inp.type = "text"; inp.className = "ts-sf-input ts-pc-input"; inp.autocomplete = "off";
    const post = document.createElement("span"); post.className = "ts-pc-lab"; post.innerHTML = "°)";
    row.appendChild(l); row.appendChild(inp); row.appendChild(post); rows.appendChild(row);
  });
  stage.appendChild(rows); mountEl.appendChild(stage);
  const mag = spec.mag != null ? Number(spec.mag) : Math.hypot(vec[0], vec[1]);
  const tx = () => parseDeg(inpX.value), ty = () => parseDeg(inpY.value);
  [inpX, inpY].forEach((inp) => inp.addEventListener("input", () => { if (locked) return; if (cbs && cbs.onInteract) cbs.onInteract(); if (tx() != null && ty() != null && cbs && cbs.onComplete) cbs.onComplete(); }));
  const okX = () => { const d = tx(); return d != null && Math.abs(mag * (bearing ? Math.sin(d * Math.PI / 180) : Math.cos(d * Math.PI / 180)) - vec[0]) < tol; };
  const okY = () => { const d = ty(); return d != null && Math.abs(mag * (bearing ? Math.cos(d * Math.PI / 180) : Math.sin(d * Math.PI / 180)) - vec[1]) < tol; };
  return {
    isComplete: () => tx() != null && ty() != null,
    check: () => ({ correct: okX() && okY(), value: [inpX.value, inpY.value] }),
    getValue: () => [inpX.value, inpY.value],
    setValue: (arr) => { if (Array.isArray(arr)) { inpX.value = arr[0] != null ? String(arr[0]) : ""; inpY.value = arr[1] != null ? String(arr[1]) : ""; } },
    getState: () => ({ value: [inpX.value, inpY.value], target: spec.answerAngle }),
    reset: () => { inpX.value = ""; inpY.value = ""; },
    reveal: (forced) => { locked = true; const a = forced != null ? forced : (spec.answerAngle != null ? spec.answerAngle : Math.round(Math.atan2(vec[1], vec[0]) * 180 / Math.PI)); inpX.value = String(a); inpY.value = String(a); [inpX, inpY].forEach((inp) => { inp.readOnly = true; inp.classList.add("is-answer"); }); }
  };
}

// VELOCITY MAGNITUDE & BEARING (Brilliant "Velocity Magnitude and Direction"):
// a resultant velocity (drawn as v_B along East + v_C along North, or described in
// a word problem) has a SPEED |v| = √(E²+N²) and a BEARING β measured CLOCKWISE
// from North. Learner TYPES the speed (√/decimal accepted) and/or the bearing as a
// quadrant-adjusted expression (e.g. "tan⁻¹(4/3)", "180° + tan⁻¹(3/4)"). Bearing is
// graded MODULO 360° in radians (tan⁻¹→radians; a bare number is radians, so a
// degree constant needs the ° which converts ·π/180 — matches Brilliant's engine).
// spec: { vec:[E,N], vB?:[E,0], vC?:[0,N], ask?:["speed","bearing"],
//         answers?:{speed,bearing}, noGraph?, graph }.
function buildVelBearing(mountEl, spec, cbs) {
  const vec = (spec.vec || [0, 0]).map(Number);
  const ask = spec.ask || ["speed", "bearing"];
  const trueSpeed = Math.hypot(vec[0], vec[1]);
  let trueBearing = Math.atan2(vec[0], vec[1]); if (trueBearing < 0) trueBearing += 2 * Math.PI;
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-md";
  const COL = { orange: "#f5a04a", teal: "#279b8d", purple: "#8a5cf6" };
  if (spec.graph && !spec.noGraph) {
    const gspec = Object.assign({ grid: { step: 1 }, xlabel: "East", ylabel: "North" }, spec.graph);
    const W0 = gspec.w || 330, plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    function draw(t, col, label) {
      const line = g.mkIn("line", { stroke: col, "stroke-width": 2.5, "stroke-linecap": "round" });
      const head = g.mkIn("path", { fill: col });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(t[0]), y2 = g.Y(t[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
      if (label) { const tl = g.mkIn("text", { x: g.X(t[0] / 2) + 7, y: g.Y(t[1] / 2) - 4, fill: col, "font-size": 12, "font-weight": 700, "font-style": "italic" }); tl.textContent = label; }
    }
    if (spec.vB) draw(spec.vB.map(Number), COL.orange, null);
    if (spec.vC) draw(spec.vC.map(Number), COL.teal, null);
    // bearing arc: clockwise from North (+y) to the resultant
    const r = 22, aN = Math.PI / 2, aV = Math.atan2(vec[1], vec[0]);
    const ax0 = g.X(0) + r * Math.cos(aN), ay0 = g.Y(0) - r * Math.sin(aN);
    const ax1 = g.X(0) + r * Math.cos(aV), ay1 = g.Y(0) - r * Math.sin(aV);
    const large = trueBearing > Math.PI ? 1 : 0;
    g.mkIn("path", { d: "M " + ax0.toFixed(1) + " " + ay0.toFixed(1) + " A " + r + " " + r + " 0 " + large + " 1 " + ax1.toFixed(1) + " " + ay1.toFixed(1), fill: "none", stroke: "#9aa0aa", "stroke-width": 1.5 });
    const midAng = aN - trueBearing / 2, tl = g.mkIn("text", { x: g.X(0) + (r + 13) * Math.cos(midAng), y: g.Y(0) - (r + 13) * Math.sin(midAng) + 4, fill: "#20242a", "font-size": 13, "font-style": "italic" }); tl.textContent = spec.angleLabel || "β";
    draw(vec, COL.purple, spec.magLabel != null ? String(spec.magLabel) : "v");
    stage.appendChild(g.el);
  }
  // ---- TILES MODE (Velocity from Bearing): drag expression-tiles into the
  // v_E / v_N component slots. Graded NUMERICALLY (sin/cos/tan in DEGREES) so
  // the placed tile must evaluate to the right component. ----
  if (spec.mode === "tiles") {
    const answer = (spec.answer || []).map(String);
    const slotLabels = spec.slotLabels || ["v<sub>E</sub> =", "v<sub>N</sub> ="];
    function parseExpr(s) {
      if (s == null) return null;
      let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "").replace(/°/g, "");
      if (!t) return null;
      t = t.replace(/sin/gi, "SIN").replace(/cos/gi, "COS").replace(/tan/gi, "TAN");
      t = t.replace(/(\d)(SIN|COS|TAN|\()/g, "$1*$2").replace(/(\))(\d|SIN|COS|TAN|\()/g, "$1*$2");
      if (!/^[-0-9.+*/()SINCOSTAN]*$/.test(t)) return null;
      try { const v = new Function("SIN", "COS", "TAN", "return (" + t + ");")((d) => Math.sin(d * Math.PI / 180), (d) => Math.cos(d * Math.PI / 180), (d) => Math.tan(d * Math.PI / 180)); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
    }
    const rows = document.createElement("div"); rows.className = "ts-md-rows"; const slots = [];
    function makeSlot() { const s = document.createElement("span"); s.className = "ts-pc-slot ts-vb-eslot is-empty"; const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s; }
    slotLabels.forEach((lb) => { const row = document.createElement("div"); row.className = "ts-md-row"; const l = document.createElement("span"); l.className = "ts-md-label"; l.innerHTML = lb; row.appendChild(l); row.appendChild(makeSlot()); rows.appendChild(row); });
    stage.appendChild(rows);
    const pal = document.createElement("div"); pal.className = "ts-pc-pal";
    function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
    function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = s.val; s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
    function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
    (spec.tiles || []).forEach((tv) => {
      const t = document.createElement("div"); t.className = "ts-pc-tile"; t.textContent = String(tv);
      t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
      t.addEventListener("pointerdown", (ev) => {
        if (locked) return; ev.preventDefault();
        const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
        let dragged = false; const move = (e) => { dragged = true; clone.style.left = (e.clientX - 30) + "px"; clone.style.top = (e.clientY - 14) + "px"; }; move(ev);
        const up = (e) => {
          document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove();
          if (!dragged) return;
          for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } }
        };
        document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
      });
      pal.appendChild(t);
    });
    stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
    const allVals = () => slots.map((s) => s.val);
    function correct() { const v = allVals(); if (v.some((x) => x == null)) return false; const a = parseExpr(v[0]), b = parseExpr(v[1]); return a != null && b != null && Math.abs(a - vec[0]) < 0.06 && Math.abs(b - vec[1]) < 0.06; }
    return {
      isComplete: () => slots.every((s) => s.val != null),
      check: () => ({ correct: correct(), value: allVals() }),
      getValue: allVals,
      setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
      getState: () => ({ value: allVals(), target: answer }),
      reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
      reveal: (forced) => { locked = true; const a = forced || answer; slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
    };
  }
  // typed rows
  const rows = document.createElement("div"); rows.className = "ts-md-rows";
  const inputs = {};
  function mkRow(key, label) {
    const row = document.createElement("div"); row.className = "ts-md-row";
    const l = document.createElement("span"); l.className = "ts-md-label"; l.innerHTML = label;
    const inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-md-input ts-md-wide"; inp.autocomplete = "off";
    row.appendChild(l); row.appendChild(inp); rows.appendChild(row); inputs[key] = inp;
  }
  if (ask.indexOf("speed") >= 0) { const ml = (spec.magLabel != null ? String(spec.magLabel) : "v_L").replace(/_\{([^}]+)\}/g, "<sub>$1</sub>").replace(/_(\w+)/g, "<sub>$1</sub>"); mkRow("speed", "<span style='font-style:normal'>|</span>" + ml + "<span style='font-style:normal'>|</span> ="); }
  if (ask.indexOf("bearing") >= 0) mkRow("bearing", "β =");
  stage.appendChild(rows); mountEl.appendChild(stage);
  function parseVal(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, "");
    if (!t) return null;
    let m = t.match(/^(-?\d*(?:\.\d+)?)?(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?$/i);
    if (m) { const coef = (m[1] == null || m[1] === "") ? 1 : (m[1] === "-" ? -1 : Number(m[1])); return coef * Math.sqrt(Number(m[2])); }
    m = t.match(/^(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)$/);
    if (m) return Number(m[1]) / Number(m[2]);
    const n = Number(t); return Number.isFinite(n) ? n : null;
  }
  // degree-aware angle evaluator: ° → ·π/180, tan⁻¹→radians (Math.atan).
  function parseBearing(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/π/g, "PI").replace(/pi/gi, "PI").replace(/tan⁻¹/g, "ATAN").replace(/tan\^?-?1/gi, "ATAN").replace(/arctan/gi, "ATAN").replace(/atan/gi, "ATAN").replace(/√/g, "SQRT").replace(/sqrt/gi, "SQRT").replace(/°/g, "*PI/180");
    // implicit multiplication so a coefficient·π bearing (2π−tan⁻¹…) evaluates rather than throwing
    t = t.replace(/(\d)(PI|ATAN|SQRT|\()/g, "$1*$2").replace(/(\))(\d|PI|ATAN|SQRT|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()PIATANSQRT]*$/.test(t)) return null;
    try { const v = new Function("PI", "ATAN", "SQRT", "return (" + t + ");")(Math.PI, Math.atan, Math.sqrt); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const near = (a, b) => a != null && b != null && Math.abs(a - b) < 1e-4;
  const bearingEq = (a, b) => { if (a == null || b == null) return false; let d = (a - b) % (2 * Math.PI); if (d > Math.PI) d -= 2 * Math.PI; if (d < -Math.PI) d += 2 * Math.PI; return Math.abs(d) < 0.02; };
  const valOf = (k) => k === "bearing" ? parseBearing(inputs[k].value) : parseVal(inputs[k].value);
  function vals() { const o = {}; for (const k in inputs) o[k] = valOf(k); return o; }
  Object.values(inputs).forEach((inp) => inp.addEventListener("input", () => {
    if (locked) return; if (cbs && cbs.onInteract) cbs.onInteract();
    const v = vals(); if (Object.keys(inputs).every((k) => v[k] != null) && cbs && cbs.onComplete) cbs.onComplete();
  }));
  return {
    isComplete: () => { const v = vals(); return Object.keys(inputs).every((k) => v[k] != null); },
    check: () => {
      const v = vals(); let ok = true;
      if (inputs.speed) ok = ok && near(v.speed, trueSpeed);
      if (inputs.bearing) ok = ok && bearingEq(v.bearing, trueBearing);
      return { correct: ok, value: Object.keys(inputs).reduce((o, k) => (o[k] = inputs[k].value, o), {}) };
    },
    getValue: () => Object.keys(inputs).reduce((o, k) => (o[k] = inputs[k].value, o), {}),
    setValue: (arr) => { if (Array.isArray(arr)) { const keys = Object.keys(inputs); keys.forEach((k, i) => { inputs[k].value = arr[i] != null ? String(arr[i]) : ""; }); } else if (arr && typeof arr === "object") { for (const k in inputs) if (arr[k] != null) inputs[k].value = String(arr[k]); } },
    getState: () => ({
      // Task #524 — same contract as magDir: the tutor sees the typed entries.
      value: Object.keys(inputs).reduce((o, k) => (o[k] = inputs[k].value, o), {}),
      target: { speed: trueSpeed, bearing: trueBearing },
    }),
    reset: () => { for (const k in inputs) inputs[k].value = ""; },
    reveal: () => { locked = true; const a = spec.answers || {}; if (inputs.speed) inputs.speed.value = a.speed != null ? a.speed : (Number.isInteger(trueSpeed) ? String(trueSpeed) : "√" + (vec[0] * vec[0] + vec[1] * vec[1])); if (inputs.bearing) inputs.bearing.value = a.bearing != null ? a.bearing : String((trueBearing * 180 / Math.PI).toFixed(1)) + "°"; for (const k in inputs) { inputs[k].readOnly = true; inputs[k].classList.add("is-answer"); } }
  };
}

// SCALE TO A TARGET MAGNITUDE (Brilliant "Scaling and Magnitude"): pick the
// scalar tile c so |c·v| equals a target magnitude (c = ±target/|v|; a unit
// vector when target = 1). The result c·v auto-computes and re-draws; graded on
// the resulting magnitude, so ANY tile giving the right length is accepted.
// spec: { vec:[x,y], targetMag, tiles:[scalar strings], answer, graph }.
function buildVecScale(mountEl, spec, cbs) {
  const vec = (spec.vec || [0, 0]).map(Number);
  const targetMag = Number(spec.targetMag);
  let locked = false, chosen = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vs";
  const COL = { orange: "#f5a04a", green: "#3aa76d" };
  // parse a scalar tile/typed string incl. fractions, surds, and surd-denominators
  // ("1/3", "-2", "4/5", "-√5", "1/√5", "2√10", "0.5") via a safe evaluator.
  function parseScalar(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?/gi, "SQRT($1)");
    t = t.replace(/(\d)(SQRT|\()/g, "$1*$2").replace(/(\))(\d|SQRT|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SQRT]*$/.test(t)) return null;
    try { const v = new Function("SQRT", "return (" + t + ");")(Math.sqrt); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  let g = null, resLine = null, resHead = null, drawArrow = null;
  if (spec.graph) {
    const gspec = Object.assign({ grid: { step: 1 } }, spec.graph);
    const W0 = gspec.w || 330, plotW = W0 - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    if (spec.unitCircle) g.mkIn("circle", { cx: g.X(0), cy: g.Y(0), r: (g.X(1) - g.X(0)).toFixed(1), fill: "none", stroke: "#c9cfd8", "stroke-width": 1.3 });
    drawArrow = function (line, head, x2, y2) { const x1 = g.X(0), y1 = g.Y(0); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z"); };
    resLine = g.mkIn("line", { stroke: COL.green, "stroke-width": 2.8, "stroke-linecap": "round", opacity: 0 }); resHead = g.mkIn("path", { fill: COL.green, opacity: 0 });
    const oline = g.mkIn("line", { stroke: COL.orange, "stroke-width": 2.4, "stroke-linecap": "round" }), ohead = g.mkIn("path", { fill: COL.orange });
    drawArrow(oline, ohead, g.X(vec[0]), g.Y(vec[1]));
    stage.appendChild(g.el);
  }
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  const typed = !spec.tiles;
  const row = document.createElement("div"); row.className = "ts-vs-row";
  let slot, inp = null;
  if (typed) {
    inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-vs-input"; inp.autocomplete = "off";
    inp.addEventListener("input", () => { if (locked) return; chosen = inp.value.trim() || null; renderChosen(); if (cbs && cbs.onInteract) cbs.onInteract(); if (chosen != null && parseScalar(chosen) != null && cbs && cbs.onComplete) cbs.onComplete(); });
    slot = inp; row.appendChild(inp);
  } else {
    slot = document.createElement("span"); slot.className = "ts-pc-slot ts-vs-slot is-empty";
    slot.addEventListener("click", () => { if (locked || chosen == null) return; chosen = null; renderChosen(); if (cbs && cbs.onInteract) cbs.onInteract(); });
    row.appendChild(slot);
  }
  function brk(cells) { const w = document.createElement("span"); w.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; w.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; cells.forEach((c) => col.appendChild(c)); w.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; w.appendChild(r); return w; }
  const sc = (n) => { const d = document.createElement("div"); d.className = "ts-vt-static"; d.textContent = fmt(n); return d; };
  row.appendChild(brk(vec.map(sc)));
  const eq = document.createElement("span"); eq.className = "ts-vt-op"; eq.textContent = "="; row.appendChild(eq);
  const resCells = [document.createElement("div"), document.createElement("div")]; resCells.forEach((c) => { c.className = "ts-vt-static ts-vs-res"; c.textContent = "?"; });
  row.appendChild(brk(resCells));
  stage.appendChild(row);
  const pal = document.createElement("div"); pal.className = "ts-pc-pal";
  function renderChosen() {
    const c = chosen == null ? null : parseScalar(chosen);
    if (!typed) { if (chosen == null) { slot.textContent = ""; slot.classList.add("is-empty"); slot.classList.remove("is-filled"); } else { slot.textContent = chosen; slot.classList.remove("is-empty"); slot.classList.add("is-filled"); } }
    if (c == null) { resCells.forEach((cell) => cell.textContent = "?"); if (resLine) { resLine.setAttribute("opacity", 0); resHead.setAttribute("opacity", 0); } return; }
    const res = [c * vec[0], c * vec[1]];
    resCells[0].textContent = fmt(res[0]); resCells[1].textContent = fmt(res[1]);
    if (g && drawArrow) { drawArrow(resLine, resHead, g.X(res[0]), g.Y(res[1])); resLine.setAttribute("opacity", 1); resHead.setAttribute("opacity", 1); }
  }
  (spec.tiles || []).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-pc-tile"; t.textContent = String(tv);
    t.addEventListener("click", () => { if (locked) return; chosen = String(tv); renderChosen(); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false; const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 14) + "px"; }; move(ev);
      const up = (e) => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove(); if (!dragged) return; const r = slot.getBoundingClientRect(); if (e.clientX >= r.left - 30 && e.clientX <= r.right + 30 && e.clientY >= r.top - 20 && e.clientY <= r.bottom + 20) { chosen = String(tv); renderChosen(); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); } };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderChosen();
  function correct() { if (chosen == null) return false; const c = parseScalar(chosen); if (c == null) return false; if (spec.samedir && c <= 0) return false; return Math.abs(Math.hypot(c * vec[0], c * vec[1]) - targetMag) < 0.02; }
  return {
    isComplete: () => chosen != null && parseScalar(chosen) != null,
    check: () => ({ correct: correct(), value: chosen }),
    getValue: () => chosen,
    setValue: (v) => { chosen = Array.isArray(v) ? (v[0] == null ? null : String(v[0])) : (v == null ? null : String(v)); if (inp) inp.value = chosen || ""; renderChosen(); },
    getState: () => ({ value: chosen, target: spec.answer }),
    reset: () => { chosen = null; if (inp) inp.value = ""; renderChosen(); },
    reveal: (forced) => { locked = true; chosen = forced || spec.answer; if (inp) { inp.value = chosen || ""; inp.readOnly = true; } renderChosen(); slot.classList.add("is-answer"); }
  };
}

// ── SCALED-MAGNITUDE IDENTITY (Brilliant "Scaling and Magnitude", complete the
// equations): several equation lines whose blanks are filled from a palette of
// SYMBOLIC tiles (e.g. c, −c, |c|, c²). Slots fill left-to-right / top-to-bottom;
// graded by string equality of each filled tile against spec.answer. A ⟦⟧ marker
// in each line string denotes a slot; the surrounding text renders verbatim.
// spec: { lines:["|cv| = √( ⟦⟧x² + ⟦⟧y² )", …], tiles:["c","−c","|c|","c²"],
//         answer:["c²","c²","c²","|c|","|c|"] }.
function buildScaleEq(mountEl, spec, cbs) {
  const answer = (spec.answer || []).map(String);
  const tiles = spec.tiles || [];
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-se";
  const slots = [];
  function makeSlot() {
    const s = document.createElement("span"); s.className = "ts-vt-slot ts-se-slot is-empty";
    s.style.cssText = "display:inline-flex;align-items:center;justify-content:center;min-width:34px;height:30px;margin:0 3px;vertical-align:middle";
    const idx = slots.length;
    s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
    slots.push({ el: s, val: null }); return s;
  }
  (spec.lines || []).forEach((ln) => {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:2px;font-size:19px;margin:8px 0;line-height:1.6";
    String(ln).split("⟦⟧").forEach((part, i) => {
      if (i > 0) row.appendChild(makeSlot());
      if (part) { const span = document.createElement("span"); span.textContent = part; row.appendChild(span); }
    });
    stage.appendChild(row);
  });
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = s.val; s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  tiles.forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = String(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false;
      const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 18) + "px"; }; move(ev);
      const up = (e) => {
        document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove();
        if (!dragged) return;
        for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } }
      };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => { const vs = allVals(); const hit = (key) => key.length === vs.length && vs.every((v, i) => v != null && v === key[i]); const alts = spec.answerAlts || []; return { correct: hit(answer) || alts.some((a) => Array.isArray(a) && hit(a)), value: vs }; },
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answer }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || answer; slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Comparing Unit Vectors (L7.3): two unit vectors on the unit circle + the
// angle θ between them; fill 1-2 slots (cos θ, |u₂−u₁|) from a tile palette.
// Graded from the DRAWN geometry, not an authored key: for unit vectors
// cos θ = cos(Δ) and |u₂−u₁| = √(2−2cos Δ) — so a wrong tile always fails.
// parseScalar handles surds ("√2", "√3", "−1/2").
function buildUnitCompare(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-pc ts-uc";
  const COL = { orange: "#f5a04a", teal: "#279b8d", blue: "#4f6ef7", green: "#3aa76d", purple: "#8a5cf6" };
  function parseScalar(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?/gi, "SQRT($1)");
    t = t.replace(/(\d)(SQRT|\()/g, "$1*$2").replace(/(\))(\d|SQRT|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SQRT]*$/.test(t)) return null;
    try { const v = new Function("SQRT", "return (" + t + ");")(Math.sqrt); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const u1deg = Number(spec.u1.deg), u2deg = Number(spec.u2.deg);
  // ---- unit circle + two unit vectors + θ arc ----
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -1.35, xmax: 1.35, ymin: -1.35, ymax: 1.35, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    g.mkIn("circle", { cx: g.X(0), cy: g.Y(0), r: (g.X(1) - g.X(0)).toFixed(1), fill: "none", stroke: "#c9cfd8", "stroke-width": 1.3 });
    const a1 = u1deg * Math.PI / 180, a2 = u2deg * Math.PI / 180, r = 26;
    let dArc = a2 - a1; while (dArc > Math.PI) dArc -= 2 * Math.PI; while (dArc < -Math.PI) dArc += 2 * Math.PI;
    const ax0 = g.X(0) + r * Math.cos(a1), ay0 = g.Y(0) - r * Math.sin(a1);
    const ax1 = g.X(0) + r * Math.cos(a2), ay1 = g.Y(0) - r * Math.sin(a2);
    const large = Math.abs(dArc) > Math.PI ? 1 : 0, sweep = dArc < 0 ? 1 : 0;
    g.mkIn("path", { d: "M " + ax0.toFixed(1) + " " + ay0.toFixed(1) + " A " + r + " " + r + " 0 " + large + " " + sweep + " " + ax1.toFixed(1) + " " + ay1.toFixed(1), fill: "rgba(150,156,166,0.16)", stroke: "#9aa0aa", "stroke-width": 1.4 });
    // θ label: on a degenerate arc (identical vectors) the bisector IS the
    // vector, so shove the label perpendicular off the line; register its
    // spot so the vector labels dodge it (goldframe #349).
    const lm = a1 + dArc / 2, thPerp = Math.abs(dArc) < 0.15 ? 13 : 0;
    const thX = g.X(0) + (r + 15) * Math.cos(lm) + thPerp * Math.cos(lm + Math.PI / 2);
    const thY = g.Y(0) - (r + 15) * Math.sin(lm) - thPerp * Math.sin(lm + Math.PI / 2) + 4;
    const tl = g.mkIn("text", { x: thX, y: thY, fill: "#20242a", "font-size": 13, "font-style": "italic" }); tl.textContent = "θ";
    const placedLabs = (g._labPts ??= []); // label collision-avoid (#349)
    placedLabs.push([thX, thY]);
    [[u1deg, spec.u1], [u2deg, spec.u2]].forEach(([deg, uspec]) => {
      const col = COL[uspec.color] || COL.orange; const a = deg * Math.PI / 180;
      const vx = Math.cos(a), vy = Math.sin(a);
      const line = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
      const head = g.mkIn("path", { fill: col });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(vx), y2 = g.Y(vy);
      line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
      if (uspec.label) {
        // identical/near-identical vectors: slide the second label toward the
        // origin so both stay legible instead of stacking 100% (#349)
        let tx, ty;
        for (const t0 of [0.66, 0.9, 0.42, 1.14]) {
          tx = g.X(vx * t0) + (vx >= 0 ? 7 : -13); ty = g.Y(vy * t0) - 7;
          if (!placedLabs.some((p) => Math.hypot(p[0] - tx, p[1] - ty) < 18)) break;
        }
        placedLabs.push([tx, ty]);
        const t = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 12.5, "font-weight": 700 }); t.innerHTML = String(uspec.label).replace(/_(\w+)/, "<tspan baseline-shift='sub' font-size='9'>$1</tspan>");
      }
    });
    stage.appendChild(g.el);
  }
  // ---- slots + tile palette ----
  const fmtN = (n) => String(n).replace(/-(\d)/g, "−$1");
  const slotDefs = spec.slots || [];
  const slots = [];
  const rows = document.createElement("div"); rows.className = "ts-pc-rows ts-uc-rows";
  const lbl = (h) => { const e = document.createElement("span"); e.className = "ts-pc-lab"; e.innerHTML = h; return e; };
  function makeSlot(idx) {
    const s = document.createElement("span"); s.className = "ts-pc-slot is-empty";
    s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
    return s;
  }
  slotDefs.forEach((sd, i) => {
    const row = document.createElement("div"); row.className = "ts-pc-row";
    row.appendChild(lbl(sd.label + " &nbsp;=&nbsp;"));
    const s = makeSlot(i); slots.push({ el: s, val: null, def: sd }); row.appendChild(s);
    rows.appendChild(row);
  });
  stage.appendChild(rows);
  const pal = document.createElement("div"); pal.className = "ts-pc-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmtN(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || []).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-pc-tile"; t.textContent = fmtN(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false; const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 14) + "px"; }; move(ev);
      const up = (e) => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove(); if (!dragged) return; for (let i = 0; i < slots.length; i++) { const rr = slots[i].el.getBoundingClientRect(); if (e.clientX >= rr.left - 8 && e.clientX <= rr.right + 8 && e.clientY >= rr.top - 8 && e.clientY <= rr.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } } };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  // geometric truth (unit vectors): cos θ = cos(Δ), |u₂−u₁| = √(2−2cos Δ)
  const cosT = Math.cos((u2deg - u1deg) * Math.PI / 180);
  function target(def) { return def.kind === "diffmag" ? Math.sqrt(Math.max(0, 2 - 2 * cosT)) : cosT; }
  const tol = spec.tol != null ? Number(spec.tol) : 0.02;
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => { if (s.val == null) return false; const v = parseScalar(s.val); if (v == null) return false; return Math.abs(v - target(s.def)) < tol; }); }
  const answerStrs = () => slots.map((s) => s.def.answer != null ? String(s.def.answer) : String(Math.round(target(s.def) * 1000) / 1000));
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answerStrs() }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || answerStrs(); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── COS θ POINT-SELECT (Brilliant "Comparing Unit Vectors"): the curve cos θ is
// plotted over θ∈[0,2π] with a few marked points; the learner TAPS the point(s)
// matching identical (θ=0, cos=1), orthogonal (θ=π/2 & 3π/2, cos=0) or opposite
// (θ=π, cos=−1) unit vectors. spec: { points:[0,90,180,270] (θ° marked),
// answer:[0] (θ° that are correct), multi?:bool, graph? }.
function buildCosSelect(mountEl, spec, cbs) {
  const pts = (spec.points || [0, 90, 180, 270]).map(Number);
  const answer = (spec.answer || []).map(Number).slice().sort((a, b) => a - b);
  const multi = !!spec.multi;
  let locked = false;
  const sel = new Set();
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-cs";
  const P = Math.PI;
  const gspec = Object.assign({
    xmin: -0.35, xmax: 6.9, ymin: -1.45, ymax: 1.45, w: 360,
    gridlines: true,
    xticks: [{ v: P / 2, label: "π/2" }, { v: P, label: "π" }, { v: 3 * P / 2, label: "3π/2" }, { v: 2 * P, label: "2π" }],
    yticks: [{ v: 1, label: "1" }, { v: -1, label: "−1" }]
  }, spec.graph);
  const plotW = (gspec.w || 360) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
  if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
  const g = lineGraphSVG(gspec);
  // the cos θ curve
  let d = "";
  for (let i = 0; i <= 120; i++) { const th = i / 120 * 2 * P; d += (i === 0 ? "M " : "L ") + g.X(th).toFixed(1) + " " + g.Y(Math.cos(th)).toFixed(1) + " "; }
  g.mkIn("path", { d, fill: "none", stroke: "#4f6ef7", "stroke-width": 2.6, "stroke-linecap": "round" });
  // θ axis label
  const axl = g.mkIn("text", { x: g.X(2 * P) + 16, y: g.Y(0) + 4, fill: "#20242a", "font-size": 13, "font-style": "italic" }); axl.textContent = "θ";
  // marked, tappable points
  const dots = pts.map((deg) => {
    const th = deg * P / 180, cx = g.X(th), cy = g.Y(Math.cos(th));
    const halo = g.mkIn("circle", { cx, cy, r: 11, fill: "rgba(79,110,247,0.14)", stroke: "none" });
    const dot = g.mkIn("circle", { cx, cy, r: 5.5, fill: "#4f6ef7", stroke: "none" });
    halo.style.cursor = "pointer"; dot.style.cursor = "pointer";
    const toggle = () => {
      if (locked) return;
      if (sel.has(deg)) sel.delete(deg); else { if (!multi) sel.clear(); sel.add(deg); }
      render(); if (cbs && cbs.onInteract) cbs.onInteract();
      if (sel.size > 0 && cbs && cbs.onComplete) cbs.onComplete();
    };
    halo.addEventListener("click", toggle); dot.addEventListener("click", toggle);
    return { deg, halo, dot };
  });
  function render() {
    dots.forEach((o) => {
      const on = sel.has(o.deg);
      o.dot.setAttribute("fill", on ? "#2f8f4e" : "#4f6ef7");
      o.halo.setAttribute("fill", on ? "rgba(47,143,78,0.22)" : "rgba(79,110,247,0.14)");
      o.halo.setAttribute("r", on ? 13 : 11);
    });
  }
  stage.appendChild(g.el); mountEl.appendChild(stage); render();
  return {
    isComplete: () => sel.size > 0,
    check: () => { const s = [...sel].sort((a, b) => a - b); return { correct: s.length === answer.length && s.every((v, i) => v === answer[i]), value: s }; },
    getValue: () => [...sel],
    setValue: (arr) => { sel.clear(); (Array.isArray(arr) ? arr : [arr]).forEach((v) => sel.add(Number(v))); render(); },
    getState: () => ({ value: [...sel], target: answer }),
    reset: () => { sel.clear(); render(); },
    reveal: () => { locked = true; sel.clear(); answer.forEach((v) => sel.add(v)); render(); }
  };
}

// ── The Dot Product (L7.4/L7.5/L8): two GIVEN column vectors joined by "·",
// type the numeric result u·v = uₓvₓ + u_yv_y. Components may be surds/fractions
// (rendered as stacked fractions). Graded from the GIVEN components (parsed) — the
// typed answer is parsed (surd/fraction-aware) and compared to the true dot product
// within tol, NOT an authored key. Optional unit-circle graph of the two vectors.
function buildDotProduct(mountEl, spec, cbs) {
  let locked = false, val = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp";
  const COL = { orange: "#f5a04a", teal: "#279b8d", blue: "#4f6ef7", green: "#3aa76d", purple: "#8a5cf6" };
  // scalar + degree-trig evaluator: surds (√2), fractions, decimals, AND
  // cos/sin/tan(…°) in DEGREES — so a numeric dot (½) and a symbolic cos(120°)
  // parse to the SAME value (grading accepts either form).
  function parseScalar(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/[×·∙⋅]/g, "*").replace(/÷/g, "/").replace(/°/g, "").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?/gi, "SQRT($1)");
    t = t.replace(/sin/gi, "SIN").replace(/cos/gi, "COS").replace(/tan/gi, "TAN");
    t = t.replace(/(\d)(SQRT|SIN|COS|TAN|\()/g, "$1*$2").replace(/(\))(\d|SQRT|SIN|COS|TAN|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SQRTINCOA]*$/.test(t)) return null;
    try { const fn = new Function("SQRT", "SIN", "COS", "TAN", "return (" + t + ");"); const v = fn(Math.sqrt, (d) => Math.sin(d * Math.PI / 180), (d) => Math.cos(d * Math.PI / 180), (d) => Math.tan(d * Math.PI / 180)); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  // ANGLE mode (L7.5): spec.u1{deg}/u2{deg} → bare "u₁·u₂=[input]" over a unit
  // circle with a θ arc; answer = cos(angle between). Otherwise COMPONENT mode.
  const angleMode = !!(spec.u1 && spec.u1.deg != null);
  const u = (spec.u || []).map(String), v = (spec.v || []).map(String);
  const un = u.map(parseScalar), vn = v.map(parseScalar);
  const trueDot = angleMode
    ? Math.cos((Number(spec.u2.deg) - Number(spec.u1.deg)) * Math.PI / 180)
    : un[0] * vn[0] + un[1] * vn[1];
  // ---- optional unit-circle graph with the two vectors (+ θ arc in angle mode) ----
  if (spec.graph !== false && (spec.unitCircle || spec.graph || angleMode)) {
    const gspec = Object.assign({ xmin: -1.35, xmax: 1.35, ymin: -1.35, ymax: 1.35, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    if (angleMode || spec.unitCircle) g.mkIn("circle", { cx: g.X(0), cy: g.Y(0), r: (g.X(1) - g.X(0)).toFixed(1), fill: "none", stroke: "#c9cfd8", "stroke-width": 1.3 });
    if (angleMode && spec.arc !== false) {
      const a1 = Number(spec.u1.deg) * Math.PI / 180, a2 = Number(spec.u2.deg) * Math.PI / 180, r = 26;
      let dArc = a2 - a1; while (dArc > Math.PI) dArc -= 2 * Math.PI; while (dArc < -Math.PI) dArc += 2 * Math.PI;
      const ax0 = g.X(0) + r * Math.cos(a1), ay0 = g.Y(0) - r * Math.sin(a1);
      const ax1 = g.X(0) + r * Math.cos(a2), ay1 = g.Y(0) - r * Math.sin(a2);
      const large = Math.abs(dArc) > Math.PI ? 1 : 0, sweep = dArc < 0 ? 1 : 0;
      g.mkIn("path", { d: "M " + ax0.toFixed(1) + " " + ay0.toFixed(1) + " A " + r + " " + r + " 0 " + large + " " + sweep + " " + ax1.toFixed(1) + " " + ay1.toFixed(1), fill: "rgba(150,156,166,0.16)", stroke: "#9aa0aa", "stroke-width": 1.4 });
      const lm = a1 + dArc / 2, degBetween = Math.round(Math.abs(dArc) * 180 / Math.PI);
      const thPerp = Math.abs(dArc) < 0.15 ? 13 : 0; // degenerate-arc dodge (#349)
      const thX = g.X(0) + (r + 20) * Math.cos(lm) + thPerp * Math.cos(lm + Math.PI / 2);
      const thY = g.Y(0) - (r + 20) * Math.sin(lm) - thPerp * Math.sin(lm + Math.PI / 2) + 4;
      const tl = g.mkIn("text", { x: thX, y: thY, fill: "#20242a", "font-size": 12.5 }); tl.textContent = degBetween + "°";
      (g._labPts ??= []).push([thX, thY]);
    }
    const pairs = angleMode
      ? [[[Math.cos(Number(spec.u1.deg) * Math.PI / 180), Math.sin(Number(spec.u1.deg) * Math.PI / 180)], spec.u1.label || "u_1", spec.u1.color || "orange"],
         [[Math.cos(Number(spec.u2.deg) * Math.PI / 180), Math.sin(Number(spec.u2.deg) * Math.PI / 180)], spec.u2.label || "u_2", spec.u2.color || "teal"]]
      : [[un, spec.uLabel, spec.uColor || "orange"], [vn, spec.vLabel, spec.vColor || "teal"]];
    const placedLabs2 = (g._labPts ??= []); // label collision-avoid (#349)
    pairs.forEach(([vec, label, color]) => {
      const col = COL[color] || COL.orange;
      const line = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
      const head = g.mkIn("path", { fill: col });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(vec[0]), y2 = g.Y(vec[1]);
      line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
      if (label) {
        let tx, ty;
        for (const t0 of [0.62, 0.86, 0.38, 1.1]) { // coincident-pair dodge (#349)
          tx = g.X(vec[0] * t0) + (vec[0] >= 0 ? 7 : -13); ty = g.Y(vec[1] * t0) - 7;
          if (!placedLabs2.some((p) => Math.hypot(p[0] - tx, p[1] - ty) < 18)) break;
        }
        placedLabs2.push([tx, ty]);
        const t = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 12.5, "font-weight": 700 }); t.innerHTML = String(label).replace(/_(\w+)/, "<tspan baseline-shift='sub' font-size='9'>$1</tspan>");
      }
    });
    stage.appendChild(g.el);
  }
  // ---- component renderer (stacked fraction w/ surd numerator support) ----
  function compNode(str) {
    const s = document.createElement("span"); s.className = "ts-vt-static";
    const t = String(str).replace(/-/g, "−");
    const m = t.match(/^(−?)(.+)\/(.+)$/);
    if (m) {
      if (m[1]) { const sg = document.createElement("span"); sg.textContent = "−"; sg.style.marginRight = "2px"; s.appendChild(sg); }
      const f = document.createElement("span"); f.className = "ts-vt-frac";
      const nu = document.createElement("span"); nu.className = "ts-vt-frac-num"; nu.textContent = m[2];
      const de = document.createElement("span"); de.className = "ts-vt-frac-den"; de.textContent = m[3];
      f.appendChild(nu); f.appendChild(de); s.appendChild(f);
    } else s.textContent = t;
    return s;
  }
  function brk(cells) { const w = document.createElement("span"); w.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; w.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; cells.forEach((c) => col.appendChild(c)); w.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; w.appendChild(r); return w; }
  function labelNode(str) { const s = document.createElement("span"); s.style.cssText = "font-size:20px;font-weight:600;color:#20242a"; s.innerHTML = String(str).replace(/_(\w+)/, "<sub>$1</sub>"); return s; }
  const row = document.createElement("div"); row.className = "ts-vs-row ts-dp-row";
  row.appendChild(angleMode ? labelNode(spec.u1.label || "u_1") : brk(u.map(compNode)));
  const dot = document.createElement("span"); dot.className = "ts-vt-op"; dot.textContent = "·"; row.appendChild(dot);
  row.appendChild(angleMode ? labelNode(spec.u2.label || "u_2") : brk(v.map(compNode)));
  const eq = document.createElement("span"); eq.className = "ts-vt-op"; eq.textContent = "="; row.appendChild(eq);
  const inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-vs-input ts-dp-input"; inp.autocomplete = "off";
  inp.addEventListener("input", () => { if (locked) return; val = inp.value.trim() || null; if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && parseScalar(val) != null && cbs && cbs.onComplete) cbs.onComplete(); });
  row.appendChild(inp);
  stage.appendChild(row); mountEl.appendChild(stage);
  const tol = spec.tol != null ? Number(spec.tol) : 0.001;
  function correct() { if (val == null) return false; const p = parseScalar(val); if (p == null) return false; return Math.abs(p - trueDot) < tol; }
  return {
    isComplete: () => val != null && parseScalar(val) != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (x) => { val = Array.isArray(x) ? (x[0] == null ? null : String(x[0])) : (x == null ? null : String(x)); inp.value = val || ""; },
    getState: () => ({ value: val, target: spec.answer != null ? String(spec.answer) : String(Math.round(trueDot * 1000) / 1000) }),
    reset: () => { val = null; inp.value = ""; },
    reveal: (forced) => { locked = true; val = forced != null ? String(forced) : (spec.answer != null ? String(spec.answer) : String(trueDot)); inp.value = val; inp.readOnly = true; }
  };
}

// ── General Dot Products (L8.1 P2/P3): "complete the vector to make the equation
// true" — one of the four components of u·v is a BLANK (null); fill it so u·v =
// result. Linear in the missing component → unique when its coefficient ≠ 0.
// Typed integer keypad ((−) allowed). Graded from the DRAWN equation: substitute
// the typed value and check u·v == result (NOT an authored key).
function buildDotSolve(mountEl, spec, cbs) {
  let locked = false, val = null, inp = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp ts-ds";
  function parseNum(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); if (!t || t === "-") return null; const n = Number(t); return Number.isFinite(n) ? n : null; }
  const u = spec.u.slice(), v = spec.v.slice();
  const result = Number(spec.result);
  function cellNode(valStr) {
    if (valStr === null) {
      inp = document.createElement("input"); inp.type = "text"; inp.inputMode = "numeric"; inp.className = "ts-sf-input ts-vf-input ts-vf-scalar"; inp.autocomplete = "off";
      inp.addEventListener("input", () => { if (locked) return; val = inp.value.trim() || null; if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && parseNum(val) != null && cbs && cbs.onComplete) cbs.onComplete(); });
      return inp;
    }
    const d = document.createElement("div"); d.className = "ts-vt-static"; d.textContent = String(valStr).replace(/-/g, "−"); return d;
  }
  function brk(cells) { const w = document.createElement("span"); w.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; w.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; cells.forEach((c) => col.appendChild(c)); w.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; w.appendChild(r); return w; }
  const row = document.createElement("div"); row.className = "ts-vs-row ts-dp-row";
  row.appendChild(brk(u.map(cellNode)));
  const dot = document.createElement("span"); dot.className = "ts-vt-op"; dot.textContent = "·"; row.appendChild(dot);
  row.appendChild(brk(v.map(cellNode)));
  const eq = document.createElement("span"); eq.className = "ts-vt-op"; eq.textContent = "="; row.appendChild(eq);
  const res = document.createElement("span"); res.className = "ts-vt-op"; res.style.fontWeight = "600"; res.textContent = String(result).replace(/-/g, "−"); row.appendChild(res);
  stage.appendChild(row); mountEl.appendChild(stage);
  const tol = spec.tol != null ? Number(spec.tol) : 0.001;
  function dotWith(x) { const uu = u.map((c) => c === null ? x : parseNum(c)), vv = v.map((c) => c === null ? x : parseNum(c)); return uu[0] * vv[0] + uu[1] * vv[1]; }
  function correct() { if (val == null) return false; const x = parseNum(val); if (x == null) return false; return Math.abs(dotWith(x) - result) < tol; }
  return {
    isComplete: () => val != null && parseNum(val) != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (x) => { val = Array.isArray(x) ? (x[0] == null ? null : String(x[0])) : (x == null ? null : String(x)); if (inp) inp.value = val || ""; },
    getState: () => ({ value: val, target: spec.answer != null ? String(spec.answer) : null }),
    reset: () => { val = null; if (inp) inp.value = ""; },
    reveal: (forced) => { locked = true; val = forced != null ? String(forced) : (spec.answer != null ? String(spec.answer) : null); if (inp) { inp.value = val || ""; inp.readOnly = true; } }
  };
}

// ── Scaling Dot Products (L8.2): given v·w = D, compute a SCALED dot such as
// (a·v)·w, v·(b·w), or (a·v)·(b·w). Scaling property: (a v)·(b w) = a·b·(v·w).
// Premise value + scalars are given; typed numeric answer graded = a·b·D.
function buildDotScale(mountEl, spec, cbs) {
  let locked = false, val = null, inp = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp ts-dsc";
  function parseScalar(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?/gi, "SQRT($1)");
    t = t.replace(/(\d)(SQRT|\()/g, "$1*$2").replace(/(\))(\d|SQRT|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SQRT]*$/.test(t)) return null;
    try { const v = new Function("SQRT", "return (" + t + ");")(Math.sqrt); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const given = Number(spec.given);
  const su = spec.scaleU != null ? parseScalar(spec.scaleU) : 1;
  const sw = spec.scaleW != null ? parseScalar(spec.scaleW) : 1;
  const trueVal = su * sw * given;
  function vecLabel(ch) { const s = document.createElement("span"); s.style.cssText = "position:relative;font-style:italic;font-weight:600;padding:0 1px"; s.textContent = ch; const ar = document.createElement("span"); ar.textContent = "→"; ar.style.cssText = "position:absolute;left:-1px;right:-1px;top:-0.72em;font-size:0.6em;text-align:center;font-style:normal;font-weight:400;line-height:1"; s.appendChild(ar); return s; }
  function fracNode(str) {
    const wrap = document.createElement("span"); wrap.style.cssText = "display:inline-flex;align-items:center";
    const t = String(str).replace(/-/g, "−");
    const m = t.match(/^(−?)(.+)\/(.+)$/);
    if (m) { if (m[1]) { const sg = document.createElement("span"); sg.textContent = "−"; sg.style.marginRight = "2px"; wrap.appendChild(sg); } const f = document.createElement("span"); f.className = "ts-vt-frac"; const nu = document.createElement("span"); nu.className = "ts-vt-frac-num"; nu.textContent = m[2]; const de = document.createElement("span"); de.className = "ts-vt-frac-den"; de.textContent = m[3]; f.appendChild(nu); f.appendChild(de); wrap.appendChild(f); }
    else wrap.textContent = t;
    return wrap;
  }
  const opTxt = (s) => { const e = document.createElement("span"); e.textContent = s; e.style.margin = "0 6px"; return e; };
  const prem = document.createElement("div"); prem.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:19px;margin-bottom:18px";
  prem.appendChild(vecLabel("v")); prem.appendChild(opTxt("·")); prem.appendChild(vecLabel("w")); prem.appendChild(opTxt("=")); const dv = document.createElement("span"); dv.textContent = String(given).replace(/-/g, "−"); dv.style.fontWeight = "600"; prem.appendChild(dv);
  stage.appendChild(prem);
  const row = document.createElement("div"); row.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:20px";
  function scaledTerm(scaleStr, ch) {
    const span = document.createElement("span"); span.style.cssText = "display:inline-flex;align-items:center";
    if (scaleStr != null) { const lp = document.createElement("span"); lp.textContent = "("; lp.style.cssText = "font-size:1.35em;font-weight:300"; span.appendChild(lp); span.appendChild(fracNode(scaleStr)); const sp = document.createElement("span"); sp.style.width = "3px"; span.appendChild(sp); span.appendChild(vecLabel(ch)); const rp = document.createElement("span"); rp.textContent = ")"; rp.style.cssText = "font-size:1.35em;font-weight:300"; span.appendChild(rp); }
    else span.appendChild(vecLabel(ch));
    return span;
  }
  row.appendChild(scaledTerm(spec.scaleU, "v")); row.appendChild(opTxt("·")); row.appendChild(scaledTerm(spec.scaleW, "w")); row.appendChild(opTxt("="));
  inp = document.createElement("input"); inp.type = "text"; inp.inputMode = "numeric"; inp.className = "ts-sf-input ts-vs-input ts-dsc-input"; inp.autocomplete = "off";
  inp.addEventListener("input", () => { if (locked) return; val = inp.value.trim() || null; if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && parseScalar(val) != null && cbs && cbs.onComplete) cbs.onComplete(); });
  row.appendChild(inp);
  stage.appendChild(row); mountEl.appendChild(stage);
  const tol = spec.tol != null ? Number(spec.tol) : 0.001;
  function correct() { if (val == null) return false; const p = parseScalar(val); if (p == null) return false; return Math.abs(p - trueVal) < tol; }
  return {
    isComplete: () => val != null && parseScalar(val) != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (x) => { val = Array.isArray(x) ? (x[0] == null ? null : String(x[0])) : (x == null ? null : String(x)); if (inp) inp.value = val || ""; },
    getState: () => ({ value: val, target: spec.answer != null ? String(spec.answer) : String(trueVal) }),
    reset: () => { val = null; if (inp) inp.value = ""; },
    reveal: (forced) => { locked = true; val = forced != null ? String(forced) : (spec.answer != null ? String(spec.answer) : String(trueVal)); if (inp) { inp.value = val; inp.readOnly = true; } }
  };
}

// ── Dot Product from Magnitudes (L8.3): given |v₁|,θ₁ and |v₂|,θ₂, compute
// v₁·v₂ = |v₁||v₂|cos(θ₂−θ₁). Typed expression (e.g. 30cos(45°)) graded on VALUE
// via a surd+degree-trig parser; a live decimal preview shows below.
function buildDotMag(mountEl, spec, cbs) {
  let locked = false, val = null, inp = null, prev = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp ts-dm2";
  function parseVal(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/[×·∙⋅]/g, "*").replace(/÷/g, "/").replace(/°/g, "").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?/gi, "SQRT($1)");
    t = t.replace(/sin/gi, "SIN").replace(/cos/gi, "COS").replace(/tan/gi, "TAN");
    t = t.replace(/(\d)(SQRT|SIN|COS|TAN|\()/g, "$1*$2").replace(/(\))(\d|SQRT|SIN|COS|TAN|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SQRTINCOA]*$/.test(t)) return null;
    try { const v = new Function("SQRT", "SIN", "COS", "TAN", "return (" + t + ");")(Math.sqrt, (d) => Math.sin(d * Math.PI / 180), (d) => Math.cos(d * Math.PI / 180), (d) => Math.tan(d * Math.PI / 180)); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const m1 = Number(spec.v1.mag), a1 = Number(spec.v1.deg), m2 = Number(spec.v2.mag), a2 = Number(spec.v2.deg);
  const trueVal = m1 * m2 * Math.cos((a2 - a1) * Math.PI / 180);
  function vecLabel(sub) { const s = document.createElement("span"); s.style.cssText = "position:relative;font-style:italic;font-weight:600;padding:0 1px"; s.innerHTML = "v<sub style='font-style:normal'>" + sub + "</sub>"; const ar = document.createElement("span"); ar.textContent = "→"; ar.style.cssText = "position:absolute;left:-1px;right:6px;top:-0.72em;font-size:0.58em;text-align:center;font-style:normal;font-weight:400;line-height:1"; s.appendChild(ar); return s; }
  function premLine(mag, sub, ang) {
    const row = document.createElement("div"); row.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:17px;margin-bottom:10px";
    const bar1 = document.createElement("span"); bar1.textContent = "|"; row.appendChild(bar1);
    row.appendChild(vecLabel(sub));
    const bar2 = document.createElement("span"); bar2.textContent = "|"; row.appendChild(bar2);
    const eq = document.createElement("span"); eq.textContent = " = " + mag; eq.style.margin = "0 4px"; row.appendChild(eq);
    const andt = document.createElement("span"); andt.textContent = "and"; andt.style.margin = "0 10px"; andt.style.color = "#6b7280"; row.appendChild(andt);
    const th = document.createElement("span"); th.innerHTML = "θ<sub>" + sub + "</sub> = " + ang + "°"; th.style.fontStyle = "italic"; row.appendChild(th);
    return row;
  }
  stage.appendChild(premLine(m1, "1", a1));
  stage.appendChild(premLine(m2, "2", a2));
  const row = document.createElement("div"); row.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:19px;margin-top:10px";
  row.appendChild(vecLabel("1")); const op = document.createElement("span"); op.textContent = "·"; op.style.margin = "0 7px"; row.appendChild(op); row.appendChild(vecLabel("2"));
  const eq = document.createElement("span"); eq.textContent = "="; eq.style.margin = "0 8px"; row.appendChild(eq);
  inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-vs-input ts-dm2-input"; inp.autocomplete = "off"; inp.placeholder = "e.g. 30cos(45°)";
  inp.addEventListener("input", () => { if (locked) return; val = inp.value.trim() || null; updatePreview(); if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && parseVal(val) != null && cbs && cbs.onComplete) cbs.onComplete(); });
  row.appendChild(inp);
  stage.appendChild(row);
  const prow = document.createElement("div"); prow.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:16px;margin-top:14px;color:#6b7280;gap:8px";
  const ap = document.createElement("span"); ap.textContent = "≈"; prow.appendChild(ap); prev = document.createElement("span"); prev.textContent = "?"; prow.appendChild(prev);
  stage.appendChild(prow);
  mountEl.appendChild(stage);
  function updatePreview() { const v = val == null ? null : parseVal(val); prev.textContent = v == null ? "?" : (Math.round(v * 100) / 100).toString().replace(/-/g, "−"); }
  const tol = spec.tol != null ? Number(spec.tol) : 0.01;
  function correct() { if (val == null) return false; const p = parseVal(val); if (p == null) return false; return Math.abs(p - trueVal) < tol; }
  return {
    isComplete: () => val != null && parseVal(val) != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (x) => { val = Array.isArray(x) ? (x[0] == null ? null : String(x[0])) : (x == null ? null : String(x)); if (inp) inp.value = val || ""; updatePreview(); },
    getState: () => ({ value: val, target: spec.answer != null ? String(spec.answer) : String(Math.round(trueVal * 100) / 100) }),
    reset: () => { val = null; if (inp) inp.value = ""; updatePreview(); },
    reveal: (forced) => { locked = true; val = forced != null ? String(forced) : (spec.answer != null ? String(spec.answer) : String(trueVal)); if (inp) { inp.value = val; inp.readOnly = true; } updatePreview(); }
  };
}

// ── Angle from Dot Product (L8.4): given two vectors on a grid, find the angle
// between them θ = cos⁻¹( (u·v)/(|u||v|) ). The cos⁻¹(…) is pre-printed; fill the
// box with the cosine RATIO (fraction/decimal/surd). Graded: parsed ratio ==
// (u·v)/(|u||v|); a live 'θ ≈ …°' preview shows below.
function buildAngleDot(mountEl, spec, cbs) {
  let locked = false, val = null, inp = null, prev = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp ts-ad";
  const COL = { orange: "#f5a04a", teal: "#279b8d" };
  function parseVal(s) {
    if (s == null) return null;
    let t = ("" + s).trim().replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s/g, "");
    if (!t) return null;
    t = t.replace(/(?:√|sqrt)\(?(\d+(?:\.\d+)?)\)?/gi, "SQRT($1)");
    t = t.replace(/(\d)(SQRT|\()/g, "$1*$2").replace(/(\))(\d|SQRT|\()/g, "$1*$2");
    if (!/^[-0-9.+*/()SQRT]*$/.test(t)) return null;
    try { const v = new Function("SQRT", "return (" + t + ");")(Math.sqrt); return Number.isFinite(v) ? v : null; } catch (e) { return null; }
  }
  const v = (spec.v || []).map(Number), w = (spec.w || []).map(Number);
  const dot = v[0] * w[0] + v[1] * w[1];
  const trueCos = dot / (Math.hypot(v[0], v[1]) * Math.hypot(w[0], w[1]));
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -3, xmax: 3, ymin: -3, ymax: 3, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    // θ arc between v and w
    const a1 = Math.atan2(v[1], v[0]), a2 = Math.atan2(w[1], w[0]), r = 26;
    let dArc = a2 - a1; while (dArc > Math.PI) dArc -= 2 * Math.PI; while (dArc < -Math.PI) dArc += 2 * Math.PI;
    const ax0 = g.X(0) + r * Math.cos(a1), ay0 = g.Y(0) - r * Math.sin(a1);
    const ax1 = g.X(0) + r * Math.cos(a2), ay1 = g.Y(0) - r * Math.sin(a2);
    const large = Math.abs(dArc) > Math.PI ? 1 : 0, sweep = dArc < 0 ? 1 : 0;
    g.mkIn("path", { d: "M " + ax0.toFixed(1) + " " + ay0.toFixed(1) + " A " + r + " " + r + " 0 " + large + " " + sweep + " " + ax1.toFixed(1) + " " + ay1.toFixed(1), fill: "rgba(150,156,166,0.16)", stroke: "#9aa0aa", "stroke-width": 1.4 });
    const lm = a1 + dArc / 2; const tl = g.mkIn("text", { x: g.X(0) + (r + 15) * Math.cos(lm), y: g.Y(0) - (r + 15) * Math.sin(lm) + 4, fill: "#20242a", "font-size": 13, "font-style": "italic" }); tl.textContent = "θ";
    [[v, spec.vLabel || "v", "orange"], [w, spec.wLabel || "w", "teal"]].forEach(([vec, label, color]) => {
      const col = COL[color] || COL.orange;
      const line = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
      const head = g.mkIn("path", { fill: col });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(vec[0]), y2 = g.Y(vec[1]);
      line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
      const tx = g.X(vec[0] * 0.62) + (vec[0] >= 0 ? 8 : -14), ty = g.Y(vec[1] * 0.62) + (vec[1] >= 0 ? -8 : 16); const t = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 13, "font-weight": 700, "font-style": "italic" }); t.textContent = label;
    });
    stage.appendChild(g.el);
  }
  // equation θ = cos⁻¹( [input] )
  const row = document.createElement("div"); row.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:20px;margin-top:6px";
  const th = document.createElement("span"); th.textContent = "θ"; th.style.fontStyle = "italic"; row.appendChild(th);
  const eq = document.createElement("span"); eq.textContent = "="; eq.style.margin = "0 8px"; row.appendChild(eq);
  const fn = document.createElement("span"); fn.innerHTML = "cos<sup style='font-size:0.62em'>−1</sup>"; row.appendChild(fn);
  const lp = document.createElement("span"); lp.textContent = "("; lp.style.cssText = "font-size:1.4em;font-weight:300;margin:0 2px"; row.appendChild(lp);
  inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-vs-input ts-ad-input"; inp.autocomplete = "off";
  inp.addEventListener("input", () => { if (locked) return; val = inp.value.trim() || null; updatePreview(); if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && parseVal(val) != null && cbs && cbs.onComplete) cbs.onComplete(); });
  row.appendChild(inp);
  const rp = document.createElement("span"); rp.textContent = ")"; rp.style.cssText = "font-size:1.4em;font-weight:300;margin:0 2px"; row.appendChild(rp);
  stage.appendChild(row);
  const prow = document.createElement("div"); prow.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:16px;margin-top:14px;color:#6b7280;gap:6px";
  const tp = document.createElement("span"); tp.textContent = "θ ≈"; prow.appendChild(tp); prev = document.createElement("span"); prev.textContent = "?"; prow.appendChild(prev);
  stage.appendChild(prow); mountEl.appendChild(stage);
  function updatePreview() { const c = val == null ? null : parseVal(val); if (c == null || c < -1.0001 || c > 1.0001) { prev.textContent = "?"; return; } const deg = Math.acos(Math.max(-1, Math.min(1, c))) * 180 / Math.PI; prev.textContent = (Math.round(deg)).toString() + "°"; }
  const tol = spec.tol != null ? Number(spec.tol) : 0.002;
  function correct() { if (val == null) return false; const c = parseVal(val); if (c == null) return false; return Math.abs(c - trueCos) < tol; }
  return {
    isComplete: () => val != null && parseVal(val) != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (x) => { val = Array.isArray(x) ? (x[0] == null ? null : String(x[0])) : (x == null ? null : String(x)); if (inp) inp.value = val || ""; updatePreview(); },
    getState: () => ({ value: val, target: spec.answer != null ? String(spec.answer) : String(Math.round(trueCos * 1000) / 1000) }),
    reset: () => { val = null; if (inp) inp.value = ""; updatePreview(); },
    reveal: (forced) => { locked = true; val = forced != null ? String(forced) : (spec.answer != null ? String(spec.answer) : String(trueCos)); if (inp) { inp.value = val; inp.readOnly = true; } updatePreview(); }
  };
}

// ── Self Dot Products (L8.5): v·v = |v|² = vₓ²+v_y². Show the vector on a grid
// (components NOT printed — the learner reads them off) and type v·v. Graded from
// the DRAWN vector: vₓ²+v_y². (Component-bracket self dots reuse dotProduct u=v.)
function buildSelfDot(mountEl, spec, cbs) {
  let locked = false, val = null, inp = null;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp ts-sd";
  function parseNum(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); if (!t || t === "-") return null; const n = Number(t); return Number.isFinite(n) ? n : null; }
  const v = (spec.v || []).map(Number);
  const trueVal = v[0] * v[0] + v[1] * v[1];
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -1, xmax: 6, ymin: -1, ymax: 6, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    const col = "#f5a04a";
    const line = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(v[0]), y2 = g.Y(v[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    const tx = g.X(v[0] * 0.55) + (v[0] >= 0 ? 8 : -14), ty = g.Y(v[1] * 0.55) + (v[1] >= 0 ? -8 : 16); const t = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 13, "font-weight": 700, "font-style": "italic" }); t.textContent = spec.label || "v";
    stage.appendChild(g.el);
  }
  function vlabel() { const s = document.createElement("span"); s.style.cssText = "position:relative;font-style:italic;font-weight:600;padding:0 1px"; s.textContent = spec.label || "v"; const ar = document.createElement("span"); ar.textContent = "→"; ar.style.cssText = "position:absolute;left:-1px;right:-1px;top:-0.72em;font-size:0.6em;text-align:center;font-style:normal;font-weight:400;line-height:1"; s.appendChild(ar); return s; }
  const row = document.createElement("div"); row.style.cssText = "display:flex;align-items:center;justify-content:center;font-size:20px;margin-top:8px";
  row.appendChild(vlabel()); const op = document.createElement("span"); op.textContent = "·"; op.style.margin = "0 7px"; row.appendChild(op); row.appendChild(vlabel());
  const eq = document.createElement("span"); eq.textContent = "="; eq.style.margin = "0 8px"; row.appendChild(eq);
  inp = document.createElement("input"); inp.type = "text"; inp.inputMode = "numeric"; inp.className = "ts-sf-input ts-vs-input ts-sd-input"; inp.autocomplete = "off";
  inp.addEventListener("input", () => { if (locked) return; val = inp.value.trim() || null; if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && parseNum(val) != null && cbs && cbs.onComplete) cbs.onComplete(); });
  row.appendChild(inp);
  stage.appendChild(row); mountEl.appendChild(stage);
  const tol = spec.tol != null ? Number(spec.tol) : 0.001;
  function correct() { if (val == null) return false; const n = parseNum(val); if (n == null) return false; return Math.abs(n - trueVal) < tol; }
  return {
    isComplete: () => val != null && parseNum(val) != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (x) => { val = Array.isArray(x) ? (x[0] == null ? null : String(x[0])) : (x == null ? null : String(x)); if (inp) inp.value = val || ""; },
    getState: () => ({ value: val, target: spec.answer != null ? String(spec.answer) : String(trueVal) }),
    reset: () => { val = null; if (inp) inp.value = ""; },
    reveal: (forced) => { locked = true; val = forced != null ? String(forced) : (spec.answer != null ? String(spec.answer) : String(trueVal)); if (inp) { inp.value = val; inp.readOnly = true; } }
  };
}

// ── Orthogonal Dot Products (L8.6): fill the dot-product EXPANSION
// [u]·[v] = uₓ·[□] + u_y·[□] = [result] by dropping v's components into the slots;
// the result auto-computes (0 ⟺ perpendicular). Graph shows the two vectors.
// Graded: slots == v's components (so the expansion is set up correctly).
function buildOrthoDot(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-dp ts-od";
  const u = (spec.u || []).map(Number), v = (spec.v || []).map(Number);
  const COL = { orange: "#f5a04a", teal: "#279b8d" };
  const fmtN = (n) => String(n).replace(/-(\d)/g, "−$1");
  const parseNum = (s) => { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); if (!t || t === "-") return null; const n = Number(t); return Number.isFinite(n) ? n : null; };
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -1, xmax: 5, ymin: -1, ymax: 5, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    [[u, "orange"], [v, "teal"]].forEach(([vec, color]) => {
      const col = COL[color];
      const line = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
      const head = g.mkIn("path", { fill: col });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(vec[0]), y2 = g.Y(vec[1]);
      line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    });
    stage.appendChild(g.el);
  }
  // ---- equation row: [u]·[v] = ux·[slot] + uy·[slot] = [result] ----
  function brk(vals) { const w = document.createElement("span"); w.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; w.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; vals.forEach((n) => { const d = document.createElement("div"); d.className = "ts-vt-static"; d.textContent = fmtN(n); col.appendChild(d); }); w.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; w.appendChild(r); return w; }
  const op = (s) => { const e = document.createElement("span"); e.className = "ts-vt-op"; e.textContent = s; return e; };
  const num = (n) => { const e = document.createElement("span"); e.style.cssText = "font-size:19px;font-weight:600"; e.textContent = fmtN(n); return e; };
  const slots = [];
  function makeSlot() { const s = document.createElement("span"); s.className = "ts-pc-slot is-empty"; const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s; }
  const row = document.createElement("div"); row.className = "ts-vs-row ts-od-row"; row.style.cssText = "display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:2px";
  row.appendChild(brk(u)); row.appendChild(op("·")); row.appendChild(brk(v)); row.appendChild(op("="));
  row.appendChild(num(u[0])); row.appendChild(op("·")); row.appendChild(makeSlot()); row.appendChild(op("+")); row.appendChild(num(u[1])); row.appendChild(op("·")); row.appendChild(makeSlot()); row.appendChild(op("="));
  const resBox = document.createElement("span"); resBox.className = "ts-pc-slot ts-od-res"; resBox.style.cssText = "min-width:2.2em;text-align:center;color:#4f6ef7;border:none;background:none;font-weight:600"; resBox.textContent = "?"; row.appendChild(resBox);
  stage.appendChild(row);
  const pal = document.createElement("div"); pal.className = "ts-pc-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() {
    slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmtN(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } });
    if (slots.every((s) => s.val != null)) { const r = u[0] * parseNum(slots[0].val) + u[1] * parseNum(slots[1].val); resBox.textContent = fmtN(r); } else resBox.textContent = "?";
  }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || []).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-pc-tile"; t.textContent = fmtN(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false; const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 14) + "px"; }; move(ev);
      const up = (e) => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove(); if (!dragged) return; for (let i = 0; i < slots.length; i++) { const rr = slots[i].el.getBoundingClientRect(); if (e.clientX >= rr.left - 8 && e.clientX <= rr.right + 8 && e.clientY >= rr.top - 8 && e.clientY <= rr.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } } };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s, i) => s.val != null && parseNum(s.val) === v[i]); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: v.map(String) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || v.map(String); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Vectors from Combinations (L9.1): write p as a·v + b·w by filling the two
// coefficient slots from tiles (a·v + b·w = p). Live grid illustration: v (teal),
// w (purple), p (orange) from origin; once both slots are filled a green path draws
// a·v then b·w tip-to-tail (landing on p when correct). Graded: slots == answer coeffs.
function buildCombFill(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-cf";
  const v = (spec.v || []).map(Number), w = (spec.w || []).map(Number), p = (spec.p || []).map(Number);
  const answer = spec.answer || [];
  const COL = { teal: "#279b8d", purple: "#8a5cf6", orange: "#f5a04a", green: "#3aa76d" };
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  // display a value: keep p/q fractions as-is (unicode minus), else format the number
  const disp = (v) => { const s = ("" + v).trim().replace(/−/g, "-"); return /^-?\d+\/\d+$/.test(s) ? s.replace(/-/g, "−") : fmt(parseTile(v)); };
  let g = null; let combEls = [];
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.4, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "5 3" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    return [line, head];
  }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -3, xmax: 5, ymin: -4, ymax: 5, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    [[v, "teal", spec.vLabel || "v"], [w, "purple", spec.wLabel || "w"], [p, "orange", spec.pLabel || "p"]].forEach(([vec, color, lab]) => {
      arrow([0, 0], vec, COL[color]);
      const tx = g.X(vec[0] * 0.55) + (vec[0] >= 0 ? 7 : -13), ty = g.Y(vec[1] * 0.55) + (vec[1] >= 0 ? -6 : 15);
      const t = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 12.5, "font-weight": 700, "font-style": "italic" }); t.textContent = lab;
    });
    stage.appendChild(g.el);
  }
  function updateComb() {
    combEls.forEach((e) => e.remove()); combEls = [];
    if (!g || !slots.every((s) => s.val != null)) return;
    const a = parseTile(slots[0].val), b = parseTile(slots[1].val); if (a == null || b == null) return;
    const av = [a * v[0], a * v[1]], sum = [av[0] + b * w[0], av[1] + b * w[1]];
    combEls.push(...arrow([0, 0], av, COL.green, 2.6, true), ...arrow(av, sum, COL.green, 2.6, true));
  }
  // ---- equation: [slot]·[v] + [slot]·[w] = [p] ----
  const typed = !spec.tiles;  // no tile list → typed keypad slots (standard-basis, large coeffs)
  const brk = document.createElement("div"); brk.className = "ts-vt-brk";
  const slots = [];
  function makeSlot() {
    const idx = slots.length;
    if (typed) {
      const s = document.createElement("input"); s.type = "text"; s.inputMode = "numeric"; s.className = "ts-sf-input ts-vf-input ts-vf-scalar ts-cf-input"; s.autocomplete = "off";
      s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); updateComb(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); });
      slots.push({ el: s, val: null, input: true }); return s;
    }
    const s = document.createElement("div"); s.className = "ts-vt-slot ts-vt-coeffslot is-empty"; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s;
  }
  function bracketOf(vec) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; vec.forEach((n) => { const c = document.createElement("div"); c.className = "ts-vt-static"; c.textContent = fmt(Number(n)); col.appendChild(c); }); wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  brk.appendChild(makeSlot()); brk.appendChild(bracketOf(v)); brk.appendChild(opSpan("+")); brk.appendChild(makeSlot()); brk.appendChild(bracketOf(w)); brk.appendChild(opSpan("=")); brk.appendChild(bracketOf(p));
  stage.appendChild(brk);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.input) { s.el.value = s.val != null ? s.val : ""; s.el.classList.toggle("is-filled", s.val != null); return; } if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = disp(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); updateComb(); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || []).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = disp(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false; const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 18) + "px"; }; move(ev);
      const up = (e) => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove(); if (!dragged) return; for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } } };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  if (!typed) stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s, i) => { if (s.val == null) return false; const c = parseTile(s.val); return c != null && c === parseTile(String(answer[i])); }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answer }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || answer.map(String); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Possible Combinations (L9.2): when v and w are parallel, a vector p on their
// line is a single multiple of EACH: p = a·v = b·w. Chained-equality layout
// '[p] = [slot]·[v] = [slot]·[w]'; fill each scalar from a tile palette (fractions ok).
// Graded: each slot's scalar times its vector equals p (componentwise).
function buildScalarChain(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-sc2";
  const p = (spec.p || []).map(Number);
  const terms = spec.terms || [];
  const COL = { teal: "#279b8d", purple: "#8a5cf6", orange: "#f5a04a" };
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  const fmtStr = (s) => String(s).replace(/-/g, "−");
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  if (spec.graph) {
    const gspec = Object.assign({ xmin: -3, xmax: 5, ymin: -4, ymax: 5, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    function arrow(to, col) { const line = g.mkIn("line", { stroke: col, "stroke-width": 2.4, "stroke-linecap": "round" }); const head = g.mkIn("path", { fill: col }); const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(to[0]), y2 = g.Y(to[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z"); }
    [[terms[0] && terms[0].vec, COL.teal, "v"], [terms[1] && terms[1].vec, COL.purple, "w"], [p, COL.orange, "p"]].forEach(([vec, col, lab]) => { if (!vec) return; arrow(vec, col); const tx = g.X(vec[0] * 0.6) + (vec[0] >= 0 ? 7 : -13), ty = g.Y(vec[1] * 0.6) + (vec[1] >= 0 ? -6 : 15); const t = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 12.5, "font-weight": 700, "font-style": "italic" }); t.textContent = lab; });
    stage.appendChild(g.el);
  }
  const brk = document.createElement("div"); brk.className = "ts-vt-brk";
  const slots = [];
  function makeSlot() { const s = document.createElement("div"); s.className = "ts-vt-slot ts-vt-coeffslot is-empty"; const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s; }
  function bracketOf(vec) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; vec.forEach((n) => { const c = document.createElement("div"); c.className = "ts-vt-static"; c.textContent = fmt(Number(n)); col.appendChild(c); }); wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  brk.appendChild(bracketOf(p));
  terms.forEach((tm) => { brk.appendChild(opSpan("=")); brk.appendChild(makeSlot()); brk.appendChild(bracketOf(tm.vec)); });
  stage.appendChild(brk);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmtStr(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || []).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmtStr(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    t.addEventListener("pointerdown", (ev) => {
      if (locked) return; ev.preventDefault();
      const clone = t.cloneNode(true); clone.style.cssText = "position:fixed;pointer-events:none;z-index:9999;opacity:0.85;margin:0"; document.body.appendChild(clone);
      let dragged = false; const move = (e) => { dragged = true; clone.style.left = (e.clientX - 18) + "px"; clone.style.top = (e.clientY - 18) + "px"; }; move(ev);
      const up = (e) => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); clone.remove(); if (!dragged) return; for (let i = 0; i < slots.length; i++) { const r = slots[i].el.getBoundingClientRect(); if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); break; } } };
      document.addEventListener("pointermove", move); document.addEventListener("pointerup", up);
    });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s, i) => { if (s.val == null) return false; const c = parseTile(s.val); if (c == null) return false; return Math.abs(c * terms[i].vec[0] - p[0]) < 1e-9 && Math.abs(c * terms[i].vec[1] - p[1]) < 1e-9; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: terms.map((t) => t.answer) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || terms.map((t) => String(t.answer)); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Possible Combinations (L9.2): COMPUTE a linear combination and FILL the result
// column vector from a numeric tile bank. Each term carries a FIXED scalar (shown as
// static text; scalar:null renders no coefficient = implicit 1), e.g. "3[1;2]+[−1;−2]".
// The two result entries are the blanks. The grid draws v (orange) and w (teal); once
// both blanks are filled the blue resultant is drawn from the origin. Graded: the
// filled column equals Σ scalarᵢ·vecᵢ (componentwise). spec {terms:[{scalar,vec}],
// answer:[x,y], tiles:[…], graph, vLabel, wLabel}.
function buildCombResult(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-cr";
  const terms = (spec.terms || []).map((t) => ({ scalar: t.scalar == null ? null : String(t.scalar), vec: (t.vec || []).map(Number) }));
  const answer = (spec.answer || []).map(Number);
  const COL = Object.assign({ result: "#4f6ef7" }, spec.colors);
  const termCols = spec.termColors || ["#f5a04a", "#279b8d", "#8a5cf6"];
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseScal(s) { if (s == null) return 1; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : NaN; }
  let g = null, resEls = [];
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.4, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "5 3" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    return [line, head];
  }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -6, xmax: 6, ymin: -6, ymax: 6, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    terms.forEach((t, i) => { arrow([0, 0], t.vec, termCols[i % termCols.length]); const lab = i === 0 ? (spec.vLabel || "v") : i === 1 ? (spec.wLabel || "w") : "u"; const tx = g.X(t.vec[0] * 0.55) + (t.vec[0] >= 0 ? 7 : -13), ty = g.Y(t.vec[1] * 0.55) + (t.vec[1] >= 0 ? -6 : 15); const tn = g.mkIn("text", { x: tx, y: ty, fill: "#20242a", "font-size": 12.5, "font-weight": 700, "font-style": "italic" }); tn.textContent = lab; });
    stage.appendChild(g.el);
  }
  function updateResult() { resEls.forEach((e) => e.remove()); resEls = []; if (!g || !slots.every((s) => s.val != null)) return; const rv = slots.map((s) => parseScal(s.val)); if (rv.some((x) => !Number.isFinite(x))) return; resEls.push(...arrow([0, 0], rv, COL.result, 3)); }
  const brk = document.createElement("div"); brk.className = "ts-vt-brk";
  const slots = [];
  function scalarSpan(txt) { const s = document.createElement("span"); s.className = "ts-vt-op ts-cr-scal"; s.textContent = txt; return s; }
  function bracketOf(vec) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; vec.forEach((n) => { const c = document.createElement("div"); c.className = "ts-vt-static"; c.textContent = fmt(Number(n)); col.appendChild(c); }); wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function bracketOfSlots(n) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; for (let i = 0; i < n; i++) { const idx = slots.length; const sl = document.createElement("div"); sl.className = "ts-vt-slot ts-vt-coeffslot is-empty"; sl.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: sl, val: null }); col.appendChild(sl); } wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  terms.forEach((t, i) => { if (i > 0) brk.appendChild(opSpan("+")); if (t.scalar != null) brk.appendChild(scalarSpan(t.scalar.replace(/-/g, "−"))); brk.appendChild(bracketOf(t.vec)); });
  brk.appendChild(opSpan("=")); brk.appendChild(bracketOfSlots(answer.length));
  stage.appendChild(brk);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseScal(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); updateResult(); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || []).forEach((tv) => { const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(Number(tv)); t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } }); pal.appendChild(t); });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s, i) => { if (s.val == null) return false; return Math.abs(parseScal(s.val) - answer[i]) < 1e-9; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answer }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || answer.map(String); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Symbolic / statement tile-fill (L9.2): fill blanks in a MATH template or a
// SENTENCE with STRING tiles, graded by exact tile-KEY match (not numeric). Reproduces
// Brilliant's "a·v + b·w = (□ + □)·v", the is/is-not statement, and the symbolic
// re-expression "p = □·v = □·w". Rows are token streams; a token is {t:"txt"} |
// {vec:"v"} (letter + overhead arrow) | {frac:[num,den]} | {slot:i} (blank). Blanks are
// filled left-to-right from a tile palette; each tile is {key, toks}. Graded slot i ==
// answer[i] by key. spec {rows:[[token…]…], tiles:[{key,toks}], answer:[key…]}.
function buildSymFill(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-sf2";
  const answer = (spec.answer || []).map(String);
  const tiles = spec.tiles || [];
  const slots = [];
  function tokNode(tk) {
    if (typeof tk === "string") tk = { t: tk };
    if (tk.vec != null) { const s = document.createElement("span"); s.className = "ts-sf2-vec"; s.textContent = tk.vec; return s; }
    if (tk.frac != null) { const s = document.createElement("span"); s.className = "ts-sf2-frac"; const nu = document.createElement("span"); nu.className = "ts-sf2-num"; renderInline(nu, tk.frac[0]); const de = document.createElement("span"); de.className = "ts-sf2-den"; renderInline(de, tk.frac[1]); s.appendChild(nu); s.appendChild(de); return s; }
    if (tk.col != null) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk ts-sf2-col"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("span"); col.className = "ts-vt-col"; tk.col.forEach((e) => { const c = document.createElement("span"); c.className = "ts-vt-static"; c.textContent = ("" + e).replace(/-/g, "−"); col.appendChild(c); }); wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
    const s = document.createElement("span"); s.className = "ts-sf2-txt"; s.textContent = tk.t != null ? tk.t : ""; return s;
  }
  function renderInline(container, val) { if (Array.isArray(val)) val.forEach((x) => container.appendChild(tokNode(x))); else container.appendChild(tokNode(typeof val === "string" ? { t: val } : val)); }
  function renderToks(container, toks) {
    toks.forEach((tk) => {
      if (tk && tk.slot != null) { const idx = slots.length; const sl = document.createElement("span"); sl.className = "ts-sf2-slot is-empty"; sl.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: sl, val: null }); container.appendChild(sl); return; }
      container.appendChild(tokNode(tk));
    });
  }
  const board = document.createElement("div"); board.className = "ts-sf2-board";
  (spec.rows || []).forEach((row) => { const r = document.createElement("div"); r.className = "ts-sf2-row"; renderToks(r, row); board.appendChild(r); });
  stage.appendChild(board);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal ts-sf2-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { s.el.innerHTML = ""; if (s.val == null) { s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); const tile = tiles.find((t) => String(t.key) === String(s.val)); renderInline(s.el, tile ? tile.toks : [{ t: String(s.val) }]); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  tiles.forEach((tile) => { const t = document.createElement("div"); t.className = "ts-vt-tile ts-sf2-tile"; renderInline(t, tile.toks); t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tile.key); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } }); pal.appendChild(t); });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s, i) => s.val != null && String(s.val) === answer[i]); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answer }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = (forced || answer).map(String); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Transforming Vectors (L10.1): apply a SEQUENCE of named transformations
// (rotate / dilate / reflect / stretch) to a start vector until it lands on a
// dashed target. Each row is an operation + parameter(s) chosen from a CASCADING
// tile palette (op names → that op's parameter tiles). The live vector redraws as
// rows fill. Graded GEOMETRICALLY — compose the chosen ops on the start and require
// the result == target — so ANY valid sequence works (exactly like Brilliant, where
// 90°+90° and 180° both pass). Rows are FIXED-op ({op:"rotate"} → fill the parameter)
// or CHOICE ({ops:[...]} → pick the op too). spec {start:[x,y], target:[x,y],
// rows:[{op}|{ops:[...]}], answer:[{op,params:[...]}], graph}.
function buildVecTransform(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-xf";
  const start = (spec.start || []).map(Number);
  const target = (spec.target || []).map(Number);
  const COL = { start: "#a9b6ce", live: "#4f6ef7", green: "#3aa76d", target: "#aeb4be" };
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  const DEG = { "90°": 90, "180°": 180, "270°": 270 };
  function parseFrac(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-"); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  function rot(v, deg) { const d = ((deg % 360) + 360) % 360; if (d === 0) return [v[0], v[1]]; if (d === 90) return [-v[1], v[0]]; if (d === 180) return [-v[0], -v[1]]; if (d === 270) return [v[1], -v[0]]; const r = d * Math.PI / 180, c = Math.cos(r), s = Math.sin(r); return [c * v[0] - s * v[1], s * v[0] + c * v[1]]; }
  function refl(v, ax) { if (ax === "x-axis") return [v[0], -v[1]]; if (ax === "y-axis") return [-v[0], v[1]]; if (ax === "y=x") return [v[1], v[0]]; if (ax === "y=−x" || ax === "y=-x") return [-v[1], -v[0]]; return [v[0], v[1]]; }
  const OPS = {
    rotate:  { verb: "rotate",  params: [{ conn: "by",   tiles: ["90°", "180°", "270°"] }], apply: (v, p) => (DEG[p[0]] != null ? rot(v, DEG[p[0]]) : null) },
    dilate:  { verb: "dilate",  params: [{ conn: "by",   tiles: ["0", "1/3", "1/2", "1", "2", "3"] }], apply: (v, p) => { const k = parseFrac(p[0]); return k == null ? null : [k * v[0], k * v[1]]; } },
    reflect: { verb: "reflect", params: [{ conn: "over", tiles: ["x-axis", "y-axis", "y=x", "y=−x"] }], apply: (v, p) => (p[0] == null ? null : refl(v, p[0])) },
    stretch: { verb: "stretch", params: [{ conn: "in", tiles: ["x", "y"] }, { conn: "by", tiles: ["0", "1/3", "1/2", "1", "2", "3"] }], apply: (v, p) => { const f = parseFrac(p[1]); if (f == null || (p[0] !== "x" && p[0] !== "y")) return null; return p[0] === "x" ? [f * v[0], v[1]] : [v[0], f * v[1]]; } },
  };
  // row state: { ops:[...]|null, op:string|null, pvals:[...] }
  const rows = (spec.rows || []).map((r) => ({ ops: r.ops || null, op: r.op || null, pvals: [] }));

  // ---- graph: faded start (light blue), dashed target (grey), live vector ----
  let g = null, liveEls = [];
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "6 4" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    return [line, head];
  }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -5, xmax: 5, ymin: -5, ymax: 5, w: 320, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 320) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    if (target.length) arrow([0, 0], target, COL.target, 2.4, true);   // dashed target
    if (start.length) arrow([0, 0], start, COL.start, 2.6);            // faded start
    stage.appendChild(g.el);
  }
  // compose the ops of every FULLY-filled row onto start; returns {vec, done}
  function compose() {
    let v = start.slice(), allDone = true;
    for (const r of rows) {
      if (!r.op) { allDone = false; break; }
      const def = OPS[r.op]; if (!def) { allDone = false; break; }
      if (r.pvals.length < def.params.length || r.pvals.some((x) => x == null)) { allDone = false; break; }
      const nv = def.apply(v, r.pvals); if (nv == null) { allDone = false; break; }
      v = nv;
    }
    return { vec: v, done: allDone };
  }
  function drawLive() {
    liveEls.forEach((e) => e.remove()); liveEls = [];
    if (!g) return;
    const { vec, done } = compose();
    const same = vec[0] === start[0] && vec[1] === start[1];
    if (same) return;                       // nothing applied yet
    const hit = done && Math.hypot(vec[0] - target[0], vec[1] - target[1]) < 1e-6;
    liveEls.push(...arrow([0, 0], vec, hit ? COL.green : COL.live, 3));
  }

  // ---- equation rows + cascading palette ----
  const eq = document.createElement("div"); eq.className = "ts-xf-rows";
  const pal = document.createElement("div"); pal.className = "ts-vt-pal ts-xf-pal";
  // the ordered list of fillable fields across all rows; rebuilt each render
  let fields = [];
  function activeIdx() { for (let i = 0; i < fields.length; i++) if (fields[i].val == null) return i; return -1; }
  function opField(r) { return { kind: "op", row: r, get val() { return r.op; }, set val(x) { r.op = x; } }; }

  function renderRows() {
    eq.innerHTML = ""; fields = [];
    rows.forEach((r, ri) => {
      const line = document.createElement("div"); line.className = "ts-xf-row";
      const num = document.createElement("span"); num.className = "ts-xf-num"; num.textContent = (ri + 1); line.appendChild(num);
      if (r.ops && !r.op) {
        // choice row, op not yet picked → single op-slot
        const f = opField(r);
        const slot = document.createElement("div"); slot.className = "ts-vt-slot ts-xf-opslot is-empty";
        slot.textContent = "";
        f.el = slot; f.isOp = true; fields.push(f); line.appendChild(slot);
      } else {
        const def = OPS[r.op];
        const verb = document.createElement("span"); verb.className = "ts-vt-op ts-xf-verb"; verb.textContent = def.verb;
        if (r.ops) verb.classList.add("ts-xf-chosen"); // clickable to reset a chosen op
        if (r.ops) verb.addEventListener("click", () => { if (locked) return; r.op = null; r.pvals = []; renderAll(); });
        line.appendChild(verb);
        def.params.forEach((pd, pi) => {
          const conn = document.createElement("span"); conn.className = "ts-vt-op ts-xf-conn"; conn.textContent = pd.conn; line.appendChild(conn);
          const slot = document.createElement("div"); slot.className = "ts-vt-slot ts-xf-pslot is-empty";
          const val = r.pvals[pi] != null ? r.pvals[pi] : null;
          const f = { kind: "param", row: r, pi: pi, get val() { return r.pvals[this.pi]; }, set val(x) { r.pvals[this.pi] = x; }, el: slot, tiles: pd.tiles };
          if (val != null) { slot.textContent = fmtTile(val); slot.classList.remove("is-empty"); slot.classList.add("is-filled"); }
          slot.addEventListener("click", () => { if (locked) return; if (r.pvals[pi] != null) { for (let k = pi; k < def.params.length; k++) r.pvals[k] = null; renderAll(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
          fields.push(f); line.appendChild(slot);
        });
      }
      eq.appendChild(line);
    });
  }
  function fmtTile(v) { return String(v); }
  function renderPalette() {
    pal.innerHTML = "";
    const ai = activeIdx();
    let tiles = [];
    if (ai >= 0) {
      const f = fields[ai];
      tiles = f.isOp ? f.row.ops.slice() : f.tiles.slice();
    }
    tiles.forEach((tv) => {
      const t = document.createElement("div"); t.className = "ts-vt-tile ts-xf-tile"; t.textContent = String(tv);
      t.addEventListener("click", () => {
        if (locked) return; const i = activeIdx(); if (i < 0) return;
        const f = fields[i];
        if (f.isOp) { f.row.op = String(tv); f.row.pvals = []; } else { f.row.pvals[f.pi] = String(tv); }
        renderAll(); if (cbs && cbs.onInteract) cbs.onInteract(); done();
      });
      pal.appendChild(t);
    });
  }
  function complete() { return rows.every((r) => { if (!r.op) return false; const def = OPS[r.op]; if (!def) return false; return def.params.every((_, i) => r.pvals[i] != null); }); }
  function done() { if (complete() && cbs && cbs.onComplete) cbs.onComplete(); }
  function renderAll() { renderRows(); renderPalette(); drawLive(); }

  stage.appendChild(eq); stage.appendChild(pal); mountEl.appendChild(stage); renderAll();

  function correct() { if (!complete()) return false; const { vec, done: d } = compose(); return d && Math.hypot(vec[0] - target[0], vec[1] - target[1]) < 1e-6; }
  const val = () => rows.map((r) => ({ op: r.op, params: r.pvals.slice() }));
  function setRows(arr) { if (!Array.isArray(arr)) return; rows.forEach((r, i) => { const a = arr[i]; if (!a) { if (r.ops) r.op = null; r.pvals = []; return; } if (r.ops) r.op = a.op != null ? a.op : r.op; r.pvals = Array.isArray(a.params) ? a.params.map((x) => (x == null ? null : String(x))) : []; }); renderAll(); }
  return {
    isComplete: () => complete(),
    check: () => ({ correct: correct(), value: val() }),
    getValue: val,
    setValue: setRows,
    getState: () => ({ value: val(), target: spec.answer || null }),
    reset: () => { rows.forEach((r) => { if (r.ops) r.op = null; r.pvals = []; }); renderAll(); },
    reveal: (forced) => { locked = true; setRows(forced || spec.answer || []); fields.forEach((f) => f.el && f.el.classList.add("is-answer")); }
  };
}

// ── Transforming Basis Vectors (L10.2): a named transformation (dilate / rotate /
// reflect / stretch) is applied to the standard basis; the student TYPES where each
// basis vector lands. The images of e₁=[1,0] and e₂=[0,1] are exactly the COLUMNS of
// the transformation's matrix — this is the bridge from transformations to matrices.
// Grid shows e₁ (orange) and e₂ (teal) faded for reference; the typed image vectors
// draw live as you fill them. Graded: typed image == spec.images[i] componentwise.
// spec { images:[[a,c],[b,d]], prompt?, graph }.
function buildBasisMap(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-bm";
  const e1 = [1, 0], e2 = [0, 1];
  const images = (spec.images || []).map((v) => v.slice());   // raw strings; parsed via parseVal (surd-aware)
  const COL = { orange: "#f5a04a", teal: "#279b8d", orangeF: "#f7d0a4", tealF: "#a9d6cf" };
  function parseFrac(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-"); if (t === "" || t === "-") return null; const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  let g = null, imgEls = [];
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "6 4" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    return [line, head];
  }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -1, xmax: 4, ymin: -1, ymax: 4, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    arrow([0, 0], e1, COL.orangeF, 2.4);   // faded reference basis
    arrow([0, 0], e2, COL.tealF, 2.4);
    if (spec.showImages) {
      // read-off mode (L10.3): draw the transformed grid + the two image vectors as
      // GIVEN, so the learner reads their components off the figure and fills tiles.
      const im1 = images[0].map(parseVal), im2 = images[1].map(parseVal), R = 4, latCol = "#e6dfce";
      for (let k = -R; k <= R; k++) {
        const A = [k * im1[0] - R * im2[0], k * im1[1] - R * im2[1]], B = [k * im1[0] + R * im2[0], k * im1[1] + R * im2[1]];
        g.mkIn("line", { x1: g.X(A[0]), y1: g.Y(A[1]), x2: g.X(B[0]), y2: g.Y(B[1]), stroke: latCol, "stroke-width": 1 });
        const C = [k * im2[0] - R * im1[0], k * im2[1] - R * im1[1]], D = [k * im2[0] + R * im1[0], k * im2[1] + R * im1[1]];
        g.mkIn("line", { x1: g.X(C[0]), y1: g.Y(C[1]), x2: g.X(D[0]), y2: g.Y(D[1]), stroke: latCol, "stroke-width": 1 });
      }
      arrow([0, 0], im1, COL.orange, 3.2); arrow([0, 0], im2, COL.teal, 3.2);
    }
    stage.appendChild(g.el);
  }
  // surd-aware parser: handles ints, p/q, and √n or √n/d (unicode √ or ascii "sqrt")
  function parseVal(s) { if (s == null) return null; let t = ("" + s).trim().replace(/−/g, "-").replace(/√/g, "sqrt").replace(/\s/g, ""); if (t === "" || t === "-") return null; const m = t.match(/^(-?)sqrt(\d+)(?:\/(\d+))?$/); if (m) { const sg = m[1] === "-" ? -1 : 1; return sg * Math.sqrt(Number(m[2])) / (m[3] ? Number(m[3]) : 1); } const f = t.match(/^(-?\d+)\/(\d+)$/); if (f) return Number(f[1]) / Number(f[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  // render a value string as a node (√ radical with overline, optional stacked fraction)
  function valNode(s) { const raw = ("" + s).replace(/√/g, "sqrt").replace(/−/g, "-").trim(); const m = raw.match(/^(-?)sqrt(\d+)(?:\/(\d+))?$/); const span = document.createElement("span"); span.className = "ts-bm-val"; if (m) { const sg = m[1] ? "−" : ""; const rad = '<span class="ts-bm-rad">√<span class="ts-bm-radn">' + m[2] + "</span></span>"; if (m[3]) span.innerHTML = '<span class="ts-bm-frac"><span class="ts-bm-fnum">' + sg + rad + '</span><span class="ts-bm-fden">' + m[3] + "</span></span>"; else span.innerHTML = sg + rad; } else span.textContent = ("" + s).replace(/-/g, "−"); return span; }
  const tiles = spec.tiles || null;
  const vals = [null, null, null, null];   // e1x, e1y, e2x, e2y (strings) — unified model
  const inputs = [];                        // typed-mode inputs (empty in tile mode)
  const slotEls = [];                       // tile-mode slots (empty in typed mode)
  function readImg(i) { const x = parseVal(vals[i * 2]), y = parseVal(vals[i * 2 + 1]); return (x == null || y == null) ? null : [x, y]; }
  function drawImgs() {
    imgEls.forEach((e) => e.remove()); imgEls = [];
    if (!g || spec.showImages) return;   // read-off mode: images are pre-drawn as GIVEN
    [[0, COL.orange], [1, COL.teal]].forEach(([i, col]) => { const im = readImg(i); if (im) imgEls.push(...arrow([0, 0], im, col, 3.2)); });
  }
  function complete() { return vals.every((v) => parseVal(v) != null); }
  function correct() { return [0, 1].every((i) => { const im = readImg(i); return im && Math.abs(im[0] - parseVal(images[i][0])) < 1e-6 && Math.abs(im[1] - parseVal(images[i][1])) < 1e-6; }); }
  function bracketStatic(vec) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; vec.forEach((n) => { const c = document.createElement("div"); c.className = "ts-vt-static"; c.textContent = String(n); col.appendChild(c); }); wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function mapsto() { const s = document.createElement("span"); s.className = "ts-vt-op ts-bm-arrow"; s.textContent = "↦"; return s; }
  function firstEmpty() { for (let i = 0; i < 4; i++) if (vals[i] == null) return i; return -1; }
  function renderSlots() { slotEls.forEach((el, i) => { el.innerHTML = ""; if (vals[i] == null) { el.classList.add("is-empty"); el.classList.remove("is-filled"); } else { el.classList.remove("is-empty"); el.classList.add("is-filled"); el.appendChild(valNode(vals[i])); } }); drawImgs(); }
  function typedBracket(base) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; for (let k = 0; k < 2; k++) { const idx = base + k; const inp = document.createElement("input"); inp.type = "text"; inp.inputMode = "numeric"; inp.className = "ts-sf-input ts-bm-input"; inp.autocomplete = "off"; inp.addEventListener("input", () => { if (locked) return; vals[idx] = inp.value.trim() || null; drawImgs(); if (cbs && cbs.onInteract) cbs.onInteract(); if (complete() && cbs && cbs.onComplete) cbs.onComplete(); }); inputs.push(inp); col.appendChild(inp); } wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function slotBracket(base) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; for (let k = 0; k < 2; k++) { const idx = base + k; const sl = document.createElement("div"); sl.className = "ts-vt-slot ts-bm-slot is-empty"; sl.addEventListener("click", () => { if (locked) return; if (vals[idx] != null) { vals[idx] = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slotEls.push(sl); col.appendChild(sl); } wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  const rowsWrap = document.createElement("div"); rowsWrap.className = "ts-bm-rows";
  [[e1, 0], [e2, 2]].forEach(([bv, base]) => { const row = document.createElement("div"); row.className = "ts-bm-row"; row.appendChild(bracketStatic(bv)); row.appendChild(mapsto()); row.appendChild(tiles ? slotBracket(base) : typedBracket(base)); rowsWrap.appendChild(row); });
  stage.appendChild(rowsWrap);
  if (tiles) {
    const pal = document.createElement("div"); pal.className = "ts-vt-pal ts-bm-pal";
    tiles.forEach((tv) => { const t = document.createElement("div"); t.className = "ts-vt-tile ts-bm-tile"; t.appendChild(valNode(tv)); t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { vals[i] = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); if (complete() && cbs && cbs.onComplete) cbs.onComplete(); } }); pal.appendChild(t); });
    stage.appendChild(pal);
  }
  mountEl.appendChild(stage); if (tiles) renderSlots(); else drawImgs();

  const val = () => vals.slice();
  function setVal(arr) { if (!Array.isArray(arr)) return; const flat = Array.isArray(arr[0]) ? arr.reduce((a, v) => a.concat(v), []) : arr; for (let i = 0; i < 4; i++) vals[i] = flat[i] != null ? String(flat[i]) : null; if (tiles) renderSlots(); else { inputs.forEach((inp, i) => { inp.value = vals[i] != null ? vals[i] : ""; }); drawImgs(); } }
  return {
    isComplete: () => complete(),
    check: () => ({ correct: correct(), value: val() }),
    getValue: val,
    setValue: setVal,
    getState: () => ({ value: val(), target: images }),
    reset: () => { for (let i = 0; i < 4; i++) vals[i] = null; if (tiles) renderSlots(); else { inputs.forEach((inp) => (inp.value = "")); drawImgs(); } },
    reveal: (forced) => { locked = true; setVal(forced || images.reduce((a, v) => a.concat(v), [])); if (tiles) slotEls.forEach((el) => el.classList.add("is-answer")); else inputs.forEach((inp) => { inp.readOnly = true; inp.classList.add("is-answer"); }); }
  };
}

// ── Linear Transformations (L10.3): a linear map is fully determined by where the
// basis vectors go — T([x,y]) = x·T(e₁) + y·T(e₂). Given the two images T(e₁)=a and
// T(e₂)=b (the matrix columns), the student TYPES where a general vector v=[x,y]
// lands. Grid shows T(e₁) (orange) and T(e₂) (teal); the typed image draws live in
// purple. Graded: typed == x·a + y·b componentwise. spec { a:[..], b:[..], v:[x,y], graph }.
function buildLinMap(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-bm ts-lm";
  const a = (spec.a || []).map(Number), b = (spec.b || []).map(Number), v = (spec.v || []).map(Number);
  const answer = [v[0] * a[0] + v[1] * b[0], v[0] * a[1] + v[1] * b[1]];
  const COL = { orange: "#f5a04a", teal: "#279b8d", purple: "#8a5cf6" };
  function parseFrac(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-"); if (t === "" || t === "-") return null; const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  let g = null, imgEls = [];
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "6 4" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10, hw = 6, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    return [line, head];
  }
  function label(vec, txt, col) { if (!g) return; const tx = g.X(vec[0] * 0.55) + (vec[0] >= 0 ? 8 : -10), ty = g.Y(vec[1] * 0.55) + (vec[1] >= 0 ? -7 : 16); const t = g.mkIn("text", { x: tx, y: ty, fill: col, "font-size": 11.5, "font-weight": 700 }); t.textContent = txt; }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -2, xmax: 6, ymin: -2, ymax: 6, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    arrow([0, 0], a, COL.orange); label(a, "T(e₁)", "#b06a12");
    arrow([0, 0], b, COL.teal); label(b, "T(e₂)", "#1c6b60");
    stage.appendChild(g.el);
  }
  const inputs = [];
  function readImg() { const x = parseFrac(inputs[0].value), y = parseFrac(inputs[1].value); return (x == null || y == null) ? null : [x, y]; }
  function drawImg() { imgEls.forEach((e) => e.remove()); imgEls = []; if (!g) return; const im = readImg(); if (im) { imgEls.push(...arrow([0, 0], im, COL.purple, 3.2)); label(im, "T(v)", "#6d3fc0"); } }
  function bracketStatic(vec) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; vec.forEach((n) => { const c = document.createElement("div"); c.className = "ts-vt-static"; c.textContent = String(n).replace(/-(\d)/g, "−$1"); col.appendChild(c); }); wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function inputBracket() { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const col = document.createElement("div"); col.className = "ts-vt-col"; for (let k = 0; k < 2; k++) { const inp = document.createElement("input"); inp.type = "text"; inp.inputMode = "numeric"; inp.className = "ts-sf-input ts-bm-input"; inp.autocomplete = "off"; inp.addEventListener("input", () => { if (locked) return; drawImg(); if (cbs && cbs.onInteract) cbs.onInteract(); if (complete() && cbs && cbs.onComplete) cbs.onComplete(); }); inputs.push(inp); col.appendChild(inp); } wrap.appendChild(col); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function mapsto() { const s = document.createElement("span"); s.className = "ts-vt-op ts-bm-arrow"; s.textContent = "↦"; return s; }
  function givenRow(bv, img, col) { const row = document.createElement("div"); row.className = "ts-bm-row ts-lm-given"; row.appendChild(bracketStatic(bv)); row.appendChild(mapsto()); const im = bracketStatic(img); im.style.color = col; row.appendChild(im); return row; }
  const rowsWrap = document.createElement("div"); rowsWrap.className = "ts-bm-rows";
  rowsWrap.appendChild(givenRow([1, 0], a, "#b06a12"));
  rowsWrap.appendChild(givenRow([0, 1], b, "#1c6b60"));
  const solve = document.createElement("div"); solve.className = "ts-bm-row ts-lm-solve"; solve.appendChild(bracketStatic(v)); solve.appendChild(mapsto()); solve.appendChild(inputBracket()); rowsWrap.appendChild(solve);
  stage.appendChild(rowsWrap); mountEl.appendChild(stage); drawImg();

  function complete() { return inputs.every((inp) => parseFrac(inp.value) != null); }
  function correct() { const im = readImg(); return im && Math.abs(im[0] - answer[0]) < 1e-9 && Math.abs(im[1] - answer[1]) < 1e-9; }
  const val = () => inputs.map((inp) => inp.value.trim());
  function setVal(arr) { if (!Array.isArray(arr)) return; inputs.forEach((inp, i) => { inp.value = arr[i] != null ? String(arr[i]) : ""; }); drawImg(); }
  return {
    isComplete: () => complete(),
    check: () => ({ correct: correct(), value: val() }),
    getValue: val,
    setValue: setVal,
    getState: () => ({ value: val(), target: answer }),
    reset: () => { inputs.forEach((inp) => (inp.value = "")); drawImg(); },
    reveal: (forced) => { locked = true; setVal(forced || answer.map(String)); inputs.forEach((inp) => { inp.readOnly = true; inp.classList.add("is-answer"); }); }
  };
}

// ── Transforming Combinations (L10.4): a linear map sends a combination to the SAME
// combination of the images — T(x·e₁ + y·e₂) = x·T(e₁) + y·T(e₂). Given T(e₁)=a and
// T(e₂)=b, write the transformed vector T([x,y]) as [·]·T(e₁) + [·]·T(e₂) by filling
// two coefficient slots from a tile palette (the answer is the ORIGINAL components
// [x,y] — that's the punchline). Grid shows T(e₁) orange, T(e₂) teal, the target
// T(v) purple, and a live green combination path. Graded geometrically: s₀·a + s₁·b
// == target. spec { a:[..], b:[..], orig:[x,y], tiles:[...], graph }.
function buildCombMap(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-cm";
  const a = (spec.a || []).map(Number), b = (spec.b || []).map(Number), orig = (spec.orig || []).map(Number);
  const target = [orig[0] * a[0] + orig[1] * b[0], orig[0] * a[1] + orig[1] * b[1]];
  const COL = { orange: "#f5a04a", teal: "#279b8d", purple: "#8a5cf6", green: "#3aa76d" };
  const typed = spec.typed === true;   // typed decimal keypad (non-integer combos) vs tile palette
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  let g = null, combEls = [];
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "5 3" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
    return [line, head];
  }
  function label(vec, txt, col) { if (!g) return; const tx = g.X(vec[0] * 0.55) + (vec[0] >= 0 ? 8 : -12), ty = g.Y(vec[1] * 0.55) + (vec[1] >= 0 ? -7 : 16); const t = g.mkIn("text", { x: tx, y: ty, fill: col, "font-size": 11, "font-weight": 700 }); t.textContent = txt; }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -2, xmax: 4, ymin: -3, ymax: 4, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    arrow([0, 0], a, COL.orange); label(a, "T(e₁)", "#b06a12");
    arrow([0, 0], b, COL.teal); label(b, "T(e₂)", "#1c6b60");
    arrow([0, 0], target, COL.purple, 2.8); label(target, "T(v)", "#6d3fc0");
    stage.appendChild(g.el);
  }
  function updateComb() {
    combEls.forEach((e) => e.remove()); combEls = [];
    if (!g || !slots.every((s) => s.val != null)) return;
    const s0 = parseTile(slots[0].val), s1 = parseTile(slots[1].val); if (s0 == null || s1 == null) return;
    const p1 = [s0 * a[0], s0 * a[1]], p2 = [p1[0] + s1 * b[0], p1[1] + s1 * b[1]];
    combEls.push(...arrow([0, 0], p1, COL.green, 2.4, true), ...arrow(p1, p2, COL.green, 2.4, true));
  }
  // ---- given line + equation: T[orig] = [slot]·T(e₁) + [slot]·T(e₂) ----
  const slots = [];
  function bracketOf(vec, col) { const wrap = document.createElement("span"); wrap.className = "ts-vt-sbrk"; if (col) wrap.style.color = col; const l = document.createElement("span"); l.className = "ts-vt-bracket"; l.textContent = "["; wrap.appendChild(l); const c = document.createElement("div"); c.className = "ts-vt-col"; vec.forEach((n) => { const d = document.createElement("div"); d.className = "ts-vt-static"; d.textContent = fmt(Number(n)); c.appendChild(d); }); wrap.appendChild(c); const r = document.createElement("span"); r.className = "ts-vt-bracket"; r.textContent = "]"; wrap.appendChild(r); return wrap; }
  function tspan(txt, col) { const s = document.createElement("span"); s.className = "ts-cm-tlab"; s.textContent = txt; if (col) s.style.color = col; return s; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  function makeSlot() {
    const idx = slots.length;
    if (typed) {
      const s = document.createElement("input"); s.type = "text"; s.inputMode = "decimal"; s.className = "ts-sf-input ts-vf-input ts-vf-scalar ts-cf-input ts-cm-input"; s.autocomplete = "off";
      s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); updateComb(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); });
      slots.push({ el: s, val: null, input: true }); return s;
    }
    const s = document.createElement("div"); s.className = "ts-vt-slot ts-vt-coeffslot is-empty"; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s;
  }
  const given = document.createElement("div"); given.className = "ts-cm-given";
  given.appendChild(tspan("T(e₁)", "#b06a12")); given.appendChild(opSpan("=")); given.appendChild(bracketOf(a, "#b06a12")); given.appendChild(opSpan(" ")); given.appendChild(tspan("T(e₂)", "#1c6b60")); given.appendChild(opSpan("=")); given.appendChild(bracketOf(b, "#1c6b60"));
  stage.appendChild(given);
  const brk = document.createElement("div"); brk.className = "ts-vt-brk ts-cm-eq";
  brk.appendChild(tspan("T", "#6d3fc0")); brk.appendChild(bracketOf(orig, "#6d3fc0")); brk.appendChild(opSpan("=")); brk.appendChild(makeSlot()); brk.appendChild(tspan("T(e₁)", "#b06a12")); brk.appendChild(opSpan("+")); brk.appendChild(makeSlot()); brk.appendChild(tspan("T(e₂)", "#1c6b60"));
  stage.appendChild(brk);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.input) { s.el.value = s.val != null ? s.val : ""; s.el.classList.toggle("is-filled", s.val != null); return; } if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); updateComb(); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  if (!typed) (spec.tiles || ["−3", "−2", "−1", "0", "1", "2", "3"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv));
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  if (!typed) stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { if (!slots.every((s) => s.val != null)) return false; const s0 = parseTile(slots[0].val), s1 = parseTile(slots[1].val); if (s0 == null || s1 == null) return false; return Math.abs(s0 * a[0] + s1 * b[0] - target[0]) < 1e-9 && Math.abs(s0 * a[1] + s1 * b[1] - target[1]) < 1e-9; }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: orig }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const ans = forced || orig.map(String); slots.forEach((s, i) => (s.val = ans[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Matrices as Transformations (L11.1): a 2×2 matrix IS a transformation — its
// COLUMNS are the images of the basis vectors, [T(e₁) | T(e₂)]. The grid shows the
// standard basis e₁ (orange) and e₂ (teal) plus their faded image arrows; the student
// completes the matrix by reading those images off the grid and filling the blank
// entries from a tile palette. Graded: each blank entry == the correct matrix value.
// spec { matrix:[[a,b],[c,d]], blanks:[[r,c],...], tiles?, graph }.  Columns: col0 =
// [a,c] = T(e₁), col1 = [b,d] = T(e₂).
function buildMatrixGrid(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mg";
  const M = (spec.matrix || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const col0 = [M[0][0], M[1][0]], col1 = [M[0][1], M[1][1]];
  const blanks = spec.blanks || [[0, 0], [1, 0]];
  const isBlank = (r, c) => blanks.some((b) => b[0] === r && b[1] === c);
  const COL = { orange: "#f5a04a", teal: "#279b8d", orangeF: "#f3c79a", tealF: "#9fd0c8" };
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  let g = null;
  function arrow(from, to, col, wdt, dash) {
    const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": wdt || 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "6 4" } : {}));
    const head = g.mkIn("path", { fill: col });
    const x1 = g.X(from[0]), y1 = g.Y(from[1]), x2 = g.X(to[0]), y2 = g.Y(to[1]);
    line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
    head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
  }
  function label(vec, txt, col) { if (!g) return; const tx = g.X(vec[0] * 0.6) + (vec[0] >= 0 ? 7 : -12), ty = g.Y(vec[1] * 0.6) + (vec[1] >= 0 ? -6 : 15); const t = g.mkIn("text", { x: tx, y: ty, fill: col, "font-size": 11, "font-weight": 700 }); t.textContent = txt; }
  function poly(verts, fill, stroke, dash) {
    let d = ""; verts.forEach((p, i) => { d += (i ? " L " : "M ") + g.X(p[0]).toFixed(1) + " " + g.Y(p[1]).toFixed(1); }); d += " Z";
    g.mkIn("path", Object.assign({ d: d, fill: fill, stroke: stroke, "stroke-width": 2.4, "stroke-linejoin": "round" }, dash ? { "stroke-dasharray": "6 4" } : {}));
  }
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    if (spec.shape) {
      // SHAPE mode (Transforming Shapes): original polygon (solid) → transformed target (dashed)
      const src = spec.shape.map((p) => p.map(Number));
      const img = src.map((p) => [M[0][0] * p[0] + M[0][1] * p[1], M[1][0] * p[0] + M[1][1] * p[1]]);
      poly(img, "none", "#e0913f", true);                 // target (dashed outline)
      poly(src, "rgba(245,160,74,0.32)", "#f5a04a", false); // original (solid fill)
    } else {
      arrow([0, 0], col0, COL.orangeF, 2.4, true); label(col0, "T(e₁)", "#b06a12");   // faded images
      arrow([0, 0], col1, COL.tealF, 2.4, true); label(col1, "T(e₂)", "#1c6b60");
      arrow([0, 0], [1, 0], COL.orange, 2.8);   // standard basis (solid)
      arrow([0, 0], [0, 1], COL.teal, 2.8);
      if (spec.v) {   // optional sample vector v (solid purple) + its image T(v) (dashed) — "the matrix that sends v to its target"
        const vv = spec.v.map(Number);
        const Tv = [M[0][0] * vv[0] + M[0][1] * vv[1], M[1][0] * vv[0] + M[1][1] * vv[1]];
        arrow([0, 0], Tv, "#b79bf0", 2.4, true);
        arrow([0, 0], vv, "#8a5cf6", 2.8); label(vv, "v", "#6d3fc7");
      }
    }
    stage.appendChild(g.el);
  }
  // ---- 2×2 matrix bracket ----
  const slots = [];   // {el, r, c, val}
  const mat = document.createElement("div"); mat.className = "ts-mg-mat";
  const lb = document.createElement("span"); lb.className = "ts-vt-bracket ts-mg-brk"; lb.textContent = "["; mat.appendChild(lb);
  const grid = document.createElement("div"); grid.className = "ts-mg-grid";
  for (let r = 0; r < 2; r++) for (let c = 0; c < 2; c++) {
    if (isBlank(r, c)) {
      const s = document.createElement("div"); s.className = "ts-vt-slot ts-vt-coeffslot ts-mg-cell is-empty";
      const rec = { el: s, r: r, c: c, val: null }; const idx = slots.length;
      s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
      slots.push(rec); grid.appendChild(s);
    } else {
      const d = document.createElement("div"); d.className = "ts-mg-cell ts-mg-static"; d.textContent = fmt(M[r][c]); grid.appendChild(d);
    }
  }
  mat.appendChild(grid);
  const rb = document.createElement("span"); rb.className = "ts-vt-bracket ts-mg-brk"; rb.textContent = "]"; mat.appendChild(rb);
  stage.appendChild(mat);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || ["−4", "−3", "−2", "−1", "0", "1", "2", "3", "4"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv));
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => { if (s.val == null) return false; const v = parseTile(s.val); return v != null && v === M[s.r][s.c]; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => M[s.r][s.c]) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || slots.map((s) => String(M[s.r][s.c])); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Transforming via Matrices (L11.2): multiply a 2×2 matrix by a vector. Renders
// one or more equations "M·v = [result]"; the student fills each 2-entry result from
// a tile palette. M·[x,y] = x·(col0) + y·(col1) = [ax+by, cx+dy]. Applying M to e₁ and
// e₂ extracts the columns — the bridge from the matrix back to the basis images. An
// optional grid draws the input vectors. spec { matrix:[[a,b],[c,d]], vs:[[x,y],...]
// (or v:[x,y]), tiles?, graph? }.
function buildMatVec(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mv";
  const M = (spec.matrix || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const vs = (spec.vs || [spec.v || [1, 0]]).map((v) => v.map(Number));
  const ans = vs.map((v) => [M[0][0] * v[0] + M[0][1] * v[1], M[1][0] * v[0] + M[1][1] * v[1]]);
  const COLS = ["#f5a04a", "#279b8d", "#8a5cf6", "#4f6ef7"];
  const typed = spec.typed === true;   // typed keypad (matrix arithmetic, large results) vs tile palette
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  if (spec.graph) {
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 280, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 280) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    function arrow(to, col) { const line = g.mkIn("line", { stroke: col, "stroke-width": 2.8, "stroke-linecap": "round" }); const head = g.mkIn("path", { fill: col }); const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(to[0]), y2 = g.Y(to[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z"); }
    vs.forEach((v, i) => arrow(v, COLS[i % COLS.length]));
    stage.appendChild(g.el);
  }
  const slots = [];   // {el, eq, comp, val}
  function brk(txt) { const s = document.createElement("span"); s.className = "ts-vt-bracket ts-mv-brk"; s.textContent = txt; return s; }
  function matNode() { const w = document.createElement("span"); w.className = "ts-mv-mat"; w.appendChild(brk("[")); const gr = document.createElement("div"); gr.className = "ts-mv-mgrid"; for (let r = 0; r < 2; r++) for (let c = 0; c < 2; c++) { const d = document.createElement("div"); d.className = "ts-mv-num"; d.textContent = fmt(M[r][c]); gr.appendChild(d); } w.appendChild(gr); w.appendChild(brk("]")); return w; }
  function vecNode(v) { const w = document.createElement("span"); w.className = "ts-mv-vec"; w.appendChild(brk("[")); const col = document.createElement("div"); col.className = "ts-mv-vcol"; v.forEach((n) => { const d = document.createElement("div"); d.className = "ts-mv-num"; d.textContent = fmt(n); col.appendChild(d); }); w.appendChild(col); w.appendChild(brk("]")); return w; }
  function resNode(ei) { const w = document.createElement("span"); w.className = "ts-mv-vec"; w.appendChild(brk("[")); const col = document.createElement("div"); col.className = "ts-mv-vcol"; for (let comp = 0; comp < 2; comp++) { const idx = slots.length; if (typed) { const s = document.createElement("input"); s.type = "text"; s.inputMode = "numeric"; s.className = "ts-sf-input ts-mv-input"; s.autocomplete = "off"; s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); if (cbs && cbs.onInteract) cbs.onInteract(); done(); }); slots.push({ el: s, eq: ei, comp: comp, val: null, input: true }); col.appendChild(s); } else { const s = document.createElement("div"); s.className = "ts-vt-slot ts-mv-slot is-empty"; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, eq: ei, comp: comp, val: null }); col.appendChild(s); } } w.appendChild(col); w.appendChild(brk("]")); return w; }
  function op(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  const eqs = document.createElement("div"); eqs.className = "ts-mv-eqs";
  vs.forEach((v, ei) => { const row = document.createElement("div"); row.className = "ts-mv-eq"; row.appendChild(matNode()); row.appendChild(vecNode(v)); row.appendChild(op("=")); row.appendChild(resNode(ei)); eqs.appendChild(row); });
  stage.appendChild(eqs);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.input) { s.el.value = s.val != null ? s.val : ""; s.el.classList.toggle("is-filled", s.val != null); return; } if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  if (!typed) (spec.tiles || ["−4", "−3", "−2", "−1", "0", "1", "2", "3", "4"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv));
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  if (!typed) stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => { if (s.val == null) return false; const v = parseTile(s.val); return v != null && v === ans[s.eq][s.comp]; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => ans[s.eq][s.comp]) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || slots.map((s) => String(ans[s.eq][s.comp])); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Matrix as Multiple Vectors (L12.2): a matrix is a bundle of column vectors. To
// transform a matrix, transform each column; the images become the columns of a new
// matrix. Grid shows each input column (solid, orange col0 / teal col1) and its image
// (dashed). Layout: [input matrix] ↦ [result matrix, 2×2 tile-slots]. Graded entrywise:
// slot == spec.result[r][c]. spec { m:[[a,b],[c,d]], result:[[..]], tiles?, graph }.
function buildMatCols(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mc";
  const m = (spec.m || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const res = (spec.result || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const NC = m[0].length;   // number of columns (2 or 3) — general 2×N "transform each column"
  const blanks = spec.blanks || null;   // if set, only these [r,c] result cells are fillable; the rest are shown (given). Default: all cells fillable.
  const isBlank = (r, c) => !blanks || blanks.some((bb) => bb[0] === r && bb[1] === c);
  const COLS = ["#f5a04a", "#279b8d", "#8a63d2"];             // solid: orange / teal / purple (Brilliant col order)
  const COLF = ["#f0bd8c", "#98cfc7", "#c3aef0"];             // faded (image, dashed)
  const colTint = ["rgba(245,160,74,0.14)", "rgba(39,155,141,0.14)", "rgba(138,99,210,0.14)"];   // per-column bg
  const slotBorder = ["#e6b483", "#9ccfc7", "#c3aef0"];
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const mm = t.match(/^(-?\d+)\/(\d+)$/); if (mm) return Number(mm[1]) / Number(mm[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  let g = null;
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 280, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 280) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    g = lineGraphSVG(gspec);
    function arrow(to, col, dash) { const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "6 4" } : {})); const head = g.mkIn("path", { fill: col }); const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(to[0]), y2 = g.Y(to[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z"); }
    for (let c = 0; c < NC; c++) arrow([res[0][c], res[1][c]], COLF[c % 3], true);    // image cols (dashed)
    for (let c = 0; c < NC; c++) arrow([m[0][c], m[1][c]], COLS[c % 3], false);       // input cols (solid)
    stage.appendChild(g.el);
  }
  const slots = [];   // {el, r, c, val}
  function brk(t) { const s = document.createElement("span"); s.className = "ts-vt-bracket ts-mv-brk"; s.textContent = t; return s; }
  function staticMat() { const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("[")); const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + NC + ", 1fr)"; for (let r = 0; r < 2; r++) for (let cc = 0; cc < NC; cc++) { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = fmt(m[r][cc]); d.style.background = colTint[cc % 3]; gr.appendChild(d); } w.appendChild(gr); w.appendChild(brk("]")); return w; }
  function slotMat() { const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("[")); const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + NC + ", 1fr)"; for (let r = 0; r < 2; r++) for (let cc = 0; cc < NC; cc++) { if (!isBlank(r, cc)) { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = fmt(res[r][cc]); d.style.background = colTint[cc % 3]; gr.appendChild(d); continue; } const s = document.createElement("div"); s.className = "ts-vt-slot ts-mc-cell ts-mc-slot is-empty"; s.style.borderColor = slotBorder[cc % 3]; const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, r: r, c: cc, val: null }); gr.appendChild(s); } w.appendChild(gr); w.appendChild(brk("]")); return w; }
  function op(t) { const s = document.createElement("span"); s.className = "ts-vt-op ts-mc-arrow"; s.textContent = t; return s; }
  const eq = document.createElement("div"); eq.className = "ts-mc-eq";
  eq.appendChild(staticMat()); eq.appendChild(op("↦")); eq.appendChild(slotMat());
  stage.appendChild(eq);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || ["−4", "−3", "−2", "−1", "0", "1", "2", "3", "4"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv));
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => { if (s.val == null) return false; const v = parseTile(s.val); return v != null && v === res[s.r][s.c]; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => res[s.r][s.c]) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || slots.map((s) => String(res[s.r][s.c])); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Matrix arithmetic (L12.3 add/subtract, L12.4 scalar): entrywise, DIMENSION-GENERAL (R×C
// from a). Modes:
//   {a, b, op:"+"|"−"}                 →  A op B, fill the RESULT (all result cells, or spec.blanks)
//   {a, b, op, blanksA:[[r,c]], blanksB:[[r,c]]} → "complete the equation": result shown FULLY,
//         fill the missing entries of A and/or B (uniquely determined by A op B == result)
//   {scalar:k, a}                      →  k·A, fill the result
// Renders "[A] op [B] = [C]" (or "k[A] = [C]"). Slots collected A-then-B-then-result in row-major;
// each graded against its authored true value. tile palette (spec.tiles) or typed (spec.typed).
function buildMatOp(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mo";
  const a = (spec.a || [[0, 0], [0, 0]]).map((r) => r.map(Number));
  const scalarMode = spec.scalar != null;
  const b = spec.b ? spec.b.map((r) => r.map(Number)) : null;
  const k = scalarMode ? Number(spec.scalar) : null;
  const op = (spec.op === "−" || spec.op === "-") ? "−" : "+";
  const R = a.length, C = a[0].length;
  const res = a.map((row, r) => row.map((val, cc) => scalarMode ? k * val : (op === "−" ? val - b[r][cc] : val + b[r][cc])));
  const typed = spec.typed === true;
  const operandMode = !!(spec.blanksA || spec.blanksB);   // fill operand entries (result may ALSO have blanks via spec.blanks)
  const blanksRes = spec.blanks || null;                  // result-cell blanks
  const inList = (list, r, c) => list && list.some((bb) => bb[0] === r && bb[1] === c);
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  // fraction-aware display (scalar mode can produce halves/thirds, e.g. ½·M): render p/q for non-integers
  const gcd = (x, y) => y ? gcd(y, x % y) : x;
  const fmtFrac = (n) => { if (Number.isInteger(n)) return fmt(n); for (let q = 2; q <= 12; q++) { if (Math.abs(n * q - Math.round(n * q)) < 1e-9) { const p = Math.round(n * q); const g = gcd(Math.abs(p), q) || 1; return fmt(p / g) + "/" + (q / g); } } return fmt(n); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const mm = t.match(/^(-?\d+)\/(\d+)$/); if (mm) return Number(mm[1]) / Number(mm[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  const slots = [];
  function brk(t) { const s = document.createElement("span"); s.className = "ts-vt-bracket ts-mv-brk"; s.textContent = t; return s; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op ts-mo-op"; s.textContent = t; return s; }
  // Render a matrix; cells listed in `blanks` become fillable slots graded against mat[r][c].
  function mkMat(mat, blanks) {
    const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("["));
    const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + C + ", 1fr)";
    for (let r = 0; r < R; r++) for (let cc = 0; cc < C; cc++) {
      if (!inList(blanks, r, cc)) { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = fmtFrac(mat[r][cc]); gr.appendChild(d); continue; }
      const idx = slots.length; const trueVal = mat[r][cc];
      if (typed) { const s = document.createElement("input"); s.type = "text"; s.inputMode = "numeric"; s.className = "ts-sf-input ts-mo-input"; s.autocomplete = "off"; s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); if (cbs && cbs.onInteract) cbs.onInteract(); done(); }); slots.push({ el: s, trueVal: trueVal, val: null, input: true }); gr.appendChild(s); }
      else { const s = document.createElement("div"); s.className = "ts-vt-slot ts-mc-cell ts-mc-slot is-empty"; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, trueVal: trueVal, val: null }); gr.appendChild(s); }
    }
    w.appendChild(gr); w.appendChild(brk("]")); return w;
  }
  const eq = document.createElement("div"); eq.className = "ts-mc-eq ts-mo-eq";
  // slot collection order: A blanks, then B blanks, then result blanks (all row-major)
  if (scalarMode) { const ks = document.createElement("span"); ks.className = "ts-mo-scalar"; ks.textContent = fmtFrac(k); eq.appendChild(ks); eq.appendChild(mkMat(a, operandMode ? spec.blanksA : null)); }
  else { eq.appendChild(mkMat(a, operandMode ? spec.blanksA : null)); eq.appendChild(opSpan(op)); eq.appendChild(mkMat(b, operandMode ? spec.blanksB : null)); }
  eq.appendChild(opSpan("="));
  // result: fully shown in operand mode; blanks (spec.blanks or all cells) in result mode
  const resBlanks = blanksRes || (operandMode ? null : res.map((row, r) => row.map((_, c) => [r, c])).flat());
  eq.appendChild(mkMat(res, resBlanks));
  stage.appendChild(eq);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.input) { s.el.value = s.val != null ? s.val : ""; s.el.classList.toggle("is-filled", s.val != null); return; } if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  if (!typed) (spec.tiles || ["−5", "−4", "−3", "−2", "−1", "0", "1", "2", "3", "4", "5"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv));
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  if (!typed) stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => { if (s.val == null) return false; const v = parseTile(s.val); return v != null && v === s.trueVal; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => s.trueVal) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const av = forced || slots.map((s) => String(s.trueVal)); slots.forEach((s, i) => (s.val = av[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Matrix multiplication (L13): (A·B)[i][j] = row i of A dotted with col j of B.
// DIMENSION-GENERAL: A is m×n, B is n×p → result m×p (covers 2×2 arithmetic AND the
// "Matrix Dimensions" lesson's non-square products like 3×1 · 1×2 = 3×2). Two static
// matrices juxtaposed "[A][B] = [result]"; SOME result cells given (static), SOME blank
// (typed slots) via spec.blanks (default all). When a blank slot is active (focused /
// first-empty), row i of A and col j of B highlight blue — the exact row·column that
// produces that entry (matches Brilliant). Brackets are border-drawn so they stretch to
// any row count. Graded res[i][j] === Σ_k A[i][k]·B[k][j].
// spec { a:[[..]], b:[[..]], blanks?:[[r,c]], typed?, tiles? }.
function buildMatMul(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mm";
  const a = (spec.a || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const b = (spec.b || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const M = a.length, N = a[0].length, P = b[0].length;   // A is M×N, B is N×P, result M×P
  const res = [];
  for (let i = 0; i < M; i++) { const row = []; for (let j = 0; j < P; j++) { let s = 0; for (let k = 0; k < N; k++) s += a[i][k] * b[k][j]; row.push(s); } res.push(row); }
  const typed = spec.typed !== false;   // multiplication results range widely → typed by default
  const blanks = spec.blanks || null;         // RESULT cells that are blank
  const blanksB = spec.blanksB || null;        // OPERAND-B cells that are blank ("complete the equation")
  const isBlank = (r, c) => !blanks || blanks.some((bb) => bb[0] === r && bb[1] === c);
  const isBlankB = (r, c) => blanksB && blanksB.some((bb) => bb[0] === r && bb[1] === c);
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const mm = t.match(/^(-?\d+)\/(\d+)$/); if (mm) return Number(mm[1]) / Number(mm[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  const slots = [];   // each: {el, r, c, val, input?, truth, matB?}  — truth = the correct numeric value for that slot
  const aCells = [];   // {el, r} A cells tagged by row (for row-highlight)
  const bCells = [];   // {el, c} B cells tagged by col (for col-highlight)
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op ts-mo-op"; s.textContent = t; return s; }
  function mkInput(r, c, truth, matB) {
    const s = document.createElement("input"); s.type = "text"; s.inputMode = "numeric"; s.className = "ts-sf-input ts-mo-input"; s.autocomplete = "off";
    const idx = slots.length;
    s.addEventListener("focus", () => { if (matB) highlight(null, null); else highlight(r, c); });
    s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); if (cbs && cbs.onInteract) cbs.onInteract(); done(); });
    slots.push({ el: s, r: r, c: c, val: null, input: true, truth: truth, matB: !!matB });
    return s;
  }
  function srcMat(mat, isA) { const rows = mat.length, cols = mat[0].length; const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("l")); const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + cols + ", 1fr)"; for (let r = 0; r < rows; r++) for (let cc = 0; cc < cols; cc++) { if (!isA && isBlankB(r, cc)) { const s = mkInput(r, cc, mat[r][cc], true); gr.appendChild(s); bCells.push({ el: s, c: cc }); continue; } const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static ts-mm-src"; d.textContent = fmt(mat[r][cc]); gr.appendChild(d); if (isA) aCells.push({ el: d, r: r }); else bCells.push({ el: d, c: cc }); } w.appendChild(gr); w.appendChild(brk("r")); return w; }
  function highlight(r, c) { aCells.forEach((x) => x.el.classList.toggle("ts-mm-hl", r != null && x.r === r)); bCells.forEach((x) => x.el.classList.toggle("ts-mm-hl", c != null && x.c === c)); }
  function activeSlot() { for (const s of slots) if (s.input && document.activeElement === s.el) return s; for (const s of slots) if (s.val == null) return s; return null; }
  function refreshHL() { const s = activeSlot(); if (s && !s.matB) highlight(s.r, s.c); else highlight(null, null); }
  function slotMat() {
    const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("l"));
    const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + P + ", 1fr)";
    for (let r = 0; r < M; r++) for (let cc = 0; cc < P; cc++) {
      if (!isBlank(r, cc)) { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = fmt(res[r][cc]); gr.appendChild(d); continue; }
      const idx = slots.length;
      if (typed) { gr.appendChild(mkInput(r, cc, res[r][cc], false)); }
      else { const s = document.createElement("div"); s.className = "ts-vt-slot ts-mc-cell ts-mc-slot is-empty"; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); refreshHL(); if (cbs && cbs.onInteract) cbs.onInteract(); } else { highlight(r, cc); } }); slots.push({ el: s, r: r, c: cc, val: null, truth: res[r][cc] }); gr.appendChild(s); }
    }
    w.appendChild(gr); w.appendChild(brk("r")); return w;
  }
  const eq = document.createElement("div"); eq.className = "ts-mc-eq ts-mm-eq";
  eq.appendChild(srcMat(a, true)); eq.appendChild(srcMat(b, false)); eq.appendChild(opSpan("=")); eq.appendChild(slotMat());
  stage.appendChild(eq);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.input) { s.el.value = s.val != null ? s.val : ""; s.el.classList.toggle("is-filled", s.val != null); return; } if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  if (!typed) (spec.tiles || ["−2", "−1", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv));
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); refreshHL(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  if (!typed) stage.appendChild(pal); mountEl.appendChild(stage); renderSlots(); refreshHL();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => { if (s.val == null) return false; const v = parseTile(s.val); return v != null && v === s.truth; }); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); refreshHL(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => s.truth) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); refreshHL(); },
    reveal: (forced) => { locked = true; const av = forced || slots.map((s) => String(s.truth)); slots.forEach((s, i) => (s.val = av[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); highlight(null, null); }
  };
}

// ── Symbolic matrix multiplication (L13.1 "how a general vector transforms"): A · [ numCol | symCol ]
// where the 2nd matrix's FIRST column is numeric (numCol, its image A·numCol shown given) and its SECOND
// column is symbolic (sym, e.g. [x;y]). The symbolic image column A·[x;y] is TYPED per-row as an algebraic
// expression and graded by NUMERIC SAMPLING at several (x,y) points against a[r]·[x,y] — so any non-equivalent
// expression fails (never a pick / tile flatten). spec { matrix, numCol, sym:["x","y"], answers:["x","2y−x"] }.
function buildMatSym(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mm";
  const A = (spec.matrix || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const numCol = (spec.numCol || [1, 0]).map(Number);
  const sym = spec.sym || ["x", "y"];
  const R = A.length;
  const numRes = A.map((row) => row.reduce((s, v, k) => s + v * numCol[k], 0));   // given numeric result column
  const trueExpr = (r, x, y) => A[r][0] * x + A[r][1] * y;                         // symbolic result value at (x,y)
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function compile(str) {
    if (str == null) return null;
    let t = ("" + str).replace(/−/g, "-").replace(/×/g, "*").replace(/\s+/g, "");
    if (!t || !/^[-+*/().0-9xy]+$/.test(t)) return null;
    t = t.replace(/(\d)([xy(])/g, "$1*$2").replace(/([xy)])(\()/g, "$1*$2").replace(/([xy)])([xy])/g, "$1*$2").replace(/(\))([0-9xy])/g, "$1*$2");
    try { const f = new Function("x", "y", "return (" + t + ");"); if (!Number.isFinite(f(1, 1))) return null; return f; } catch (e) { return null; }
  }
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op ts-mo-op"; s.textContent = t; return s; }
  function matNode(cells) {   // cells: R×2 array of {text?} or {input:true,idx}
    const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("l"));
    const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(2, 1fr)";
    for (let r = 0; r < R; r++) for (let c = 0; c < 2; c++) {
      const cell = cells[r][c];
      if (cell.input) {
        const s = document.createElement("input"); s.type = "text"; s.className = "ts-sf-input ts-mo-input"; s.autocomplete = "off"; s.style.minWidth = "58px";
        const idx = cell.idx;
        s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); if (cbs && cbs.onInteract) cbs.onInteract(); if (slots.every((x) => x.val != null) && cbs && cbs.onComplete) cbs.onComplete(); });
        slots[idx].el = s; gr.appendChild(s);
      } else { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = cell.text; gr.appendChild(d); }
    }
    w.appendChild(gr); w.appendChild(brk("r")); return w;
  }
  const slots = [];
  for (let r = 0; r < R; r++) slots.push({ r: r, val: null, el: null });
  const aCells = A.map((row) => row.map((v) => ({ text: fmt(v) })));
  const nCells = []; for (let r = 0; r < R; r++) nCells.push([{ text: fmt(numCol[r]) }, { text: sym[r] }]);
  const rCells = []; for (let r = 0; r < R; r++) rCells.push([{ text: fmt(numRes[r]) }, { input: true, idx: r }]);
  const eq = document.createElement("div"); eq.className = "ts-mc-eq ts-mm-eq";
  eq.appendChild(matNode(aCells)); eq.appendChild(matNode(nCells)); eq.appendChild(opSpan("=")); eq.appendChild(matNode(rCells));
  stage.appendChild(eq); mountEl.appendChild(stage);
  const pts = [[2, 3], [5, -1], [-3, 4], [4, 7]];
  function correct() { return slots.every((s) => { const f = compile(s.val); if (!f) return false; return pts.every(([x, y]) => Math.abs(f(x, y) - trueExpr(s.r, x, y)) < 1e-9); }); }
  const allVals = () => slots.map((s) => s.val);
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; if (s.el) { s.el.value = s.val || ""; s.el.classList.toggle("is-filled", s.val != null); } }); },
    getState: () => ({ value: allVals(), target: (spec.answers || slots.map(() => "")).slice() }),
    reset: () => { slots.forEach((s) => { s.val = null; if (s.el) { s.el.value = ""; s.el.classList.remove("is-filled"); } }); },
    reveal: (forced) => { locked = true; const av = forced || spec.answers || slots.map(() => ""); slots.forEach((s, i) => { s.val = av[i]; if (s.el) { s.el.value = av[i]; s.el.classList.add("is-answer"); } }); }
  };
}

// ── Token-matrix TILE fill (L13.4 "The Identity Matrix"): renders a product/equation of
// matrices whose entries are TOKENS (strings — numeric OR symbolic). ONE matrix has blank
// cells the student fills from a tile tray (click tile → first-empty; click a filled cell
// to clear). Covers the symbolic identity fills I·[[a,b],[c,d]] = [[a,b],[c,d]] (tiles
// a,b,c,d) AND the numeric "build the identity" fills [blank]·B = B where the blank operand
// = the identity from {0,1} tiles. Graded by string equality per blank cell against its
// authored token — a genuine tile-fill of the matrix figure, never a pick/MCQ.
//   spec { mats:[[["1","0"],["0","1"]], [["a","b"],["c","d"]], [["a","b"],["c","d"]]],
//          blank:{mat:2, cells:[[r,c]...]}, tiles:["a","b","c","d"], ops?:["·","="] }
// blank.cells omitted → all cells of blank.mat are blank. Slots collected row-major.
function buildMatTile(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mm";
  const mats = (spec.mats || [[["1", "0"], ["0", "1"]]]).map((m) => m.map((row) => row.map((v) => String(v))));
  const blankMat = spec.blank && spec.blank.mat != null ? spec.blank.mat : mats.length - 1;
  const blankCells = (spec.blank && spec.blank.cells) || null;   // null → all cells of blankMat
  const norm = (s) => s == null ? "" : String(s).replace(/-/g, "−").replace(/\s/g, "");
  const disp = (s) => String(s).replace(/-/g, "−");
  const isBlank = (mi, r, c) => mi === blankMat && (!blankCells || blankCells.some((bb) => bb[0] === r && bb[1] === c));
  const ops = spec.ops || mats.map((_, i) => i === 0 ? null : (i === mats.length - 1 ? "=" : "·")).filter((x) => x != null);
  const slots = [];   // {el, mi, r, c, val, target}
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op ts-mo-op"; s.textContent = t; return s; }
  function matNode(mat, mi) {
    const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("l"));
    const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + mat[0].length + ", 1fr)";
    for (let r = 0; r < mat.length; r++) for (let c = 0; c < mat[0].length; c++) {
      if (!isBlank(mi, r, c)) { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = disp(mat[r][c]); gr.appendChild(d); continue; }
      const idx = slots.length; const s = document.createElement("div"); s.className = "ts-vt-slot ts-mc-cell ts-mc-slot is-empty";
      s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
      slots.push({ el: s, mi: mi, r: r, c: c, val: null, target: mat[r][c] }); gr.appendChild(s);
    }
    w.appendChild(gr); w.appendChild(brk("r")); return w;
  }
  const eq = document.createElement("div"); eq.className = "ts-mc-eq ts-mm-eq";
  for (let i = 0; i < mats.length; i++) { if (i > 0) eq.appendChild(opSpan(ops[i - 1])); eq.appendChild(matNode(mats[i], i)); }
  stage.appendChild(eq);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = disp(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || ["0", "1"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = disp(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.every((s) => s.val != null && norm(s.val) === norm(s.target)); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => s.target) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const av = forced || slots.map((s) => String(s.target)); slots.forEach((s, i) => (s.val = av[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Sizing a matrix product (L13.3 "Sizing and Multiplying"): given A (m×n) and B
// (n×p) shown as empty-cell matrices with dimension labels, RESIZE the result grid to
// the correct m×p. Drag the right-edge handle to change COLUMNS, the bottom-edge handle
// to change ROWS; the live "R × C" label (blue) updates. Graded rows===m && cols===p.
// A real resize interaction (never a dimensions MCQ). spec { left:[lr,lc], right:[rr,rc],
// start?:[r,c], max?:n }.
// Modes (which matrix/matrices in [A][B] = [C] are RESIZED by dragging handles):
//   default / "result"   : {left:[m,n], right:[n,p]}          → resize C to m×p (A,B given).
//   "middle"             : {mode:"middle", left:[m,n], result:[m,p]} → resize B to n×p (A,C given).
//   "operands"           : {mode:"operands", result:[m,p], aCols, bCols} → resize BOTH A (m×aCols, cols locked)
//                          and B (aCols×bCols, cols locked) so A·B = C (C given). Only rows are draggable.
function buildMatSize(mountEl, spec, cbs) {
  let locked = false, touched = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-sz";
  const MAXR = spec.max || 5, MAXC = spec.max || 5, PITCH = 31;   // cell 26 + gap 5
  const mode = spec.mode || "result";
  const parts = [];   // three positions A,B,C — each {fixed, r,c} OR {fixed:false, ansR,ansC, lockC, curR,curC, host, lab, matEl}
  const fixedPart = (r, c) => ({ fixed: true, r: r, c: c });
  const rezPart = (ansR, ansC, lockC) => ({ fixed: false, ansR: ansR, ansC: ansC, lockC: !!lockC, curR: 1, curC: 1 });
  if (mode === "middle") {
    const A = spec.left || [2, 2], C = spec.result || [2, 3];
    parts.push(fixedPart(A[0], A[1]), rezPart(A[1], C[1], false), fixedPart(C[0], C[1]));
  } else if (mode === "operands") {
    const C = spec.result || [3, 2], aCols = spec.aCols || 1, bCols = spec.bCols || C[1];
    parts.push(rezPart(C[0], aCols, true), rezPart(aCols, bCols, true), fixedPart(C[0], C[1]));
  } else {
    const A = spec.left || [1, 3], B = spec.right || [3, 2];
    parts.push(fixedPart(A[0], A[1]), fixedPart(B[0], B[1]), rezPart(A[0], B[1], false));
  }
  // choose a start size for each resizable part that differs from its answer (respecting a locked column)
  parts.forEach((p) => { if (p.fixed) return;
    let r = Math.min(MAXR, p.ansR === 4 ? 2 : 4);
    let c = p.lockC ? p.ansC : Math.min(MAXC, p.ansC === 3 ? 1 : 3);
    if (r === p.ansR) r = Math.max(1, p.ansR === 1 ? 3 : 1);
    if (!p.lockC && c === p.ansC) c = Math.max(1, p.ansC === 1 ? 3 : 1);
    p.curR = r; p.curC = c;
  });
  const rez = parts.filter((p) => !p.fixed);
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  function emptyMat(r, c, cls) { const w = document.createElement("span"); w.className = "ts-sz-mat" + (cls ? " " + cls : ""); w.appendChild(brk("l")); const gr = document.createElement("div"); gr.className = "ts-sz-grid"; gr.style.gridTemplateColumns = "repeat(" + c + ", 26px)"; gr.style.gridTemplateRows = "repeat(" + r + ", 26px)"; for (let i = 0; i < r * c; i++) { const d = document.createElement("div"); d.className = "ts-sz-cell"; gr.appendChild(d); } w.appendChild(gr); w.appendChild(brk("r")); return { w: w, gr: gr }; }
  function col(dim, live) { const c = document.createElement("div"); c.className = "ts-sz-col"; const lab = document.createElement("div"); lab.className = "ts-sz-dim" + (live ? " is-live" : ""); lab.textContent = dim; c.appendChild(lab); return { col: c, lab: lab }; }
  const wrap = document.createElement("div"); wrap.className = "ts-sz-wrap";
  parts.forEach((p, i) => {
    if (i === parts.length - 1) { const op = document.createElement("div"); op.className = "ts-sz-op"; op.textContent = "="; wrap.appendChild(op); }
    const c = col(p.fixed ? (p.r + "×" + p.c) : (p.curR + " × " + p.curC), !p.fixed);
    p.col = c.col; p.lab = c.lab;
    if (p.fixed) { c.col.appendChild(emptyMat(p.r, p.c).w); }
    else { p.host = document.createElement("div"); p.host.className = "ts-sz-reshost"; c.col.appendChild(p.host); }
    wrap.appendChild(c.col);
  });
  stage.appendChild(wrap); mountEl.appendChild(stage);
  function setSize(p, r, c) { p.curR = Math.max(1, Math.min(MAXR, r)); p.curC = p.lockC ? p.ansC : Math.max(1, Math.min(MAXC, c)); }
  function renderRes(p) {
    if (p.matEl) p.matEl.remove();
    const em = emptyMat(p.curR, p.curC, "ts-sz-result"); p.matEl = em.w;
    if (!p.lockC) { const hR = document.createElement("div"); hR.className = "ts-sz-handle ts-sz-h-r"; p.matEl.appendChild(hR); dragify(p, hR, "c"); }
    const hB = document.createElement("div"); hB.className = "ts-sz-handle ts-sz-h-b"; p.matEl.appendChild(hB); dragify(p, hB, "r");
    p.host.appendChild(p.matEl);
    p.lab.textContent = p.curR + " × " + p.curC;
  }
  function interact(p, r, c) { setSize(p, r, c); touched = true; renderRes(p); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); }
  function dragify(p, handle, axis) {
    handle.addEventListener("pointerdown", (e) => {
      if (locked) return; e.preventDefault();
      const startX = e.clientX, startY = e.clientY, startR = p.curR, startC = p.curC;
      function mv(ev) { if (axis === "c") { const dc = Math.round((ev.clientX - startX) / PITCH); interact(p, startR, startC + dc); } else { const dr = Math.round((ev.clientY - startY) / PITCH); interact(p, startR + dr, startC); } }
      function up() { document.removeEventListener("pointermove", mv); document.removeEventListener("pointerup", up); }
      document.addEventListener("pointermove", mv); document.addEventListener("pointerup", up);
    });
  }
  rez.forEach((p) => renderRes(p));
  return {
    isComplete: () => touched,
    check: () => ({ correct: rez.every((p) => p.curR === p.ansR && p.curC === p.ansC), value: rez.flatMap((p) => [p.curR, p.curC]) }),
    getValue: () => rez.flatMap((p) => [p.curR, p.curC]),
    setValue: (v) => { if (!Array.isArray(v)) return; rez.forEach((p, i) => { setSize(p, v[2 * i], v[2 * i + 1]); renderRes(p); }); touched = true; },
    getState: () => ({ value: rez.flatMap((p) => [p.curR, p.curC]), target: rez.flatMap((p) => [p.ansR, p.ansC]) }),
    reset: () => { touched = false; },
    reveal: () => { locked = true; rez.forEach((p) => { setSize(p, p.ansR, p.ansC); renderRes(p); p.matEl.classList.add("is-answer"); }); }
  };
}

// ── Determinants — transformed areas (L15): the determinant of T is the signed area
// of the parallelogram spanned by its columns (= the area of the transformed unit
// square). Draws the original unit square (faint dashed) + the transformed parallelogram
// (shaded orange) with vertices O, col0, col0+col1, col1, and asks det(T)=ad−bc via a
// typed input (or tile palette). Handles det<0 (orientation flip) and det=0 (collapsed
// to a segment). spec { matrix:[[a,b],[c,d]], graph?, tiles?, typed? }.
function buildDetArea(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-da";
  const M = (spec.matrix || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const a = M[0][0], b = M[0][1], c = M[1][0], d = M[1][1];
  const ans = spec.abs === true ? Math.abs(a * d - b * c) : (a * d - b * c);   // signed det, or |det| = area when spec.abs (L15.6 area problem)
  const typed = spec.typed !== false;
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  const disp = (s) => String(s).replace(/-/g, "−");   // keep raw string (fractions like "1/2") with unicode minus
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  if (spec.graph !== false) {   // GRAPH mode: draw the transformed unit square as a shaded parallelogram (skip for pure-arithmetic det problems)
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 320, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 320) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    const col0 = [a, c], col1 = [b, d];
    const pathOf = (verts) => { let s = ""; verts.forEach((p, i) => { s += (i ? " L " : "M ") + g.X(p[0]).toFixed(1) + " " + g.Y(p[1]).toFixed(1); }); return s + " Z"; };
    const par = [[0, 0], col0, [col0[0] + col1[0], col0[1] + col1[1]], col1];
    g.mkIn("path", { d: pathOf(par), fill: "rgba(245,160,74,0.13)", stroke: "#e0913f", "stroke-width": 2.2, "stroke-linejoin": "round", "stroke-dasharray": "6 4" });   // transformed parallelogram (dashed image)
    g.mkIn("path", { d: pathOf([[0, 0], [1, 0], [1, 1], [0, 1]]), fill: "rgba(245,160,74,0.38)", stroke: "#e0913f", "stroke-width": 1.8 });   // original unit square (solid)
    const arrow = (to, col) => { const line = g.mkIn("line", { stroke: col, "stroke-width": 2.4, "stroke-linecap": "round" }); const head = g.mkIn("path", { fill: col }); const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(to[0]), y2 = g.Y(to[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const an = Math.atan2(y2 - y1, x2 - x1), hl = 8, hw = 5, bx = x2 - hl * Math.cos(an), by = y2 - hl * Math.sin(an); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(an)).toFixed(1) + " " + (by + hw * Math.cos(an)).toFixed(1) + " L " + (bx + hw * Math.sin(an)).toFixed(1) + " " + (by - hw * Math.cos(an)).toFixed(1) + " Z"); };
    arrow(col0, "#f5a04a"); arrow(col1, "#279b8d");   // column vectors
    stage.appendChild(g.el);
  }
  // ---- det( [matrix] ) = [input] ----  (or |det( [matrix] )| when spec.abs — area problem)
  const eq = document.createElement("div"); eq.className = "ts-da-eq";
  if (spec.abs === true) { const b0 = document.createElement("span"); b0.className = "ts-da-det ts-da-bar"; b0.textContent = "|"; eq.appendChild(b0); }
  const lab = document.createElement("span"); lab.className = "ts-da-det"; lab.textContent = "det"; eq.appendChild(lab);
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  const mat = document.createElement("span"); mat.className = "ts-mc-mat"; mat.appendChild(brk("l"));
  const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(2, 1fr)";
  for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) { const cell = document.createElement("div"); cell.className = "ts-mc-cell ts-mc-static"; cell.textContent = fmt(M[r][cc]); gr.appendChild(cell); }
  mat.appendChild(gr); mat.appendChild(brk("r")); eq.appendChild(mat);
  if (spec.abs === true) { const b1 = document.createElement("span"); b1.className = "ts-da-det ts-da-bar"; b1.textContent = "|"; eq.appendChild(b1); }
  const eqs = document.createElement("span"); eqs.className = "ts-vt-op ts-mo-op"; eqs.textContent = "="; eq.appendChild(eqs);
  let val = null;
  let inputEl = null, slot = null;
  if (typed) {
    inputEl = document.createElement("input"); inputEl.type = "text"; inputEl.inputMode = "numeric"; inputEl.className = "ts-sf-input ts-da-input"; inputEl.autocomplete = "off";
    inputEl.addEventListener("input", () => { if (locked) return; val = inputEl.value.trim() || null; inputEl.classList.toggle("is-filled", val != null); if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && cbs && cbs.onComplete) cbs.onComplete(); });
    eq.appendChild(inputEl);
  } else {
    slot = document.createElement("div"); slot.className = "ts-vt-slot ts-da-slot is-empty";
    slot.addEventListener("click", () => { if (locked) return; if (val != null) { val = null; renderSlot(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
    eq.appendChild(slot);
  }
  stage.appendChild(eq);
  function renderSlot() { if (!slot) return; if (val == null) { slot.textContent = ""; slot.classList.add("is-empty"); slot.classList.remove("is-filled"); } else { slot.textContent = disp(val); slot.classList.remove("is-empty"); slot.classList.add("is-filled"); } }
  if (!typed) {
    const pal = document.createElement("div"); pal.className = "ts-vt-pal";
    (spec.tiles || ["−4", "−2", "−1", "0", "1", "2", "4", "6"]).forEach((tv) => {
      const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = disp(tv);
      t.addEventListener("click", () => { if (locked) return; val = String(tv); renderSlot(); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); });
      pal.appendChild(t);
    });
    stage.appendChild(pal);
  }
  mountEl.appendChild(stage);
  return {
    isComplete: () => val != null,
    check: () => ({ correct: val != null && parseTile(val) === ans, value: val }),
    getValue: () => val,
    setValue: (v) => { val = v != null ? String(v) : null; if (inputEl) { inputEl.value = val != null ? val : ""; inputEl.classList.toggle("is-filled", val != null); } else renderSlot(); },
    getState: () => ({ value: val, target: ans }),
    reset: () => { val = null; if (inputEl) inputEl.value = ""; else renderSlot(); },
    reveal: () => { locked = true; val = String(ans); if (inputEl) { inputEl.value = val; inputEl.classList.add("is-answer"); } else { renderSlot(); slot.classList.add("is-answer"); } }
  };
}

// ── Triangular determinant COMPLETE / SOLVE (L15.2): two related fill interactions.
//  (a) COMPLETE mode (spec.det == null): "complete the matrix that transforms the unit
//      square to the shown parallelogram." The figure (unit square → transformed
//      parallelogram, exactly like buildDetArea) is drawn from the TRUE matrix; some cells
//      are pre-given, the blanks are tile/keypad-filled and graded per-cell === true.
//  (b) SOLVE mode (spec.det != null): "fill the missing value so det = target." Shows
//      "det( [matrix, blank] ) = target" (no figure by default); the single blank is graded
//      by the RESULTING determinant === target (so a plausible wrong value fails, rule #4).
//  spec { matrix:[[a,b],[c,d]] (TRUE values), blanks:[[r,c],...], det?:number, graph?, tiles?, typed? }
function buildDetComplete(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-da ts-dc";
  // parse matrix entries by VALUE (so fractional statics like "3/2" compute correctly) but keep
  // the original string for display (so "3/2" shows as 3/2, not 1.5).
  const M = spec.matrix.map((r) => r.map((x) => parseTile(String(x))));
  const Mdisp = spec.matrix.map((r) => r.map((x) => String(x).replace(/-/g, "−")));
  const a = M[0][0], b = M[0][1], c = M[1][0], d = M[1][1];
  const blanks = (spec.blanks && spec.blanks.length) ? spec.blanks : [[1, 1]];
  const isBlank = (r, cc) => blanks.some((bb) => bb[0] === r && bb[1] === cc);
  const solveMode = spec.det != null;
  const targetDet = solveMode ? Number(spec.det) : null;
  const typed = spec.typed === true;
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  // graph: default ON in complete mode, OFF in solve mode (unless explicitly supplied)
  const wantGraph = solveMode ? (spec.graph && spec.graph !== false) : (spec.graph !== false);
  if (wantGraph) {
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 320, grid: { step: 1 } }, (spec.graph === true ? {} : spec.graph));
    const plotW = (gspec.w || 320) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    const col0 = [a, c], col1 = [b, d];
    const pathOf = (vs) => { let s = ""; vs.forEach((p, i) => { s += (i ? " L " : "M ") + g.X(p[0]).toFixed(1) + " " + g.Y(p[1]).toFixed(1); }); return s + " Z"; };
    const par = [[0, 0], col0, [col0[0] + col1[0], col0[1] + col1[1]], col1];
    g.mkIn("path", { d: pathOf(par), fill: "rgba(245,160,74,0.13)", stroke: "#e0913f", "stroke-width": 2.2, "stroke-linejoin": "round", "stroke-dasharray": "6 4" });   // transformed parallelogram (dashed image)
    g.mkIn("path", { d: pathOf([[0, 0], [1, 0], [1, 1], [0, 1]]), fill: "rgba(245,160,74,0.38)", stroke: "#e0913f", "stroke-width": 1.8 });   // original unit square (solid)
    const arrow = (to, col) => { const line = g.mkIn("line", { stroke: col, "stroke-width": 2.4, "stroke-linecap": "round" }); const head = g.mkIn("path", { fill: col }); const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(to[0]), y2 = g.Y(to[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const an = Math.atan2(y2 - y1, x2 - x1), hl = 8, hw = 5, bx = x2 - hl * Math.cos(an), by = y2 - hl * Math.sin(an); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(an)).toFixed(1) + " " + (by + hw * Math.cos(an)).toFixed(1) + " L " + (bx + hw * Math.sin(an)).toFixed(1) + " " + (by - hw * Math.cos(an)).toFixed(1) + " Z"); };
    arrow(col0, "#f5a04a"); arrow(col1, "#279b8d");   // column vectors
    stage.appendChild(g.el);
  }
  const eq = document.createElement("div"); eq.className = "ts-da-eq";
  if (solveMode) { const lab = document.createElement("span"); lab.className = "ts-da-det"; lab.textContent = "det"; eq.appendChild(lab); }
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  const mat = document.createElement("span"); mat.className = "ts-mc-mat"; mat.appendChild(brk("l"));
  const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(2, 1fr)";
  const slots = [];   // {el, r, c, val, input}
  for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) {
    if (isBlank(r, cc)) {
      const idx = slots.length;
      if (typed) { const s = document.createElement("input"); s.type = "text"; s.inputMode = "numeric"; s.className = "ts-sf-input ts-da-input"; s.autocomplete = "off"; s.addEventListener("input", () => { if (locked) return; slots[idx].val = s.value.trim() || null; s.classList.toggle("is-filled", slots[idx].val != null); if (cbs && cbs.onInteract) cbs.onInteract(); done(); }); slots.push({ el: s, r: r, c: cc, val: null, input: true }); gr.appendChild(s); }
      else { const s = document.createElement("div"); s.className = "ts-vt-slot ts-mc-cell ts-mc-slot is-empty"; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, r: r, c: cc, val: null }); gr.appendChild(s); }
    } else { const dd = document.createElement("div"); dd.className = "ts-mc-cell ts-mc-static"; dd.textContent = Mdisp[r][cc]; gr.appendChild(dd); }
  }
  mat.appendChild(gr); mat.appendChild(brk("r")); eq.appendChild(mat);
  if (solveMode) { const eqs = document.createElement("span"); eqs.className = "ts-vt-op ts-mo-op"; eqs.textContent = "="; eq.appendChild(eqs); const tv = document.createElement("span"); tv.className = "ts-mo-op"; tv.style.cssText = "font-weight:600"; tv.textContent = fmt(targetDet); eq.appendChild(tv); }
  stage.appendChild(eq);
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.input) { s.el.value = s.val != null ? s.val : ""; s.el.classList.toggle("is-filled", s.val != null); return; } if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  if (!typed) { const pal = document.createElement("div"); pal.className = "ts-vt-pal"; (spec.tiles || ["−4", "−3", "−2", "−1", "0", "1", "2", "3", "4"]).forEach((tv) => { const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv)); t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } }); pal.appendChild(t); }); stage.appendChild(pal); }
  mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  const targetVals = () => slots.map((s) => M[s.r][s.c]);   // solved value for each blank (unique)
  function correct() {
    if (slots.some((s) => s.val == null)) return false;
    if (solveMode) { const mm = [[a, b], [c, d]]; slots.forEach((s) => { const v = parseTile(s.val); if (v != null) mm[s.r][s.c] = v; }); return Math.abs((mm[0][0] * mm[1][1] - mm[0][1] * mm[1][0]) - targetDet) < 1e-9; }
    return slots.every((s) => { const v = parseTile(s.val); return v != null && v === M[s.r][s.c]; });
  }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) arr = [arr]; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: targetVals() }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const aa = forced || targetVals().map(String); slots.forEach((s, i) => (s.val = aa[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Symbolic determinant TYPED answer (L15.3 "Determinant of any 2×2"): the matrix has
// SYMBOLIC entries (variable names "a","b","c","d", or numbers), and the learner TYPES the
// determinant as an algebraic expression via a symbol keypad (matches Brilliant's a/b/c/d/+/−/×
// keypad). Graded by NUMERIC SAMPLING: the entered expression must equal det = M00·M11 − M01·M10
// at several random (a,b,c,d) points (so "ad", "a·d", "da" all pass; "ad−bc" for a triangular
// matrix would fail because bc term ≠ 0 at the sample points). spec { matrix:[["a","b"],["0","d"]],
// answer:"ad" (a canonical correct expression, used for reveal + driver setValue), vars? }.
function buildDetSym(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-da ts-ds";
  const M = (spec.matrix || [["a", "b"], ["0", "d"]]).map((r) => r.map(String));
  const answer = spec.answer != null ? String(spec.answer) : "";
  let val = null, inputEl = null;
  function compile(str) {
    if (str == null) return null;
    let t = ("" + str).replace(/−/g, "-").replace(/×/g, "*").replace(/÷/g, "/").replace(/\s+/g, "");
    if (!t || !/^[-+*/().0-9abcd]+$/.test(t)) return null;
    t = t.replace(/([0-9a-d)])(?=[a-d(])/g, "$1*");   // implicit multiplication: ad → a*d, 2a → 2*a, )a → )*a
    try { const f = new Function("a", "b", "c", "d", "return (" + t + ");"); if (!Number.isFinite(f(1, 1, 1, 1))) return null; return f; } catch (e) { return null; }
  }
  const evEntry = (e, va) => { const s = String(e).trim(); return /^-?\d+$/.test(s) ? Number(s) : va[s]; };
  const trueDet = (a, b, c, d) => { const va = { a: a, b: b, c: c, d: d }; return evEntry(M[0][0], va) * evEntry(M[1][1], va) - evEntry(M[0][1], va) * evEntry(M[1][0], va); };
  const pts = [[2, 3, 5, 7], [3, 1, 4, 2], [5, 2, 1, 3], [4, 6, 2, 5]];
  function correct() { const f = compile(val); if (!f) return false; return pts.every(([a, b, c, d]) => Math.abs(f(a, b, c, d) - trueDet(a, b, c, d)) < 1e-9); }
  // ── det( [matrix] ) = [input]
  const eq = document.createElement("div"); eq.className = "ts-da-eq";
  const lab = document.createElement("span"); lab.className = "ts-da-det"; lab.textContent = "det"; eq.appendChild(lab);
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  const mat = document.createElement("span"); mat.className = "ts-mc-mat"; mat.appendChild(brk("l"));
  const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(2, 1fr)";
  for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = M[r][cc]; gr.appendChild(d); }
  mat.appendChild(gr); mat.appendChild(brk("r")); eq.appendChild(mat);
  const eqs = document.createElement("span"); eqs.className = "ts-vt-op ts-mo-op"; eqs.textContent = "="; eq.appendChild(eqs);
  inputEl = document.createElement("input"); inputEl.type = "text"; inputEl.className = "ts-sf-input ts-da-input"; inputEl.autocomplete = "off"; inputEl.setAttribute("placeholder", "ad");
  inputEl.addEventListener("input", () => { if (locked) return; val = inputEl.value.trim() || null; inputEl.classList.toggle("is-filled", val != null); if (cbs && cbs.onInteract) cbs.onInteract(); if (val != null && cbs && cbs.onComplete) cbs.onComplete(); });
  eq.appendChild(inputEl);
  stage.appendChild(eq); mountEl.appendChild(stage);
  return {
    isComplete: () => val != null,
    check: () => ({ correct: correct(), value: val }),
    getValue: () => val,
    setValue: (v) => { val = v != null ? String(v) : null; inputEl.value = val != null ? val : ""; inputEl.classList.toggle("is-filled", val != null); },
    getState: () => ({ value: val, target: answer }),
    reset: () => { val = null; inputEl.value = ""; inputEl.classList.remove("is-filled"); },
    reveal: () => { locked = true; val = answer; inputEl.value = answer; inputEl.classList.add("is-answer"); }
  };
}

// ── Determinant = 0 (L15.5): COMPLETE a matrix so det(M) = target (usually 0). One
// entry is a tile slot; pick the value that makes the columns parallel (det = 0), which
// collapses the transformed unit square onto a line. The grid shows the unit square + the
// fully-known column as an arrow along the dashed "collapse line" (the direction the blank
// column must match). Graded picked value === spec.answer. spec { matrix:[[a,b],[c,d]]
// (blank cell may be null), blank:[r,c], answer:v, tiles?, graph? }.
function buildDetFill(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-da";
  const M = spec.matrix.map((r) => r.map((x) => (x == null ? null : Number(x))));
  const blank = spec.blank || [1, 1];
  const answer = Number(spec.answer);
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  let val = null;
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 320, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 320) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    const known = [M[0][1 - blank[1]], M[1][1 - blank[1]]];   // the fully-known column (opposite the blank)
    const pathOf = (vs) => { let s = ""; vs.forEach((p, i) => { s += (i ? " L " : "M ") + g.X(p[0]).toFixed(1) + " " + g.Y(p[1]).toFixed(1); }); return s + " Z"; };
    const kx = known[0], ky = known[1];
    const line = g.mkIn("line", { stroke: "#e0913f", "stroke-width": 1.6, "stroke-dasharray": "6 4", "stroke-linecap": "round" });   // collapse line through origin
    line.setAttribute("x1", g.X(-kx)); line.setAttribute("y1", g.Y(-ky)); line.setAttribute("x2", g.X(kx)); line.setAttribute("y2", g.Y(ky));
    g.mkIn("path", { d: pathOf([[0, 0], [1, 0], [1, 1], [0, 1]]), fill: "rgba(245,160,74,0.38)", stroke: "#e0913f", "stroke-width": 1.8 });   // unit square
    const al = g.mkIn("line", { stroke: "#f5a04a", "stroke-width": 2.4, "stroke-linecap": "round" }); const ah = g.mkIn("path", { fill: "#f5a04a" });
    const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(kx), y2 = g.Y(ky); al.setAttribute("x1", x1); al.setAttribute("y1", y1); al.setAttribute("x2", x2); al.setAttribute("y2", y2);
    const an = Math.atan2(y2 - y1, x2 - x1), hl = 8, hw = 5, bx = x2 - hl * Math.cos(an), by = y2 - hl * Math.sin(an); ah.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(an)).toFixed(1) + " " + (by + hw * Math.cos(an)).toFixed(1) + " L " + (bx + hw * Math.sin(an)).toFixed(1) + " " + (by - hw * Math.cos(an)).toFixed(1) + " Z");
    stage.appendChild(g.el);
  }
  const eq = document.createElement("div"); eq.className = "ts-da-eq";
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  const mat = document.createElement("span"); mat.className = "ts-mc-mat"; mat.appendChild(brk("l"));
  const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(2, 1fr)";
  let slot = null;
  for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) {
    if (r === blank[0] && cc === blank[1]) { slot = document.createElement("div"); slot.className = "ts-vt-slot ts-mc-cell ts-mc-slot is-empty"; slot.addEventListener("click", () => { if (locked) return; if (val != null) { val = null; renderSlot(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); gr.appendChild(slot); }
    else { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = fmt(M[r][cc]); gr.appendChild(d); }
  }
  mat.appendChild(gr); mat.appendChild(brk("r")); eq.appendChild(mat);
  stage.appendChild(eq);
  function renderSlot() { if (val == null) { slot.textContent = ""; slot.classList.add("is-empty"); slot.classList.remove("is-filled"); } else { slot.textContent = fmt(parseTile(val)); slot.classList.remove("is-empty"); slot.classList.add("is-filled"); } }
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  (spec.tiles || ["−4", "−3", "−2", "−1", "0", "1", "2", "3", "4"]).forEach((tv) => { const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv)); t.addEventListener("click", () => { if (locked) return; val = String(tv); renderSlot(); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); }); pal.appendChild(t); });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlot();
  return {
    isComplete: () => val != null,
    check: () => ({ correct: val != null && parseTile(val) === answer, value: val }),
    getValue: () => val,
    setValue: (v) => { val = v != null ? String(v) : null; renderSlot(); },
    getState: () => ({ value: val, target: answer }),
    reset: () => { val = null; renderSlot(); },
    reveal: () => { locked = true; val = String(answer); renderSlot(); slot.classList.add("is-answer"); }
  };
}

// ── Inverse matrix (L16): A⁻¹ UNDOES A. The grid shows where A sends e₁,e₂ (solid arrows
// to A's columns) with dashed arrows returning them to the standard basis — the action of
// A⁻¹. Fill the 2×2 inverse from a tile palette that may include FRACTIONS; tiles/slots
// display the raw fraction string (not decimal), graded parseTile(slot)===parseTile(inv[r][c]).
// spec { a:[[..]], inv:[["1/3","0"],["0","1/4"]], tiles:[strings], graph? }.
function buildInvFill(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mg ts-iv";
  const A = (spec.a || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const inv = (spec.inv || [["1", "0"], ["0", "1"]]).map((r) => r.map(String));
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  const disp = (s) => String(s).replace(/-/g, "−");   // show raw string (keep fractions), unicode minus
  const invVal = inv.map((r) => r.map(parseTile));
  const COL = { orange: "#f5a04a", teal: "#279b8d", orangeF: "#f3c79a", tealF: "#9fd0c8" };
  if (spec.graph !== false) {
    const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 300, grid: { step: 1 } }, spec.graph);
    const plotW = (gspec.w || 300) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
    if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
    const g = lineGraphSVG(gspec);
    function arrow(to, col, dash) { const line = g.mkIn("line", Object.assign({ stroke: col, "stroke-width": dash ? 2.2 : 2.6, "stroke-linecap": "round" }, dash ? { "stroke-dasharray": "6 4" } : {})); const head = g.mkIn("path", { fill: col }); const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(to[0]), y2 = g.Y(to[1]); line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2); const an = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(an), by = y2 - hl * Math.sin(an); head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(an)).toFixed(1) + " " + (by + hw * Math.cos(an)).toFixed(1) + " L " + (bx + hw * Math.sin(an)).toFixed(1) + " " + (by - hw * Math.cos(an)).toFixed(1) + " Z"); }
    const ac0 = [A[0][0], A[1][0]], ac1 = [A[0][1], A[1][1]];   // A's columns = where A sends e₁,e₂
    arrow([1, 0], COL.orangeF, true); arrow([0, 1], COL.tealF, true);   // dashed: returned to the standard basis
    arrow(ac0, COL.orange, false); arrow(ac1, COL.teal, false);         // solid: A's transformed basis
    stage.appendChild(g.el);
  }
  const slots = [];
  const mat = document.createElement("div"); mat.className = "ts-mg-mat";
  const lb = document.createElement("span"); lb.className = "ts-vt-bracket ts-mg-brk"; lb.textContent = "["; mat.appendChild(lb);
  const grid = document.createElement("div"); grid.className = "ts-mg-grid";
  for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) {
    const s = document.createElement("div"); s.className = "ts-vt-slot ts-vt-coeffslot ts-mg-cell is-empty";
    const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
    slots.push({ el: s, r: r, c: cc, val: null }); grid.appendChild(s);
  }
  mat.appendChild(grid);
  const rb = document.createElement("span"); rb.className = "ts-vt-bracket ts-mg-brk"; rb.textContent = "]"; mat.appendChild(rb);
  if (spec.equation) {
    // EQUATION mode (L16.2 "Producing the Identity"): [fill A⁻¹]·[A] = [I]
    const staticMat = (m) => { const w = document.createElement("div"); w.className = "ts-mg-mat"; const l = document.createElement("span"); l.className = "ts-vt-bracket ts-mg-brk"; l.textContent = "["; w.appendChild(l); const gr = document.createElement("div"); gr.className = "ts-mg-grid"; for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) { const d = document.createElement("div"); d.className = "ts-mg-cell ts-mg-static"; d.textContent = disp(m[r][cc]); gr.appendChild(d); } w.appendChild(gr); const rr = document.createElement("span"); rr.className = "ts-vt-bracket ts-mg-brk"; rr.textContent = "]"; w.appendChild(rr); return w; };
    const opSpan = (t) => { const s = document.createElement("span"); s.className = "ts-vt-op ts-mo-op"; s.textContent = t; return s; };
    const eq = document.createElement("div"); eq.className = "ts-mc-eq ts-iv-eq";
    eq.appendChild(mat); eq.appendChild(staticMat(A)); eq.appendChild(opSpan("=")); eq.appendChild(staticMat([[1, 0], [0, 1]]));
    stage.appendChild(eq);
  } else {
    stage.appendChild(mat);
  }
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = disp(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || ["0", "1", "1/2", "1/3", "1/4", "−1", "2", "3"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = disp(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() { const norm = (x) => String(x).replace(/−/g, "-").replace(/\s/g, ""); return slots.every((s) => { if (s.val == null) return false; const wv = invVal[s.r][s.c], gv = parseTile(s.val); if (wv != null && gv != null) return Math.abs(gv - wv) < 1e-9; return norm(s.val) === norm(inv[s.r][s.c]); }); }   // numeric fractions compared by value; symbolic tokens (1/a) by normalized string (L16.1 P6)
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: slots.map((s) => inv[s.r][s.c]) }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: (forced) => { locked = true; const a = forced || slots.map((s) => inv[s.r][s.c]); slots.forEach((s, i) => (s.val = a[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Computing the inverse (L16.4): A⁻¹ = (1/det)·adjugate. Renders the formula
// (1/[det]) · [adj 2×2 slots] · [static A] = [static I]; the student fills the DETERMINANT
// and the four ADJUGATE entries (all integers: adj = [[d,−b],[−c,a]]). Graded det slot ===
// det(A) AND each adj slot === adjugate[r][c]. spec { a:[[..]], det:D, adj:[[d,−b],[−c,a]], tiles }.
function buildInvFormula(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mg ts-if";
  const A = spec.a.map((r) => r.map(Number));
  const detA = Number(spec.det);
  const adj = spec.adj.map((r) => r.map(String));
  const adjVal = adj.map((r) => r.map((x) => Number(String(x).replace(/−/g, "-"))));
  const disp = (s) => String(s).replace(/-/g, "−");
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  // detGiven mode (L16.4 "build the full inverse" beats): the scale factor 1/det is
  // PRE-FILLED (Brilliant shows 1/6 already), so only the 4 adjugate entries are graded.
  const detGiven = spec.detGiven === true;
  let detVal = detGiven ? String(detA) : null;
  const adjSlots = [];   // {el, r, c, val}
  function brk(t) { const s = document.createElement("span"); s.className = "ts-vt-bracket ts-mg-brk"; s.textContent = t; return s; }
  function opSpan(t) { const s = document.createElement("span"); s.className = "ts-vt-op ts-mo-op"; s.textContent = t; return s; }
  function staticMat(m) { const w = document.createElement("div"); w.className = "ts-mg-mat"; w.appendChild(brk("[")); const gr = document.createElement("div"); gr.className = "ts-mg-grid"; for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) { const d = document.createElement("div"); d.className = "ts-mg-cell ts-mg-static"; d.textContent = disp(m[r][cc]); gr.appendChild(d); } w.appendChild(gr); w.appendChild(brk("]")); return w; }
  const eq = document.createElement("div"); eq.className = "ts-mc-eq ts-if-eq";
  // 1/[det] fraction
  const frac = document.createElement("div"); frac.className = "ts-if-frac";
  const one = document.createElement("div"); one.className = "ts-if-num"; one.textContent = "1"; frac.appendChild(one);
  const bar = document.createElement("div"); bar.className = "ts-if-bar"; frac.appendChild(bar);
  const detSlot = document.createElement("div"); detSlot.className = "ts-vt-slot ts-if-detslot " + (detGiven ? "is-fixed" : "is-empty");
  if (!detGiven) detSlot.addEventListener("click", () => { if (locked) return; if (detVal != null) { detVal = null; renderAll(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
  frac.appendChild(detSlot); eq.appendChild(frac);
  // adjugate matrix (4 tile slots)
  const adjMat = document.createElement("div"); adjMat.className = "ts-mg-mat"; adjMat.appendChild(brk("["));
  const agr = document.createElement("div"); agr.className = "ts-mg-grid";
  for (let r = 0; r < 2; r++) for (let cc = 0; cc < 2; cc++) { const s = document.createElement("div"); s.className = "ts-vt-slot ts-vt-coeffslot ts-mg-cell is-empty"; const idx = adjSlots.length; s.addEventListener("click", () => { if (locked) return; if (adjSlots[idx].val != null) { adjSlots[idx].val = null; renderAll(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); adjSlots.push({ el: s, r: r, c: cc, val: null }); agr.appendChild(s); }
  adjMat.appendChild(agr); adjMat.appendChild(brk("]")); eq.appendChild(adjMat);
  eq.appendChild(staticMat(A)); eq.appendChild(opSpan("=")); eq.appendChild(staticMat([[1, 0], [0, 1]]));
  stage.appendChild(eq);
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { if (!detGiven && detVal == null) return "det"; for (let i = 0; i < adjSlots.length; i++) if (adjSlots[i].val == null) return i; return -1; }
  function renderAll() { if (detVal == null) { detSlot.textContent = ""; detSlot.classList.add("is-empty"); detSlot.classList.remove("is-filled"); } else { detSlot.textContent = disp(detVal); detSlot.classList.remove("is-empty"); detSlot.classList.add("is-filled"); } adjSlots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = disp(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function done() { if (firstEmpty() === -1 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || ["−4", "−3", "−2", "−1", "1", "2", "3", "4", "6"]).forEach((tv) => { const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = disp(tv); t.addEventListener("click", () => { if (locked) return; const w = firstEmpty(); if (w === "det") { detVal = String(tv); } else if (w >= 0) { adjSlots[w].val = String(tv); } else return; renderAll(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); }); pal.appendChild(t); });
  stage.appendChild(pal); mountEl.appendChild(stage); renderAll();
  const adjTargets = () => [adj[0][0], adj[0][1], adj[1][0], adj[1][1]];
  const allVals = () => detGiven ? adjSlots.map((s) => s.val) : [detVal, ...adjSlots.map((s) => s.val)];
  function correct() { if (!detGiven && (detVal == null || parseTile(detVal) !== detA)) return false; return adjSlots.every((s) => { if (s.val == null) return false; return parseTile(s.val) === adjVal[s.r][s.c]; }); }
  return {
    isComplete: () => adjSlots.every((s) => s.val != null) && (detGiven || detVal != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; if (detGiven) { adjSlots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); } else { detVal = arr[0] != null ? String(arr[0]) : null; adjSlots.forEach((s, i) => { s.val = arr[i + 1] != null ? String(arr[i + 1]) : null; }); } renderAll(); },
    getState: () => ({ value: allVals(), target: detGiven ? adjTargets() : [String(detA), adj[0][0], adj[0][1], adj[1][0], adj[1][1]] }),
    reset: () => { if (!detGiven) detVal = null; adjSlots.forEach((s) => (s.val = null)); renderAll(); },
    reveal: () => { locked = true; detVal = String(detA); adjSlots.forEach((s) => (s.val = adj[s.r][s.c])); renderAll(); if (!detGiven) detSlot.classList.add("is-answer"); adjSlots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── Symbolic derivation / equation-fill (L16.3 "Inverting a Matrix"): render a stack
// of algebra "lines" — most are static context, some carry tile-fill slots. Slots and
// static tokens can sit inside a fraction (numerator/denominator). Reproduces
// Brilliant's "Complete the equations" and step-by-step "solve for p/q/r/s"
// derivations 1:1 (numeric AND symbolic tokens like "a", "ad−bc", "−bd"). String-graded
// per slot (normalized), first-empty fill from a tile tray; reveal shows the true tokens.
// spec: { prompt?, tiles:[...], answers:["1","a",…],  // answers[i] = target of {slot:i}
//   lines:[ { cells:[ cell, … ] }, … ] }              // cell is one of:
//     "text"                       → static token span
//     { slot:i }                   → a fill slot (target = answers[i])
//     { frac:[numCell, denCell] }  → a fraction; numCell/denCell each "text" or {slot:i}
function buildSymDeriv(mountEl, spec, cbs) {
  let locked = false;
  const answers = (spec.answers || []).map(String);
  const vals = answers.map(() => null);
  const slotEls = new Array(answers.length).fill(null);
  const norm = (s) => s == null ? "" : String(s).replace(/-/g, "−").replace(/\s/g, "");
  const disp = (s) => String(s).replace(/-/g, "−");
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-sd";
  function mkText(t) { const s = document.createElement("span"); s.style.fontSize = "17px"; s.style.fontWeight = "500"; s.style.color = "#20242a"; s.style.margin = "0 2px"; s.textContent = disp(t); return s; }
  function mkSlot(i, small) {
    const s = document.createElement("div"); s.className = "ts-vt-slot is-empty";
    if (small) { s.style.width = "42px"; s.style.height = "34px"; s.style.fontSize = "16px"; }
    slotEls[i] = s;
    s.addEventListener("click", () => { if (locked) return; if (vals[i] != null) { vals[i] = null; render(); if (cbs && cbs.onInteract) cbs.onInteract(); } });
    return s;
  }
  function mkFracPart(cell) {
    if (cell && cell.slot != null) return mkSlot(cell.slot, true);
    const sp = document.createElement("span"); sp.style.fontSize = "15px"; sp.style.fontWeight = "600"; sp.style.color = "#20242a"; sp.style.lineHeight = "1"; sp.textContent = disp(cell); return sp;
  }
  function mkFrac(fr) {
    const w = document.createElement("div"); w.className = "ts-if-frac";
    w.appendChild(mkFracPart(fr[0]));
    const bar = document.createElement("div"); bar.className = "ts-if-bar"; w.appendChild(bar);
    w.appendChild(mkFracPart(fr[1]));
    return w;
  }
  function mkCell(cell) {
    if (typeof cell === "string") return mkText(cell);
    if (cell && cell.slot != null) return mkSlot(cell.slot, false);
    if (cell && cell.frac) return mkFrac(cell.frac);
    return mkText("");
  }
  (spec.lines || []).forEach((ln) => {
    const row = document.createElement("div"); row.className = "ts-mc-eq"; row.style.margin = "8px 0"; row.style.flexWrap = "wrap";
    (ln.cells || []).forEach((c) => row.appendChild(mkCell(c)));
    stage.appendChild(row);
  });
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  function firstEmpty() { for (let i = 0; i < vals.length; i++) if (vals[i] == null) return i; return -1; }
  function render() { for (let i = 0; i < vals.length; i++) { const el = slotEls[i]; if (!el) continue; if (vals[i] == null) { el.textContent = ""; el.classList.add("is-empty"); el.classList.remove("is-filled"); } else { el.textContent = disp(vals[i]); el.classList.remove("is-empty"); el.classList.add("is-filled"); } } }
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  (spec.tiles || ["0", "1"]).forEach((tv) => {
    const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = disp(tv);
    t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { vals[i] = String(tv); render(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } });
    pal.appendChild(t);
  });
  stage.appendChild(pal); mountEl.appendChild(stage); render();
  const allVals = () => vals.slice();
  function correct() { return vals.every((v, i) => v != null && norm(v) === norm(answers[i])); }
  return {
    isComplete: () => vals.every((v) => v != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; for (let i = 0; i < vals.length; i++) vals[i] = arr[i] != null ? String(arr[i]) : null; render(); },
    getState: () => ({ value: allVals(), target: answers.slice() }),
    reset: () => { for (let i = 0; i < vals.length; i++) vals[i] = null; render(); },
    reveal: () => { locked = true; for (let i = 0; i < vals.length; i++) vals[i] = answers[i]; render(); slotEls.forEach((el) => el && el.classList.add("is-answer")); }
  };
}

// ── Select-one MCQ over RENDERED options (L16.5 "Non-invertible Matrices"): each option is
// a 2×2 MATRIX (drawn with brackets) or a text label; the learner clicks one card. Optionally
// draws a collapse LINE through the origin above the options (the det-0 concept). Graded by
// selected index === answer. A faithful figure/visual MCQ (the options are the visual), never a
// bare-text proxy for a typed/drag interaction. spec:
//   { options:[ {mat:[[..]]} | {text:"…"} , … ], answer:<index>, collapse?:[dx,dy] }.
function buildMatPick(mountEl, spec, cbs) {
  let locked = false, sel = null;
  const answer = Number(spec.answer);
  const opts = spec.options || [];
  const disp = (s) => String(s).replace(/-/g, "−");
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-mp";
  if (Array.isArray(spec.collapse)) {   // optional collapse-line figure (line through origin along [dx,dy])
    const g = lineGraphSVG({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 300, grid: { step: 1 } });
    const dx = Number(spec.collapse[0]), dy = Number(spec.collapse[1]);
    const el = g.mkIn("line", { stroke: "#e0913f", "stroke-width": 2.6, "stroke-linecap": "round" });
    if (dx === 0) { el.setAttribute("x1", g.X(0)); el.setAttribute("y1", g.Y(g.ymin)); el.setAttribute("x2", g.X(0)); el.setAttribute("y2", g.Y(g.ymax)); }
    else { g.setLine(el, dy / dx, 0); }
    stage.appendChild(g.el);
  }
  function brk(side) { const s = document.createElement("span"); s.className = "ts-mm-brk ts-mm-brk-" + side; return s; }
  function matNode(m) { const w = document.createElement("span"); w.className = "ts-mc-mat"; w.appendChild(brk("l")); const gr = document.createElement("div"); gr.className = "ts-mc-grid"; gr.style.gridTemplateColumns = "repeat(" + m[0].length + ", 1fr)"; m.forEach((row) => row.forEach((v) => { const d = document.createElement("div"); d.className = "ts-mc-cell ts-mc-static"; d.textContent = disp(v); gr.appendChild(d); })); w.appendChild(gr); w.appendChild(brk("r")); return w; }
  const grid = document.createElement("div"); grid.className = "ts-mp-grid"; grid.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:12px;max-width:460px;margin:16px auto 0;";
  const cards = [];
  opts.forEach((o, i) => {
    const card = document.createElement("button"); card.type = "button"; card.className = "ts-mp-card";
    card.style.cssText = "display:flex;align-items:center;justify-content:center;min-height:76px;padding:10px 16px;border:1.5px solid #dfe3e9;border-radius:12px;background:#fff;cursor:pointer;font-size:18px;font-weight:600;color:#20242a;transition:border-color .12s,background .12s;";
    if (o.mat) card.appendChild(matNode(o.mat)); else { const t = document.createElement("span"); t.textContent = disp(o.text); card.appendChild(t); }
    card.addEventListener("click", () => { if (locked) return; sel = i; render(); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); });
    cards.push(card); grid.appendChild(card);
  });
  function render() { cards.forEach((c, i) => { c.style.borderColor = (i === sel) ? "#4f6ef7" : "#dfe3e9"; c.style.background = (i === sel) ? "#eef1fe" : "#fff"; }); }
  stage.appendChild(grid); mountEl.appendChild(stage); render();
  return {
    isComplete: () => sel != null,
    check: () => ({ correct: sel === answer, value: sel }),
    getValue: () => sel,
    setValue: (v) => { sel = (v == null || v === "") ? null : Number(v); render(); },
    getState: () => ({ value: sel, target: answer }),
    reset: () => { sel = null; render(); },
    reveal: () => { locked = true; sel = answer; render(); if (cards[answer]) { cards[answer].style.borderColor = "#4a9e5c"; cards[answer].style.background = "#eaf6ec"; } }
  };
}

// ── L17 Systems of Equations: lines in the plane (bespoke, per fidelity gate) ──
// A linear equation a·x + b·y = c is a LINE. Two modes, matching Brilliant's
// "Systems of Equations" lesson exactly:
//   plot  → DRAG two control points so each editable line matches its equation
//           (Brilliant "Plot the line x+y=3" / "Plot both lines"); graded when
//           every editable line's two control points both satisfy a·x+b·y=c and
//           are distinct.
//   solve → the lines are fixed; FILL the intersection point (x,y) from a tile
//           palette (Brilliant "What point (x,y) solves this pair?"); a live dot
//           lands at the entered point; graded when (x,y) is the intersection.
// spec: { mode?, lines:[{a,b,c,color,editable?,p1?,p2?}], answer:[x,y],
//         tiles?:[min,max], graph:{xmin,xmax,ymin,ymax} }.
function buildSysLines(mountEl, spec, cbs) {
  const COL = { blue: "#4f6ef7", orange: "#f5a04a", green: "#3aa76d", purple: "#8a5cf6" };
  const lines = (spec.lines || []).map((l) => ({
    a: Number(l.a), b: Number(l.b), c: Number(l.c),
    color: l.color || "blue", editable: !!l.editable,
    p1: (l.p1 || [0, 0]).map(Number), p2: (l.p2 || [2, 0]).map(Number)
  }));
  const mode = spec.mode || (lines.some((l) => l.editable) ? "plot" : "solve");
  const answer = (spec.answer || [0, 0]).map(Number);
  let locked = false, moved = false;
  let renderSlots = function () { };
  const stage = document.createElement("div");
  stage.className = "ts-fw-stage ts-sl";
  const fmtN = (n) => String(n).replace(/-/, "−");
  function eqStr(a, b, c) {
    const co = (k) => Math.abs(k) === 1 ? "" : String(Math.abs(k));
    let s = "";
    if (a !== 0) s += (a < 0 ? "−" : "") + co(a) + "x";
    if (b !== 0) { if (s === "") s += (b < 0 ? "−" : "") + co(b) + "y"; else s += (b < 0 ? " − " : " + ") + co(b) + "y"; }
    if (s === "") s += "0";
    return s + " = " + fmtN(c);
  }
  // equation legend (colored slash + equation, like Brilliant)
  const NS = "http://www.w3.org/2000/svg";
  const legend = document.createElement("div"); legend.className = "ts-sl-legend";
  lines.forEach((l) => {
    const row = document.createElement("div"); row.className = "ts-sl-row";
    const ic = document.createElementNS(NS, "svg"); ic.setAttribute("viewBox", "0 0 18 18"); ic.setAttribute("class", "ts-sl-slash");
    const ln = document.createElementNS(NS, "line"); ln.setAttribute("x1", 3); ln.setAttribute("y1", 15); ln.setAttribute("x2", 15); ln.setAttribute("y2", 3); ln.setAttribute("stroke", COL[l.color] || COL.blue); ln.setAttribute("stroke-width", 2.6); ln.setAttribute("stroke-linecap", "round"); ic.appendChild(ln);
    const eq = document.createElement("span"); eq.className = "ts-sl-eq"; eq.textContent = eqStr(l.a, l.b, l.c);
    row.appendChild(ic); row.appendChild(eq); legend.appendChild(row);
  });
  stage.appendChild(legend);
  // graph
  const gspec = Object.assign({ grid: { step: 1 }, xmin: -5, xmax: 5, ymin: -5, ymax: 5, w: 340 }, spec.graph || {});
  const xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
  if (!gspec.h) gspec.h = Math.round(40 + (gspec.w - 68) * (yr / xr));
  const g = lineGraphSVG(gspec);
  function setEq(el, a, b, c) {
    if (b === 0) { if (a === 0) { el.setAttribute("visibility", "hidden"); return; } const xv = c / a; el.removeAttribute("visibility"); el.setAttribute("x1", g.X(xv)); el.setAttribute("y1", g.Y(g.ymin)); el.setAttribute("x2", g.X(xv)); el.setAttribute("y2", g.Y(g.ymax)); }
    else g.setLine(el, -a / b, c / b);
  }
  function setThrough(el, P, Q) {
    if (P[0] === Q[0] && P[1] === Q[1]) { el.setAttribute("visibility", "hidden"); return; }
    if (P[0] === Q[0]) { el.removeAttribute("visibility"); el.setAttribute("x1", g.X(P[0])); el.setAttribute("y1", g.Y(g.ymin)); el.setAttribute("x2", g.X(P[0])); el.setAttribute("y2", g.Y(g.ymax)); return; }
    el.removeAttribute("visibility");
    const m = (Q[1] - P[1]) / (Q[0] - P[0]), b0 = P[1] - m * P[0];
    g.setLine(el, m, b0);
  }
  function evtXY(ev) {
    const r = g.el.getBoundingClientRect(), sx = r.width / g.W, sy = r.height / g.H;
    const px = (ev.clientX - r.left) / sx, py = (ev.clientY - r.top) / sy;
    const gx = g.xmin + (px - g.X(g.xmin)) / (g.X(g.xmax) - g.X(g.xmin)) * (g.xmax - g.xmin);
    const gy = g.ymin + (g.Y(g.ymin) - py) / (g.Y(g.ymin) - g.Y(g.ymax)) * (g.ymax - g.ymin);
    return [Math.max(g.xmin, Math.min(g.xmax, Math.round(gx))), Math.max(g.ymin, Math.min(g.ymax, Math.round(gy)))];
  }
  // fixed lines
  lines.filter((l) => !l.editable).forEach((l) => { const el = g.mkIn("line", { stroke: COL[l.color] || COL.blue, "stroke-width": 2.6, "stroke-linecap": "round" }); setEq(el, l.a, l.b, l.c); });
  // editable lines (draggable control points)
  const editables = lines.filter((l) => l.editable);
  const handles = [];
  function redrawH(H) {
    setThrough(H.lineEl, H.pts[0], H.pts[1]);
    H.pts.forEach((p, i) => { H.rings[i].setAttribute("cx", g.X(p[0])); H.rings[i].setAttribute("cy", g.Y(p[1])); H.knobs[i].setAttribute("cx", g.X(p[0])); H.knobs[i].setAttribute("cy", g.Y(p[1])); });
  }
  editables.forEach((l) => {
    const col = COL[l.color] || COL.blue;
    const lineEl = g.mkIn("line", { stroke: col, "stroke-width": 2.6, "stroke-linecap": "round" });
    const H = { lineEl, l, pts: [l.p1.slice(), l.p2.slice()], rings: [], knobs: [], _drag: -1 };
    H.pts.forEach(() => { H.rings.push(g.mkIn("circle", { r: 12, fill: "#fff", "fill-opacity": 0.85, stroke: col, "stroke-width": 1.5, class: "ts-gt-handle" })); H.knobs.push(g.mkIn("circle", { r: 5.5, fill: col, class: "ts-gt-handle" })); });
    handles.push(H); redrawH(H);
    H.pts.forEach((_, i) => {
      [H.rings[i], H.knobs[i]].forEach((h) => {
        h.addEventListener("pointerdown", (ev) => { if (locked) return; h.setPointerCapture && h.setPointerCapture(ev.pointerId); ev.preventDefault(); moved = true; H._drag = i; if (cbs && cbs.onInteract) cbs.onInteract(); });
        h.addEventListener("pointermove", (ev) => { if (locked || H._drag !== i) return; H.pts[i] = evtXY(ev); redrawH(H); });
        h.addEventListener("pointerup", () => { if (H._drag === i) { H._drag = -1; if (cbs && cbs.onComplete) cbs.onComplete(); } });
        h.addEventListener("pointercancel", () => { H._drag = -1; });
      });
    });
  });
  // solve-mode intersection dot
  const solDot = mode === "solve" ? g.mkIn("circle", { r: 5.5, fill: COL.blue, visibility: "hidden" }) : null;
  stage.appendChild(g.el);
  // solve-mode (x, y) tile fill
  const slots = [];
  const allVals = () => slots.map((s) => s.val);
  if (mode === "solve") {
    const brk = document.createElement("div"); brk.className = "ts-sl-point";
    const paren = (t) => { const s = document.createElement("span"); s.className = "ts-sl-paren"; s.textContent = t; return s; };
    const makeSlot = () => { const s = document.createElement("div"); s.className = "ts-vt-slot is-empty"; const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s; };
    brk.appendChild(paren("(")); brk.appendChild(makeSlot()); brk.appendChild(paren(",")); brk.appendChild(makeSlot()); brk.appendChild(paren(")"));
    stage.appendChild(brk);
    let lo = -4, hi = 4; if (Array.isArray(spec.tiles) && spec.tiles.length === 2) { lo = +spec.tiles[0]; hi = +spec.tiles[1]; }
    const firstEmpty = () => { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; };
    renderSlots = function () {
      slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmtN(s.val); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } });
      if (slots.length === 2 && slots.every((s) => s.val != null) && solDot) { solDot.setAttribute("cx", g.X(slots[0].val)); solDot.setAttribute("cy", g.Y(slots[1].val)); solDot.removeAttribute("visibility"); } else if (solDot) solDot.setAttribute("visibility", "hidden");
    };
    const pal = document.createElement("div"); pal.className = "ts-vt-pal";
    for (let v = lo; v <= hi; v++) {
      const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmtN(v);
      t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = v; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); } });
      pal.appendChild(t);
    }
    stage.appendChild(pal); renderSlots();
  }
  mountEl.appendChild(stage);
  function twoPointsOn(a, b, c) {
    if (b !== 0) return [[g.xmin, (c - a * g.xmin) / b], [g.xmax, (c - a * g.xmax) / b]];
    if (a !== 0) { const x = c / a; return [[x, g.ymin], [x, g.ymax]]; }
    return null;
  }
  function plotCorrect() {
    return handles.length > 0 && handles.every((H) => {
      const [P, Q] = H.pts;
      if (P[0] === Q[0] && P[1] === Q[1]) return false;
      const on = (pt) => Math.abs(H.l.a * pt[0] + H.l.b * pt[1] - H.l.c) < 1e-6;
      return on(P) && on(Q);
    });
  }
  function solveCorrect() { const v = allVals(); return v.length === 2 && v.every((x, i) => x != null && Number(x) === answer[i]); }
  return {
    isComplete: () => mode === "solve" ? (slots.length === 2 && slots.every((s) => s.val != null)) : moved,
    check: () => mode === "solve" ? { correct: solveCorrect(), value: allVals() } : { correct: plotCorrect(), value: handles.map((H) => H.pts.map((p) => p.slice())) },
    getValue: () => mode === "solve" ? allVals() : handles.map((H) => H.pts.map((p) => p.slice())),
    setValue: (v) => {
      if (mode === "solve") { if (!Array.isArray(v)) return; slots.forEach((s, i) => { s.val = v[i] != null ? Number(v[i]) : null; }); renderSlots(); return; }
      moved = true;
      let per = v;
      if (Array.isArray(v) && Array.isArray(v[0]) && typeof v[0][0] === "number") per = [v];
      handles.forEach((H, k) => { if (per[k]) { H.pts = [per[k][0].map(Number), per[k][1].map(Number)]; redrawH(H); } });
    },
    getState: () => mode === "solve" ? ({ value: allVals(), target: answer }) : ({ value: handles.map((H) => H.pts.map((p) => p.slice())), target: editables.map((l) => ({ a: l.a, b: l.b, c: l.c })) }),
    reset: () => { if (mode === "solve") { slots.forEach((s) => (s.val = null)); renderSlots(); } else { moved = false; handles.forEach((H) => { H.pts = [H.l.p1.slice(), H.l.p2.slice()]; redrawH(H); }); } },
    reveal: () => {
      locked = true;
      if (mode === "solve") { slots.forEach((s, i) => (s.val = answer[i])); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
      else handles.forEach((H) => { const pts = twoPointsOn(H.l.a, H.l.b, H.l.c); if (pts) { H.pts = pts; redrawH(H); } });
    }
  };
}

// ── L17 Matrices as Systems / Solving Systems (bespoke, per fidelity gate) ──
// The matrix equation A·[x;y] = [e;f] IS the system a·x + b·y = e, c·x + d·y = f.
// Two forms, matching Brilliant's "Matrices as Systems" + "Solving Systems":
//   system → the matrix eq is shown static on top; below it the SYSTEM is written
//            out with the coefficients (blanks:"coef", default) or the RHS
//            (blanks:"rhs") as tile-slots — expand the matrix product into the
//            scalar equations (Brilliant "complete the result / the system").
//   solve  → [A]·[▢;▢] = [b]: fill the INPUT vector from tiles so A·[x,y] = b
//            (Brilliant "find the vector that transforms to the target"); graded
//            by the EQUATION A·[x,y]==b (not a memorized pair); optional dashed
//            target-vector graph.
// spec: { form, matrix:[[a,b],[c,d]], target:[e,f], blanks?, answer?(solve),
//         tiles?:[min,max]|[vals], graph? }.
function buildSysSolve(mountEl, spec, cbs) {
  let locked = false;
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-ss";
  const M = (spec.matrix || [[1, 0], [0, 1]]).map((r) => r.map(Number));
  const target = (spec.target || [0, 0]).map(Number);
  const form = spec.form || "solve";
  const fmt = (n) => { const r = Math.round(n * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  function parseTile(s) { if (s == null) return null; const t = ("" + s).trim().replace(/−/g, "-").replace(/\s/g, ""); const m = t.match(/^(-?\d+)\/(\d+)$/); if (m) return Number(m[1]) / Number(m[2]); const n = Number(t); return Number.isFinite(n) ? n : null; }
  function brk(t) { const s = document.createElement("span"); s.className = "ts-vt-bracket ts-mv-brk"; s.textContent = t; return s; }
  function staticMat(m) { const w = document.createElement("span"); w.className = "ts-mv-mat"; w.appendChild(brk("[")); const gr = document.createElement("div"); gr.className = "ts-mv-mgrid"; for (let r = 0; r < 2; r++) for (let c = 0; c < 2; c++) { const d = document.createElement("div"); d.className = "ts-mv-num"; d.textContent = fmt(m[r][c]); gr.appendChild(d); } w.appendChild(gr); w.appendChild(brk("]")); return w; }
  function staticVec(v) { const w = document.createElement("span"); w.className = "ts-mv-vec"; w.appendChild(brk("[")); const col = document.createElement("div"); col.className = "ts-mv-vcol"; v.forEach((n) => { const d = document.createElement("div"); d.className = "ts-mv-num" + (typeof n === "string" ? " ts-ss-var" : ""); d.textContent = (typeof n === "number") ? fmt(n) : n; col.appendChild(d); }); w.appendChild(col); w.appendChild(brk("]")); return w; }
  function op(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.textContent = t; return s; }
  const slots = [];
  function firstEmpty() { for (let i = 0; i < slots.length; i++) if (slots[i].val == null) return i; return -1; }
  function renderSlots() { slots.forEach((s) => { if (s.val == null) { s.el.textContent = ""; s.el.classList.add("is-empty"); s.el.classList.remove("is-filled"); } else { s.el.textContent = fmt(parseTile(s.val)); s.el.classList.remove("is-empty"); s.el.classList.add("is-filled"); } }); }
  function makeSlot(cls) { const s = document.createElement("div"); s.className = "ts-vt-slot " + (cls || "") + " is-empty"; const idx = slots.length; s.addEventListener("click", () => { if (locked) return; if (slots[idx].val != null) { slots[idx].val = null; renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); } }); slots.push({ el: s, val: null }); return s; }
  let answer = [];
  if (form === "solve") {
    if (spec.graph && spec.graph !== false) {
      const gspec = Object.assign({ xmin: -4, xmax: 4, ymin: -4, ymax: 4, w: 280, grid: { step: 1 } }, spec.graph === true ? {} : spec.graph);
      const plotW = (gspec.w || 280) - 68, xr = gspec.xmax - gspec.xmin, yr = gspec.ymax - gspec.ymin;
      if (!gspec.h) gspec.h = Math.round(60 + plotW * (yr / xr));
      const g = lineGraphSVG(gspec);
      const line = g.mkIn("line", { stroke: "#f5a04a", "stroke-width": 2.8, "stroke-linecap": "round", "stroke-dasharray": "6 4" });
      const head = g.mkIn("path", { fill: "#f5a04a" });
      const x1 = g.X(0), y1 = g.Y(0), x2 = g.X(target[0]), y2 = g.Y(target[1]);
      line.setAttribute("x1", x1); line.setAttribute("y1", y1); line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      const ang = Math.atan2(y2 - y1, x2 - x1), hl = 9, hw = 5.5, bx = x2 - hl * Math.cos(ang), by = y2 - hl * Math.sin(ang);
      head.setAttribute("d", "M " + x2.toFixed(1) + " " + y2.toFixed(1) + " L " + (bx - hw * Math.sin(ang)).toFixed(1) + " " + (by + hw * Math.cos(ang)).toFixed(1) + " L " + (bx + hw * Math.sin(ang)).toFixed(1) + " " + (by - hw * Math.cos(ang)).toFixed(1) + " Z");
      const tl = g.mkIn("text", { x: x2 + 8, y: y2 - 4, fill: "#f5a04a", "font-size": 13, "font-weight": 700, "font-style": "italic" }); tl.textContent = "b";
      stage.appendChild(g.el);
    }
    const eq = document.createElement("div"); eq.className = "ts-mv-eq ts-ss-eq";
    eq.appendChild(staticMat(M));
    const vw = document.createElement("span"); vw.className = "ts-mv-vec"; vw.appendChild(brk("["));
    const col = document.createElement("div"); col.className = "ts-mv-vcol"; col.appendChild(makeSlot("ts-mv-slot")); col.appendChild(makeSlot("ts-mv-slot")); vw.appendChild(col); vw.appendChild(brk("]"));
    eq.appendChild(vw); eq.appendChild(op("=")); eq.appendChild(staticVec(target));
    stage.appendChild(eq);
    answer = (spec.answer || [0, 0]).map(Number);
  } else if (form === "solveSystem") {
    // the system is shown as two scalar equations; fill x and y (Brilliant
    // "What is the solution to this system?"). Graded by A·[x,y] == target.
    const sys = document.createElement("div"); sys.className = "ts-ss-sys";
    const vlabel = (t) => { const s = document.createElement("span"); s.className = "ts-ss-var"; s.textContent = t; return s; };
    const stat = (n) => { const s = document.createElement("span"); s.className = "ts-ss-static"; s.textContent = fmt(n); return s; };
    for (let r = 0; r < 2; r++) {
      const row = document.createElement("div"); row.className = "ts-ss-row";
      row.appendChild(stat(M[r][0])); row.appendChild(vlabel("x")); row.appendChild(op("+"));
      row.appendChild(stat(M[r][1])); row.appendChild(vlabel("y")); row.appendChild(op("="));
      row.appendChild(stat(target[r]));
      sys.appendChild(row);
    }
    const solrow = document.createElement("div"); solrow.className = "ts-ss-row ts-ss-solrow";
    solrow.appendChild(vlabel("x")); solrow.appendChild(op("=")); solrow.appendChild(makeSlot("ts-ss-cslot"));
    const andsp = document.createElement("span"); andsp.className = "ts-ss-and"; andsp.textContent = "and"; solrow.appendChild(andsp);
    solrow.appendChild(vlabel("y")); solrow.appendChild(op("=")); solrow.appendChild(makeSlot("ts-ss-cslot"));
    sys.appendChild(solrow);
    stage.appendChild(sys);
    answer = (spec.answer || [0, 0]).map(Number);
  } else {
    const top = document.createElement("div"); top.className = "ts-mv-eq ts-ss-top";
    top.appendChild(staticMat(M)); top.appendChild(staticVec(["x", "y"])); top.appendChild(op("=")); top.appendChild(staticVec(target));
    stage.appendChild(top);
    const conn = document.createElement("div"); conn.className = "ts-ss-connect"; conn.textContent = spec.connect || "is the system"; stage.appendChild(conn);
    const blanks = spec.blanks || "coef";
    const sys = document.createElement("div"); sys.className = "ts-ss-sys";
    const vlabel = (t) => { const s = document.createElement("span"); s.className = "ts-ss-var"; s.textContent = t; return s; };
    const stat = (n) => { const s = document.createElement("span"); s.className = "ts-ss-static"; s.textContent = fmt(n); return s; };
    const ansArr = [];
    const blankCoef = blanks === "coef" || blanks === "all";
    const blankRhs = blanks === "rhs" || blanks === "all";
    for (let r = 0; r < 2; r++) {
      const row = document.createElement("div"); row.className = "ts-ss-row";
      if (blankCoef) { row.appendChild(makeSlot("ts-ss-cslot")); ansArr.push(M[r][0]); } else row.appendChild(stat(M[r][0]));
      row.appendChild(vlabel("x")); row.appendChild(op("+"));
      if (blankCoef) { row.appendChild(makeSlot("ts-ss-cslot")); ansArr.push(M[r][1]); } else row.appendChild(stat(M[r][1]));
      row.appendChild(vlabel("y")); row.appendChild(op("="));
      if (blankRhs) { row.appendChild(makeSlot("ts-ss-cslot")); ansArr.push(target[r]); } else row.appendChild(stat(target[r]));
      sys.appendChild(row);
    }
    stage.appendChild(sys);
    answer = ansArr;
  }
  const pal = document.createElement("div"); pal.className = "ts-vt-pal";
  let tileList = spec.tiles;
  if (Array.isArray(tileList) && tileList.length === 2 && typeof tileList[0] === "number") { const lo = tileList[0], hi = tileList[1]; tileList = []; for (let v = lo; v <= hi; v++) tileList.push(v); }
  if (!Array.isArray(tileList)) tileList = [-4, -3, -2, -1, 0, 1, 2, 3, 4];
  function done() { if (firstEmpty() < 0 && cbs && cbs.onComplete) cbs.onComplete(); }
  tileList.forEach((tv) => { const t = document.createElement("div"); t.className = "ts-vt-tile"; t.textContent = fmt(parseTile(tv)); t.addEventListener("click", () => { if (locked) return; const i = firstEmpty(); if (i >= 0) { slots[i].val = String(tv); renderSlots(); if (cbs && cbs.onInteract) cbs.onInteract(); done(); } }); pal.appendChild(t); });
  stage.appendChild(pal); mountEl.appendChild(stage); renderSlots();
  const allVals = () => slots.map((s) => s.val);
  function correct() {
    if (slots.some((s) => s.val == null)) return false;
    if (form === "solve" || form === "solveSystem") { const x = parseTile(slots[0].val), y = parseTile(slots[1].val); if (x == null || y == null) return false; return (M[0][0] * x + M[0][1] * y === target[0]) && (M[1][0] * x + M[1][1] * y === target[1]); }
    return slots.every((s, i) => { const v = parseTile(s.val); return v != null && v === answer[i]; });
  }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; }); renderSlots(); },
    getState: () => ({ value: allVals(), target: answer.slice() }),
    reset: () => { slots.forEach((s) => (s.val = null)); renderSlots(); },
    reveal: () => { locked = true; slots.forEach((s, i) => (s.val = String(answer[i]))); renderSlots(); slots.forEach((s) => s.el.classList.add("is-answer")); }
  };
}

// ── L17.2 Matrices as Systems of Equations (bespoke, per fidelity gate) ──
// "Complete the system / the matrix equation" — a TYPED keypad interaction
// (Brilliant's MathLive keypad: digits, x, y, +, −, /, negatives). Blanks can be
// scalar constants (5, −8), FRACTIONS (1/2), or symbolic linear expressions
// (2x+3y, −4x+2y). Every blank is graded by compiling BOTH the typed value and the
// authored answer and sampling them at several (x,y) points — uniform for numbers,
// fractions, and expressions (NOT a tile-pick, so no typed→tiles flatten).
// spec: { lines:[{parts:[ part, ... ]}], answers:[str,...], keys?:[str,...] }
//   part: {t:"…"} static text/operator | {mat:[[…]], blanks?:[[r,c],…]} 2×N matrix
//         | {vec:[…], blanks?:[i,…]} column | {blank:true} standalone input.
//   Blanks are numbered in reading order (lines top→bottom, parts left→right, a
//   matrix's `blanks` list order, a vector's `blanks` list order); answers[] matches.
function buildSysMatrix(mountEl, spec, cbs) {
  let locked = false;
  const answers = (spec.answers || []).map((a) => String(a));
  const keys = spec.keys || ["−"];
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-vt ts-sm";
  const fmt = (n) => { const r = Math.round(Number(n) * 100) / 100; return String(r).replace(/-(\d)/g, "−$1"); };
  const disp = (v) => (typeof v === "number") ? fmt(v) : String(v).replace(/-/g, "−");
  // Variables the blanks may use (default x,y). L17.4 P2 passes vars:["a","b","c","d"]
  // to grade the SYMBOLIC general inverse 1/(ad−bc)·[[d,−b],[−c,a]]; spec.pts must then
  // be 4-tuples. With the default vars this path is byte-identical to the original
  // x,y grading (same regexes, same new Function(x,y), same f(1,1) probe, same pts).
  const vars = (spec.vars && spec.vars.length) ? spec.vars.slice() : ["x", "y"];
  const vc = vars.join("");
  const reOK = new RegExp("^[-+*/().0-9" + vc + "]+$");
  const reA = new RegExp("(\\d)([" + vc + "(])", "g");
  const reB = new RegExp("([" + vc + ")])(\\()", "g");
  const reC = new RegExp("([" + vc + ")])([" + vc + "])", "g");
  const reD = new RegExp("(\\))([0-9" + vc + "])", "g");
  function compile(str) {
    if (str == null) return null;
    let t = ("" + str).replace(/−/g, "-").replace(/×/g, "*").replace(/\s+/g, "");
    if (!t || !reOK.test(t)) return null;
    t = t.replace(reA, "$1*$2").replace(reB, "$1*$2").replace(reC, "$1*$2").replace(reD, "$1*$2");
    try { const f = new Function(vars.join(","), "return (" + t + ");"); if (!Number.isFinite(f.apply(null, vars.map(() => 1)))) return null; return f; } catch (e) { return null; }
  }
  const pts = spec.pts || [[1, 0], [0, 1], [2, 3], [-1, 2], [3, -2]];
  function sampleEq(a, b) { const fa = compile(a), fb = compile(b); if (!fa || !fb) return false; return pts.every((pt) => Math.abs(fa.apply(null, pt) - fb.apply(null, pt)) < 1e-9); }
  const slots = [];  // {val, el}
  let lastInput = null;
  function mkInput(cls) {
    const inp = document.createElement("input"); inp.type = "text"; inp.className = "ts-sf-input ts-sm-in" + (cls ? " " + cls : ""); inp.autocomplete = "off";
    inp.style.height = "38px"; inp.style.fontSize = "18px"; inp.style.width = cls === "wide" ? "108px" : "62px"; inp.style.margin = "0 3px";
    const idx = slots.length; slots.push({ val: null, el: inp });
    inp.addEventListener("focus", () => { lastInput = inp; });
    inp.addEventListener("input", () => { if (locked) return; slots[idx].val = inp.value.trim() || null; if (cbs && cbs.onInteract) cbs.onInteract(); if (slots.every((s) => s.val != null) && cbs && cbs.onComplete) cbs.onComplete(); });
    return inp;
  }
  function brk(t) { const s = document.createElement("span"); s.className = "ts-vt-bracket ts-mv-brk"; s.textContent = t; return s; }
  function op(t) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.style.margin = "0 6px"; s.textContent = t; return s; }
  function renderPart(part) {
    if (part.t != null) { const s = document.createElement("span"); s.className = "ts-vt-op"; s.style.margin = "0 4px"; s.textContent = part.t; return s; }
    if (part.blank) return mkInput("wide");
    if (part.mat) {
      const bl = (part.blanks || []).map((b) => b[0] + "," + b[1]);
      const w = document.createElement("span"); w.className = "ts-mv-mat"; w.appendChild(brk("["));
      const gr = document.createElement("div"); gr.className = "ts-mv-mgrid"; gr.style.gridTemplateColumns = "repeat(" + part.mat[0].length + ", 1fr)";
      for (let r = 0; r < part.mat.length; r++) for (let c = 0; c < part.mat[0].length; c++) {
        if (bl.indexOf(r + "," + c) >= 0) { gr.appendChild(mkInput()); }
        else { const d = document.createElement("div"); d.className = "ts-mv-num"; d.textContent = disp(part.mat[r][c]); gr.appendChild(d); }
      }
      w.appendChild(gr); w.appendChild(brk("]")); return w;
    }
    if (part.vec) {
      const bl = part.blanks || [];
      const w = document.createElement("span"); w.className = "ts-mv-vec"; w.appendChild(brk("["));
      const col = document.createElement("div"); col.className = "ts-mv-vcol";
      for (let i = 0; i < part.vec.length; i++) {
        if (bl.indexOf(i) >= 0) { col.appendChild(mkInput("wide")); }
        else { const d = document.createElement("div"); d.className = "ts-mv-num"; d.textContent = disp(part.vec[i]); col.appendChild(d); }
      }
      w.appendChild(col); w.appendChild(brk("]")); return w;
    }
    return document.createElement("span");
  }
  (spec.lines || []).forEach((ln) => {
    const row = document.createElement("div"); row.className = "ts-mv-eq ts-sm-line";
    row.style.display = "flex"; row.style.alignItems = "center"; row.style.justifyContent = "center"; row.style.gap = "2px"; row.style.margin = "6px 0";
    (ln.parts || []).forEach((p) => row.appendChild(renderPart(p)));
    stage.appendChild(row);
  });
  // typed keypad (fidelity to Brilliant's on-screen keys; inputs also accept typing)
  const pad = document.createElement("div"); pad.className = "ts-sm-pad";
  pad.style.display = "flex"; pad.style.flexWrap = "wrap"; pad.style.gap = "6px"; pad.style.justifyContent = "center"; pad.style.maxWidth = "360px"; pad.style.margin = "14px auto 0";
  const keyList = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].concat(keys, ["⌫"]);
  keyList.forEach((k) => {
    const b = document.createElement("button"); b.type = "button"; b.className = "ts-sm-key"; b.textContent = k;
    b.style.minWidth = "38px"; b.style.height = "40px"; b.style.fontSize = "18px"; b.style.border = "1.5px solid #d4d9e0"; b.style.borderRadius = "9px"; b.style.background = "#fff"; b.style.cursor = "pointer";
    b.addEventListener("click", () => {
      if (locked || !lastInput) return;
      if (k === "⌫") lastInput.value = lastInput.value.slice(0, -1);
      else lastInput.value += (k === "−" ? "-" : k);
      lastInput.dispatchEvent(new Event("input", { bubbles: true })); lastInput.focus();
    });
    pad.appendChild(b);
  });
  stage.appendChild(pad); mountEl.appendChild(stage);
  const allVals = () => slots.map((s) => s.val);
  function correct() { return slots.length === answers.length && slots.every((s, i) => s.val != null && sampleEq(s.val, answers[i])); }
  return {
    isComplete: () => slots.every((s) => s.val != null),
    check: () => ({ correct: correct(), value: allVals() }),
    getValue: allVals,
    setValue: (arr) => { if (!Array.isArray(arr)) return; slots.forEach((s, i) => { s.val = arr[i] != null ? String(arr[i]) : null; if (s.el) s.el.value = s.val || ""; }); },
    getState: () => ({ value: allVals(), target: answers.slice() }),
    reset: () => { slots.forEach((s) => { s.val = null; if (s.el) s.el.value = ""; }); },
    reveal: (forced) => { locked = true; const av = forced || answers; slots.forEach((s, i) => { s.val = av[i]; if (s.el) { s.el.value = av[i]; s.el.classList.add("is-answer"); } }); }
  };
}

// ── Scientific Thinking: real meshed-gear widget (bespoke, per fidelity gate) ──
// Renders actual toothed wheels that touch and spin in correct relative directions
// (tooth-frequency matched so meshing reads true). Two modes:
//   options[]  → direction question (spinning gears + answer buttons, graded by answerIndex)
//   answer[]   → tap-select ("pick every gear that turns like the driver"), graded as a set.
function gearOutlinePath(cx, cy, teeth, ro, ri) {
  const step = Math.PI * 2 / teeth;
  const fr = [0.03, 0.17, 0.33, 0.47], rr = [ri, ro, ro, ri];
  let d = "";
  for (let i = 0; i < teeth; i++) {
    for (let k = 0; k < 4; k++) {
      const ang = (i + fr[k]) * step - Math.PI / 2;
      const x = cx + Math.cos(ang) * rr[k], y = cy + Math.sin(ang) * rr[k];
      d += (d ? "L" : "M") + x.toFixed(2) + "," + y.toFixed(2);
    }
  }
  return d + "Z";
}
function buildGearSystem(mountEl, spec, cbs) {
  const NS = "http://www.w3.org/2000/svg";
  const gears = (spec.gears || []).map((g) => ({
    id: String(g.id), x: +g.x, y: +g.y, teeth: g.teeth || 14,
    r: g.r != null ? +g.r : (g.teeth ? g.teeth * 2.7 : 42), color: g.color || null
  }));
  const byId = {}; gears.forEach((g) => byId[g.id] = g);
  const meshes = (spec.meshes || []).map((m) => [String(m[0]), String(m[1])]);
  const driver = spec.driver != null ? String(spec.driver) : gears[0] && gears[0].id;
  const spin = spec.spin !== false;
  const answer = (spec.answer || []).map(String).sort();
  // Angular-velocity propagation: mesh edges flip sign & scale by tooth ratio (tooth-freq
  // matched); lock edges = compound gears on a shared axle → same omega regardless of teeth.
  const locks = (spec.lock || []).map((m) => [String(m[0]), String(m[1])]);
  const adj = {}; gears.forEach((g) => adj[g.id] = []);
  meshes.forEach(([a, b]) => { if (adj[a]) adj[a].push([b, "mesh"]); if (adj[b]) adj[b].push([a, "mesh"]); });
  locks.forEach(([a, b]) => { if (adj[a]) adj[a].push([b, "lock"]); if (adj[b]) adj[b].push([a, "lock"]); });
  const omega = {}, q = [];
  if (byId[driver]) { omega[driver] = 1; q.push(driver); }
  while (q.length) { const u = q.shift(); adj[u].forEach(([v, ty]) => { if (omega[v] == null) { omega[v] = ty === "lock" ? omega[u] : -omega[u] * (byId[u].teeth / byId[v].teeth); q.push(v); } }); }
  gears.forEach((g) => { if (omega[g.id] == null) omega[g.id] = 1; });
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  gears.forEach((g) => { const R = g.r * 1.16; minX = Math.min(minX, g.x - R); maxX = Math.max(maxX, g.x + R); minY = Math.min(minY, g.y - R); maxY = Math.max(maxY, g.y + R); });
  const pad = 14;
  const svg = document.createElementNS(NS, "svg");
  const vbw = maxX - minX + pad * 2, vbh = maxY - minY + pad * 2;
  svg.setAttribute("viewBox", [minX - pad, minY - pad, vbw, vbh].join(" "));
  svg.setAttribute("width", vbw.toFixed(0));
  svg.setAttribute("height", vbh.toFixed(0));
  svg.setAttribute("class", "ts-gears");
  svg.style.cssText = "width:min(540px,92%);height:auto;max-width:100%;display:block;margin:0 auto";
  const st = document.createElementNS(NS, "style");
  st.textContent = "@keyframes ts-gs-cw{to{transform:rotate(360deg)}}@keyframes ts-gs-ccw{to{transform:rotate(-360deg)}}.ts-gear-rot{transform-box:fill-box;transform-origin:center}";
  svg.appendChild(st);
  meshes.forEach(([a, b]) => {
    const ga = byId[a], gb = byId[b]; if (!ga || !gb) return;
    const ln = document.createElementNS(NS, "line");
    ln.setAttribute("x1", ga.x); ln.setAttribute("y1", ga.y); ln.setAttribute("x2", gb.x); ln.setAttribute("y2", gb.y);
    ln.setAttribute("stroke", "#e6e9ef"); ln.setAttribute("stroke-width", "2"); svg.appendChild(ln);
  });
  const COL = { driver: { fill: "#f2c14e", stroke: "#d9a531" }, neutral: { fill: "#c9ced7", stroke: "#aeb4bf" }, picked: { fill: "#8ea2f8", stroke: "#4f6ef7" }, right: { fill: "#8ed6a6", stroke: "#2f9e63" }, wrong: { fill: "#e7a3a3", stroke: "#cf5b5b" } };
  const chosen = new Set(); let locked = false; const nodes = {};
  gears.forEach((g) => {
    const grp = document.createElementNS(NS, "g");
    const rot = document.createElementNS(NS, "g"); rot.setAttribute("class", "ts-gear-rot");
    if (spin) rot.style.animation = "ts-gs-" + (omega[g.id] > 0 ? "cw" : "ccw") + " " + (5.4 / Math.abs(omega[g.id])).toFixed(2) + "s linear infinite";
    const ro = g.r, ri = g.r * 0.86;
    const body = document.createElementNS(NS, "path"); body.setAttribute("d", gearOutlinePath(g.x, g.y, g.teeth, ro, ri)); body.setAttribute("stroke-width", "2.5"); body.setAttribute("stroke-linejoin", "round"); rot.appendChild(body);
    const hub = document.createElementNS(NS, "circle"); hub.setAttribute("cx", g.x); hub.setAttribute("cy", g.y); hub.setAttribute("r", g.r * 0.5); hub.setAttribute("fill", "rgba(0,0,0,0.06)"); hub.setAttribute("pointer-events", "none"); rot.appendChild(hub);
    const spoke = document.createElementNS(NS, "line"); spoke.setAttribute("x1", g.x); spoke.setAttribute("y1", g.y); spoke.setAttribute("x2", g.x); spoke.setAttribute("y2", g.y - g.r * 0.72); spoke.setAttribute("stroke", "rgba(0,0,0,0.28)"); spoke.setAttribute("stroke-width", "2.5"); spoke.setAttribute("stroke-linecap", "round"); spoke.setAttribute("pointer-events", "none"); rot.appendChild(spoke);
    const hole = document.createElementNS(NS, "circle"); hole.setAttribute("cx", g.x); hole.setAttribute("cy", g.y); hole.setAttribute("r", g.r * 0.17); hole.setAttribute("fill", "#fff"); hole.setAttribute("stroke", "rgba(0,0,0,0.18)"); hole.setAttribute("stroke-width", "1.5"); hole.setAttribute("pointer-events", "none"); rot.appendChild(hole);
    grp.appendChild(rot);
    if (Array.isArray(spec.answer) && g.id !== driver) {
      const hit = document.createElementNS(NS, "circle"); hit.setAttribute("cx", g.x); hit.setAttribute("cy", g.y); hit.setAttribute("r", g.r); hit.setAttribute("fill", "transparent"); hit.setAttribute("class", "ts-gear-hit"); hit.style.cursor = "pointer";
      hit.addEventListener("click", () => { if (locked) return; if (chosen.has(g.id)) chosen.delete(g.id); else chosen.add(g.id); paint(); LessonSound_default.play("choice"); if (cbs && cbs.onInteract) cbs.onInteract(); if (chosen.size && cbs && cbs.onComplete) cbs.onComplete(); });
      grp.appendChild(hit);
    }
    svg.appendChild(grp); nodes[g.id] = { body };
  });
  function paint(ov) {
    gears.forEach((g) => {
      let c;
      if (ov && ov[g.id]) c = COL[ov[g.id]];
      else c = g.id === driver ? COL.driver : chosen.has(g.id) ? COL.picked : g.color ? { fill: g.color, stroke: "#4f6ef7" } : COL.neutral;
      nodes[g.id].body.setAttribute("fill", c.fill); nodes[g.id].body.setAttribute("stroke", c.stroke);
    });
  }
  paint();
  const stage = document.createElement("div"); stage.className = "ts-fw-stage ts-gears-stage"; stage.appendChild(svg); mountEl.appendChild(stage);
  if (Array.isArray(spec.options)) {
    let picked = -1;
    const row = document.createElement("div"); row.className = "ts-gears-options"; row.style.cssText = "display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-top:18px";
    const btns = spec.options.map((opt, i) => {
      const b = document.createElement("button"); b.type = "button"; b.className = "ts-fw-pickopt"; b.textContent = opt;
      b.addEventListener("click", () => { if (locked) return; picked = i; btns.forEach((x, j) => { x.style.borderColor = j === i ? "#4f6ef7" : ""; x.classList.toggle("is-sel", j === i); }); LessonSound_default.play("choice"); if (cbs && cbs.onInteract) cbs.onInteract(); if (cbs && cbs.onComplete) cbs.onComplete(); });
      row.appendChild(b); return b;
    });
    stage.appendChild(row);
    return {
      isComplete: () => picked >= 0,
      check: () => ({ correct: picked === spec.answerIndex, value: picked }),
      getValue: () => picked,
      getState: () => ({ value: picked, target: spec.answerIndex, label: picked >= 0 ? spec.options[picked] : "none" }),
      reset: () => { picked = -1; btns.forEach((x) => { x.style.borderColor = ""; x.classList.remove("is-sel"); }); },
      reveal: () => { locked = true; btns.forEach((x, j) => x.classList.toggle("is-correct", j === spec.answerIndex)); }
    };
  }
  const value = () => [...chosen].sort();
  return {
    isComplete: () => chosen.size > 0,
    check: () => { const v = value(); return { correct: v.length === answer.length && v.every((x, i) => x === answer[i]), value: v }; },
    getValue: value,
    getState: () => ({ value: value(), target: answer, label: chosen.size ? "selected " + value().join(",") : "none" }),
    reset: () => { chosen.clear(); paint(); },
    reveal: () => { locked = true; const ov = {}; gears.forEach((g) => { if (g.id === driver) { ov[g.id] = "driver"; return; } if (answer.includes(g.id)) ov[g.id] = "right"; else if (chosen.has(g.id)) ov[g.id] = "wrong"; else ov[g.id] = "neutral"; }); paint(ov); }
  };
}
